// src/App.js
import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import {
  Bell,
  Activity,
  CalendarDays,
  Link as LinkIcon,
  TrendingUp,
  Cloud,
  Info,
  X,
  BarChart3,
  Flag,
  RotateCcw,
  Sliders,
  HeartPulse,
  TimerReset,
  Download,
  Plus,
  Minus,

  ChevronDown,
  ChevronRight,
  Check,
  Eye,
  EyeOff,
  Lock,
  Users,
} from "lucide-react";
import "./App.css";
import "./styles/theme.css";
import { api } from "./api";
import LoginScreen from "./components/LoginScreen";
import Onboarding, { hasOnboarded } from "./components/Onboarding";
import GoalsPanel from "./components/GoalsPanel";
import GoalWizard from "./components/GoalWizard";
import CoachChat from "./components/CoachChat";
import MethodSheet from "./components/MethodSheet";
import { useSheetPull } from "./components/useSheetPull";
import { PickerWheel, WHEEL_ITEMH, wpad } from "./components/PickerWheel";
import {
  generatePlan as clientGeneratePlan,
  assessReadiness,
  SESSION_THRESHOLD_MULTIPLIER,
  PLAN_FORMAT_VERSION,
} from "./lib/planGenerator";
import { computeUserBaseline } from "./lib/userBaseline";
import {
  pushSupport, SUPPORT_COPY, fetchPushConfig, currentSubscription,
  enablePush, disablePush, sendTestPush, saveNotifySettings,
} from "./lib/pushClient";
import { matchPlanSessions as clientMatchPlan } from "./lib/matchPlan";
import { applyGateOverrides, isDuplicateMilestone, regenerateAcknowledge, generatorWeeksAvailable, bannerFromReadiness, planHasAppliedChanges } from "./lib/readinessGate";
import { goalTypeFromKm, resolveGoalDistanceKm, validateAdjustGoal } from "./lib/goalInput";
import { weeklyLoadToDate, weeklyLoadVerdict } from "./lib/weeklyLoad";
import { chartStartMs } from "./lib/chartRange";
import { available as healthAvailable, healthLabel, healthSource, syncHealth, uploadActivityFile } from "./lib/healthBridge";

/* =========================
   Constants + helpers
========================= */

const GOAL_KEY = "ultrarunner_goal_v1"; // draft (type + date)
const PLAN_KEY = "ultrarunner_plan_v1"; // saved goal + knobs + reset knobs
const SERVER_PLAN_KEY = "ultrarunner_server_plan_v1"; // full server/client plan object
const SERVER_PLAN_SYNC_KEY = "ultrarunner_server_plan_sync_v1"; // dedupe key for plan→server backfill (per-tab session)
const TAB_KEY = "ultrarunner_active_tab_v1";
const SESSION_EDITS_KEY = "ultrarunner_session_edits_v1"; // { "YYYY-MM-DD|type": { distanceKm, dateISO, notes, updatedAt } }

// Stable key for a planned session (ORIGINAL date + type).
// Used as the identifier in SESSION_EDITS_KEY so edits survive date tweaks.
function sessionEditKey(originalDate, type) {
  const d = originalDate instanceof Date ? originalDate : parseLocalDate(originalDate);
  const iso = `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
  return `${iso}|${type || ""}`;
}
function dateToLocalISO(d) {
  if (!(d instanceof Date)) d = new Date(d);
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
}
const TABS = ["today", "train", "coach", "stats"];

const RUN_TYPES = new Set(["Run", "TrailRun", "Treadmill", "VirtualRun"]);

/* "Was this a run?" — a run-type activity far slower than the athlete runs
   (walking pace) is usually a walk, a GPS fault or a watch left running.
   Until the athlete answers, it stays out of the pace-based finish forecast.
   Steep terrain is exempt: slow is normal at 25+ m climb per km. */
const SUSPECT_MIN_PACE_SEC = 8.5 * 60;
function isSuspectRun(a, comfortPaceSec) {
  if (!a || !RUN_TYPES.has(a.type || "Run")) return false;
  if (!(a.distance_m > 0 && a.moving_time_s > 0)) return false;
  const km = a.distance_m / 1000;
  const pace = a.moving_time_s / km;
  if ((Number(a.elev_m) || 0) / km >= 25) return false;
  const limit = Math.max(SUSPECT_MIN_PACE_SEC, (comfortPaceSec || 6 * 60) * 1.5);
  return pace > limit;
}

/** Where an actual activity came from, read off its engine id. */
function activitySourceLabel(id) {
  const v = String(id == null ? "" : id);
  if (/^\d+$/.test(v)) return "Strava";
  if (v.startsWith("hk-")) return "Apple Health";
  if (v.startsWith("hc-")) return "Health Connect";
  if (v.startsWith("fit-") || v.startsWith("gpx-")) return "File";
  return "Run";
}
// Strava activity types that are hikes/walks. Counted toward weekly volume
// ONLY when the user opts in via the `includeHikesInVolume` setting. Default
// off so flat/road-ultra plans don't get their adherence inflated by
// Sunday strolls. See `/docs/training_methodology.md` §Pillar 4.
const HIKE_TYPES = new Set(["Hike", "Walk"]);
const DAY_MS = 24 * 60 * 60 * 1000;
// Backend URL.
//  - Prod (Vercel): set REACT_APP_API_BASE_URL="" so API calls are same-origin
//    (/api/*), which Vercel rewrites route to Fly. Keeps cookies first-party
//    and avoids mobile-browser cross-site cookie issues during OAuth.
//  - Local dev: leave the env var unset → falls back to http://localhost:3001.
// Use `typeof` so an explicitly-empty string is preserved (JS would otherwise
// coerce "" to false and trip the fallback).
const API_BASE =
  typeof process.env.REACT_APP_API_BASE_URL === "string"
    ? process.env.REACT_APP_API_BASE_URL
    : "http://localhost:3001";
const DAY_NAMES = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

/* --------------------------------------------------------------------
 * Metric-tooltip copy. Kept in module-scope constants so the desktop
 * <Stat/> cards and the mobile v7metricGrid use the EXACT same text —
 * no drift between platforms when the underlying logic changes.
 *
 * Each constant documents the *actual* implementation:
 *   TOOLTIP_WEEKLY_VOLUME   — see `currentWeeklyKm` + `plannedThisWeekKm`
 *   TOOLTIP_COMFORT_PACE    — see `buildWeeklyComfortPace()` +
 *                              `comfortPaceDelta` (4-week rolling baseline)
 *   TOOLTIP_ADHERENCE       — from /api/metrics adherence score
 *   TOOLTIP_GOAL_LIKELIHOOD — from /api/metrics goalLikelihood score
 * -------------------------------------------------------------------- */
const TOOLTIP_WEEKLY_VOLUME =
  "Total km logged this week (Mon–Sun, local time). The delta compares your actual km to the plan's target for this week. Green = on track or ahead, red = behind.";

const TOOLTIP_COMFORT_PACE =
  "Median pace of your easy/moderate runs (6–20 km, slowest 60% of that set) computed over a rolling 28-day window. " +
  "The delta compares THIS week's pace to your median pace of the previous 4 weeks — negative/green means you're running faster than your recent baseline. " +
  "Delta is hidden until you have a pace point this week AND at least 2 pace points in the prior 4 weeks.";

const TOOLTIP_ADHERENCE =
  "Percentage of planned sessions in the last 28 days that you completed within distance and pace tolerance. 100% = every planned session hit. Fixed at 28 days — pulling earlier runs into the Plan vs Actual list below does not change it.";

const TOOLTIP_GOAL_LIKELIHOOD =
  "0–100 score combining plan adherence (55%), pace readiness (30% — gap between your current comfort pace and your target goal pace), and volume readiness (15% — actual vs. planned weekly km). The estimated finish time updates as fitness changes.";

/* -------------------------------------------------------------------
 * Training days — canonical model.
 *
 * `availableDays` is a sorted array of JS-Date day-of-week values
 * (0 = Sunday, 1 = Monday, ..., 6 = Saturday).
 *
 * This is the SINGLE SOURCE OF TRUTH for which days the user can train.
 * Both the Plan Generator wizard and the Goal & Plan Settings screen
 * read/write this same state. The server-side plan generator receives
 * these as day-name strings via `DAY_NAMES_LONG`, and its own assignment
 * rules (long on latest day, B2B consecutive, tempo with buffer) derive
 * the actual session-to-day mapping.
 *
 * Legacy `trainingDays: [{dow, role}]` (which is what the old Goal & Plan
 * Settings dropdowns wrote) is migrated on load — see the PLAN_KEY reader.
 * ------------------------------------------------------------------- */
const DAY_NAMES_LONG = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
// Visual pill order (Mon-first is what users expect) → JS-Date dow values.
const PILL_DOW_ORDER = [1, 2, 3, 4, 5, 6, 0];
const PILL_LABELS = ["M", "T", "W", "T", "F", "S", "S"];
const DEFAULT_AVAILABLE_DAYS = [0, 2, 4]; // Sun, Tue, Thu — matches legacy DEFAULT_TRAINING_DAYS

function sortDow(arr) {
  return [...arr].map(Number).filter(n => Number.isInteger(n) && n >= 0 && n <= 6).sort((a, b) => a - b);
}

/**
 * Migrate legacy `trainingDays: [{dow, role}]` to `availableDays: number[]`.
 * Used on initial load to preserve user settings from before the consolidation.
 */
function availableDaysFromLegacyTrainingDays(trainingDays) {
  if (!Array.isArray(trainingDays)) return null;
  const dows = trainingDays.map(d => Number(d?.dow)).filter(n => Number.isInteger(n) && n >= 0 && n <= 6);
  const unique = Array.from(new Set(dows));
  if (unique.length === 0) return null;
  return unique.sort((a, b) => a - b);
}

/* Zone descriptions for info tooltips */
const ZONE_DESCS = {
  z1: "Zone 1 — Very easy effort. Conversational pace, active recovery.",
  z1z2: "Zone 1–2 — Easy effort. Comfortable pace, builds aerobic base.",
  z2: "Zone 2 — Moderate effort. Steady and sustainable, the bread-and-butter training zone.",
  z2z3: "Zone 2–3 — Moderate-hard. Tempo territory, comfortably uncomfortable.",
  z3: "Zone 3 — Hard effort. Threshold pace, improves lactate clearance.",
  z3z4: "Zone 3–4 — Hard. Near-threshold intervals, race-pace work.",
  z4: "Zone 4 — Very hard. VO2max intervals, short and intense.",
  z4z5: "Zone 4–5 — Near-max effort. Speed work and power intervals.",
  z5: "Zone 5 — Maximum effort. All-out sprints, very short duration.",
  race: "Race effort — Target race intensity. Pacing and execution focus.",
};
const getZoneDesc = (z) => { // eslint-disable-line no-unused-vars
  const key = (z || "").replace(/[–\s]/g, "").toLowerCase();
  return ZONE_DESCS[key] || `Training zone: ${z}`;
};

/* =========================
   Mobile layout hook — returns true when viewport ≤ 640px.
   Used by components that need JS-driven responsive dimensions
   (SVG charts whose viewBox can't be swapped via CSS media queries).
========================= */
function useIsMobileLayout() {
  const getState = () => {
    if (typeof window === "undefined") return false;
    return window.matchMedia("(max-width: 640px)").matches;
  };
  const [isMobile, setIsMobile] = useState(getState);

  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    const mq = window.matchMedia("(max-width: 640px)");
    const recompute = () => setIsMobile(getState());
    recompute();
    if (mq.addEventListener) mq.addEventListener("change", recompute);
    else mq.addListener(recompute); // legacy Safari
    return () => {
      if (mq.removeEventListener) mq.removeEventListener("change", recompute);
      else mq.removeListener(recompute);
    };
  }, []);

  return isMobile;
}

/**
 * Return a sensible default `availableDays` array (dow numbers) of length `count`.
 * Preserves existing days and appends weekend/mid-week candidates as needed.
 * Used when runsPerWeek is increased and the user has fewer available days.
 */
function defaultAvailableDaysForCount(count, current) {
  const base = current && current.length ? sortDow(current) : [...DEFAULT_AVAILABLE_DAYS];
  if (count <= base.length) return base.slice(0, count);
  const used = new Set(base);
  // Candidate order: Sat, Wed, Mon, Fri — adds weekend + mid-week balance first.
  const candidates = [6, 3, 1, 5];
  for (const dow of candidates) {
    if (base.length >= count) break;
    if (!used.has(dow)) { base.push(dow); used.add(dow); }
  }
  return base.sort((a, b) => a - b);
}

/**
 * Compare an active plan's `availableDays` (day-name strings) to the current
 * client-side `availableDays` (dow numbers). Used to show a "regenerate" prompt
 * when they diverge — see Goal & Plan Settings.
 */
function sameDayNameSet(planDayNames, dowArray) {
  if (!Array.isArray(planDayNames) || !Array.isArray(dowArray)) return false;
  const fromNames = new Set(
    planDayNames.map((n) => DAY_NAMES_LONG.indexOf(n)).filter((i) => i >= 0)
  );
  if (fromNames.size !== dowArray.length) return false;
  for (const dow of dowArray) if (!fromNames.has(dow)) return false;
  return true;
}

/**
 * Parse "HH:MM:SS" (or "HH:MM") to total seconds. Returns null on bad input.
 */
function parseFinishToSec(s) {
  if (!s) return null;
  const parts = String(s).split(':').map(Number);
  if (parts.some(isNaN)) return null;
  if (parts.length === 3) return parts[0] * 3600 + parts[1] * 60 + parts[2];
  if (parts.length === 2) return parts[0] * 60 + parts[1];
  return null;
}

/**
 * Types that render no pace / duration. Hills are too variable for a single
 * pace estimate, so vertical sessions prescribe effort (HR + RPE) only.
 */
const NO_PACE_TYPES = new Set(['vertical']);

/**
 * Riegel threshold-pace projection, replicated client-side. Must match
 * thresholdPaceSecPerKm in src/lib/planGenerator.js (exponent 1.30).
 * Projects ultra goal time back to a half-marathon-equivalent time, which
 * approximates threshold/tempo pace for a trained runner.
 */
function thresholdPaceSecPerKmClient(raceDistanceKm, targetFinishSec) {
  if (!(raceDistanceKm > 0) || !(targetFinishSec > 0)) return null;
  const HM_KM = 21.0975;
  const RIEGEL = 1.30;
  const halfSec = targetFinishSec * Math.pow(HM_KM / raceDistanceKm, RIEGEL);
  return halfSec / HM_KM;
}

/**
 * Get plannedDuration (minutes) for a session. THRESHOLD-ANCHORED.
 *
 * Resolution:
 *   1. Duration-based types ('hike', 'time-on-feet') → baked-in value.
 *   2. 'vertical' → null (no pace).
 *   3. Everything else → `threshold_pace × type_multiplier`, where:
 *      a. threshold_pace = observedThresholdPaceSec (fastest recent 3–15 km
 *         pace from Strava) if available — reflects actual current fitness.
 *      b. Else threshold_pace = Riegel 1.30 projection from plan.targetFinish
 *         + plan.raceDistance → half-marathon distance.
 *
 * NO separate terrain multiplier — the goal time encoded by the user already
 * reflects terrain. Applying one on top double-counts.
 *
 * This function fully OVERRIDES any baked-in `plannedDuration` from the
 * server generator for km-based, non-vertical sessions. Baked-in values
 * are only the cold-start fallback; at render time we prefer observed
 * data so Tempo / Easy / Long / B2B / Recovery all scale with the runner's
 * actual fitness as it evolves in Strava.
 *
 * @param observedThresholdPaceSec  User's observed threshold pace (sec/km),
 *                                  or null if not yet computed.
 */
function derivePlannedDurationMin(session, plan, observedThresholdPaceSec = null) {
  if (!session) return null;
  if (NO_PACE_TYPES.has(session.type)) return null;

  // Duration-based types use their baked-in duration.
  if (session.type === 'hike' || session.type === 'time-on-feet') {
    return session.plannedDuration > 0 ? session.plannedDuration : null;
  }

  if (!(session.plannedKm > 0)) return null;

  // Resolve threshold pace: observed → Riegel fallback.
  let thresholdPaceSec = observedThresholdPaceSec > 0 ? observedThresholdPaceSec : null;
  if (thresholdPaceSec == null) {
    if (!plan) return null;
    const finishSec = parseFinishToSec(plan.targetFinish);
    if (!finishSec || !(plan.raceDistance > 0)) return null;
    thresholdPaceSec = thresholdPaceSecPerKmClient(plan.raceDistance, finishSec);
  }
  if (thresholdPaceSec == null) return null;

  const mult = SESSION_THRESHOLD_MULTIPLIER[session.type];
  if (mult == null) return null;

  const paceSec = thresholdPaceSec * mult;
  return Math.round((session.plannedKm * paceSec) / 60);
}

/**
 * Current canonical plan-format version.
 * Must match server/services/planGenerator.js → generatePlan() `planFormatVersion`.
 *
 * Bumped 2 → 7 on 2026-04-28 with the cyclic tempo placement fix. v6 plans
 * placed tempo on the calendar day immediately after a Sun long run because
 * the buffer math used naive |a-b| within a Mon-start week (treating Sun →
 * next-Mon as 6 days apart). Forcing regeneration so users on stale plans
 * pick up the fix instead of keeping their buggy schedule.
 *
 * Bumped 7 → 8 on 2026-05-20 for the baseline-driven generator (readiness
 * gate, pace sanity guard, finish-only mode, observed-pace anchoring). A
 * stored v7 plan is flagged 'old-generator' and auto-regenerates on next
 * load via regeneratePlanWithCurrentDays(). It carries forward only an
 * override the athlete made explicitly on that plan (acknowledgedStretch);
 * if the gate refuses, the gate screen opens rather than a silent bypass.
 *
 * PLAN_FORMAT_VERSION is IMPORTED from the generator mirror (sense-check
 * 2026-09-19 M1): a hand-copied constant sat at 10 while the generator
 * emitted 11, so stale plans were never detected.
 */

/**
 * Diagnose whether a server plan is stale. Returns a string reason if stale,
 * or null if the plan is current. Three signals:
 *
 *   - (a) Missing/outdated planFormatVersion — the server generating this plan
 *     was an older binary that didn't ship the current generator pipeline.
 *   - (b) Session dates land on days NOT in the plan's OWN availableDays —
 *     the fingerprint of the pre-fix toISOString() timezone bug. A server
 *     process running the OLD planGenerator.js will produce this even after
 *     the file on disk was edited, because Node.js caches modules until the
 *     process restarts.
 *   - (c) User's currently selected availableDays diverges from the plan's.
 *
 * Reasons (a) and (b) indicate "restart the server + regenerate". Reason (c)
 * means "the user changed days after generation — just regenerate".
 */
function diagnosePlanStale(serverPlan, currentAvailableDays) {
  if (!serverPlan) return null;

  if ((serverPlan.planFormatVersion ?? 1) < PLAN_FORMAT_VERSION) {
    return 'old-generator';
  }

  // (b) dates on wrong days — strong signal the server is running pre-fix code.
  const planDowSet = new Set(
    (serverPlan.availableDays || [])
      .map((n) => DAY_NAMES_LONG.indexOf(n))
      .filter((i) => i >= 0)
  );
  for (const w of serverPlan.weeks || []) {
    for (const s of w.sessions || []) {
      const d = typeof s.date === 'string' ? (() => {
        const [y, m, x] = s.date.split('-').map(Number);
        return new Date(y, m - 1, x);
      })() : null;
      if (!d) continue;
      if (!planDowSet.has(d.getDay())) return 'corrupt-dates';
    }
  }

  if (!sameDayNameSet(serverPlan.availableDays, currentAvailableDays)) {
    return 'days-changed';
  }

  return null;
}

/**
 * Reusable multi-select day pills — Mon-first visual order, dow-number state.
 * Used in BOTH the Plan Generator wizard and Goal & Plan Settings.
 * Single component ⇒ same behavior, same styling, no drift.
 */
function DayPills({ value, onChange, minDays = 0, longRunDow = null, onLongRunChange }) {
  const selectedSet = new Set((value || []).map(Number));
  const pressTimer = useRef(null);
  const longFired = useRef(false);

  const toggleDow = (dow) => {
    const isSelected = selectedSet.has(dow);
    if (isSelected && selectedSet.size <= minDays) return; // enforce minimum
    const next = isSelected ? [...selectedSet].filter((d) => d !== dow) : [...selectedSet, dow];
    // If we just removed the pinned long-run day, clear the pin.
    if (isSelected && dow === longRunDow && onLongRunChange) onLongRunChange(null);
    onChange(next.sort((a, b) => a - b));
  };

  // Long-press (or right-click on desktop) marks a selected day as the
  // preferred long-run day. Tapping a day that's already the long-run pin
  // again clears it.
  const setLong = (dow) => {
    if (!onLongRunChange) return;
    if (!selectedSet.has(dow)) onChange([...selectedSet, dow].sort((a, b) => a - b));
    onLongRunChange(longRunDow === dow ? null : dow);
  };

  return (
    <div style={{ display: "flex", gap: 6 }}>
      {PILL_DOW_ORDER.map((dow, visIdx) => {
        const selected = selectedSet.has(dow);
        const isLong = dow === longRunDow;
        return (
          <button
            key={dow}
            type="button"
            className={`pgDayPill${isLong ? " pgDayPill--long" : ""}`}
            style={{
              borderColor: isLong ? "var(--viz-race)" : selected ? "var(--accent-base)" : "var(--line-default)",
              background: isLong ? "rgb(var(--race-rgb) / 0.14)" : selected ? "var(--surface-raised)" : "transparent",
              color: isLong ? "var(--viz-race)" : selected ? "var(--accent-base)" : "var(--content-tertiary)",
            }}
            onClick={() => { if (!longFired.current) toggleDow(dow); longFired.current = false; }}
            onContextMenu={(e) => { e.preventDefault(); setLong(dow); }}
            onTouchStart={() => { longFired.current = false; pressTimer.current = setTimeout(() => { longFired.current = true; setLong(dow); }, 450); }}
            onTouchEnd={() => clearTimeout(pressTimer.current)}
            onTouchMove={() => clearTimeout(pressTimer.current)}
            aria-label={DAY_NAMES_LONG[dow] + (isLong ? " (long run day)" : "")}
            title={isLong ? DAY_NAMES_LONG[dow] + " — long run" : DAY_NAMES_LONG[dow]}
          >
            {PILL_LABELS[visIdx]}
            {isLong && <span className="pgDayPill__L">L</span>}
          </button>
        );
      })}
    </div>
  );
}

function clamp(n, lo, hi) {
  return Math.max(lo, Math.min(hi, n));
}
function round1(n) {
  return Math.round(Number(n || 0) * 10) / 10;
}
function round0(n) {
  return Math.round(Number(n || 0));
}
function fmt1(n) {
  const x = Number(n || 0);
  return isFinite(x) ? x.toFixed(1) : "0.0";
}
function fmtDelta1(n) {
  const x = Number(n || 0);
  if (!isFinite(x)) return "n/a";
  const sign = x >= 0 ? "+" : "";
  return `${sign}${fmt1(x)}`;
}
/** Format seconds (or minutes when `unit` === "min") to human-readable duration.
 *  < 1 hr → "42 min"   ≥ 1 hr → "1h 22m"   0/null → null */
function fmtDuration(val, unit = "sec") {
  if (val == null || !isFinite(val) || val <= 0) return null;
  const totalMin = unit === "min" ? Math.round(val) : Math.round(val / 60);
  if (totalMin <= 0) return null;
  if (totalMin < 60) return `${totalMin} min`;
  const h = Math.floor(totalMin / 60);
  const m = totalMin % 60;
  return m > 0 ? `${h}h ${String(m).padStart(2, "0")}m` : `${h}h`;
}
function pad2(x) {
  return String(x).padStart(2, "0");
}
function formatDateLong(d) {
  const dt = new Date(d);
  return `${pad2(dt.getDate())}.${pad2(dt.getMonth() + 1)}.${dt.getFullYear()}`;
}
function formatDateShort(d) {
  // Includes the weekday prefix everywhere a "short" date appears so the
  // user immediately knows which day of the week a session / race / row
  // sits on. Example: "Wed 28 Apr". Slightly wider than the old
  // "28 Apr" — every existing caller was checked to confirm there's
  // room. If a future caller needs the old form back, use the
  // formatDateNoDow helper below.
  const dd = new Date(d);
  return dd.toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short" });
}
/**
 * Relative-day phrasing for an upcoming or just-past session.
 *
 *   today          → "Today"
 *   tomorrow       → "Tomorrow"
 *   yesterday      → "Yesterday"
 *   day after / before → "In 2 days" / "2 days ago"
 *   ≥ ±7 days      → null   (caller should fall back to the absolute date)
 *
 * Used together with formatDateShort to produce strings like
 * "Today · Wed 28 Apr" or "In 3 days · Sat 1 May" for the next-session
 * card on Today and Train. Returning null past a week keeps the card
 * from saying "In 47 days" — at that distance the weekday + date alone
 * is more informative.
 */
function formatRelativeDay(d) {
  if (!d) return null;
  const target = startOfDay(new Date(d));
  const today = startOfDay(new Date());
  const diff = Math.round((+target - +today) / (24 * 60 * 60 * 1000));
  if (diff === 0)  return "Today";
  if (diff === 1)  return "Tomorrow";
  if (diff === -1) return "Yesterday";
  if (diff > 1  && diff <= 7)  return `In ${diff} days`;
  if (diff < -1 && diff >= -7) return `${-diff} days ago`;
  return null;
}
function kmFromMeters(m) {
  return (Number(m || 0) / 1000) || 0;
}
function SheetOverlay({ sheetClass, children }) {
  // Scrollable sheets (wheel picker, week detail) close ONLY via their X /
  // Cancel button — never by tapping the backdrop or swiping. While open we
  // lock background scroll so the wheels can't drag the page or the sheet.
  useEffect(() => {
    document.body.classList.add("pkOpen");
    return () => document.body.classList.remove("pkOpen");
  }, []);
  return (
    <div className="pkOverlay">
      <div className={"pkSheet" + (sheetClass ? " " + sheetClass : "")}>
        {children}
      </div>
    </div>
  );
}

function WheelSheet({ open, title, cols, unit, onSet, onCancel }) {
  const refs = useRef([]);
  if (!open) return null;
  (cols || []).forEach((_, i) => { if (!refs.current[i]) refs.current[i] = { current: null }; });
  const read = () => (cols || []).map((c, i) => {
    const base = c.min || 0;
    const el = refs.current[i] && refs.current[i].current;
    return el ? base + Math.max(0, Math.round(el.scrollTop / WHEEL_ITEMH)) : (c.initial || 0);
  });
  const children = [];
  (cols || []).forEach((c, i) => {
    if (i > 0) children.push(<div className="gwWsep" key={"sep" + i}>{c.sepChar || ":"}</div>);
    children.push(<PickerWheel key={"w" + i} max={c.max} min={c.min || 0} initial={c.initial} refObj={refs.current[i]} />);
  });
  return (
    <SheetOverlay onClose={onCancel}>
        <div className="gwGrip" />
        <div className="gwShTitle">{title}</div>
        <div className="gwWheels">{children}</div>
        {unit ? <div className="gwWunit">{unit}</div> : null}
        <button type="button" className="gwShSet" onClick={() => onSet(read())}>Apply</button>
        <button type="button" className="gwShClear" onClick={onCancel}>Cancel</button>
    </SheetOverlay>
  );
}

function runTypeColor(t) {
  const s = String(t || "").toLowerCase();
  if (s.includes("recovery")) return "var(--viz-recovery)";
  if (s.includes("easy") || s.includes("moderate")) return "var(--viz-easy)";
  if (s.includes("tempo") || s.includes("threshold") || s.includes("interval") || s.includes("hill") || s.includes("vertical")) return "var(--viz-tempo)";
  if (s.includes("long") || s.includes("b2b") || s.includes("time")) return "var(--viz-long)";
  if (s.includes("race")) return "var(--critical)";
  return "var(--viz-recovery)";
}

/* Manual reference (threshold) pace: 2:30–9:00 /km. Anything outside is not
   a threshold pace for this app's runners, and an absurd value would anchor
   every prescribed pace (the worker enforces the same range). */
const REF_PACE_MIN_SEC = 150;
const REF_PACE_MAX_SEC = 540;
function refPaceSec(str) {
  const sec = paceStrToSec(str);
  return sec != null && sec >= REF_PACE_MIN_SEC && sec <= REF_PACE_MAX_SEC ? sec : null;
}

function paceStrToSec(str) {
  if (!str) return null;
  const s = String(str).trim().replace(",", ":").replace(".", ":");
  const m = s.match(/^(\d{1,2}):(\d{1,2})$/);
  if (m) { const mm = +m[1], ss = +m[2]; if (ss < 60) return mm * 60 + ss; }
  const n = Number(s);
  if (isFinite(n) && n > 0) return n < 20 ? Math.round(n * 60) : Math.round(n);
  return null;
}

function secToMMSS(sec) {
  if (!sec || !isFinite(sec) || sec <= 0) return "";
  const m = Math.floor(sec / 60);
  const s = Math.round(sec % 60);
  return `${m}:${pad2(s)}`;
}

function secPerKmToPace(secPerKm) {
  if (!secPerKm || !isFinite(secPerKm) || secPerKm <= 0) return "";
  const m = Math.floor(secPerKm / 60);
  const s = Math.round(secPerKm % 60);
  return `${m}:${pad2(s)}/km`;
}
/** Format a pace delta in seconds as "+M:SS / km" or "-M:SS / km".
 *  Convention: positive = slower than target (bad), negative = faster (good). */
function formatPaceDelta(seconds) {
  const x = Number(seconds);
  if (!isFinite(x)) return "";
  const sign = x >= 0 ? "+" : "-";
  const abs = Math.round(Math.abs(x));
  const m = Math.floor(abs / 60);
  const s = abs % 60;
  return `${sign}${m}:${pad2(s)} / km`;
}
/** Local calendar date 'YYYY-MM-DD' (never toISOString — that is UTC). */
function localIsoDate(d) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}
function secondsToHhmmss(totalSec) {
  if (totalSec == null || !isFinite(totalSec) || totalSec <= 0) return "";
  const sec = Math.round(totalSec);
  const h = Math.floor(sec / 3600);
  const m = Math.floor((sec % 3600) / 60);
  const s = sec % 60;
  return `${pad2(h)}:${pad2(m)}:${pad2(s)}`;
}
function timeStrToSeconds(t) {
  if (!t) return null;
  const parts = String(t).split(":").map(Number);
  if (parts.some((x) => !isFinite(x))) return null;
  if (parts.length === 3) return parts[0] * 3600 + parts[1] * 60 + parts[2];
  if (parts.length === 2) return parts[0] * 60 + parts[1];
  return null;
}
function median(arr) {
  if (!arr?.length) return null;
  const a = [...arr].sort((x, y) => x - y);
  const mid = Math.floor(a.length / 2);
  return a.length % 2 ? a[mid] : (a[mid - 1] + a[mid]) / 2;
}

function hrZoneLabel(avgHr, effectiveMaxHr) {
  if (!avgHr || !effectiveMaxHr || effectiveMaxHr <= 0) return null;
  const pct = avgHr / effectiveMaxHr;
  // Zone thresholds: Z1 <60%, Z2 60-70%, Z3 70-80%, Z4 80-90%, Z5 >90%
  // Colours: Z1 blue-grey, Z2 green, Z3 amber, Z4 orange-red, Z5 deep-red
  if (pct < 0.60) return { zone: "Z1", color: "var(--content-tertiary)" };   // --text-muted
  if (pct < 0.70) return { zone: "Z2", color: "var(--accent-base)" };   // --accent (sage)
  if (pct < 0.80) return { zone: "Z3", color: "var(--caution)" };   // --warning
  if (pct < 0.90) return { zone: "Z4", color: "var(--critical)" };   // --danger (warmer)
  return { zone: "Z5", color: "var(--critical-fill)" };                   // danger deepened
}

/**
 * Pick the best long-run reference for ultra goal-time projection.
 *
 * Returns { refDistKm, refTimeSec, source: "longRun" } for the longest run
 * in the last `windowDays` that is at least `minDistKm`, or null if nothing
 * qualifies.
 */
function pickLongRunReference(runs, windowDays = 60, minDistKm = 18) {
  if (!Array.isArray(runs) || runs.length === 0) return null;
  const cutoff = Date.now() - windowDays * DAY_MS;

  let best = null;
  for (const r of runs) {
    const distKm = kmFromMeters(r.distance_m);
    if (!isFinite(distKm) || distKm < minDistKm) continue;
    const t = new Date(r.start_date).getTime();
    if (!isFinite(t) || t < cutoff) continue;
    const timeSec = r.moving_time_s && r.moving_time_s > 0
      ? r.moving_time_s
      : (r.avg_speed > 0 ? (distKm * 1000) / r.avg_speed : null);
    if (!timeSec || !isFinite(timeSec) || timeSec <= 0) continue;
    if (!best || distKm > best.refDistKm) {
      best = { refDistKm: distKm, refTimeSec: timeSec, source: "longRun" };
    }
  }
  return best;
}

/**
 * Phase 2 fatigue multiplier — distance-based trail-reality adjustment.
 *
 *   ≤ 42.2 km → 1.00  |  42.2–60 → 1.05  |  60–80 → 1.12
 *   80–100   → 1.20  |  100–160 → 1.25  |  > 160 → 1.30
 */
function fatigueMultiplier(goalDistanceKm) {
  if (goalDistanceKm <= 42.2) return 1.00;
  if (goalDistanceKm <= 60)   return 1.05;
  if (goalDistanceKm <= 80)   return 1.12;
  if (goalDistanceKm <= 100)  return 1.20;
  if (goalDistanceKm <= 160)  return 1.25;
  return 1.30;
}

/**
 * Phase 2 flat aid-station buffer: 45 min at 100k, scaled linearly with
 * distance. Zero for marathon and shorter. Returned in seconds.
 */
function aidStationBufferSec(goalDistanceKm) {
  if (goalDistanceKm <= 42.2) return 0;
  return 45 * 60 * (goalDistanceKm / 100);
}

/**
 * Estimate ultra goal time using Riegel + Phase 2 trail adjustments.
 *
 * Phase 1: anchor on longest recent long run (≥18km, last 60 days) rather
 *   than comfortable pace × 13km — real long-run output is the honest
 *   predictor for ultras. Ultra-tuned Riegel exponent ladder.
 *
 * Phase 2: Riegel alone was still ~45–90 min optimistic for real trail
 *   ultras because the math assumes a smooth pace curve; real 100k races
 *   have hikes, nutrition breakdown, and ~45 min of aid-station time that
 *   pace curves can't capture. Phase 2 layers two explicit adjustments on
 *   top of the Riegel projection:
 *     (a) a distance-based fatigue multiplier (up to +30% for 100+ milers)
 *     (b) a flat aid-station buffer (45 min at 100k, scaled linearly)
 *
 * Args:
 *   comfortPaceSecPerKm — used only for the fallback branch
 *   goalDistanceKm      — target race distance
 *   reference           — optional { refDistKm, refTimeSec, source } from
 *                         pickLongRunReference(); if null, falls back
 *
 * Returns total estimated goal time in seconds, or null if inputs unusable.
 */
function estimateGoalTime(comfortPaceSecPerKm, goalDistanceKm, reference = null) {
  if (!goalDistanceKm || goalDistanceKm <= 0) return null;

  let refDistKm, refTimeSec, source;
  if (reference && reference.refDistKm > 0 && reference.refTimeSec > 0) {
    refDistKm = reference.refDistKm;
    refTimeSec = reference.refTimeSec;
    source = reference.source || "longRun";
  } else {
    if (!comfortPaceSecPerKm || comfortPaceSecPerKm <= 0) return null;
    refDistKm = 20;
    refTimeSec = comfortPaceSecPerKm * 0.94 * refDistKm;
    source = "comfortPace";
  }

  // Exponent ladder: gentler when we have a real long-run anchor.
  const longRef = source === "longRun" && refDistKm >= 25;
  let exponent;
  if (goalDistanceKm <= 42.2)       exponent = longRef ? 1.05 : 1.06;
  else if (goalDistanceKm <= 50)    exponent = longRef ? 1.06 : 1.08;
  else if (goalDistanceKm <= 80)    exponent = longRef ? 1.08 : 1.11;
  else if (goalDistanceKm <= 100)   exponent = longRef ? 1.10 : 1.14;
  else                               exponent = longRef ? 1.12 : 1.17;

  const riegelSec = refTimeSec * Math.pow(goalDistanceKm / refDistKm, exponent);

  // Phase 2: trail-reality adjustments layered on top of Riegel.
  const fatigueAdjustedSec = riegelSec * fatigueMultiplier(goalDistanceKm);
  const aidSec = aidStationBufferSec(goalDistanceKm);

  return fatigueAdjustedSec + aidSec;
}
function startOfDay(d) {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x;
}
function startOfWeek(d) {
  const x = startOfDay(d);
  const day = x.getDay(); // 0..6
  const diff = (day === 0 ? -6 : 1) - day; // Monday start
  x.setDate(x.getDate() + diff);
  return x;
}
function addWeeks(d, w) {
  const x = new Date(d);
  x.setDate(x.getDate() + w * 7);
  return x;
}
// Parse date-only "YYYY-MM-DD" strings as local time (not UTC)
function parseLocalDate(s) {
  if (!s) return new Date(NaN);
  if (typeof s === 'string' && !s.includes('T')) return new Date(s + 'T00:00:00');
  return new Date(s);
}
function weeksBetween(a, b) {
  const A = +startOfWeek(a);
  const B = +startOfWeek(b);
  return Math.max(0, Math.ceil((B - A) / (7 * DAY_MS)));
}
function buildWeeksRange(startWeek, endWeek) {
  const s = startOfWeek(startWeek);
  const e = startOfWeek(endWeek);
  const out = [];
  const total = weeksBetween(s, e);
  for (let i = 0; i <= total; i++) out.push(startOfWeek(addWeeks(s, i)));
  return out;
}
function dayKey(d) {
  return +startOfDay(d);
}
function tableWindowLabel(days) {
  return `in last ${days} days`;
}

/* =========================
   ICS calendar export
========================= */
function generateICS(sessions) {
  const escapeICS = (str) => String(str || "").replace(/[,;\\]/g, (m) => "\\" + m);
  const toICSDate = (d) => {
    const dt = new Date(d);
    return `${dt.getFullYear()}${pad2(dt.getMonth() + 1)}${pad2(dt.getDate())}`;
  };
  const uid = () => `${Date.now()}-${Math.random().toString(36).slice(2)}@ultrarunner`;

  const lines = [
    "BEGIN:VCALENDAR",
    "VERSION:2.0",
    "PRODID:-//UltrarunnerApp//EN",
    "CALSCALE:GREGORIAN",
    "METHOD:PUBLISH",
    "X-WR-CALNAME:Ultrarunner Training Plan",
  ];

  for (const s of sessions) {
    if (!s?.date) continue;
    const dateStr = toICSDate(s.date);
    const nextDay = new Date(s.date);
    nextDay.setDate(nextDay.getDate() + 1);
    const endDateStr = toICSDate(nextDay);

    const summary = `${s.title || "Training"} - ${fmt1(s.distanceKm)}km`;
    const description = [
      `Type: ${s.title}`,
      `Distance: ${fmt1(s.distanceKm)}km`,
      `Target Pace: ${s.pace || "n/a"}`,
      `Zone: ${s.zone || "n/a"}`,
      `Est. Time: ${s.timeStr || "n/a"}`,
      s.badge ? `Phase: ${s.badge}` : null,
    ].filter(Boolean).join("\\n");

    lines.push(
      "BEGIN:VEVENT",
      `UID:${uid()}`,
      `DTSTART;VALUE=DATE:${dateStr}`,
      `DTEND;VALUE=DATE:${endDateStr}`,
      `SUMMARY:${escapeICS(summary)}`,
      `DESCRIPTION:${escapeICS(description)}`,
      "STATUS:CONFIRMED",
      "END:VEVENT"
    );
  }

  lines.push("END:VCALENDAR");
  return lines.join("\r\n");
}

function downloadICS(filename, content) {
  const blob = new Blob([content], { type: "text/calendar;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

/* =========================
   fetch helper
========================= */
async function fetchJsonSafe(url, opts = {}) {
  const res = await fetch(url, opts);
  const text = await res.text();
  if (!res.ok) throw new Error(`HTTP ${res.status}: ${text.slice(0, 200)}`);
  const t = text.trim();
  if (!t.startsWith("{") && !t.startsWith("["))
    throw new Error(`Expected JSON but got: ${t.slice(0, 120)}`);
  return JSON.parse(t);
}

function humanizeStravaError(raw) {
  const str = String(raw || "");
  if (/rate limit/i.test(str))
    return "Strava is rate-limited right now (too many requests in a short time). Wait a few minutes, then try Connect again.";
  try { const j = JSON.parse(str); if (j && j.message) return "Strava: " + j.message; } catch (e) {}
  return str.length > 140 ? "Couldn’t reach Strava — please try again." : str;
}

/* =========================
   Pace from run
========================= */
function paceFromRun(r) {
  const distKm = kmFromMeters(r.distance_m);
  if (distKm <= 0) return null;
  if (r.moving_time_s && r.moving_time_s > 0) return r.moving_time_s / distKm;
  if (r.avg_speed && r.avg_speed > 0) return 1000 / r.avg_speed;
  return null;
}

/* =========================
   Weekly aggregation
========================= */
function buildWeeklyActualKm(runs) {
  const byWeek = new Map();
  for (const r of runs || []) {
    const wk = startOfWeek(new Date(r.start_date));
    const k = +wk;
    byWeek.set(k, (byWeek.get(k) || 0) + kmFromMeters(r.distance_m));
  }
  const keys = Array.from(byWeek.keys()).sort((a, b) => a - b);
  return keys.map((k) => ({ x: new Date(Number(k)), y: byWeek.get(k) || 0 }));
}

function buildWeeklyActualSeconds(runs) {
  const byWeek = new Map();
  for (const r of runs || []) {
    const wk = +startOfWeek(new Date(r.start_date));
    byWeek.set(wk, (byWeek.get(wk) || 0) + (r.moving_time_s || 0));
  }
  return byWeek; // Map<weekKey, totalSeconds>
}

function buildWeeklyComfortPace(runs) {
  // 4-week rolling median: at each week point, look back 28 days at all
  // easy runs (6–20km) and compute the median pace. This produces a smooth
  // curve rather than sharp per-week jumps.
  const WINDOW_MS = 28 * 24 * 60 * 60 * 1000; // 28 days

  // Preferred: easy-distance runs (6–20 km) with valid pace, slowest 60%.
  const preferred = (runs || []).filter((r) => {
    const d = kmFromMeters(r.distance_m);
    if (d < 6 || d > 20) return false;
    const p = paceFromRun(r);
    return p && isFinite(p);
  });

  let easyRuns;
  if (preferred.length) {
    // Additionally filter by pace percentile: slowest 60% are "easy"
    const sortedByPace = [...preferred].sort((a, b) => paceFromRun(a) - paceFromRun(b));
    const cutoffIndex = Math.floor(sortedByPace.length * 0.4);
    const easyRunIds = new Set(sortedByPace.slice(cutoffIndex).map((r) => r.id));

    easyRuns = preferred
      .filter((r) => easyRunIds.has(r.id))
      .map((r) => ({ ts: +new Date(r.start_date), pace: paceFromRun(r) }))
      .sort((a, b) => a.ts - b.ts);
  } else {
    // Fallback: any run with a valid pace. Keeps the chart's pace line
    // visible even when the user has only short runs (< 6 km) or very few
    // activities — covers new users and low-volume weeks.
    easyRuns = (runs || [])
      .map((r) => {
        const p = paceFromRun(r);
        return p && isFinite(p)
          ? { ts: +new Date(r.start_date), pace: p }
          : null;
      })
      .filter(Boolean)
      .sort((a, b) => a.ts - b.ts);
  }

  if (!easyRuns.length) return [];

  // Build the set of week keys that have any run activity
  const weekKeys = new Set();
  for (const r of runs || []) {
    weekKeys.add(+startOfWeek(new Date(r.start_date)));
  }
  const sortedWeeks = Array.from(weekKeys).sort((a, b) => a - b);

  return sortedWeeks
    .map((wk) => {
      // End of this week (Sunday night) — look back 28 days from week end
      const weekEnd = wk + 7 * 24 * 60 * 60 * 1000;
      const windowStart = weekEnd - WINDOW_MS;
      const paces = easyRuns
        .filter((r) => r.ts >= windowStart && r.ts < weekEnd)
        .map((r) => r.pace);
      const m = median(paces);
      if (!m || !isFinite(m)) return null;
      return { x: new Date(Number(wk)), y: m };
    })
    .filter(Boolean);
}

/* =========================
   Auto estimates (from Strava)
========================= */
function estimateWeeklyKmNow(actualWeeklyMap) {
  const weeks = Array.from(actualWeeklyMap.keys()).sort((a, b) => a - b);
  const lookback = weeks.slice(-10);

  const nonTrivial = lookback
    .map((k) => actualWeeklyMap.get(k))
    .filter((v) => v != null && isFinite(v) && v >= 10);

  const take = nonTrivial.slice(-4);
  const vals = take.length
    ? take
    : lookback
        .map((k) => actualWeeklyMap.get(k))
        .filter((v) => v != null && isFinite(v) && v > 0)
        .slice(-4);

  if (!vals.length) return 35; // fallback
  const avg = vals.reduce((a, b) => a + b, 0) / vals.length;
  return clamp(avg, 15, 130);
}

function pickCurrentComfortPace(actualComfort) {
  const pts = (actualComfort || []).slice().sort((a, b) => +a.x - +b.x);
  const last = pts.length ? pts[pts.length - 1] : null;
  return last && isFinite(last.y) ? last.y : 6 * 60; // fallback 6:00/km
}

/* =========================
   Races
========================= */
function raceLabel(distanceKm) {
  if (Math.abs(distanceKm - 10) < 0.2) return "10K";
  if (Math.abs(distanceKm - 21.0975) < 0.5) return "Half Marathon";
  if (Math.abs(distanceKm - 42.195) < 0.8) return "Marathon";
  if (distanceKm >= 80) return `Ultra ${Math.round(distanceKm)}K`;
  if (distanceKm >= 45) return `Ultra ${Math.round(distanceKm)}K`;
  return `${Math.round(distanceKm)}K Race`;
}
function raceColor(distanceKm) {
  if (Math.abs(distanceKm - 10) < 0.2) return "var(--race-10k)";
  if (Math.abs(distanceKm - 21.0975) < 0.5) return "var(--race-half)";
  if (Math.abs(distanceKm - 42.195) < 0.8) return "var(--race-marathon)";
  return "var(--race-ultra)";
}

function generateTuneUpRaces({
  startDate,
  goalDate,
  goalDistanceKm,
  comfortablePaceSecPerKm,
  goalPaceSecPerKm,
}) {
  if (!goalDate) return [];
  const start = startOfDay(startDate);
  const goal = startOfDay(goalDate);
  if (+goal <= +start) return [];

  const cp = comfortablePaceSecPerKm || 6 * 60;
  const gp = goalPaceSecPerKm || cp * 1.05;

  let tuneUps = [];
  if (goalDistanceKm >= 90) tuneUps = [10, 21.0975, 30, 50];
  else if (goalDistanceKm >= 45) tuneUps = [10, 21.0975, 30];
  else if (goalDistanceKm >= 41) tuneUps = [10, 21.0975];
  else tuneUps = [5, 10];

  const totalDays = Math.floor((+goal - +start) / DAY_MS);
  const taperDays = goalDistanceKm >= 50 ? 21 : 14;
  const usableDays = Math.max(7, totalDays - taperDays);

  const fractions = [0.22, 0.44, 0.62, 0.78].slice(0, tuneUps.length);

  const races = tuneUps.map((distKm, idx) => {
    const dayOffset = Math.round(usableDays * fractions[idx]);
    const d = startOfDay(new Date(+start + dayOffset * DAY_MS));

    let paceSec;
    if (distKm <= 10.5) paceSec = clamp(cp * 0.86, 190, 520);
    else if (distKm <= 22) paceSec = clamp(cp * 0.9, 200, 560);
    else if (distKm <= 32) paceSec = clamp(cp * 0.98, 220, 620);
    else paceSec = clamp(gp, 240, 720);

    const zone = distKm <= 10.5 ? "Z4–Z5" : distKm <= 22 ? "Z3–Z4" : "Z2–Z3";

    return {
      kind: "race",
      date: d,
      title: raceLabel(distKm),
      zone,
      pace: secPerKmToPace(paceSec),
      paceSecPerKm: paceSec,
      distanceKm: round1(distKm),
      timeStr: secondsToHhmmss(distKm * paceSec),
      badge: `Race • ${raceLabel(distKm)}`,
      raceColor: raceColor(distKm),
    };
  });

  races.push({
    kind: "race",
    date: goal,
    title: `${raceLabel(goalDistanceKm)} (Goal)`,
    zone: "Race",
    pace: secPerKmToPace(clamp(gp, 240, 720)),
    paceSecPerKm: clamp(gp, 240, 720),
    distanceKm: round1(goalDistanceKm),
    timeStr: secondsToHhmmss(goalDistanceKm * clamp(gp, 240, 720)),
    badge: "Race • Goal",
    raceColor: raceColor(goalDistanceKm),
  });

  races.sort((a, b) => +a.date - +b.date);

  for (let i = 1; i < races.length; i++) {
    if (+races[i].date === +races[i - 1].date) {
      races[i].date = startOfDay(new Date(+races[i].date + DAY_MS));
    }
  }

  return races.filter((r) => +r.date <= +goal);
}

/* =========================
   Chart helpers
========================= */
function monthShort(d) {
  const dt = new Date(d);
  const m = dt.getMonth();
  return ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"][m];
}

/* =========================
   Legend (clean & accurate)
   All the structural styling (padding / gap / font-size / swatch size)
   lives on the .iconLegend* classes in App.css so the mobile media
   query can shrink everything without having to fight inline
   !important rules.
========================= */
function IconLegend() {
  const isMobile = useIsMobileLayout();
  // On mobile, use short labels — the swatch shape already carries meaning.
  // "Comfortable pace" matches the methodology doc's own naming (§KPIs —
  // the median of easy-run paces). Reads more clearly than "Actual pace"
  // now that the pace line has its own distinct color (cyan) separate
  // from the actual/planned volume bars.
  const labels = isMobile
    ? { actual: "Actual", planned: "Planned", actualPace: "Comfortable pace" }
    : { actual: "Actual", planned: "Planned", actualPace: "Comfortable pace" };

  return (
    <div className="card iconLegend">
      {/* Actual — solid bright-green rect */}
      <div className="iconLegendItem">
        <div className="iconLegendSwatch">
          <span className="iconLegendBar iconLegendBarActual" />
        </div>
        <div className="iconLegendLabel">{labels.actual}</div>
      </div>

      {/* Planned — outline-only ghost rect (same ghost style as the bar) */}
      <div className="iconLegendItem">
        <div className="iconLegendSwatch">
          <span className="iconLegendBar iconLegendBarPlanned" />
        </div>
        <div className="iconLegendLabel">{labels.planned}</div>
      </div>

      {/* Actual pace — short line with a dot in the middle */}
      <div className="iconLegendItem">
        <div className="iconLegendSwatch">
          <div className="iconLegendLine">
            <div className="iconLegendLineBarSolid" />
            <div className="iconLegendDot iconLegendDotActual" />
          </div>
        </div>
        <div className="iconLegendLabel">{labels.actualPace}</div>
      </div>
    </div>
  );
}

/* =========================
   HR zone descriptions — short, human-readable copy used in the session sheet.
========================= */
const HR_ZONE_DESCRIPTIONS = {
  "Z1": "Zone 1 — recovery effort, fully conversational pace.",
  "Z2": "Zone 2 — easy effort, conversational pace. Builds aerobic base.",
  "Z1–Z2": "Zones 1–2 — easy, conversational effort for aerobic base.",
  "Z2–Z3": "Zones 2–3 — easy to steady effort, comfortable but engaged.",
  "Z3": "Zone 3 — steady effort, still comfortable, short sentences.",
  "Z3–Z4": "Zones 3–4 — tempo/threshold effort, words not sentences.",
  "Z4": "Zone 4 — threshold effort, controlled hard, sustainable ~30–60 min.",
  "Z4–Z5": "Zones 4–5 — hard interval effort, limited words.",
  "Z5": "Zone 5 — VO₂max / max effort. Short intervals only.",
};
function hrZoneDescription(zone) {
  if (!zone) return "No zone specified.";
  return HR_ZONE_DESCRIPTIONS[zone] || `Zone ${zone} — follow prescribed effort.`;
}

/* The width of an element, kept current. Charts draw 1:1 in pixels so
   their type is 12 px wherever they are. */
function useElementWidth(ref, fallback = 343) {
  const [w, setW] = useState(fallback);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const measure = () => { const x = el.clientWidth; if (x > 0) setW((p) => (p === x ? p : x)); };
    measure();
    if (typeof ResizeObserver === "undefined") return undefined;
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [ref]);
  return w;
}

/* =========================
   Metric detail — the development behind a number
   -------------------------
   Every number on Stats opens one of these: the value, how it moved over
   the period, and the rows it was computed from. One chart component for
   all of them: a line for daily/weekly readings, bars for per-period
   comparisons, signed bars around a zero for deltas.
========================= */
function DetailChart({ type, points, color, yFormat, zeroLine, unitLabel, kind }) {
  const wrapRef = useRef(null);
  const width = useElementWidth(wrapRef);
  const H = 176;
  const padL = 36, padR = 12, padT = 14, padB = 26;
  const pts = (points || []).filter((p) => p && p.t != null);
  const n = pts.length;
  const vals = [];
  for (const p of pts) {
    if (p.v != null && isFinite(p.v)) vals.push(p.v);
    if (p.v2 != null && isFinite(p.v2)) vals.push(p.v2);
  }
  if (n === 0 || vals.length === 0) {
    return <div className="sdChart sdChart--empty" ref={wrapRef}>Not enough data yet.</div>;
  }
  let lo = Math.min(...vals), hi = Math.max(...vals);
  if (zeroLine) { lo = Math.min(lo, 0); hi = Math.max(hi, 0); }
  if (kind === "pct") { lo = 0; hi = Math.max(hi, 100); }
  if (kind === "km") lo = 0;
  const span = Math.max(1e-6, hi - lo);
  const pad = kind === "pct" ? 0 : span * 0.12;
  const yMin = kind === "pct" || kind === "km" ? lo : lo - pad;
  const yMax = kind === "pct" ? 100 : hi + pad;
  const plotW = Math.max(40, width - padL - padR);
  const plotH = H - padT - padB;
  const X = (i) => padL + (n === 1 ? plotW / 2 : (i / (n - 1)) * plotW);
  const slotW = plotW / Math.max(1, n);
  const Xslot = (i) => padL + i * slotW + slotW / 2;
  const Y = (v) => padT + (1 - (v - yMin) / Math.max(1e-6, yMax - yMin)) * plotH;
  const fmt = yFormat || ((v) => String(Math.round(v)));
  // Three or four round ticks inside the range.
  const ticks = (() => {
    // Step from the visible span, so a 26–85 fitness window gets 40/60/80
    // and not a lone "50".
    const raw = niceTicks(Math.max(1e-6, yMax - yMin), 4);
    const out = [];
    const step = raw.length > 1 ? raw[1] - raw[0] : Math.max(1, (yMax - yMin) / 3);
    const first = Math.ceil(yMin / step) * step;
    for (let v = first; v <= yMax + 1e-9; v += step) out.push(Math.round(v * 100) / 100);
    return out;
  })();
  const baseY = Y(Math.max(yMin, 0));
  // X labels: first, middle and last point.
  const xLabelIdx = n <= 3 ? pts.map((_, i) => i) : [0, Math.floor((n - 1) / 2), n - 1];
  const xLabel = (p) => { const d = new Date(p.t); return `${d.getDate()} ${monthShort(d)}`; };
  const isBars = type === "bars" || type === "signed";
  const linePts = pts.map((p, i) => (p.v != null && isFinite(p.v) ? { x: X(i), y: Y(p.v) } : null)).filter(Boolean);
  const linePath = linePts.length >= 2 ? monotonePath(linePts) : "";
  const areaPath = linePath ? `${linePath}L${linePts[linePts.length - 1].x.toFixed(2)},${(padT + plotH).toFixed(2)}L${linePts[0].x.toFixed(2)},${(padT + plotH).toFixed(2)}Z` : "";
  const last = linePts.length ? linePts[linePts.length - 1] : null;
  return (
    <div className="sdChart" ref={wrapRef} aria-hidden="true">
      <svg width={width} height={H} viewBox={`0 0 ${width} ${H}`} style={{ display: "block" }}>
        {ticks.map((tk) => (
          <g key={`t-${tk}`}>
            <line x1={padL} x2={width - padR} y1={Y(tk)} y2={Y(tk)} stroke="var(--viz-grid)" strokeWidth="0.5" />
            <text x={padL - 6} y={Y(tk) + 4} fontSize="12" textAnchor="end" fill="var(--viz-axis-label)">{fmt(tk)}</text>
          </g>
        ))}
        {zeroLine ? <line x1={padL} x2={width - padR} y1={Y(0)} y2={Y(0)} stroke="var(--viz-baseline)" strokeWidth="1" /> : null}
        {isBars ? pts.map((p, i) => {
          const cx = Xslot(i);
          const bw = clamp(slotW * 0.5, 6, 22);
          const els = [];
          if (p.v2 != null && isFinite(p.v2)) {
            // The reference (planned) as a hollow bar behind the reading.
            els.push(<rect key="v2" x={cx - bw / 2 - 2} y={Y(p.v2)} width={bw + 4} height={Math.max(1, baseY - Y(p.v2))} rx="4" fill="none" stroke="var(--content-tertiary)" strokeOpacity="0.45" strokeWidth="1" />);
          }
          if (p.v != null && isFinite(p.v)) {
            const neg = p.v < 0;
            const top = neg ? Y(0) : Y(p.v);
            const hgt = Math.max(2, Math.abs(Y(p.v) - Y(0)));
            const col = type === "signed" ? (neg ? "var(--accent)" : "var(--caution)") : (p.color || color);
            els.push(<rect key="v" x={cx - bw / 2} y={top} width={bw} height={hgt} rx="4" fill={col} opacity={i === n - 1 ? 1 : 0.8} />);
          }
          return <g key={`b-${i}`}>{els}</g>;
        }) : (
          <>
            {areaPath ? <path d={areaPath} fill={color} opacity="0.12" /> : null}
            {linePath ? <path d={linePath} fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" /> : null}
            {last ? <><circle cx={last.x} cy={last.y} r="5" fill="var(--viz-halo)" /><circle cx={last.x} cy={last.y} r="3" fill={color} /></> : null}
          </>
        )}
        {xLabelIdx.map((i) => (
          <text key={`x-${i}`} x={isBars ? Xslot(i) : X(i)} y={H - 8} fontSize="12" fill="var(--viz-axis-label)" textAnchor={i === 0 ? "start" : i === n - 1 ? "end" : "middle"}>{xLabel(pts[i])}</text>
        ))}
        {unitLabel ? <text x={width - padR} y={padT - 2} fontSize="12" fill="var(--viz-axis-label)" textAnchor="end">{unitLabel}</text> : null}
      </svg>
    </div>
  );
}

function StatDetailSheet({ sheet, onClose }) {
  const sheetRef = useRef(null);
  const bodyRef = useRef(null);
  const overlayRef = useRef(null);
  const closeRef = useRef(onClose);
  closeRef.current = onClose;
  useSheetPull({ sheetRef, bodyRef, overlayRef, closeRef, dirtyRef: null });
  if (!sheet) return null;
  const { title, color, value, unit, verdict, period, chart, rows, note, legend } = sheet;
  return (
    <div className="bottomSheetOverlay" ref={overlayRef} onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div ref={sheetRef} className="bottomSheetPanel tsSheet sdSheet" style={{ "--type": color }} onMouseDown={(e) => e.stopPropagation()} role="dialog" aria-label={title}>
        <div className="bottomSheetDragHandle" aria-hidden="true" />
        <div className="tsHead">
          <div className="tsHead__spine" aria-hidden="true" />
          <div className="tsHead__text">
            <div className="tsHead__title">{title}</div>
            <div className="tsHead__date">{period}</div>
          </div>
          <button type="button" className="bottomSheetClose" onClick={onClose} aria-label="Close"><X size={16} aria-hidden="true" /></button>
        </div>
        <div ref={bodyRef} className="bottomSheetBody tsBody sdBody">
          <div className="sdReadout">
            <span className="sdReadout__num">{value}</span>
            {unit ? <span className="sdReadout__unit">{unit}</span> : null}
            {verdict ? <span className="sdReadout__verdict">{verdict}</span> : null}
          </div>
          {chart ? <DetailChart {...chart} color={color} /> : null}
          {legend ? (
            <div className="sdLegend">
              {legend.map((l, i) => (
                <span key={i} className="sdLegend__item"><span className={"sdLegend__key" + (l.hollow ? " is-hollow" : "")} style={l.hollow ? undefined : { background: l.color || color }} />{l.label}</span>
              ))}
            </div>
          ) : null}
          {rows && rows.length ? (
            <div className="sdRows">
              {rows.map((r, i) => (
                <div key={i} className="sdRow">
                  <span className="sdRow__label">{r.label}{r.sub ? <span className="sdRow__sub">{r.sub}</span> : null}</span>
                  <span className={"sdRow__value" + (r.tone ? ` is-${r.tone}` : "")}>{r.value}</span>
                </div>
              ))}
            </div>
          ) : null}
          {note ? <div className="sdNote">{note}</div> : null}
        </div>
      </div>
    </div>
  );
}

/* =========================
   Train session detail / edit bottom sheet
   -------------------------
   Opens when a TrainRow is tapped. Shows session info + allows the user to
   adjust distance / date and add freeform notes. Saved edits are persisted
   to localStorage under SESSION_EDITS_KEY, keyed by original date + type.
========================= */
function TrainSessionSheet({ session, onClose, onSave }) {
  const sheetRef = useRef(null);
  const bodyRef = useRef(null);
  const overlayRef = useRef(null);
  const closeRef = useRef(onClose);
  closeRef.current = onClose;
  /* Read by the touch handlers, which are bound once. */
  const dirtyRef = useRef(false);
  const [distanceKm, setDistanceKm] = useState(session?.distanceKm || 0);
  const [date, setDate] = useState(() =>
    session?.date instanceof Date ? new Date(session.date) : parseLocalDate(session?.date)
  );
  const [notes, setNotes] = useState(session?.notes || "");

  // Re-sync local form state whenever a different session is opened
  useEffect(() => {
    if (!session) return;
    setDistanceKm(session.distanceKm || 0);
    setDate(session.date instanceof Date ? new Date(session.date) : parseLocalDate(session.date));
    setNotes(session.notes || "");
  }, [session]);

  /* Pull down to close — from anywhere on the sheet, not just the grip.
     The rule is the one every iOS sheet follows: if the content is scrolled
     to the top (or you are on the header), a downward drag moves the sheet;
     otherwise it scrolls the content. The decision is made on the first few
     pixels and held for the rest of the gesture.

     Native listeners, not React's: React registers touch handlers as
     passive, so a drag could never preventDefault the scroll under it and
     the body rubber-banded while the sheet moved. */
  useSheetPull({ sheetRef, bodyRef, overlayRef, closeRef, dirtyRef });

  if (!session) return null;

  const zoneStr = session.zone || "";
  const zoneClass =
    zoneStr === "Z1" || zoneStr === "Z2" || zoneStr === "Z1–Z2" ? "green" :
    zoneStr === "Z5" ? "red" : "amber";
  const isRace = session.kind === "race";
  const typeCol = isRace ? (session.raceColor || "var(--viz-long)") : sessionTypeColor(session.title || session.type);
  const typeLabel = formatSessionTypeLabel(session.type) || session.title || "";
  /* Tense comes from the session as it stands, never from the stepper —
     stepping today's run back a day must not relabel it or change the
     question. And nothing that reaches this sheet is a completed run (those
     open the Stats detail sheet), so it never says "Done". */
  const origDate = session.date instanceof Date ? session.date : parseLocalDate(session.date);
  const todayMs = +startOfDay(new Date());
  const isFuture = +startOfDay(origDate) > todayMs;
  const isToday = +startOfDay(origDate) === todayMs;
  const origKm = Number(session.distanceKm) || 0;
  const origDateIso = dateToLocalISO(origDate);
  const editable = !isRace && !!session.editKey;
  const dirty = editable && (Math.abs(Number(distanceKm) - origKm) > 0.05 || dateToLocalISO(date) !== origDateIso || (notes || "").trim() !== (session.notes || "").trim());
  dirtyRef.current = dirty;
  // "Zone 2 — easy effort…" under a Z2 pill said the zone twice.
  const effortText = (() => {
    if (!zoneStr || zoneStr === "Race") return "";
    const s = hrZoneDescription(zoneStr).replace(/^Zone\s+\S+\s+—\s+/, "");
    return s.charAt(0).toUpperCase() + s.slice(1);
  })();

  const adjDistance = (delta) => {
    setDistanceKm(prev => Math.max(0, Math.round((Number(prev) + delta) * 10) / 10));
  };
  const adjDate = (deltaDays) => {
    setDate(prev => {
      const d = new Date(prev);
      d.setDate(d.getDate() + deltaDays);
      return d;
    });
  };

  const notesRemaining = 140 - notes.length;
  const durStr = fmtDuration(session.durationMin, "min");
  const paceStr = session.paceSecPerKm ? secPerKmToPace(session.paceSecPerKm).replace("/km", "") : null;

  const handleSave = () => {
    const payload = {
      distanceKm: Number(distanceKm),
      dateISO: dateToLocalISO(date),
      notes: notes.trim(),
      updatedAt: new Date().toISOString(),
    };
    onSave(session.editKey, payload);
    onClose();
  };

  const when = isToday ? "Today" : isFuture ? "Planned" : "Past";
  return (
    <div
      className="bottomSheetOverlay"
      ref={overlayRef}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div
        ref={sheetRef}
        className={"bottomSheetPanel tsSheet" + (isRace ? " tsSheet--race" : "")}
        style={{ "--type": typeCol }}
        onMouseDown={(e) => e.stopPropagation()}
        role="dialog"
        aria-label={`${session.title || typeLabel || "Session"}, ${formatDateShort(date)}`}
      >
        <div className="bottomSheetDragHandle" aria-hidden="true" />

        <div className="tsHead">
          <div className="tsHead__spine" aria-hidden="true" />
          <div className="tsHead__text">
            <div className="tsHead__title">{session.title || session.label || typeLabel || "Session"}</div>
            <div className="tsHead__date">
              <span className="tsHead__when">{when}</span>
              <span className="tsHead__sep" />
              {formatDateShort(date)}
            </div>
          </div>
          {zoneStr ? <span className={`pvaZonePill pvaZonePill--${zoneClass}`}>{zoneStr}</span> : null}
          <button type="button" className="bottomSheetClose" onClick={onClose} aria-label="Close">
            <X size={16} aria-hidden="true" />
          </button>
        </div>

        <div ref={bodyRef} className="bottomSheetBody tsBody">
          {/* The prescription, as three numbers. */}
          <div className="tsStats">
            <div className="tsStat">
              <span className="tsStat__v">{fmt1(session.distanceKm)}<span className="tsStat__u">km</span></span>
              <span className="tsStat__l">Distance</span>
            </div>
            {paceStr ? (
              <div className="tsStat">
                <span className="tsStat__v">{paceStr}<span className="tsStat__u">/km</span></span>
                <span className="tsStat__l">Pace</span>
              </div>
            ) : null}
            {durStr ? (
              <div className="tsStat">
                <span className="tsStat__v">{durStr}</span>
                <span className="tsStat__l">Duration</span>
              </div>
            ) : null}
          </div>

          {/* Effort, in the zone's colour, and what the session is for. */}
          {effortText ? (
            <div className={`tsEffort tsEffort--${zoneClass}`}>
              <span className="tsEffort__dot" aria-hidden="true" />
              <span>{effortText}</span>
            </div>
          ) : null}
          {session.purpose ? <div className="tsPurpose">{session.purpose}</div> : null}

          {/* Adjust — two rows, one block. */}
          {!isRace ? (
            <div className="tsAdjust">
              <div className="tsAdjust__row">
                <span className="tsAdjust__label">Distance</span>
                <div className="tsStepper">
                  <button type="button" className="tsStepper__btn" onClick={() => adjDistance(-1)} aria-label="Decrease 1 km"><Minus size={16} /></button>
                  <span className="tsStepper__v">{fmt1(distanceKm)}<span className="tsStepper__u">km</span></span>
                  <button type="button" className="tsStepper__btn" onClick={() => adjDistance(+1)} aria-label="Increase 1 km"><Plus size={16} /></button>
                </div>
              </div>
              <div className="tsAdjust__row">
                <span className="tsAdjust__label">Date</span>
                <div className="tsStepper">
                  <button type="button" className="tsStepper__btn" onClick={() => adjDate(-1)} aria-label="One day earlier"><Minus size={16} /></button>
                  <span className="tsStepper__v tsStepper__v--date">{formatDateShort(date)}</span>
                  <button type="button" className="tsStepper__btn" onClick={() => adjDate(+1)} aria-label="One day later"><Plus size={16} /></button>
                </div>
              </div>
            </div>
          ) : null}

          {/* Notes — never "how did it feel": nothing on this sheet has
              been run. Past here means a session the plan had and no run
              matched. */}
          {editable ? (
            <div className="tsNotes">
              <div className="tsNotes__head">
                <span>Notes</span>
                <span className={"tsNotes__count" + (notesRemaining < 0 ? " is-over" : "")}>{notesRemaining}</span>
              </div>
              <textarea
                className="sessionSheetNotes tsNotes__field"
                value={notes}
                onChange={(e) => setNotes(e.target.value.slice(0, 140))}
                placeholder={isFuture || isToday ? "Anything to remember for this one?" : "Any context for this one?"}
                maxLength={140}
                rows={2}
              />
            </div>
          ) : null}
        </div>

        <div className="tsActions">
          <button type="button" className="sessionSheetBtn sessionSheetBtn--secondary" onClick={onClose}>
            {dirty ? "Cancel" : "Close"}
          </button>
          {editable ? (
            <button type="button" className="sessionSheetBtn sessionSheetBtn--primary" onClick={handleSave} disabled={!dirty} aria-disabled={!dirty}>
              Save
            </button>
          ) : null}
        </div>
      </div>
    </div>
  );
}

/* =========================
   Adjust goal — the sheet
   -------------------------
   It was a centred modal with two bordered cards, a free-text "h:mm:ss"
   input and a native date field. Now it is the house sheet: a race-coloured
   spine, the target as one big number with the suggestion one tap away,
   three tiles that recompute while the wheel is still turning (pace,
   where the trajectory lands, weeks to go), and two rows that each unfold
   an inline wheel. No apply step: the wheel IS the value.
========================= */
const AG_MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const AG_TERRAINS = [["road", "Road"], ["trail", "Trail"], ["mixed", "Mixed"], ["mountain", "Mountain"]];
const agTerrainLabel = (v) => { const f = AG_TERRAINS.find((x) => x[0] === v); return f ? f[1] : "Road"; };
const AG_TERRAIN_HINT = {
  road: "Flat and paced. No elevation targets, no hill sessions.",
  trail: "Elevation targets and hill work in the build. Paces read slower.",
  mixed: "Some climbing. Lighter elevation targets than trail.",
  mountain: "Big vertical. Hiking is part of the specific phase.",
};
const agDaysInMonth = (y, m) => new Date(y, m, 0).getDate();
const agFmtDateDow = (iso) => {
  const d = iso ? new Date(`${iso}T00:00:00`) : null;
  return d && !isNaN(+d) ? d.toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short", year: "numeric" }) : "—";
};
const agFmtDateShort = (iso) => {
  const d = iso ? new Date(`${iso}T00:00:00`) : null;
  return d && !isNaN(+d) ? d.toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short" }) : "—";
};
const agWeeksLeft = (iso) => {
  const d = iso ? new Date(`${iso}T00:00:00`) : null;
  return d && !isNaN(+d) ? Math.max(0, Math.ceil((+d - Date.now()) / (7 * 864e5))) : null;
};
/* Where the draft target lands at the current execution rate — the same
   adherence penalty the main trajectory applies, so the preview never shows
   a faster-than-goal projection at low adherence. */
const agProject = (trajectory, draftSec) => {
  if (!draftSec || !trajectory) return null;
  const penalty = trajectory.adhPenalty || 1;
  const raw = draftSec * trajectory.ratio * penalty;
  const adhScore = trajectory.adherenceScore ?? 1;
  const projectedSec = adhScore < 0.5 && raw < draftSec ? draftSec : raw;
  return { projectedSec, deltaSec: projectedSec - draftSec, status: trajectory.status === "onplan" ? "onplan" : trajectory.status };
};
// Whole minutes, floored — the same thing the wheel can hold, so a label
// and the value it sets never disagree.
const agTimeLabel = (sec, withSec) => {
  if (sec == null || !isFinite(sec)) return "—";
  const h = Math.floor(sec / 3600);
  const m = Math.floor((sec % 3600) / 60);
  const s = Math.floor(sec % 60);
  return withSec ? `${h}:${pad2(m)}:${pad2(s)}` : `${h}:${pad2(m)}`;
};
const agNormSec = (sec, withSec) => (sec == null ? null : withSec ? Math.floor(sec) : sec - (sec % 60));

function AdjustGoalSheet({ goal, suggestedTimeStr, planTrajectory, fmtFinish, fmtDelta, saving, error, onSave, onClose }) {
  const sheetRef = useRef(null);
  const bodyRef = useRef(null);
  const overlayRef = useRef(null);
  const closeRef = useRef(onClose);
  closeRef.current = onClose;
  const dirtyRef = useRef(false);
  useSheetPull({ sheetRef, bodyRef, overlayRef, closeRef, dirtyRef });

  const distanceKm = Number(goal.distanceKm) || 0;
  const withSec = distanceKm > 0 && distanceKm <= 42.195;
  const savedSec = agNormSec(timeStrToSeconds(goal.timeStr) || null, withSec);
  const suggestedSec = agNormSec(timeStrToSeconds(suggestedTimeStr) || null, withSec);
  const [draftSec, setDraftSec] = useState(savedSec);
  const [draftDate, setDraftDate] = useState(goal.date || "");
  const [draftTerrain, setDraftTerrain] = useState(goal.terrain || "road");
  const [openRow, setOpenRow] = useState(null);
  const [seed, setSeed] = useState(0);
  const [lastSec, setLastSec] = useState(savedSec);
  const timeRowRef = useRef(null);
  const dateRowRef = useRef(null);
  const terrainRowRef = useRef(null);

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape" && !dirtyRef.current) closeRef.current && closeRef.current(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, []);

  const timeChanged = draftSec !== savedSec;
  const dateChanged = draftDate !== (goal.date || "");
  const terrainChanged = draftTerrain !== (goal.terrain || "road");
  const dirty = timeChanged || dateChanged || terrainChanged;
  dirtyRef.current = dirty;

  const paceStr = draftSec && distanceKm ? secPerKmToPace(draftSec / distanceKm).replace("/km", "") : null;
  const proj = agProject(planTrajectory, draftSec);
  const weeks = agWeeksLeft(draftDate);
  const weeksDelta = dateChanged && weeks != null ? weeks - (agWeeksLeft(goal.date) ?? weeks) : 0;
  const todayIso = localIsoDate(new Date());
  const liveError = draftSec === 0 ? "Pick a time above 0:00." : draftDate && draftDate < todayIso ? "The race date is in the past." : null;
  const tone = !proj ? "neutral" : proj.status === "onplan" ? "green" : proj.status === "slightly" ? "amber" : "red";
  const typeCol = raceColor(distanceKm);
  const readout = draftSec == null ? "Finish" : agTimeLabel(draftSec, withSec);

  // Wheel positions from the draft.
  // Wheel positions while there is no target: the last one, dimmed.
  const wheelSec = draftSec != null ? draftSec : (lastSec || suggestedSec || 43200);
  const hh = Math.floor(wheelSec / 3600);
  const mm = Math.floor((wheelSec % 3600) / 60);
  const ss = Math.floor(wheelSec % 60);
  const dParts = (draftDate || todayIso).split("-").map(Number);
  const dy = dParts[0] || new Date().getFullYear();
  const dm = dParts[1] || 1;
  const dd = dParts[2] || 1;
  const thisYear = new Date().getFullYear();
  // Frozen at mount: a range that moves under the finger drops the item
  // being scrolled.
  const [yearBounds] = useState(() => { const sy = Number((goal.date || "").slice(0, 4)) || thisYear; return [Math.min(thisYear, sy), Math.max(thisYear + 3, sy)]; });
  const y0 = Math.min(yearBounds[0], dy);
  const y1 = Math.max(yearBounds[1], dy);
  const daysMax = agDaysInMonth(dy, dm);

  const setTime = (h, m, s) => setDraftSec(h * 3600 + m * 60 + (withSec ? s : 0));
  const setDate = (y, m, d) => {
    const max = agDaysInMonth(y, m);
    const day = Math.min(d, max);
    setDraftDate(`${y}-${pad2(m)}-${pad2(day)}`);
  };
  const toggleRow = (row) => {
    const next = openRow === row ? null : row;
    setOpenRow(next);
    // The time wheel fits under the readout; only the date row, further
    // down, needs bringing into view.
    if (next === "date" || next === "terrain") {
      const ref = next === "date" ? dateRowRef : terrainRowRef;
      setTimeout(() => { try { ref.current && ref.current.scrollIntoView({ block: "nearest", behavior: "smooth" }); } catch (_) {} }, 280);
    }
  };
  /* One dismissal rule for every exit but Cancel: unsaved edits stay. The
     pull already snaps back when dirty; the scrim and Escape now agree. */
  const requestClose = () => { if (!dirtyRef.current) onClose(); };
  const useSuggested = () => {
    if (!suggestedSec || draftSec === suggestedSec) return;
    setDraftSec(suggestedSec);
    setSeed((s) => s + 1);
    setOpenRow("time");
  };
  const clearTime = () => { setLastSec(draftSec); setDraftSec(null); };
  const restoreTime = () => { setDraftSec(lastSec || suggestedSec || 43200); setSeed((s) => s + 1); };

  const note = (() => {
    const t0 = savedSec != null ? agTimeLabel(savedSec, withSec) : null;
    const t1 = draftSec != null ? agTimeLabel(draftSec, withSec) : null;
    if (terrainChanged) return <>Surface <strong>{agTerrainLabel(goal.terrain)}</strong> → <strong>{agTerrainLabel(draftTerrain)}</strong>{dateChanged ? <>, race <strong>{agFmtDateShort(goal.date)}</strong> → <strong>{agFmtDateShort(draftDate)}</strong></> : null}{timeChanged ? <>, target <strong>{t0 || "none"}</strong> → <strong>{t1 || "none"}</strong></> : null}. The plan is rebuilt from today for the new surface.</>;
    if (draftSec == null) return <>No target time: a finish-focused plan. The trajectory has nothing to measure against.</>;
    if (timeChanged && dateChanged) return <>Target <strong>{t0 || "none"}</strong> → <strong>{t1}</strong>, race <strong>{agFmtDateShort(goal.date)}</strong> → <strong>{agFmtDateShort(draftDate)}</strong>. The plan is rebuilt from today for the new date and time.</>;
    if (timeChanged) return <>Target <strong>{t0 || "none"}</strong> → <strong>{t1}</strong>. The plan keeps its dates; the trajectory is measured against the new time.</>;
    if (dateChanged) return <>Race <strong>{agFmtDateShort(goal.date)}</strong> → <strong>{agFmtDateShort(draftDate)}</strong>, {agWeeksLeft(goal.date) ?? "—"} → {weeks ?? "—"} wks. The plan is rebuilt from today for the new date.</>;
    return <>Changing the time moves where the trajectory lands. Changing the date or the surface rebuilds the plan.</>;
  })();

  const title = goal.name || (distanceKm ? `${Math.round(distanceKm)}K goal` : "Goal");
  const errText = liveError || error || null;

  return (
    <div className="bottomSheetOverlay" ref={overlayRef} onClick={(e) => { if (e.target === e.currentTarget) requestClose(); }}>
      <div ref={sheetRef} className="bottomSheetPanel tsSheet agSheet" style={{ "--type": typeCol }} onMouseDown={(e) => e.stopPropagation()} role="dialog" aria-label="Adjust goal">
        <div className="bottomSheetDragHandle" aria-hidden="true" />
        <div className="tsHead">
          <div className="tsHead__spine" aria-hidden="true" />
          <div className="tsHead__text">
            <div className="tsHead__title">{title}</div>
            <div className="tsHead__date">
              <span className="tsHead__when">Goal race</span>
              {distanceKm ? <><span className="tsHead__sep" />{Math.round(distanceKm)} km</> : null}
              <span className="tsHead__sep" /><span key={draftTerrain} className="agLive">{agTerrainLabel(draftTerrain)}</span>
            </div>
          </div>
          <button type="button" className="bottomSheetClose" onClick={onClose} aria-label="Close"><X size={16} aria-hidden="true" /></button>
        </div>

        <div ref={bodyRef} className="bottomSheetBody tsBody agBody">
          <div className="agReadout">
            <div className="agReadout__main">
              <span key={`r-${draftSec}`} className="agReadout__num agLive">{readout}</span>
              {draftSec != null ? <span className="agReadout__unit">h</span> : null}
              {suggestedSec ? (
                <button
                  type="button"
                  className={"agSuggest" + (draftSec === suggestedSec ? " is-active" : "")}
                  aria-pressed={draftSec === suggestedSec}
                  aria-label={`Use the suggested time, ${agTimeLabel(suggestedSec, withSec)}, from your comfortable pace`}
                  onClick={useSuggested}
                >
                  <span className="agSuggest__t">Suggested {agTimeLabel(suggestedSec, withSec)}</span>
                  <span className="agSuggest__cta">{draftSec === suggestedSec ? <Check size={12} aria-hidden="true" /> : "Use"}</span>
                </button>
              ) : null}
            </div>
          </div>

          <div className="tsStats agStats">
            <div className="tsStat">
              <span key={`p-${paceStr}`} className="tsStat__v agLive">{paceStr ? <>{paceStr}<span className="tsStat__u">/km</span></> : "—"}</span>
              <span className="tsStat__l">Pace</span>
            </div>
            <div className={`tsStat agStat--trend is-${tone}`}>
              <span key={`t-${proj ? Math.round(proj.projectedSec) : "none"}`} className="tsStat__v agLive">
                {proj ? <>{fmtFinish(proj.projectedSec)}<span className="agStat__d">{proj.status === "onplan" ? "on plan" : fmtDelta(proj.deltaSec)}</span></> : "—"}
              </span>
              <span className="tsStat__l">Trends to</span>
            </div>
            <div className="tsStat">
              <span key={`w-${weeks}`} className="tsStat__v agLive">
                {weeks != null ? <>{weeks}<span className="tsStat__u">wks</span>{dateChanged && weeksDelta !== 0 ? <span className="agStat__d">{weeksDelta > 0 ? "+" : "−"}{Math.abs(weeksDelta)}</span> : null}</> : "—"}
              </span>
              <span className="tsStat__l">To race</span>
            </div>
          </div>

          <div className="tsAdjust agRows">
            <div ref={timeRowRef} className={"agRow agRow--time" + (openRow === "time" ? " is-open" : "") + (draftSec === 0 ? " is-invalid" : "")}>
              <button type="button" className="agRow__btn" aria-expanded={openRow === "time"} aria-controls="agPickTime" onClick={() => toggleRow("time")}>
                <span className="tsAdjust__label">Target time</span>
                <span className="agRow__value">{draftSec == null ? "None" : `${agTimeLabel(draftSec, withSec)} h`}<ChevronDown size={16} className="agRow__chev" aria-hidden="true" /></span>
              </button>
              <div className="agPick" id="agPickTime" aria-hidden={openRow !== "time"}>
                <div className="agPick__in">
                  {openRow === "time" ? (
                    <>
                      <div className={"gwWheels agWheels" + (draftSec == null ? " is-off" : "")} key={`tw-${seed}`} aria-disabled={draftSec == null}>
                        <div className="agWheels__band" aria-hidden="true" />
                        <PickerWheel min={0} max={99} initial={hh} ariaLabel="Hours" onChange={(v) => setTime(v, mm, ss)} />
                        <div className="gwWsep">:</div>
                        <PickerWheel min={0} max={59} initial={mm} ariaLabel="Minutes" onChange={(v) => setTime(hh, v, ss)} />
                        {withSec ? <><div className="gwWsep">:</div><PickerWheel min={0} max={59} initial={ss} ariaLabel="Seconds" onChange={(v) => setTime(hh, mm, v)} /></> : null}
                      </div>
                      <div className="gwWunit agWunit"><span>h</span><span className="sep" /><span>min</span>{withSec ? <><span className="sep" /><span>sec</span></> : null}</div>
                      <button type="button" className="agLink" onClick={draftSec == null ? restoreTime : clearTime}>{draftSec == null ? "Set a time" : "No target time"}</button>
                    </>
                  ) : null}
                </div>
              </div>
            </div>
            <div ref={dateRowRef} className={"agRow agRow--date" + (openRow === "date" ? " is-open" : "") + (draftDate && draftDate < todayIso ? " is-invalid" : "")}>
              <button type="button" className="agRow__btn" aria-expanded={openRow === "date"} aria-controls="agPickDate" onClick={() => toggleRow("date")}>
                <span className="tsAdjust__label">Race date</span>
                <span className="agRow__value">{agFmtDateDow(draftDate)}<ChevronDown size={16} className="agRow__chev" aria-hidden="true" /></span>
              </button>
              <div className="agPick" id="agPickDate" aria-hidden={openRow !== "date"}>
                <div className="agPick__in">
                  {openRow === "date" ? (
                    <>
                      <div className="gwWheels agWheels">
                        <div className="agWheels__band" aria-hidden="true" />
                        <PickerWheel min={1} max={daysMax} initial={dd} ariaLabel="Day" onChange={(v) => setDate(dy, dm, v)} />
                        <PickerWheel min={1} max={12} initial={dm} labels={AG_MONTHS} ariaLabel="Month" onChange={(v) => setDate(dy, v, dd)} />
                        <PickerWheel min={y0} max={y1} initial={dy} ariaLabel="Year" onChange={(v) => setDate(v, dm, dd)} />
                      </div>
                      <div className="gwWunit agWunit"><span>day</span><span>month</span><span>year</span></div>
                    </>
                  ) : null}
                </div>
              </div>
            </div>
            <div ref={terrainRowRef} className={"agRow agRow--terrain" + (openRow === "terrain" ? " is-open" : "")}>
              <button type="button" className="agRow__btn" aria-expanded={openRow === "terrain"} aria-controls="agPickTerrain" onClick={() => toggleRow("terrain")}>
                <span className="tsAdjust__label">Surface</span>
                <span className="agRow__value">{agTerrainLabel(draftTerrain)}<ChevronDown size={16} className="agRow__chev" aria-hidden="true" /></span>
              </button>
              <div className="agPick" id="agPickTerrain" aria-hidden={openRow !== "terrain"}>
                <div className="agPick__in">
                  {openRow === "terrain" ? (
                    <>
                      <div className="geSeg geSeg--4" role="radiogroup" aria-label="Surface">
                        {AG_TERRAINS.map(([v, l]) => (
                          <button key={v} type="button" role="radio" aria-checked={draftTerrain === v} className={"geSeg__opt" + (draftTerrain === v ? " is-on" : "")} onClick={() => setDraftTerrain(v)}>{l}</button>
                        ))}
                      </div>
                      <div className="geHint">{AG_TERRAIN_HINT[draftTerrain] || AG_TERRAIN_HINT.road}</div>
                    </>
                  ) : null}
                </div>
              </div>
            </div>
          </div>

          <div className={"agNote" + (dirty ? " is-change" : "")}>{note}</div>
          {errText ? <div className="agError" role="alert">{errText}</div> : null}
        </div>

        <div className="tsActions">
          <button type="button" className="sessionSheetBtn sessionSheetBtn--secondary" onClick={onClose}>{dirty ? "Cancel" : "Close"}</button>
          <button
            type="button"
            className="sessionSheetBtn sessionSheetBtn--primary"
            disabled={!dirty || !!saving || !!liveError}
            aria-disabled={!dirty || !!saving || !!liveError}
            onClick={() => onSave({ time: draftSec == null ? "" : secondsToHhmmss(draftSec), date: draftDate, terrain: draftTerrain })}
          >
            {saving ? "Saving…" : dateChanged || terrainChanged ? "Save and rebuild" : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
}



/* =========================
   Event chips row — plan start, tune-up races, goal race as clickable pills.
   Colour-coded by distance. Rendered in the Stats tab above the chart.
   Parent passes an optional onSelect handler (e.g. goal opens adjust sheet).
========================= */
function EventChips({ markers = [], onSelect }) {
  if (!markers.length) return null;
  const cls = (label) => {
    if (label === "Start") return "eventChip--start";
    if (label === "Goal")  return "eventChip--goal";
    if (label === "10K" || label === "5K") return "eventChip--amber";
    if (label === "HM")    return "eventChip--info";
    return "eventChip--coral";
  };
  return (
    <div className="eventChips">
      {markers.map((m, i) => (
        <button
          key={`ec-${i}-${+m.date}`}
          type="button"
          className={`eventChip ${cls(m.label)}`}
          onClick={() => onSelect && onSelect(m)}
        >
          <span className="eventChip__dot" style={{ background: m.color }} />
          <span className="eventChip__label">{m.label}</span>
          <span className="eventChip__date">{formatDateShort(new Date(m.date))}</span>
        </button>
      ))}
    </div>
  );
}

/* =========================
   Combined chart
========================= */
let volIntroPlayed = false;   // see introRef below

/* The chart is one scroller. You drag the plot itself left and right with
   native momentum, the week under the centre line is the week the headline
   reads, and a three-stop pill (6w · 12w · All) reframes around that week.
   This replaced a fixed 400-unit viewBox that pinned 35 weeks into ~9 px each
   plus a separate strip underneath that re-rendered the whole SVG — gradients,
   blur filter, 35 bars — from a scroll handler, which is what made moving
   around it feel like dragging through mud. */
const AXIS_W = 28;                 // the km axis, an HTML column left of the plot
const VOL_RANGE_KEY = "stats.volRange";
const prefersReducedMotion = () => {
  try { return window.matchMedia("(prefers-reduced-motion: reduce)").matches; } catch (_) { return false; }
};

/* Nice round axis ticks. Evenly dividing kmMax gave 0/26/53/79/106 —
   values no runner thinks in. This picks a 1/2/2.5/5/10 x 10^n step and
   emits the ticks that fall inside the existing scale. Labels and
   gridlines only: kmMax, and therefore every bar height, is unchanged. */
const niceTicks = (max, target = 4) => {
  if (!isFinite(max) || max <= 0) return [0];
  const raw = max / target;
  const mag = Math.pow(10, Math.floor(Math.log10(raw)));
  const norm = raw / mag;
  const stepK = (norm <= 1 ? 1 : norm <= 2 ? 2 : norm <= 2.5 ? 2.5 : norm <= 5 ? 5 : 10) * mag;
  const out = [];
  for (let v = 0; v <= max + 1e-9; v += stepK) out.push(Math.round(v * 100) / 100);
  return out;
};

/* Monotone cubic interpolation (Fritsch–Carlson). Used to draw the planned
   volume as a smooth curve instead of 35 separate bars, which at phone
   width rendered as an 8px-wide barcode.

   Monotone specifically, NOT Catmull-Rom: a naive spline overshoots local
   peaks, and an overshoot here would draw a weekly volume the plan never
   prescribes. This one is guaranteed to stay within the data. */
const monotonePath = (pts) => {
  const k = pts.length;
  if (k === 0) return "";
  if (k === 1) return `M${pts[0].x},${pts[0].y}`;
  if (k === 2) return `M${pts[0].x},${pts[0].y}L${pts[1].x},${pts[1].y}`;
  const dx = [], dy = [], slope = [];
  for (let i = 0; i < k - 1; i++) {
    dx[i] = pts[i + 1].x - pts[i].x;
    dy[i] = pts[i + 1].y - pts[i].y;
    slope[i] = dx[i] === 0 ? 0 : dy[i] / dx[i];
  }
  const m = [slope[0]];
  for (let i = 1; i < k - 1; i++) {
    if (slope[i - 1] * slope[i] <= 0) m[i] = 0;
    else {
      const wA = 2 * dx[i] + dx[i - 1];
      const wB = dx[i] + 2 * dx[i - 1];
      m[i] = (wA + wB) / (wA / slope[i - 1] + wB / slope[i]);
    }
  }
  m[k - 1] = slope[k - 2];
  let d = `M${pts[0].x.toFixed(2)},${pts[0].y.toFixed(2)}`;
  for (let i = 0; i < k - 1; i++) {
    const c = dx[i] / 3;
    d += `C${(pts[i].x + c).toFixed(2)},${(pts[i].y + c * m[i]).toFixed(2)} ` +
         `${(pts[i + 1].x - c).toFixed(2)},${(pts[i + 1].y - c * m[i + 1]).toFixed(2)} ` +
         `${pts[i + 1].x.toFixed(2)},${pts[i + 1].y.toFixed(2)}`;
  }
  return d;
};

const phaseInk = (p) =>
  p === "base"     ? "var(--phase-base)" :
  p === "build"    ? "var(--phase-build)" :
  p === "specific" ? "var(--phase-specific)" :
  p === "taper"    ? "var(--phase-taper)" : "var(--content-tertiary)";

// NOTE: this expression stays a raw rgba literal on purpose.
// server/test/planHistory.test.js greps THIS FILE for the exact string
// `phaseName === "base" ? "rgba(95,174,122,<alpha>)"` and asserts the
// alpha is <= 0.2 — a subtle phase tint is what keeps the matched (same
// green) actual marks visible over it. Tokenise it together with the
// test, which lives under server/ and is out of scope here.
const phaseTint = (phaseName) =>
  phaseName === "base"     ? "rgba(95,174,122,0.10)" :
  phaseName === "build"    ? "rgb(var(--accent-rgb) / 0.17)" :
  phaseName === "specific" ? "rgb(var(--accent-rgb) / 0.26)" :
  phaseName === "taper"    ? "rgb(var(--neutral-rgb) / 0.14)" :
                             "var(--chart-planned-fill)";

const titleCase = (p) => (p ? p.charAt(0).toUpperCase() + p.slice(1) : "");

/**
 * Bar color is VOLUME-DELTA driven, not match-status driven — per
 * product call: "only green if positive" (actual ≥ planned).
 *
 *   - actual ≥ reference plan           → bright green  (matched)
 *   - below it                          → red           (under)
 *   - No plan                           → soft green    (extras / unplanned
 *                                                         runs still earn
 *                                                         a faint marker)
 *
 * "Reference plan" is FULL weekly plan for past weeks, and PLAN-TO-DATE
 * for the current week (mid-week shouldn't flash red just because Sunday
 * hasn't happened yet). Plan-to-date is passed in from the parent.
 */
function barColorForWeek(wk, actualWeeklyMap, plannedWeeklyMap, todayW, plannedThisWeekToDateKm) {
  const actual = (actualWeeklyMap && actualWeeklyMap.get(wk)) ?? 0;
  const plannedFull = (plannedWeeklyMap && plannedWeeklyMap.get(wk)) ?? null;
  // For the current (in-progress) week, the reference is plan-to-date —
  // what was supposed to be done by END of YESTERDAY. We DON'T fall back
  // to the full-week plan when plan-to-date is 0 (early in the week,
  // nothing due yet): falling back would flash a fresh week red just
  // because Sunday hasn't happened. plan-to-date=0 with any actual ≥ 0 is
  // the on-track state.
  const planRef = wk === todayW
    ? (plannedThisWeekToDateKm != null ? plannedThisWeekToDateKm : plannedFull)
    : plannedFull;
  if (planRef == null) return "var(--chart-bar-soft-matched)";
  return actual >= planRef ? "var(--chart-bar-matched)" : "var(--chart-bar-under)";
}

/* Everything drawn in SVG. Memoised: nothing in here depends on which week
   is focused, hovered or open, so a scroll never touches it — the cursor,
   the readout and the week labels are HTML on top. */
const VolPlot = React.memo(function VolPlot({
  xs, cellW, W, h, padT, padB, kmMax, kmTicks,
  actualWeeklyMap, plannedWeeklyMap, phaseByWeek, markerLines,
  todayW, plannedThisWeekToDateKm, range, onPhaseTap, onMarkerTap,
}) {
  const n = xs.length;
  const plotH = h - padT - padB;
  const baseY = padT + plotH;
  const Xpos = (idx) => idx * cellW + cellW / 2;
  const Ykm = (km) => padT + (1 - (km || 0) / Math.max(1e-9, kmMax)) * plotH;
  const todayIdx = xs.indexOf(todayW);
  const splitX = todayIdx >= 0 ? Xpos(todayIdx) : (todayW < xs[0] ? 0 : W);
  const todayOffset = clamp(splitX / Math.max(1, W), 0, 1);

  const plannedPtsXY = [];
  xs.forEach((wk, idx) => {
    const v = plannedWeeklyMap.get(wk);
    if (v != null && isFinite(v)) plannedPtsXY.push({ wk, x: Xpos(idx), y: Ykm(v) });
  });
  const plannedCurve = plannedPtsXY.length >= 2 ? monotonePath(plannedPtsXY) : "";
  const plannedAreaPath = plannedCurve
    ? `${plannedCurve}L${plannedPtsXY[plannedPtsXY.length - 1].x.toFixed(2)},${baseY}L${plannedPtsXY[0].x.toFixed(2)},${baseY}Z`
    : "";

  const monthTicks = [];
  let lastM = null;
  xs.forEach((wk, idx) => {
    const d = new Date(wk);
    if (d.getMonth() !== lastM) { monthTicks.push({ wk, idx, label: monthShort(d) }); lastM = d.getMonth(); }
  });
  /* A plan that starts in the last days of a month gives that month one or
     two weeks; at All its label then collided with the next month's and the
     collision rule dropped the FULL month ("Jun, Aug, Sep…"). Drop the
     partial one instead. */
  if (monthTicks.length > 1 && (monthTicks[1].idx - monthTicks[0].idx) * cellW < 30) monthTicks.shift();

  const phaseOf = (wk) => (phaseByWeek && phaseByWeek.get(wk) ? phaseByWeek.get(wk).phase : null) || null;
  const isRecovery = (wk) => !!(phaseByWeek && phaseByWeek.get(wk) && phaseByWeek.get(wk).isRecoveryWeek);
  const phaseSegs = [];
  xs.forEach((wk, idx) => {
    const p = phaseOf(wk);
    const last = phaseSegs[phaseSegs.length - 1];
    if (last && last.phase === p) last.end = idx;
    else phaseSegs.push({ phase: p, start: idx, end: idx });
  });

  const todayCy = todayIdx >= 0 && plannedWeeklyMap.get(todayW) != null ? Ykm(plannedWeeklyMap.get(todayW)) : null;
  const railY = baseY + 28;
  const railH = 20;
  const minLabelGap = 30;
  let lastLabelX = -Infinity;

  return (
    <svg
      width={W}
      height={h}
      viewBox={`0 0 ${W} ${h}`}
      role="img"
      aria-label={`Weekly volume, ${n} weeks.`}
      style={{ display: "block" }}
    >
      <defs>
        {/* Planned volume reads as a lit surface, not 35 grey bars. */}
        <linearGradient id="volAreaFill" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%"   stopColor="var(--viz-measure)" stopOpacity="0.46" />
          <stop offset="42%"  stopColor="var(--viz-measure)" stopOpacity="0.16" />
          <stop offset="100%" stopColor="var(--viz-measure)" stopOpacity="0" />
        </linearGradient>
        {/* The curve brightens into today and cools after it, so "done" and
            "to come" are legible without a second colour key. */}
        <linearGradient id="volStroke" x1="0" y1="0" x2="1" y2="0">
          <stop offset="0" stopColor="var(--viz-measure)" stopOpacity="0.8" />
          <stop offset={todayOffset.toFixed(3)} stopColor="var(--viz-measure)" stopOpacity="1" />
          <stop offset="1" stopColor="var(--viz-measure)" stopOpacity="0.55" />
        </linearGradient>
        {["Ok", "Under", "Soft"].map((k) => {
          const c = k === "Ok" ? "var(--chart-bar-matched)"
            : k === "Under" ? "var(--chart-bar-under)"
            : "var(--chart-bar-soft-matched)";
          return (
            <linearGradient key={k} id={`volBar${k}`} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%"   stopColor={c} stopOpacity="1" />
              <stop offset="100%" stopColor={c} stopOpacity="0.38" />
            </linearGradient>
          );
        })}
        <clipPath id="volPast"><rect x="0" y="0" width={Math.max(0, splitX)} height={h} /></clipPath>
        <clipPath id="volAhead"><rect x={splitX} y="0" width={Math.max(0, W - splitX)} height={h} /></clipPath>
      </defs>

      {plannedCurve ? (
        <g pointerEvents="none">
          <path d={plannedAreaPath} fill="url(#volAreaFill)" clipPath="url(#volPast)" className="volArea" />
          <path d={plannedAreaPath} fill="url(#volAreaFill)" clipPath="url(#volAhead)" opacity="0.42" className="volArea" />
          {/* A wide, faint under-stroke in place of a blur filter: the same
              soft edge, but it stays a plain vector layer on the compositor. */}
          {range !== "all" ? (
            <path d={plannedCurve} pathLength="1" fill="none" stroke="var(--viz-measure)" strokeWidth="7" opacity="0.16" strokeLinecap="round" strokeLinejoin="round" className="volLine volLine--under" />
          ) : null}
          {/* At All the weeks are 9 px apart; the full-weight stroke and its
              halo turned the curve into a ribbon. */}
          <path d={plannedCurve} pathLength="1" fill="none" stroke="url(#volStroke)" strokeWidth={range === "all" ? 1.8 : 2.4} strokeLinecap="round" strokeLinejoin="round" className="volLine" />
        </g>
      ) : null}

      {/* Gridlines, barely there, and drawn over the fill. The km labels
          are HTML in the fixed axis column so they do not scroll away. */}
      {kmTicks.map((t, idx) => {
        if (t <= 0) return null;
        const y = Ykm(t);
        return <line key={`km-${idx}`} x1="0" y1={y} x2={W} y2={y} stroke="var(--viz-grid)" strokeWidth="0.5" pointerEvents="none" />;
      })}

      {/* Zero baseline — the reference every bar is measured from. */}
      <line x1="0" y1={baseY} x2={W} y2={baseY} stroke="var(--viz-baseline)" strokeWidth="1" pointerEvents="none" />

      {/* Actual volume — one rounded column per week, lit at the top. */}
      {xs.map((wk, idx) => {
        const actual = actualWeeklyMap.get(wk);
        if (wk > todayW || actual == null || !isFinite(actual) || actual <= 0) return null;
        const x = Xpos(idx);
        const bw = clamp(cellW * 0.56, 5, 16);
        const col = barColorForWeek(wk, actualWeeklyMap, plannedWeeklyMap, todayW, plannedThisWeekToDateKm);
        const grad = col.indexOf("under") >= 0 ? "volBarUnder" : col.indexOf("soft") >= 0 ? "volBarSoft" : "volBarOk";
        const top = Ykm(actual);
        return (
          <rect
            key={`bar-${wk}`}
            x={x - bw / 2}
            y={top}
            width={bw}
            height={Math.max(3, baseY - top)}
            fill={`url(#${grad})`}
            rx={Math.min(bw / 2, 5)}
            className="volBar"
            pointerEvents="none"
          />
        );
      })}

      {/* Today — a quiet rule to the baseline and a pulse on the plan line. */}
      {todayCy != null ? (
        <g pointerEvents="none">
          <line x1={splitX} y1={todayCy} x2={splitX} y2={baseY} stroke="var(--viz-today)" strokeWidth="1" opacity="0.28" />
          <circle cx={splitX} cy={todayCy} r="5" fill="var(--viz-today)" className="volPulse" />
          <circle cx={splitX} cy={todayCy} r="2.6" fill="var(--viz-today)" />
        </g>
      ) : null}

      {markerLines.map((m, i) => {
        const idx = xs.indexOf(m.wk);
        if (idx < 0) return null;
        const x = Xpos(idx);
        const col = m.color || "var(--content-tertiary)";
        const isGoal = m.label === "Goal";
        const dotY = padT - 8;            // sits in the top padding area
        return (
          <g key={`mk-${i}-${m.wk}`}>
            <line x1={x} y1={padT} x2={x} y2={baseY} stroke={col} strokeWidth="0.5" opacity="0.3" pointerEvents="none" />
            <circle cx={x} cy={dotY} r={isGoal ? 6 : 4} fill={col} className={"volPulse" + (isGoal ? " volPulse--goal" : "")} pointerEvents="none" />
            <circle cx={x} cy={dotY} r={isGoal ? 4 : 3} fill={col} pointerEvents="none" />
            {/* Large invisible hit target so the dot is easy to tap. */}
            <circle
              cx={x} cy={dotY} r="14" fill="transparent"
              style={{ cursor: "pointer" }}
              onClick={(e) => { e.stopPropagation(); onMarkerTap(m, x); }}
            />
          </g>
        );
      })}

      {monthTicks.map(({ wk, idx, label }) => {
        const x = Xpos(idx);
        /* Drop a month label that would collide with the one before it —
           at All, four weeks are ~40 px apart and a 3-letter label is ~24. */
        if (x - lastLabelX < minLabelGap) return null;
        lastLabelX = x;
        // At All the first week sits 4 px from the edge: a centred label
        // there is half outside the SVG. Anchor the ends inward.
        const anchor = x < 14 ? "start" : x > W - 14 ? "end" : "middle";
        return (
          <text key={`mo-${wk}`} x={anchor === "start" ? 1 : anchor === "end" ? W - 1 : x} y={baseY + 18} fontSize="12" fill="var(--viz-axis-label)" fontWeight="600" textAnchor={anchor} pointerEvents="none">
            {label}
          </text>
        );
      })}

      {/* Phase rail — one segment per block, named in place. At All it is
          also the way in: tap a phase to zoom to it. */}
      {phaseSegs.map((seg, i) => {
        if (!seg.phase) return null;
        const x0 = seg.start * cellW + 1;
        const x1 = (seg.end + 1) * cellW - 1;
        const tappable = range === "all";
        return (
          <g
            key={`ph-${i}`}
            className="volPhaseSeg"
            pointerEvents={tappable ? "all" : "none"}
            style={tappable ? { cursor: "pointer" } : undefined}
            onClick={tappable ? (e) => { e.stopPropagation(); onPhaseTap(seg); } : undefined}
          >
            <rect x={x0} y={railY} width={Math.max(2, x1 - x0)} height={railH} rx={railH / 2} fill={phaseTint(seg.phase)} />
            {x1 - x0 > 46 ? (
              <text x={(x0 + x1) / 2} y={railY + railH / 2 + 4} fontSize="12" fill={phaseInk(seg.phase)} fontWeight="600" textAnchor="middle">
                {titleCase(seg.phase)}
              </text>
            ) : null}
          </g>
        );
      })}
      {/* Recovery weeks — a notch on the rail, inside the parent phase. */}
      {xs.map((wk, idx) => isRecovery(wk) ? (
        <circle key={`rc-${wk}`} cx={Xpos(idx)} cy={railY + railH / 2} r="1.8" fill="var(--viz-halo)" opacity="0.75" pointerEvents="none" />
      ) : null)}
    </svg>
  );
});

function CombinedProgressChart({
  weeks,
  actualWeeklyMap,
  plannedWeeklyMap,
  phaseByWeek,             // Map<weekStartTs, { phase: "base"|"build"|"specific"|"taper", isRecoveryWeek: bool }>
  planStartWeek,
  goalWeek,
  todayWeekStartMs,        // +startOfWeek(today); a number so the memo holds
  planStartMs = null,      // +startOfWeek(plan anchor): week numbers count from here, as Train does
  onMarkerClick,
  plannedThisWeekToDateKm = null,  // sum of this-week plan for days < today
  markers = [],
  weekRuns = null,                 // Map<weekStartTs, [{name, km, status, done}]>
}) {
  /* The intro animation plays once per session, not once per visit to Stats.
     Module-level, not state: it must not survive a reload (the chart should
     still introduce itself when the app opens) and it must not re-render
     anything when it flips. */
  const introRef = useRef(!volIntroPlayed);
  useEffect(() => { volIntroPlayed = true; }, []);
  const isMobile = useIsMobileLayout();

  /* How many weeks fill the screen. Remembered per device; '12' when there
     is nothing remembered, and it must render right without storage at all
     (private mode, a PWA whose storage was cleared). */
  const [range, setRange] = useState(() => {
    try {
      const v = localStorage.getItem(VOL_RANGE_KEY);
      return v === "6" || v === "12" || v === "all" ? v : "12";
    } catch (_) { return "12"; }
  });
  /* The week under the centre line. It drives the headline. */
  const [focusWk, setFocusWk] = useState(null);
  /* Mouse only. A finger never hovers, and a touch that set this used to snap
     the readout back to today the moment it lifted. */
  const [hoverWk, setHoverWk] = useState(null);
  /* Whether the week panel under the chart is open. WHICH week it shows is
     whatever is focused — scrolling with it open re-reads it. */
  const [panelOpen, setPanelOpen] = useState(false);
  const [raceDetail, setRaceDetail] = useState(null);  // { left, marker } — popup for a race dot
  const [viewportW, setViewportW] = useState(375);
  const [todayOff, setTodayOff] = useState(false);
  const [settled, setSettled] = useState(true);
  const [zooming, setZooming] = useState(false);

  const scrollerRef = useRef(null);
  const plotRef = useRef(null);
  const panelRef = useRef(null);
  const rafRef = useRef(0);
  const settleTimer = useRef(0);
  const zoomTimer = useRef(0);
  const panelTimer = useRef(0);
  const scrubRef = useRef({ downX: null, moved: false });
  const prevRangeRef = useRef(null);
  /* The latest syncFocus, so a frame scheduled before a re-render does not
     run with the previous render's cellW. */
  const syncRef = useRef(null);
  /* A scroll we caused (centring on mount, on a zoom) must not play the
     readout's landing animation as if the user had flung the chart. */
  const progRef = useRef(false);
  /* Where the keyboard is heading. Each arrow press starts a smooth scroll,
     and the focus only follows once it lands — without this, three quick
     presses moved one week. */
  const keyIdxRef = useRef(null);

  const todayW = +startOfWeek(new Date(todayWeekStartMs));
  const minX = +startOfWeek(planStartWeek);
  const maxX = +startOfWeek(goalWeek);
  const xs = useMemo(
    () => (weeks || []).map((d) => +startOfWeek(d)).filter((x) => x >= minX && x <= maxX),
    [weeks, minX, maxX],
  );
  const n = xs.length;
  const todayIdx = xs.indexOf(todayW);

  /* Stops that make sense for this plan: a 9-week plan has no 12w view. */
  const stops = useMemo(() => [6, 12].filter((s) => n > s).map(String).concat("all"), [n]);
  const effRange = stops.includes(range) ? range : "all";
  const isAll = effRange === "all";
  /* Cell widths are quarter-pixel so a snap position and the scroll target
     computed from it never disagree by more than rounding. */
  const cellW = isAll
    ? Math.max(4, (viewportW - AXIS_W - 8) / Math.max(1, n))
    : Math.round((viewportW / Number(effRange)) * 4) / 4;
  const W = n * cellW;
  const h = isMobile ? 268 : 380;
  const padT = isMobile ? 26 : 30;  // headroom for race dot
  // Under the plot, in order: month labels, then the phase rail with the
  // phase name set inside it.
  const padB = isMobile ? 58 : 64;
  const plotH = h - padT - padB;

  /* One y scale for every zoom level, so changing the range never changes
     what a bar height means. */
  const kmMax = useMemo(() => {
    const all = [];
    for (const wk of xs) {
      const a = actualWeeklyMap.get(wk);
      const p = plannedWeeklyMap.get(wk);
      if (a != null && isFinite(a)) all.push(a);
      if (p != null && isFinite(p)) all.push(p);
    }
    return clamp(Math.max(10, ...all), 10, 240) * 1.15;
  }, [xs, actualWeeklyMap, plannedWeeklyMap]);
  // Three gridlines, not five: on a phone the extra two were noise.
  const kmTicks = useMemo(() => niceTicks(kmMax, isMobile ? 4 : 5), [kmMax, isMobile]);
  const Ykm = (km) => padT + (1 - (km || 0) / Math.max(1e-9, kmMax)) * plotH;

  const markerLines = useMemo(
    () => (markers || [])
      .map((m) => ({ ...m, wk: +startOfWeek(m.date) }))
      .filter((m) => m.wk >= minX && m.wk <= maxX)
      .sort((a, b) => a.wk - b.wk),
    [markers, minX, maxX],
  );

  /* ---- Scroll ↔ focus ---------------------------------------------------
     focusWk is derived from scrollLeft, never the other way round while a
     finger is down. One state update per week passed, none per frame. */
  const syncFocus = () => {
    const el = scrollerRef.current;
    if (!el || !n || isAll) return;
    const idx = clamp(Math.round(el.scrollLeft / cellW), 0, n - 1);
    const wk = xs[idx];
    setFocusWk((p) => (p === wk ? p : wk));
    // "Off screen" means not fully visible — the axis column counts as
    // covered, so a week half under the km labels gets the Today button.
    const off = todayIdx >= 0 && Math.abs(todayIdx - idx) * cellW > el.clientWidth / 2 - cellW / 2 - AXIS_W;
    setTodayOff((p) => (p === off ? p : off));
  };
  syncRef.current = syncFocus;
  const onScroll = () => {
    if (!rafRef.current) {
      rafRef.current = requestAnimationFrame(() => { rafRef.current = 0; if (syncRef.current) syncRef.current(); });
    }
    if (raceDetail) setRaceDetail(null);   // it would ride away under the edge fade
    if (progRef.current) { progRef.current = false; return; }
    setSettled((s) => (s ? false : s));
    clearTimeout(settleTimer.current);
    settleTimer.current = setTimeout(() => { keyIdxRef.current = null; setSettled(true); }, 140);
  };
  const centreCell = (wk, behavior) => {
    const idx = xs.indexOf(wk);
    if (idx < 0) return;
    if (isAll) { setFocusWk(wk); return; }
    const el = scrollerRef.current;
    if (!el) return;
    const b = behavior || (prefersReducedMotion() ? "auto" : "smooth");
    if (b === "auto" && Math.abs(el.scrollLeft - idx * cellW) > 0.5) progRef.current = true;
    el.scrollTo({ left: idx * cellW, behavior: b });
  };

  /* Measure the scroller before first paint and again on rotation. */
  useLayoutEffect(() => {
    const el = scrollerRef.current;
    if (!el) return undefined;
    const measure = () => {
      const w = el.clientWidth;
      if (w > 0) setViewportW((p) => (p === w ? p : w));
    };
    measure();
    if (typeof ResizeObserver === "undefined") return undefined;
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  /* Whenever the geometry changes — a new range, a rotation, first mount —
     put the focused week (or today) back under the centre line before the
     frame paints. A range change also plays the settle animation. */
  useLayoutEffect(() => {
    if (!n) return undefined;
    // A frame scheduled under the old geometry must not run under the new.
    if (rafRef.current) { cancelAnimationFrame(rafRef.current); rafRef.current = 0; }
    const target = (focusWk != null && xs.indexOf(focusWk) >= 0) ? focusWk : (todayIdx >= 0 ? todayW : xs[0]);
    if (isAll) {
      setFocusWk((p) => (p != null && xs.indexOf(p) >= 0 ? p : target));
      setTodayOff(false);
    } else {
      centreCell(target, "auto");
      syncFocus();
    }
    if (prevRangeRef.current != null && prevRangeRef.current !== effRange && !prefersReducedMotion()) {
      setZooming(true);
      clearTimeout(zoomTimer.current);
      zoomTimer.current = setTimeout(() => setZooming(false), 260);
    }
    prevRangeRef.current = effRange;
    setRaceDetail(null);
    try { localStorage.setItem(VOL_RANGE_KEY, effRange); } catch (_) {}
    return undefined;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [effRange, cellW, n]);

  /* Opening the panel must show the panel. The cells you tap sit near the
     bottom of the screen, so the panel grows open straight under the tab
     bar — bring it up once the height transition has finished. */
  useEffect(() => {
    if (!panelOpen) return undefined;
    const el = panelRef.current;
    if (!el || typeof el.scrollIntoView !== "function") return undefined;
    const reduced = prefersReducedMotion();
    clearTimeout(panelTimer.current);
    panelTimer.current = setTimeout(() => {
      try { el.scrollIntoView({ block: "nearest", behavior: reduced ? "auto" : "smooth" }); } catch (_) {}
    }, reduced ? 0 : 340);
    return () => clearTimeout(panelTimer.current);
  }, [panelOpen]);

  useEffect(() => () => {
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    clearTimeout(settleTimer.current);
    clearTimeout(zoomTimer.current);
    clearTimeout(panelTimer.current);
  }, []);

  const onPhaseTap = useCallback((seg) => {
    const wk = xs[seg.start];
    if (wk == null) return;
    setFocusWk(wk);
    setRange(stops.includes("12") ? "12" : stops[0]);
  }, [xs, stops]);
  const onMarkerTap = useCallback((m, x) => {
    setRaceDetail((prev) => {
      if (prev && prev.marker === m) return null;
      // Clamp to the visible window so the popup never hangs off the edge.
      const el = scrollerRef.current;
      const padStart = isAll ? AXIS_W : viewportW / 2 - cellW / 2;
      const sl = el ? el.scrollLeft : 0;
      const cw = el ? el.clientWidth : viewportW;
      return { marker: m, left: clamp(x, sl - padStart + 112, sl - padStart + cw - 112) };
    });
  }, [isAll, viewportW, cellW]);

  if (!n) return null;

  const isRecovery = (wk) => !!(phaseByWeek && phaseByWeek.get(wk) && phaseByWeek.get(wk).isRecoveryWeek);

  /* ---- Readout ----------------------------------------------------------
     The chart leads with a number, and the number is the week under the
     centre line. Mouse hover (desktop) can preview another week. */
  const hoverOk = hoverWk != null && xs.indexOf(hoverWk) >= 0;
  const focusOk = focusWk != null && xs.indexOf(focusWk) >= 0;
  const readWk = hoverOk ? hoverWk : focusOk ? focusWk : (todayIdx >= 0 ? todayW : xs[n - 1]);
  const readIdx = xs.indexOf(readWk);
  /* The panel, the lit cell and the cursor all mean the FOCUSED week; a
     mouse hovering elsewhere previews the headline only. */
  const panelWk = focusOk ? focusWk : readWk;
  /* Week numbers count from the plan's start, the same origin Train uses —
     the chart used to count from the first week of history, so the two
     tabs disagreed about which week it was. Weeks before the plan have no
     number. */
  const wkNum = (wk) => (planStartMs != null && wk >= planStartMs) ? Math.round((wk - planStartMs) / (7 * 864e5)) + 1 : null;
  const wkLabel = (wk) => { const k = wkNum(wk); return k != null ? `Week ${k}` : "Before plan"; };
  const wkDate = (wk) => `${new Date(wk).getDate()} ${monthShort(new Date(wk))}`;
  const readPlannedFull = plannedWeeklyMap.get(readWk);
  const readActual = actualWeeklyMap.get(readWk);
  const readIsCurrent = readWk === todayW;
  const readIsFuture = readWk > todayW;
  // Mid-week, the fair reference is the plan due so far — the same one the
  // bar colour uses. Comparing Wednesday against the whole week reads as a
  // deficit that has not happened yet.
  const readRef = readIsCurrent && plannedThisWeekToDateKm != null ? plannedThisWeekToDateKm : readPlannedFull;
  const readValue = readIsFuture ? readPlannedFull : readActual;
  const readDelta = (!readIsFuture && readValue != null && readRef != null) ? readValue - readRef : null;
  const readSub = readIsFuture
    ? "Planned"
    : readValue == null || readValue === 0
      ? "No runs logged"
      : readRef != null
        ? `of ${fmt1(readRef)} km planned${readIsCurrent ? " so far" : ""}`
        : "Logged";
  // Week on week. Past weeks compare what was run; this week and the weeks
  // ahead compare what the plan asks, which is the only like-for-like there is.
  const prevWk = readIdx > 0 ? xs[readIdx - 1] : null;
  const cmpPlanned = readIsFuture || readIsCurrent;
  const cmpA = prevWk == null ? null : cmpPlanned ? plannedWeeklyMap.get(readWk) : actualWeeklyMap.get(readWk);
  const cmpB = prevWk == null ? null : cmpPlanned ? plannedWeeklyMap.get(prevWk) : actualWeeklyMap.get(prevWk);
  const cmpDelta = (cmpA != null && cmpB != null && isFinite(cmpA) && isFinite(cmpB)) ? cmpA - cmpB : null;

  /* The cursor dot rides the plan curve at the focused week. */
  const cursorWk = isAll ? readWk : (focusOk ? focusWk : readWk);
  const cursorIdx = xs.indexOf(cursorWk);
  const cursorPlanned = plannedWeeklyMap.get(cursorWk);
  const cursorY = cursorPlanned != null && isFinite(cursorPlanned) ? Ykm(cursorPlanned) : null;
  const cursorX = isAll ? `${AXIS_W + cursorIdx * cellW + cellW / 2}px` : "50%";

  /* Which week is under a client x, in the plot's own (scrolling) frame. */
  const wkAtClientX = (clientX) => {
    const el = plotRef.current;
    if (!el) return null;
    const r = el.getBoundingClientRect();
    return xs[clamp(Math.floor((clientX - r.left) / cellW), 0, n - 1)];
  };
  /* Touch at All scrubs the focus by x. A tap must still be a tap, so the
     scrub only starts after 6 px of travel and a scrub swallows the click
     that follows it. */
  const onPlotPointerDown = (e) => { scrubRef.current = { downX: e.clientX, moved: false }; };
  const onPlotPointerMove = (e) => {
    if (e.pointerType === "mouse") { setHoverWk(wkAtClientX(e.clientX)); return; }
    if (!isAll || scrubRef.current.downX == null) return;
    if (!scrubRef.current.moved && Math.abs(e.clientX - scrubRef.current.downX) < 6) return;
    scrubRef.current.moved = true;
    const wk = wkAtClientX(e.clientX);
    if (wk != null) setFocusWk((p) => (p === wk ? p : wk));
  };
  const onPlotPointerUp = () => { scrubRef.current.downX = null; };
  const onPlotPointerCancel = () => { scrubRef.current = { downX: null, moved: false }; };
  const onPlotPointerLeave = (e) => { if (e.pointerType === "mouse") setHoverWk(null); };
  const onPlotClick = (e) => {
    if (scrubRef.current.moved) { scrubRef.current.moved = false; return; }
    if (raceDetail) { setRaceDetail(null); return; }
    const wk = wkAtClientX(e.clientX);
    if (wk == null) return;
    // At All a cell is ~9 px wide; a tap one cell off the focused week
    // still means "this one".
    const near = isAll && focusOk && Math.abs(xs.indexOf(wk) - xs.indexOf(focusWk)) <= 1;
    if (wk === focusWk || near) setPanelOpen((p) => !p);
    else centreCell(wk);
  };
  const onCellClick = (wk) => {
    // Keep keyboard focus on the scroller so arrows and Enter keep working
    // after a click lands on a cell button.
    try { scrollerRef.current.focus({ preventScroll: true }); } catch (_) {}
    if (wk === focusWk) setPanelOpen((p) => !p);
    else centreCell(wk);
  };
  const onKeyDown = (e) => {
    if ((e.key === "Enter" || e.key === " ") && e.target !== e.currentTarget) return;  // a cell button handles its own
    const idx = keyIdxRef.current != null ? keyIdxRef.current : xs.indexOf(focusOk ? focusWk : readWk);
    let next = null;
    if (e.key === "ArrowLeft") next = Math.max(0, idx - 1);
    else if (e.key === "ArrowRight") next = Math.min(n - 1, idx + 1);
    else if (e.key === "Home") next = 0;
    else if (e.key === "End") next = n - 1;
    else if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setPanelOpen((p) => !p); return; }
    if (next == null) return;
    e.preventDefault();
    keyIdxRef.current = next;
    if (isAll) keyIdxRef.current = null;
    centreCell(xs[next]);
  };

  const eyebrowText = wkLabel(readWk);
  const eyebrowDate = wkDate(readWk);

  return (
    <div className={"volChart" + (introRef.current ? " volIntro" : "")} style={{ marginTop: 8, width: "100%", userSelect: "none", WebkitUserSelect: "none" }}>
      {/* Headline — the week under the centre line, stated in words and one
          big number. The number lands once the scroll settles, not on every
          week a fling passes. Every line has a fixed height, so the plot
          under it never moves while you scroll. */}
      <div className={"volChart__head" + (settled ? " is-settled" : "")}>
        <div className="volChart__readBlock">
          <div className="volChart__eyebrow" id="volChartEyebrow">
            {eyebrowText}
            <span className="volChart__dotSep" />
            {eyebrowDate}
            {isRecovery(readWk) ? <span className="volChart__tag">Recovery</span> : null}
          </div>
          <div className="volChart__readout">
            <span className="volChart__num">{readValue != null ? fmt1(readValue) : "—"}</span>
            <span className="volChart__unit">km</span>
            {readDelta != null && Math.abs(readDelta) >= 0.05 ? (
              <span className={"volChart__delta" + (readDelta >= 0 ? " is-up" : " is-down")}>
                {readDelta >= 0 ? "+" : "−"}{fmt1(Math.abs(readDelta))}
              </span>
            ) : null}
          </div>
          <div className="volChart__sub">{readSub}</div>
        </div>
      </div>
      {/* Week on week on the left, the range on the right. The pill used to
          sit beside the readout and squeezed it to 145 px at 375 px. */}
      <div className="volChart__bar">
        <div className="volChart__cmp">
          {cmpDelta != null && Math.abs(cmpDelta) >= 0.05
            ? `${cmpDelta >= 0 ? "+" : "−"}${fmt1(Math.abs(cmpDelta))} km vs last week`
            : ""}
        </div>
        <div className="volChart__ctl">
          {todayOff && !isAll ? (
            <button type="button" className="volChart__now" onClick={() => centreCell(todayW)}>Today</button>
          ) : null}
          {stops.length > 1 ? (
            <div className="chartSeg" role="group" aria-label="Range">
              {stops.map((s) => (
                <button
                  key={s}
                  type="button"
                  className={"chartSeg__btn" + (effRange === s ? " on" : "")}
                  aria-pressed={effRange === s}
                  onClick={() => setRange(s)}
                >
                  {s === "all" ? "All" : `${s}w`}
                </button>
              ))}
            </div>
          ) : null}
        </div>
      </div>

      <div
        className={"volPlotWrap" + (zooming ? " is-zooming" : "")}
        data-range={effRange}
        style={{ "--cellW": `${cellW}px`, "--cursor-x": cursorX, "--cursor-y": cursorY != null ? `${cursorY}px` : "-100px" }}
      >
        {/* The km axis stays put while the plot scrolls under it. */}
        <div className="volAxis" aria-hidden="true" style={{ height: h }}>
          {kmTicks.map((t) => t > 0 ? (
            <span key={`ax-${t}`} style={{ top: Ykm(t) }}>{Math.round(t)}</span>
          ) : null)}
        </div>
        <div
          className="volScroller"
          ref={scrollerRef}
          data-range={effRange}
          onScroll={isAll ? undefined : onScroll}
          onKeyDown={onKeyDown}
          tabIndex={0}
          role="group"
          aria-label={`Weekly volume, ${n} weeks. ${eyebrowText}: ${readValue != null ? fmt1(readValue) : "no"} km.`}
        >
          <div className="volScroller__body" style={{ width: W }}>
            <div
              className="volScroller__plot"
              ref={plotRef}
              style={{ height: h }}
              onPointerDown={onPlotPointerDown}
              onPointerMove={onPlotPointerMove}
              onPointerUp={onPlotPointerUp}
              onPointerCancel={onPlotPointerCancel}
              onPointerLeave={onPlotPointerLeave}
              onClick={onPlotClick}
            >
              <VolPlot
                xs={xs}
                cellW={cellW}
                W={W}
                h={h}
                padT={padT}
                padB={padB}
                kmMax={kmMax}
                kmTicks={kmTicks}
                actualWeeklyMap={actualWeeklyMap}
                plannedWeeklyMap={plannedWeeklyMap}
                phaseByWeek={phaseByWeek}
                markerLines={markerLines}
                todayW={todayW}
                plannedThisWeekToDateKm={plannedThisWeekToDateKm}
                range={effRange}
                onPhaseTap={onPhaseTap}
                onMarkerTap={onMarkerTap}
              />
              {raceDetail ? (() => {
                const m = raceDetail.marker;
                const weeksTo = Math.max(0, Math.ceil((+m.date - +new Date()) / (7 * 864e5)));
                const isStart = m.label === "Start";
                const isGoal  = m.label === "Goal";
                const heading = isStart ? "Plan start" : isGoal ? "Goal race" : (m.title || `${m.label} race`);
                return (
                  <div
                    className="raceDetail"
                    role="dialog"
                    aria-label={heading}
                    style={{ left: raceDetail.left }}
                    onClick={(e) => e.stopPropagation()}
                    onPointerDown={(e) => e.stopPropagation()}
                  >
                    <button type="button" className="raceDetail__close" onClick={() => setRaceDetail(null)} aria-label="Close">✕</button>
                    <div className="raceDetail__head">
                      <span className="raceDetail__dot" style={{ background: m.color }} />
                      <span className="raceDetail__title">{heading}</span>
                    </div>
                    <div className="raceDetail__date">{formatDateLong(new Date(m.date))}</div>
                    {!isStart ? (
                      <div className="raceDetail__meta">
                        {weeksTo > 0 ? `${weeksTo} ${weeksTo === 1 ? "week" : "weeks"} out` : "This week"}
                      </div>
                    ) : null}
                    {m.distanceKm != null && !isStart ? (
                      <div className="raceDetail__rows">
                        <div className="raceDetail__row"><span>Distance</span><b>{fmt1(m.distanceKm)} km</b></div>
                        {m.pace ? <div className="raceDetail__row"><span>Target pace</span><b>{m.pace}/km</b></div> : null}
                        {m.zone ? <div className="raceDetail__row"><span>Zone</span><b>{m.zone}</b></div> : null}
                        {m.timeStr ? <div className="raceDetail__row"><span>Target time</span><b>{m.timeStr}</b></div> : null}
                      </div>
                    ) : null}
                    {isGoal ? (
                      <button
                        type="button"
                        className="raceDetail__cta"
                        onClick={() => { setRaceDetail(null); if (onMarkerClick) onMarkerClick(m); }}
                      >Adjust goal →</button>
                    ) : null}
                  </div>
                );
              })() : null}
            </div>

            {/* Week labels — quiet, one per cell, the snap targets. */}
            <div className="volScroller__axis" role="listbox" aria-label="Weeks">
              {xs.map((wk) => {
                const isNow = wk === todayW;
                const isFocus = wk === panelWk;
                const planned = plannedWeeklyMap.get(wk);
                const actual = actualWeeklyMap.get(wk);
                // A week with nothing run yet — today's included — shows what
                // it asks for rather than a dash.
                const shown = (wk <= todayW && actual != null && actual > 0) ? actual : planned;
                return (
                  <button
                    type="button"
                    key={`wc-${wk}`}
                    data-wk={wk}
                    role="option"
                    aria-selected={isFocus}
                    aria-label={`${wkLabel(wk)}, ${wkDate(wk)}, ${shown != null ? fmt1(shown) + " km" : "no data"}`}
                    className={"wkCell" + (isFocus ? " is-focus" : "") + (isNow ? " is-now" : "") + (isRecovery(wk) ? " is-recovery" : "")}
                    onClick={() => onCellClick(wk)}
                    tabIndex={-1}
                  >
                    <span className="wkCell__km">{shown != null ? Math.round(shown) : "—"}</span>
                    {/* The Monday's date — a week number is in the headline,
                        and "W23" told you nothing a date does not. */}
                    <span className="wkCell__lbl">{isNow ? "Now" : wkDate(wk)}</span>
                  </button>
                );
              })}
            </div>
          </div>
        </div>
        {/* The cursor is HTML over the scroller: a centre line that never
            moves while the content does, and a dot that rides the curve. */}
        <div className="volCursor" aria-hidden="true" style={{ top: 0, height: h, "--plotTop": `${padT}px`, "--plotH": `${plotH}px` }}>
          <span className="volCursor__spot" />
          <span className="volCursor__line" />
          <span className="volCursor__dot" />
        </div>
      </div>

      {/* The week panel — in flow under the chart, grows open. Always
          mounted so its height can animate; the content follows readWk. */}
      <div className={"weekPanel__wrap" + (panelOpen ? " is-open" : "")} aria-hidden={!panelOpen} ref={panelRef}>
        <div className="weekPanel__inner">
          {(() => {
            const wk = panelWk;
            const runs = (weekRuns && weekRuns.get(wk)) ? weekRuns.get(wk) : [];
            const plK = plannedWeeklyMap.get(wk);
            const acK = actualWeeklyMap.get(wk);
            const races = (markers || []).filter((mk) => mk && mk.date && +startOfWeek(new Date(mk.date)) === wk);
            const wkIdx = xs.indexOf(wk);
            const mon = new Date(wk);
            const sun = new Date(wk + 6 * 864e5);
            // "7–13 Dec", or "28 Sep – 4 Oct" when the week straddles a month.
            const rangeStr = mon.getMonth() === sun.getMonth()
              ? `${mon.getDate()}–${sun.getDate()} ${monthShort(sun)}`
              : `${mon.getDate()} ${monthShort(mon)} – ${sun.getDate()} ${monthShort(sun)}`;
            const isCurrent = wk === todayW;
            const isFuture = wk > todayW;
            const pct = (plK != null && plK > 0 && acK != null) ? clamp((acK / plK) * 100, 0, 100) : 0;
            const ph = phaseByWeek && phaseByWeek.get(wk) ? phaseByWeek.get(wk) : null;
            const statusLabel = (st) =>
              st === "matched" ? "Done" :
              st === "partial" ? "Partial" :
              st === "missed"  ? "Missed" :
              st === "extra"   ? "Extra" :
              st === "today"   ? "Today" :
              st === "upcoming" ? "Upcoming" : "";
            const tab = panelOpen ? 0 : -1;
            return (
              <div className="weekPanel" role="region" aria-labelledby="weekPanelEyebrow">
                {/* No second headline: the number for this week is already
                    the big one above the chart. The panel is what the week
                    CONTAINED. */}
                <div className="weekSheet__head">
                  <div className="weekSheet__headL">
                    <div className="weekSheet__eyebrow" id="weekPanelEyebrow">
                      {wkIdx >= 0 ? wkLabel(wk) : "Week"}
                      <span className="volChart__dotSep" />
                      {rangeStr}
                    </div>
                  </div>
                  <button type="button" className="weekDetail__close" onClick={() => setPanelOpen(false)} aria-label="Close" tabIndex={tab}>
                    <X size={16} aria-hidden="true" />
                  </button>
                </div>

                <div className="weekSheet__tags">
                  {ph && ph.phase ? (
                    <span className="weekSheet__tag" style={{ color: phaseInk(ph.phase), background: phaseTint(ph.phase) }}>
                      {titleCase(ph.phase)}
                    </span>
                  ) : null}
                  {ph && ph.isRecoveryWeek ? <span className="weekSheet__tag">Recovery week</span> : null}
                  {isCurrent ? <span className="weekSheet__tag weekSheet__tag--now">This week</span> : null}
                </div>

                {/* Actual against the full week, so the panel answers "how much
                    of this week is behind me" without reading two numbers. */}
                {!isFuture && plK != null && plK > 0 ? (
                  <div className="weekSheet__bar" aria-hidden="true">
                    <span style={{ width: `${pct}%` }} />
                  </div>
                ) : null}

                {races.length > 0 && (
                  <div className="weekSheet__races">
                    {races.map((mk, i) => (
                      <div key={`wr-${i}`} className="weekSheet__race">
                        <span className="weekSheet__raceDot" style={{ background: mk.color }} />
                        {mk.label || "Race"}{mk.title ? ` · ${mk.title}` : ""}
                      </div>
                    ))}
                  </div>
                )}

                <div className="weekSheet__runs">
                  {runs.length === 0 ? (
                    <div className="weekDetail__empty">{isFuture ? "No sessions scheduled." : "No runs this week."}</div>
                  ) : runs.map((r, i) => {
                    const d = r.date ? parseLocalDate(r.date) : null;
                    return (
                      <div key={`rn-${i}`} className={"weekSheet__run" + (r.done ? " is-done" : " is-planned")}>
                        <span className="weekSheet__day">{d ? DAY_NAMES[d.getDay()] : ""}</span>
                        <span className="weekSheet__runDot" style={{ background: runTypeColor(r.type) }} />
                        <span className="weekSheet__runName">{r.name}</span>
                        <span className="weekSheet__runKm">{r.km != null ? `${r.est ? "~" : ""}${fmt1(r.km)} km` : "—"}</span>
                        {r.status && r.status !== "upcoming" ? (
                          <span className={`weekSheet__badge weekSheet__badge--${r.status}`}>{statusLabel(r.status)}</span>
                        ) : null}
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })()}
        </div>
      </div>
    </div>
  );
}


/* =========================
   List disclosure + Train rows
   Module scope on purpose: TrainRow and PhasePill used to be declared inside
   the Train tab's render closure, so React saw a new component type on every
   render and remounted every row — the press feedback flashed and nothing in
   a row could animate.
========================= */
/* =========================
   Run detail sheet
   A run against its plan, in the session-sheet idiom: a type-coloured
   spine, the verdict where the train sheet says "Today", three tiles with
   the run's numbers and their deltas, a bar of the distance against the
   plan, the heart rate as the effort line, the plan and the Strava run as
   two rows, one note that says what happened. Replaces the old
   section-and-table modal the Last run row and the Stats list opened.
========================= */
const rdZoneName = (z) => (z === "Z1" ? "Recovery" : z === "Z2" ? "Easy" : z === "Z3" ? "Tempo" : z === "Z4" ? "Threshold" : z === "Z5" ? "VO2max" : "");
const rdZoneClass = (z) => (z === "Z1" || z === "Z2" ? "green" : z === "Z5" ? "red" : "amber");
const rdSessionPhrase = (title) => { const s = String(title || "").toLowerCase(); return s ? `${/^[aeiou]/.test(s) ? "an" : "a"} ${s} session` : "this session"; };
const rdPaceWords = (dp) => { const a = Math.abs(dp); return a < 60 ? `${Math.round(a)} s/km` : `${Math.floor(a / 60)}:${pad2(Math.round(a % 60))} min/km`; };
const rdPaceDelta = (dp) => {
  const a = Math.abs(dp);
  const m = Math.floor(a / 60);
  const s = Math.round(a % 60);
  return `${dp < 0 ? "−" : "+"}${m > 0 ? `${m}:${pad2(s)}` : `0:${pad2(s)}`}`;
};
function RunDetailSheet({ row, effectiveMaxHr, onClose }) {
  const sheetRef = useRef(null);
  const bodyRef = useRef(null);
  const overlayRef = useRef(null);
  const closeRef = useRef(onClose);
  closeRef.current = onClose;
  useSheetPull({ sheetRef, bodyRef, overlayRef, closeRef, dirtyRef: null });
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") closeRef.current && closeRef.current(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, []);

  const ds = row || {};
  const planned = ds.planned || null;
  const actual = ds.actual || null;
  const plannedTitle = planned ? (planned.title || planned.label || formatSessionTypeLabel(planned.type) || null) : null;
  const title = plannedTitle || (actual && actual.name) || "Run";
  const typeCol = planned ? sessionTypeColor(plannedTitle || planned.type) : "var(--viz-easy)";
  const dateStr = ds.dateLabel || (ds.date ? formatDateShort(ds.date) : "");
  const startAt = actual && actual.start_date ? new Date(actual.start_date) : null;
  const timeStr = startAt && !isNaN(+startAt) ? startAt.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit", hour12: false }) : null;

  const actualDurMin = actual && actual.moving_time_s ? Math.round(actual.moving_time_s / 60) : null;
  const plannedDurMin = planned && planned.durationMin ? planned.durationMin : null;
  const actualKm = ds.actualKm != null ? ds.actualKm : null;
  const plannedKm = ds.plannedKm != null ? ds.plannedKm : null;
  /* The matcher only sets actualP when the source supplied it; distance
     over time is the definition of pace. */
  const shownActualP = ds.actualP != null ? ds.actualP : (actualKm > 0 && actualDurMin > 0 ? Math.round((actualDurMin * 60) / actualKm) : null);
  const dk = plannedKm != null && actualKm != null ? actualKm - plannedKm : null;
  const dp = ds.plannedP != null && shownActualP != null ? shownActualP - ds.plannedP : null;
  const dd = actualDurMin != null && plannedDurMin != null ? actualDurMin - plannedDurMin : null;
  const badge = statusBadge(ds.status, dp);
  const status = String(ds.status || "").toLowerCase();

  const avgHr = actual && actual.avg_hr ? round0(actual.avg_hr) : null;
  const maxHr = actual && actual.max_hr ? round0(actual.max_hr) : null;
  const zl = actual ? hrZoneLabel(actual.avg_hr, effectiveMaxHr) : null;
  const zoneClass = zl ? rdZoneClass(zl.zone) : "green";
  const expectedHrRange = (() => {
    const t = (plannedTitle || "").toLowerCase();
    const zone = t.includes("easy") || t.includes("recovery") ? "Z2"
      : t.includes("tempo") || t.includes("threshold") ? "Z3-Z4"
      : t.includes("long") || t.includes("b2b") ? "Z2-Z3"
      : t.includes("time") || t.includes("feet") || t.includes("hike") ? "Z2"
      : (planned && planned.zone) || null;
    if (!effectiveMaxHr || !zone) return null;
    const bounds = { Z1: [0.50, 0.60], Z2: [0.60, 0.70], Z3: [0.70, 0.80], Z4: [0.80, 0.85], Z5: [0.85, 1.0] };
    const parts = zone.split(/[–-]/).map((s) => s.trim());
    const lo = bounds[parts[0]];
    const hi = bounds[parts[parts.length - 1]];
    if (!lo || !hi) return null;
    return { low: Math.round(lo[0] * effectiveMaxHr), high: Math.round(hi[1] * effectiveMaxHr) };
  })();
  const hrGap = avgHr && expectedHrRange ? avgHr - expectedHrRange.high : null;

  const pct = plannedKm > 0 && (actual || status === "missed") ? Math.min(100, ((actualKm || 0) / plannedKm) * 100) : null;
  const fillTone = status === "missed" ? "is-missed" : pct != null && pct < 95 ? "is-under" : "";

  // Deltas: suppressed when they are rounding noise.
  const dkChip = dk == null || Math.abs(dk) < 0.05 ? null : { text: `${dk >= 0 ? "+" : "−"}${fmt1(Math.abs(dk))}`, tone: dk >= 0 ? "green" : "red" };
  const dpChip = dp == null || Math.abs(dp) < 5 ? null : { text: rdPaceDelta(dp), tone: dp < -30 ? "red" : dp <= 0 ? "green" : dp <= 30 ? "amber" : "red" };
  const ddChip = dd == null || Math.abs(dd) < 1 ? null : { text: `${dd >= 0 ? "+" : "−"}${Math.abs(dd)} min`, tone: Math.abs(dd) <= 2 ? "green" : dd > 0 ? "amber" : "green" };

  const tile = (v, u, l, chip, plannedOnly) => (
    <div className={"tsStat rdStat" + (plannedOnly ? " rdStat--plan" : "")}>
      <span className="tsStat__v">{v}{u ? <span className="tsStat__u">{u}</span> : null}{chip ? <span className={`rdStat__d is-${chip.tone}`}>{chip.text}</span> : null}</span>
      <span className="tsStat__l">{l}</span>
    </div>
  );
  const paceOf = (p) => secPerKmToPace(p).replace("/km", "");
  const plannedBits = planned ? [plannedKm > 0 ? `${fmt1(plannedKm)} km` : null, ds.plannedP ? `${paceOf(ds.plannedP)}/km` : null, plannedDurMin ? fmtDuration(plannedDurMin, "min") : null].filter(Boolean) : [];
  const actualBits = actual ? [actualKm != null ? `${fmt1(actualKm)} km` : null, shownActualP ? `${paceOf(shownActualP)}/km` : null, actualDurMin ? fmtDuration(actualDurMin, "min") : null].filter(Boolean) : [];

  const note = (() => {
    if (!actual) {
      if (status === "missed") return `No run matched this session${plannedBits.length ? ` (${plannedBits.join(", ")})` : ""}. The week's load counts it as missed.`;
      if (status === "today") return `Due today${plannedBits.length ? `: ${plannedBits.join(", ")}` : ""}. Not run yet.`;
      if (status === "upcoming") return "Not run yet.";
      return "No run matched this session.";
    }
    const parts = [];
    if (status === "extra") parts.push("A run the plan did not ask for. It counts toward the week's volume.");
    else if (pct != null && pct < 95) parts.push(`${Math.round(pct)}% of the planned distance.`);
    if (dp != null) {
      if (Math.abs(dp) < 10) parts.push("On pace.");
      else parts.push(`${rdPaceWords(dp)} ${dp > 0 ? "slower" : "faster"} than planned.`);
    }
    if (hrGap != null) {
      if (hrGap <= 0) parts.push("Heart rate in the zone.");
      else if (hrGap <= 15) parts.push("Heart rate slightly above the zone.");
      else parts.push(`Heart rate well above the zone for ${rdSessionPhrase(plannedTitle)}: a harder effort than intended.`);
    }
    if (!parts.length) {
      const src = activitySourceLabel(actual && actual.id);
      parts.push(src === "File" ? "Logged from an uploaded file." : src === "Run" ? "Logged from your watch." : `Logged from ${src}.`);
    }
    return parts.join(" ");
  })();

  const stravaHref = actual && actual.id != null && /^\d+$/.test(String(actual.id)) ? `https://www.strava.com/activities/${actual.id}` : null;

  return (
    <div className="bottomSheetOverlay" ref={overlayRef} onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div
        ref={sheetRef}
        className="bottomSheetPanel tsSheet rdSheet"
        style={{ "--type": typeCol }}
        onMouseDown={(e) => e.stopPropagation()}
        role="dialog"
        aria-label={`${title}, ${dateStr}`}
      >
        <div className="bottomSheetDragHandle" aria-hidden="true" />
        <div className="tsHead">
          <div className="tsHead__spine" aria-hidden="true" />
          <div className="tsHead__text">
            <div className="tsHead__title">{title}</div>
            <div className="tsHead__date">
              <span className={`tsHead__when rdWhen rdWhen--${badge.tone}`}>{badge.label}{badge.subLabel ? ` · ${badge.subLabel}` : ""}</span>
              {dateStr ? <><span className="tsHead__sep" />{dateStr}</> : null}
              {timeStr ? <><span className="tsHead__sep" />{timeStr}</> : null}
            </div>
          </div>
          {zl ? <span className={`pvaZonePill pvaZonePill--${zoneClass}`}>{zl.zone}</span> : null}
          <button type="button" className="bottomSheetClose" onClick={onClose} aria-label="Close"><X size={16} aria-hidden="true" /></button>
        </div>

        <div ref={bodyRef} className="bottomSheetBody tsBody rdBody">
          <div className="tsStats rdStats">
            {tile(actual ? (actualKm != null ? fmt1(actualKm) : "—") : plannedKm != null ? fmt1(plannedKm) : "—", "km", actual ? "Distance" : "Planned", actual ? dkChip : null, !actual)}
            {tile(shownActualP ? paceOf(shownActualP) : ds.plannedP ? paceOf(ds.plannedP) : "—", "/km", actual ? "Pace" : "Planned pace", actual ? dpChip : null, !actual)}
            {tile(actualDurMin ? fmtDuration(actualDurMin, "min") : plannedDurMin ? fmtDuration(plannedDurMin, "min") : "—", null, actual ? "Time" : "Planned time", actual ? ddChip : null, !actual)}
          </div>

          {pct != null ? (
            <div className="rdTrack" role="img" aria-label={`${fmt1(actualKm || 0)} of ${fmt1(plannedKm)} kilometres planned, ${Math.round(pct)} percent`}>
              <div className="rdTrack__bar">
                <span className={"rdTrack__fill " + fillTone} style={{ width: `${pct}%` }} />
                <span className="rdTrack__plan" aria-hidden="true" />
              </div>
              <div className="rdTrack__legend">
                <span>{fmt1(actualKm || 0)} of {fmt1(plannedKm)} km</span>
                <span>{Math.round(pct)}%</span>
              </div>
            </div>
          ) : null}

          {avgHr && zl ? (
            <div className={`tsEffort rdEffort tsEffort--${zoneClass}`}>
              <span className="tsEffort__dot" aria-hidden="true" />
              <span>
                <strong>{avgHr} bpm</strong> avg{maxHr ? `, ${maxHr} max` : ""} · {zl.zone} {rdZoneName(zl.zone)}
                {expectedHrRange ? `. Expected ${expectedHrRange.low}–${expectedHrRange.high} for ${rdSessionPhrase(plannedTitle)}.` : "."}
              </span>
            </div>
          ) : null}

          {planned || actual ? (
            <div className="rdRows">
              {planned ? (
                <div className="rdRow">
                  <span className="rdRow__l">Plan</span>
                  <span className="rdRow__v">{plannedTitle}{plannedBits.length ? <em> · {plannedBits.join(" · ")}</em> : null}</span>
                </div>
              ) : null}
              {actual ? (
                <div className="rdRow">
                  <span className="rdRow__l">{activitySourceLabel(actual.id) === "Run" ? "Actual" : activitySourceLabel(actual.id)}</span>
                  <span className="rdRow__v">{actual.name || "Run"}{actualBits.length ? <em> · {actualBits.join(" · ")}</em> : null}</span>
                </div>
              ) : null}
            </div>
          ) : null}

          <div className="agNote rdNote">{note}</div>
        </div>

        <div className="tsActions">
          <button type="button" className="sessionSheetBtn sessionSheetBtn--secondary" onClick={onClose}>Close</button>
          {stravaHref ? (
            <a className="sessionSheetBtn sessionSheetBtn--primary rdStrava" href={stravaHref} target="_blank" rel="noopener noreferrer">Open in Strava</a>
          ) : null}
        </div>
      </div>
    </div>
  );
}

function ListMore({ label, count, open, onClick, className }) {
  return (
    <button
      type="button"
      className={"listMore" + (className ? ` ${className}` : "")}
      aria-expanded={open == null ? undefined : open}
      onClick={onClick}
    >
      <span className="listMore__label">
        {label}
        {count != null ? (
          <span className="listMore__count"> · {count}{typeof count === "number" ? " more" : ""}</span>
        ) : null}
      </span>
      <ChevronDown size={16} aria-hidden="true" />
    </button>
  );
}

/* Phase-tinted pill used on every week section header. Uses phaseByWeek
   (same map the chart reads) so Train + Chart stay in lockstep. Recovery
   weeks: parent phase colour + striped pattern + " · Recovery" suffix.
   Matches methodology §Pillar 3. */
const PHASE_PILL_COLOURS = {
  base:     { fg: "var(--accent-on-tint)", bg: "rgb(var(--accent-rgb) / 0.16)", br: "rgb(var(--accent-rgb) / 0.38)" },
  build:    { fg: "var(--caution)", bg: "rgb(var(--caution-rgb) / 0.16)", br: "rgb(var(--caution-rgb) / 0.38)" },
  specific: { fg: "var(--critical)", bg: "rgb(var(--critical-rgb) / 0.16)", br: "rgb(var(--critical-rgb) / 0.38)" },
  taper:    { fg: "var(--content-secondary)", bg: "rgb(var(--neutral-rgb) / 0.22)", br: "rgb(var(--neutral-rgb) / 0.45)" },
};
function PhasePill({ meta }) {
  if (!meta || !meta.phase) return null;
  const c = PHASE_PILL_COLOURS[meta.phase] || PHASE_PILL_COLOURS.base;
  const label = meta.phase.charAt(0).toUpperCase() + meta.phase.slice(1);
  return (
    <span
      className={`phasePill${meta.isRecoveryWeek ? " phasePill--recovery" : ""}`}
      style={{ color: c.fg, background: c.bg, borderColor: c.br }}
    >
      <span className="phasePill__dot" style={{ background: c.fg }} />
      {label}{meta.isRecoveryWeek ? " · Recovery" : ""}
    </span>
  );
}

/* One planned session in the Train tab.
   status   — the matcher's verdict for a past session, or null
   likely   — a week the coach may still move: drawn provisional
   isToday  — marks the row you are standing on
   onOpen   — the row tap; the caller decides which sheet it opens */
function TrainRow({ s, status, effectiveMaxHr, likely, isToday, onOpen }) {
  const isRace = s.kind === "race";
  const name = s.title || s.label || s.type;
  const zoneStr = s.zone || "";
  const zoneClass = zoneStr === "Z1" || zoneStr === "Z2" || zoneStr === "Z1–Z2" ? "green" : zoneStr === "Z5" ? "red" : "amber";
  const typeCol = isRace ? (s.raceColor || "var(--critical)") : sessionTypeColor(s.title || s.type);

  // Match status drives the right-hand pill for non-race rows.
  // matched/partial → Done (dim row), missed → Missed (loud), else unchanged.
  const isDone   = status === "matched" || status === "partial";
  const isMissed = status === "missed";
  const lc = (s.type || "").toLowerCase();
  const isDurationBased = lc.includes("time-on-feet") || lc.includes("time on feet") || lc.includes("hike");
  const durStr = fmtDuration(s.durationMin, "min");
  // planGenerator v3 sets plannedDuration on every km-based session, so
  // durStr is populated for all types — not only duration-based ones.
  const metaStr = isDurationBased && durStr
    ? `${durStr}${s.distanceKm > 0 ? ` · ${fmt1(s.distanceKm)} km` : ""} · ${formatDateShort(s.date)}`
    : `${fmt1(s.distanceKm)} km${durStr ? ` · ${durStr}` : ""} · ${formatDateShort(s.date)}`;
  // Expected BPM range — uses the same zone bounds as the Plan-vs-Actual
  // view (expectedHrRange) so the range shown on the card matches the range
  // the Plan-vs-Actual table validates against later.
  const expectedBpm = (() => {
    if (!effectiveMaxHr || !zoneStr) return null;
    const zoneBounds = { Z1: [0.50, 0.60], Z2: [0.60, 0.70], Z3: [0.70, 0.80], Z4: [0.80, 0.85], Z5: [0.85, 1.0] };
    const parts = zoneStr.split(/[–-]/).map((p) => p.trim());
    const lo = zoneBounds[parts[0]];
    const hi = zoneBounds[parts[parts.length - 1]];
    if (!lo || !hi) return null;
    return { low: Math.round(lo[0] * effectiveMaxHr), high: Math.round(hi[1] * effectiveMaxHr) };
  })();
  return (
    <div
      className={
        "v7trainRow v7trainRow--tappable" +
        (likely ? " v7trainRow--likely" : "") +
        (isToday ? " is-today" : "") +
        (isDone ? " is-done" : "")
      }
      role="button"
      tabIndex={0}
      onClick={onOpen}
      onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onOpen(); } }}
      style={{ "--row-accent": typeCol }}
    >
      <div className="v7trainRowAccent" style={{ background: typeCol }} />
      <div className="v7trainRow__body">
        <div className="v7trainRow__name">
          {isRace ? <span className="v7trainRow__raceTag" style={{ color: typeCol, background: `${typeCol}22`, border: `0.5px solid ${typeCol}44` }}>RACE</span> : null}
          <span>{name}</span>
          {s.edited ? <span className="v7trainRow__edited">EDITED</span> : null}
        </div>
        <div className="v7trainRow__meta">
          {isToday ? <span className="trainToday">Today · </span> : null}
          {metaStr}
        </div>
        {isRace ? (
          s.pace ? <div className="v7trainRow__meta" style={{ fontVariantNumeric: "tabular-nums" }}>Target {String(s.pace).replace("/km", "")} /km</div> : null
        ) : expectedBpm ? (
          <div className="v7trainRow__meta" style={{ fontVariantNumeric: "tabular-nums" }}>
            {expectedBpm.low}–{expectedBpm.high} bpm
          </div>
        ) : zoneStr && !effectiveMaxHr ? (
          <div className="v7trainRow__meta">{zoneStr} effort · set max HR in settings</div>
        ) : null}
      </div>
      {isDone ? (
        <span className="pvaZonePill pvaZonePill--green">Done</span>
      ) : isMissed ? (
        <span className="pvaZonePill pvaZonePill--red">Missed</span>
      ) : zoneStr ? (
        <span className={`pvaZonePill pvaZonePill--${zoneClass}`}>{zoneStr}</span>
      ) : null}
    </div>
  );
}

/* =========================
   Controlled RESET triggers
========================= */
function adherenceBelowThresholdForWeeks(adherenceHistory, threshold = 0.6, weeks = 3) {
  if (!adherenceHistory || adherenceHistory.length < weeks) return false;
  const last = adherenceHistory.slice(-weeks);
  return last.every((w) => (w?.score ?? 0) < threshold);
}

function computeVolumeDeficit(actualWeeklyMap, plannedWeeklyMap) {
  const now = +startOfWeek(new Date());
  const weeks = [];
  for (let i = 1; i <= 4; i++) {
    const wk = +startOfWeek(addWeeks(new Date(), -i));
    if (wk < now) weeks.push(wk);
  }
  if (weeks.length < 2) return { deficit: false, avgRatio: 1 };

  let totalActual = 0;
  let totalPlanned = 0;
  for (const wk of weeks) {
    totalActual += actualWeeklyMap.get(wk) || 0;
    totalPlanned += plannedWeeklyMap.get(wk) || 0;
  }

  const avgRatio = totalPlanned > 0 ? totalActual / totalPlanned : 1;
  return { deficit: avgRatio < 0.75, avgRatio };
}

function computeVolumeAhead(actualWeeklyMap, plannedWeeklyMap) {
  const now = +startOfWeek(new Date());
  const weeks = [];
  for (let i = 1; i <= 4; i++) {
    const wk = +startOfWeek(addWeeks(new Date(), -i));
    if (wk < now) weeks.push(wk);
  }
  if (weeks.length < 2) return { ahead: false, avgRatio: 1 };

  let totalActual = 0;
  let totalPlanned = 0;
  for (const wk of weeks) {
    totalActual += actualWeeklyMap.get(wk) || 0;
    totalPlanned += plannedWeeklyMap.get(wk) || 0;
  }

  const avgRatio = totalPlanned > 0 ? totalActual / totalPlanned : 1;
  return { ahead: avgRatio > 1.15, avgRatio };
}

/* =========================
   InfoTooltip — ⓘ icon with hover tooltip (desktop) / tap modal (mobile)
========================= */
function InfoTooltip({ text, isMobile }) {
  /* NOTE: `isMobile` prop is retained for API compat but intentionally
     ignored. The modal (bottom-sheet) variant is used on EVERY viewport
     because:
       - Desktop hover tooltip was a narrow right-anchored popover (max-
         width 280px) that got clipped inside tight cells (statCells on
         Stats KPI strip).
       - Tap/click is an intentional gesture — no accidental reveal on
         hover, better readability on touch devices, dismissible.
       - Mobile breakpoint (640px) was too narrow; tablet-portrait sizes
         fell through to the desktop tooltip and looked awful. */
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    document.addEventListener("touchstart", handler);
    return () => {
      document.removeEventListener("mousedown", handler);
      document.removeEventListener("touchstart", handler);
    };
  }, [open]);

  // Modal content is portaled to document.body so that position:fixed
  // always anchors to the viewport. Without a portal, any ancestor with
  // a `transform`, `filter`, `will-change`, `perspective`, or `contain`
  // creates a containing block and the modal gets pinned inside the
  // ancestor (e.g. trapped inside a statCell, showing mid-page instead
  // of as a bottom sheet).
  const modal = open ? (
    <>
      <div className="infoModalBackdrop" onClick={() => setOpen(false)} />
      <div className="infoModal" onClick={(e) => e.stopPropagation()}>
        <div className="bottomSheetDragHandle" />
        <div className="infoModal__header">
          <span className="infoModal__title">Info</span>
          <button type="button" className="infoModal__close" onClick={() => setOpen(false)}>✕</button>
        </div>
        <div className="infoModal__body">{text}</div>
      </div>
    </>
  ) : null;

  return (
    <span className="infoTooltipWrap" ref={ref}>
      <button
        type="button"
        className="infoBtn"
        onClick={(e) => { e.stopPropagation(); setOpen((v) => !v); }}
        aria-label="More info"
      >ⓘ</button>
      {modal && typeof document !== "undefined" ? createPortal(modal, document.body) : modal}
    </span>
  );
}

/* =========================
   UI helpers
========================= */
function Stat({ label, value, sub, subTone, infoText, isMobile }) {
  return (
    <div className="card stat">
      <div className="muted statLabelRow">{label}{infoText && <InfoTooltip text={infoText} isMobile={isMobile} />}</div>
      <div className="statValue">{value}</div>
      {sub ? (
        <div className={`muted ${subTone || ""}`} style={{ marginTop: 6 }}>
          {sub}
        </div>
      ) : null}
    </div>
  );
}

function Pill({ ok, warn, children }) {
  const cls = warn ? "pillWarn" : ok ? "pillOk" : "pillOff";
  return <span className={`pill ${cls}`}>{children}</span>;
}

/**
 * Status badge resolver. Extended so `matched` rows can communicate pace
 * verdict in the badge itself (On pace / Too fast / Too slow), which lets
 * the list view be scannable without reading numbers.
 *
 * Input `status` is case-insensitive — callers pass either the lowercase
 * raw value ('matched') or the display-cased version ('Matched').
 *
 * @param paceDeltaSec  actualPaceSec − plannedPaceSec. Negative = user was
 *                      faster than plan. If null, no sub-verdict is shown.
 */
function statusBadge(status, paceDeltaSec = null) {
  const s = String(status || "").toLowerCase();
  if (s === "matched") {
    let subLabel = null;
    let tone = 'ok';
    if (paceDeltaSec != null) {
      // ±10 sec/km tolerance — anything beyond is a meaningful effort miss.
      if (paceDeltaSec < -10)      { subLabel = 'Too fast'; tone = 'danger'; }
      else if (paceDeltaSec > 10)  { subLabel = 'Too slow'; tone = 'warn'; }
      else                          { subLabel = 'On pace';  tone = 'ok'; }
    }
    // No emoji anywhere in the UI (brand rule): the pill's colour and the
    // word already say the state, and a ✅ in a training app reads as a
    // sticker. `icon` stays in the shape for callers that still read it.
    return { icon: "", label: "Matched", subLabel, tone, ok: tone === 'ok', warn: tone === 'warn' };
  }
  if (s === "today")    return { icon: "", label: "Today",    subLabel: null, tone: 'warn',    ok: false, warn: true };
  if (s === "missed")   return { icon: "", label: "Missed",   subLabel: null, tone: 'danger',  ok: false, warn: false };
  if (s === "partial")  return { icon: "", label: "Partial",  subLabel: null, tone: 'warn',    ok: false, warn: true };
  // Off-pace = full distance hit, but actual pace > 5% slower than planned.
  // Distinct from "Partial" (which is distance-shortfall). Amber tone keeps
  // visual hierarchy: Off pace is a real miss, but less severe than missed
  // distance because the volume work was completed.
  if (s === "off-pace") return { icon: "",  label: "Off pace", subLabel: paceDeltaSec != null && isFinite(paceDeltaSec) ? `+${Math.round(paceDeltaSec)}s/km` : null, tone: 'warn', ok: false, warn: true };
  if (s === "extra")    return { icon: "", label: "Extra",    subLabel: null, tone: 'neutral', ok: false, warn: true };
  if (s === "upcoming") return { icon: "",  label: "Upcoming", subLabel: null, tone: 'neutral', ok: false, warn: false };
  return { icon: "", label: "—", subLabel: null, tone: 'neutral', ok: false, warn: false };
}

/* =========================
   UI: plan vs actual table
========================= */
function PlanVsActualTable({ rows, effectiveMaxHr }) {
  if (!rows?.length) return <div className="muted">No data yet.</div>;

  return (
    <div className="tableScrollWrap">
      <table style={{ width: "100%", borderCollapse: "separate", borderSpacing: 0 }}>
        <thead>
          <tr>
            {["Date", "Plan", "Actual", "Δ km", "Δ pace", "Avg HR", "Max HR", "Status"].map((h) => (
              <th
                key={h}
                style={{
                  textAlign: "left",
                  padding: "10px 10px",
                  fontSize: "var(--text-xs-size)",
                  color: "var(--muted)",
                  borderBottom: "1px solid var(--line)",
                  whiteSpace: "nowrap",
                }}
              >
                {h}
              </th>
            ))}
          </tr>
        </thead>

        <tbody>
          {rows.map((r, i) => {
            const badge = statusBadge(r.status);

            const plannedKm = r.plannedKm;
            const actualKm = r.actualKm;
            const plannedP = r.plannedP;
            const actualP = r.actualP;

            const dk = plannedKm != null && actualKm != null ? actualKm - plannedKm : null;
            const dp = plannedP != null && actualP != null ? actualP - plannedP : null;

            const plannedTxt = r.planned ? `${r.planned.title} · ${fmt1(r.planned.distanceKm)}km · ${r.planned.pace}` : "—";
            const actualTxt = r.actual
              ? `${r.actual.name || "Run"} · ${fmt1(kmFromMeters(r.actual.distance_m))}km · ${secPerKmToPace(paceFromRun(r.actual)) || "—"}`
              : "—";

            return (
              <tr key={i}>
                <td style={{ padding: "10px 10px", borderBottom: "1px solid var(--line)", whiteSpace: "nowrap" }}>
                  <div style={{ fontWeight: 900 }}>{formatDateLong(r.date)}</div>
                  <div className="muted" style={{ fontSize: "var(--text-xs-size)" }}>
                    {r.planned?.badge || (r.status === "extra" ? "Unplanned run" : "—")}
                  </div>
                </td>

                <td style={{ padding: "10px 10px", borderBottom: "1px solid var(--line)", minWidth: 240 }}>
                  <div style={{ fontWeight: 800 }}>{plannedTxt}</div>
                </td>

                <td style={{ padding: "10px 10px", borderBottom: "1px solid var(--line)", minWidth: 240 }}>
                  <div style={{ fontWeight: 800 }}>{actualTxt}</div>
                </td>

                <td style={{ padding: "10px 10px", borderBottom: "1px solid var(--line)" }}>
                  {dk != null ? (
                    <span className={dk > 0 ? "deltaPos" : dk < 0 ? "deltaNeg" : ""}>
                      {dk > 0 ? "+" : ""}
                      {fmt1(dk)} km
                    </span>
                  ) : (
                    "—"
                  )}
                </td>

                <td style={{ padding: "10px 10px", borderBottom: "1px solid var(--line)" }}>
                  {dp != null ? (
                    <span className={dp < 0 ? "deltaPos" : dp > 0 ? "deltaNeg" : ""}>
                      {dp > 0 ? "+" : ""}
                      {fmt1(dp)} sec/km
                    </span>
                  ) : (
                    "—"
                  )}
                </td>

                <td style={{ padding: "10px 10px", borderBottom: "1px solid var(--line)", whiteSpace: "nowrap" }}>
                  {r.actual?.avg_hr ? (() => {
                    const zl = hrZoneLabel(r.actual.avg_hr, effectiveMaxHr);
                    return (
                      <span style={{ fontWeight: 800 }}>
                        {round0(r.actual.avg_hr)} <span className="muted" style={{ fontSize: "var(--text-xs-size)" }}>bpm</span>
                        {zl ? <span style={{ marginLeft: 4, color: zl.color, fontSize: "var(--text-xs-size)", fontWeight: 900 }}>{zl.zone}</span> : null}
                      </span>
                    );
                  })() : "—"}
                </td>

                <td style={{ padding: "10px 10px", borderBottom: "1px solid var(--line)", whiteSpace: "nowrap" }}>
                  {r.actual?.max_hr ? (
                    <span style={{ fontWeight: 800 }}>
                      {round0(r.actual.max_hr)} <span className="muted" style={{ fontSize: "var(--text-xs-size)" }}>bpm</span>
                    </span>
                  ) : "—"}
                </td>

                <td style={{ padding: "10px 10px", borderBottom: "1px solid var(--line)", whiteSpace: "nowrap" }}>
                  <Pill ok={badge.ok} warn={badge.warn}>
                    {badge.icon} {badge.label}
                  </Pill>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

/* =========================
   Next sessions list (Races highlighted, session-type colors)
========================= */
function sessionTypeColor(title) {
  const t = (title || "").toLowerCase();
  // Every long effort shares the long colour — "B2B Day 2" and "Time on
  // feet" used to fall through to grey, so their rows and the session sheet
  // had no colour at all.
  if (t.includes("long") || t.includes("b2b") || t.includes("back-to-back") || t.includes("time-on-feet") || t.includes("time on feet")) return "var(--c-long)";
  if (t.includes("tempo") || t.includes("steady") || t.includes("interval") || t.includes("threshold")) return "var(--c-tempo)";
  if (t.includes("race")) return "var(--warning)";
  if (t.includes("recovery") || t.includes("hike") || t.includes("shakeout")) return "var(--c-recovery)";
  if (t.includes("hills") || t.includes("hill") || t.includes("vertical")) return "var(--c-hills)";
  if (t.includes("easy")) return "var(--c-easy)";
  return "var(--text-muted)";
}

/* =========================
   Session-type display labels
   -------------------------
   Canonical human-readable labels for server plan types.
   Used EVERYWHERE session names appear (Train tab cards, Plan vs Actual rows,
   Today tab "Next session" card, session detail sheet).
   Falls back to hyphen-to-space title-casing for unknown types.
========================= */
const SESSION_TYPE_LABELS = {
  "b2b-day1":      "B2B Day 1",
  "b2b-day2":      "B2B Day 2",
  "time-on-feet":  "Time on Feet",
  "recovery-easy": "Recovery Easy",
  "vertical":      "Vertical",
  "easy":          "Easy",
  "tempo":         "Tempo",
  "long":          "Long",
  "hike":          "Hike",
  "race":          "Race",
  "intervals":     "Intervals",
};
function formatSessionTypeLabel(type) {
  if (!type) return "";
  const key = String(type).toLowerCase();
  if (SESSION_TYPE_LABELS[key]) return SESSION_TYPE_LABELS[key];
  // Fallback: hyphen → space, then Title Case each word.
  return key.replace(/-/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

/* =========================
   Goal Status Card
========================= */
function GoalStatusCard({ likelihood, goalSaved, goalDateObj, suggestedGoalTimeStr, goalTimeStr, deltaWeeklyVsPlan, deltaWeeklyVsPlanToDate, plannedThisWeekToDateKm, plannedThisWeekKm, weeklyAdherenceHistory, desktop, onAdjust }) {
  if (!goalSaved?.date) return null;

  const score = likelihood?.score ?? 0;
  const goalType = goalSaved?.type || "—";
  const goalDateStr = goalDateObj ? formatDateShort(goalDateObj) : "—";

  /* status badge */
  const status = score >= 65 ? { label: "ON TRACK", color: "var(--accent)" }
    : score >= 40 ? { label: "UNCERTAIN", color: "var(--warning)" }
    : { label: "AT RISK", color: "var(--danger)" };

  /* est. finish vs target */
  const estFinish = suggestedGoalTimeStr || "—";
  const target = goalTimeStr || "—";


  /* weeks left */
  const weeksLeft = goalDateObj ? Math.max(0, Math.ceil((+goalDateObj - Date.now()) / (7 * 864e5))) : null;

  /* consecutive bad weeks (score < 60%) */
  const recentBad = (() => {
    if (!weeklyAdherenceHistory || weeklyAdherenceHistory.length === 0) return 0;
    let count = 0;
    for (let i = weeklyAdherenceHistory.length - 1; i >= 0; i--) {
      if ((weeklyAdherenceHistory[i]?.score ?? 0) < 0.6) count++; else break;
    }
    return count;
  })();

  /* recommendation logic
     Adjust is recommended when:
       - Goal likelihood is low (<40%). Below 30% we're effectively off-track.
       - Running out of time (<8 weeks) — less room to recover.
       - Sustained underperformance (3+ bad weeks in a row).
     Keep is the default: one tough week doesn't break a plan when the
     trajectory is still on-track. */
  const recommendAdjust =
    score < 40 ||
    (weeksLeft != null && weeksLeft < 8) ||
    recentBad >= 3;

  const keepBadge = recommendAdjust ? "IF IMPROVING" : "RECOMMENDED";
  const adjustBadge = recommendAdjust ? "RECOMMENDED" : "IF INJURED";
  const keepBadgeColor = recommendAdjust ? "var(--warning)" : "var(--accent)";
  const adjustBadgeColor = recommendAdjust ? "var(--accent)" : "var(--warning)";

  const keepReason = score < 40
    ? `Current trajectory is at ${score}% likelihood — keeping the plan likely won't get you there.`
    : `${weeksLeft ?? "—"} weeks left — enough time to recover. One tough week doesn't break a plan.`;
  const adjustReason = score < 40
    ? `Likelihood is ${score}% — shift the target time or date so the plan has a realistic chance.`
    : "Only consider this if you can't increase load — not just because of one bad week.";

  return (
    <div className="goalStatusCard card" style={desktop ? {} : {}}>
      {/* Header */}
      <div className="goalStatusCard__header">
        <span className="goalStatusCard__title">Goal status <InfoTooltip text="Shows your current goal progress at a glance — likelihood of hitting your target, estimated finish time, and how your weekly volume compares to the plan. The recommendation adapts based on weeks left and recent adherence." isMobile={!desktop} /></span>
        <span className="goalStatusCard__badge" style={{ color: status.color, borderColor: status.color }}>{status.label}</span>
      </div>

      {/* Data rows */}
      <div className="goalStatusCard__rows">
        <div className="goalStatusCard__row">
          <span className="goalStatusCard__rowLabel">Likelihood</span>
          <span className="goalStatusCard__rowValue">{score}% for {goalType} · {goalDateStr}</span>
        </div>
        <div className="goalStatusCard__row">
          <span className="goalStatusCard__rowLabel">Est. finish</span>
          <span className="goalStatusCard__rowValue">{estFinish} <span className="muted">vs {target} target</span></span>
        </div>
        {/* Volume intentionally omitted — lives in KEY METRICS / WEEKLY VOLUME
            card. Keeping it here was duplicating the same signal on the same
            page, and the Goal card stays focused on goal-specific numbers
            (likelihood + est. finish vs target). */}
      </div>

      {/* Divider */}
      <div className="goalStatusCard__divider" />

      {/* Action section */}
      <div className="goalStatusCard__actionLabel">What do you want to do?</div>
      <div className={desktop ? "goalStatusCard__buttons goalStatusCard__buttons--desktop" : "goalStatusCard__buttons"}>
        {/* Keep plan */}
        <button type="button" className="goalStatusCard__btn goalStatusCard__btn--keep">
          <div style={{ flex: 1 }}>
            <div className="goalStatusCard__btnTitle">Keep plan <InfoTooltip text={keepReason} isMobile={!desktop} /></div>
            <div className="goalStatusCard__btnSub">Stay the course, build volume</div>
          </div>
          <span className="goalStatusCard__btnBadge" style={{ color: keepBadgeColor, borderColor: keepBadgeColor }}>{keepBadge}</span>
        </button>

        {/* Adjust plan */}
        <button type="button" className="goalStatusCard__btn goalStatusCard__btn--adjust" onClick={onAdjust || undefined}>
          <div style={{ flex: 1 }}>
            <div className="goalStatusCard__btnTitle">Adjust plan <InfoTooltip text={adjustReason} isMobile={!desktop} /></div>
            <div className="goalStatusCard__btnSub">Shift target time or race date</div>
          </div>
          <span className="goalStatusCard__btnBadge" style={{ color: adjustBadgeColor, borderColor: adjustBadgeColor }}>{adjustBadge}</span>
        </button>
      </div>
    </div>
  );
}

function NextSessionsList({ sessions }) {
  if (!sessions?.length) return <div className="muted">No upcoming sessions.</div>;

  const isRace = (s) => s?.kind === "race";

  const rowStyle = (s) => {
    const race = isRace(s);
    return {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      gap: 12,
      padding: "12px 12px",
      borderRadius: 14,
      border: "1px solid var(--line)",
      background: race ? "var(--bg-hover)" : "var(--bg-card)",
      boxShadow: "0 1px 0 rgb(var(--shadow-rgb) / 0.25)",
      position: "relative",
      overflow: "hidden",
    };
  };

  const leftBarStyle = (s) => {
    const race = isRace(s);
    const col = race ? (s?.raceColor || "var(--race-ultra)") : sessionTypeColor(s?.title);
    return {
      width: 10,
      alignSelf: "stretch",
      borderRadius: 999,
      background: col,
      opacity: 0.95,
      flex: "0 0 10px",
    };
  };

  const pillStyle = (s) => {
    const race = isRace(s);
    const col = race ? (s?.raceColor || "var(--race-ultra)") : sessionTypeColor(s?.title);
    return {
      padding: "6px 10px",
      borderRadius: 999,
      fontWeight: 900,
      fontSize: "var(--text-xs-size)",
      whiteSpace: "nowrap",
      border: `1px solid ${race ? col : "var(--line)"}`,
      color: race ? col : "var(--text)",
      background: race ? "transparent" : "var(--bg)",
    };
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
      {sessions.map((s, idx) => {
        const race = isRace(s);
        return (
          <div key={`${+new Date(s.date)}-${idx}`} style={rowStyle(s)}>
            <div style={leftBarStyle(s)} />

            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ display: "flex", alignItems: "baseline", gap: 10, flexWrap: "wrap" }}>
                <div style={{ fontWeight: 950, fontSize: "var(--text-base-size)" }}>{s.title || (race ? "Race" : "Session")}</div>
                <div className="muted" style={{ fontSize: "var(--text-xs-size)", fontWeight: 800 }}>
                  {formatDateLong(s.date)}
                </div>
              </div>

              <div
                className="muted"
                style={{ marginTop: 4, fontSize: "var(--text-sm-size)", display: "flex", gap: 10, flexWrap: "wrap" }}
              >
                <span>{fmt1(s.distanceKm)}km</span>
                <span>·</span>
                <span>{s.pace || "—"}</span>
                <span>·</span>
                <span>{s.timeStr || "—"}</span>
              </div>

              <div className="muted" style={{ marginTop: 4, fontSize: "var(--text-xs-size)" }}>
                {s.badge || (race ? "Race" : "Planned")}
              </div>
            </div>

            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <div style={pillStyle(s)}>{s.zone || (race ? "Race" : "—")}</div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

/* =========================
   Modal
========================= */
function Modal({ open, title, children, onClose, icon: Icon }) {
  const isMobile = useIsMobileLayout();
  const sheetRef = useRef(null);
  const dragStartY = useRef(null);
  const dragCurrentY = useRef(0);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;

  /* --- Touch handlers for swipe-to-dismiss --- */
  const onTouchStart = (e) => {
    dragStartY.current = e.touches[0].clientY;
    if (sheetRef.current) sheetRef.current.style.transition = "none";
  };
  const onTouchMove = (e) => {
    if (dragStartY.current == null) return;
    const dy = e.touches[0].clientY - dragStartY.current;
    dragCurrentY.current = Math.max(0, dy); // only allow downward drag
    if (sheetRef.current) sheetRef.current.style.transform = `translateY(${dragCurrentY.current}px)`;
  };
  const onTouchEnd = () => {
    if (sheetRef.current) sheetRef.current.style.transition = "transform 0.25s ease";
    if (dragCurrentY.current > 120) {
      // Dismiss
      if (sheetRef.current) sheetRef.current.style.transform = "translateY(100%)";
      setTimeout(onClose, 250);
    } else {
      if (sheetRef.current) sheetRef.current.style.transform = "translateY(0)";
    }
    dragStartY.current = null;
    dragCurrentY.current = 0;
  };

  if (isMobile) {
    return (
      <div className="bottomSheetOverlay" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
        <div
          ref={sheetRef}
          className="bottomSheetPanel"
          onMouseDown={(e) => e.stopPropagation()}
          onTouchStart={onTouchStart}
          onTouchMove={onTouchMove}
          onTouchEnd={onTouchEnd}
        >
          <div className="bottomSheetDragHandle" />
          <div className="bottomSheetHeader">
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              {Icon ? <Icon size={18} /> : <Info size={18} />}
              <div style={{ fontWeight: 900, fontSize: "var(--text-base-size)" }}>{title}</div>
            </div>
            <button type="button" className="bottomSheetClose" onClick={onClose} aria-label="Close">✕</button>
          </div>
          <div className="bottomSheetBody">{children}</div>
        </div>
      </div>
    );
  }

  /* Desktop: centred card modal (unchanged) */
  return (
    <div
      style={{
        position: "fixed", inset: 0, background: "var(--overlay, rgb(var(--shadow-rgb) / 0.35))",
        display: "flex", alignItems: "center", justifyContent: "center", zIndex: 50, padding: 16,
      }}
      onMouseDown={onClose}
    >
      <div className="card" style={{ width: "min(980px, 96vw)", maxHeight: "86vh", overflow: "hidden", borderRadius: 18 }}
        onMouseDown={(e) => e.stopPropagation()}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "14px 16px", borderBottom: "1px solid var(--line)" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            {Icon ? <Icon size={18} /> : <Info size={18} />}
            <div style={{ fontWeight: 900, fontSize: "var(--text-base-size)" }}>{title}</div>
          </div>
          <button className="btn btnGhost" style={{ marginLeft: "auto" }} onClick={onClose}>
            <X size={16} /> Close
          </button>
        </div>
        <div style={{ padding: 16, overflowY: "auto", maxHeight: "calc(86vh - 56px)" }}>{children}</div>
      </div>
    </div>
  );
}

/* =========================
   Recommendation Panel (replaces old ResetActionsPanel)
========================= */
function RecommendationPanel({ recommendations, onAction }) {
  if (!recommendations?.length) return null;

  return (
    <>
      {recommendations.map((rec, idx) => {
        const isAdherence = rec.type === "adherence_low";
        const isAhead = rec.type === "ahead_of_plan";
        // Recommendation cards pull from the RESET palette so the UI stays
        // on-brand regardless of severity. --danger / --accent / --warning.
        const borderColor = isAdherence
          ? "rgb(var(--critical-rgb) / 0.48)"
          : isAhead
          ? "rgb(var(--accent-rgb) / 0.48)"
          : "rgb(var(--caution-rgb) / 0.48)";
        const iconBg = isAdherence
          ? "rgb(var(--critical-rgb) / 0.18)"
          : isAhead
          ? "rgb(var(--accent-rgb) / 0.18)"
          : "rgb(var(--caution-rgb) / 0.18)";
        const iconColor = isAdherence ? "var(--critical)" : isAhead ? "var(--accent-base)" : "var(--caution)";
        const title = isAdherence ? "Controlled RESET action" : isAhead ? "Ahead of plan" : "Training recommendation";
        const Icon = isAdherence ? TimerReset : isAhead ? TrendingUp : Info;

        return (
          <div key={idx} className="card" style={{ marginTop: 14, padding: 14, border: `1px solid ${borderColor}` }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
              <div className="iconBox" style={{ background: iconBg, color: iconColor }}>
                <Icon size={18} />
              </div>
              <div style={{ fontWeight: 950, fontSize: "var(--text-base-size)" }}>{title}</div>
              <div className="muted" style={{ marginLeft: "auto", fontSize: "var(--text-xs-size)" }}>{rec.reason}</div>
            </div>

            <div className="muted" style={{ marginTop: 10, fontSize: "var(--text-sm-size)", lineHeight: 1.4 }}>
              {rec.description}
            </div>

            <div style={{ marginTop: 12, display: "flex", gap: 10, flexWrap: "wrap" }}>
              {(rec.actions || []).map((a) => (
                <button
                  key={a.id}
                  className={`btn ${a.primary ? "btnOk" : "btnGhost"}`}
                  onClick={() => onAction(a.id)}
                >
                  {a.icon ? <a.icon size={16} /> : null} {a.label}
                </button>
              ))}
            </div>
          </div>
        );
      })}
    </>
  );
}

/* =========================
   Tab bar icons (inline SVGs, no library dependency)
========================= */
/* Dock icons come in PAIRS: an outline for the resting tabs, a solid for the
   active one. The outline -> solid switch is how a native tab bar says "you
   are here", and it was previously attempted with `fill: currentColor` on the
   outline path — which on a stroke-drawn glyph fills the wrong region (the
   chart's axis corner, the calendar's grid) and renders a blob. Each solid is
   its own path, with holes cut by fill-rule="evenodd" so the glass shows
   through them instead of an opaque knockout. */
const TabIconHouse = ({ active }) => active ? (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M12 2.35 2.74 10.1a1.1 1.1 0 0 0-.39.84V19.6A2.4 2.4 0 0 0 4.75 22H9.1v-6.1a1 1 0 0 1 1-1h3.8a1 1 0 0 1 1 1V22h4.35a2.4 2.4 0 0 0 2.4-2.4v-8.66a1.1 1.1 0 0 0-.39-.84z"/>
  </svg>
) : (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M3 10.5 12 3l9 7.5V20a1 1 0 0 1-1 1h-5v-7h-6v7H4a1 1 0 0 1-1-1z"/></svg>
);
const TabIconCircle = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><path d="M12 6v6l4 2"/></svg>
);
const TabIconPlay = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="5 3 19 12 5 21 5 3"/></svg>
);
const TabIconDiamond = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M2.7 10.3a2.41 2.41 0 0 0 0 3.41l7.59 7.59a2.41 2.41 0 0 0 3.41 0l7.59-7.59a2.41 2.41 0 0 0 0-3.41l-7.59-7.59a2.41 2.41 0 0 0-3.41 0Z"/></svg>
);
const TabIconCalendar = ({ active }) => active ? (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path fillRule="evenodd" clipRule="evenodd" d="M8 2.2a1 1 0 0 1 1 1V4h6v-.8a1 1 0 1 1 2 0V4h1a3 3 0 0 1 3 3v11a3 3 0 0 1-3 3H6a3 3 0 0 1-3-3V7a3 3 0 0 1 3-3h1v-.8a1 1 0 0 1 1-1zM5 10.5V18a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1v-7.5zM8 13a1.15 1.15 0 1 0 0 2.3A1.15 1.15 0 0 0 8 13zm4 0a1.15 1.15 0 1 0 0 2.3 1.15 1.15 0 0 0 0-2.3zm4 0a1.15 1.15 0 1 0 0 2.3 1.15 1.15 0 0 0 0-2.3z"/>
  </svg>
) : (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><rect x="3" y="5" width="18" height="16" rx="2"/><path d="M3 10h18M8 3v4M16 3v4"/></svg>
);
const TabIconChat = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6A8.38 8.38 0 0 1 12.5 3h.5a8.48 8.48 0 0 1 8 8v.5z"/></svg>
);
/* Three rising columns, not an axis with a squiggle over it: the Stats screen
   is a volume chart, and at 22px the old glyph's 1px axis and 4-point
   polyline read as lint. */
const TabIconChart = ({ active }) => active ? (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <rect x="3.6" y="13" width="4.2" height="8" rx="1.6"/>
    <rect x="9.9" y="8.4" width="4.2" height="12.6" rx="1.6"/>
    <rect x="16.2" y="3.6" width="4.2" height="17.4" rx="1.6"/>
  </svg>
) : (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" aria-hidden="true">
    <rect x="3.6" y="13" width="4.2" height="8" rx="1.6"/>
    <rect x="9.9" y="8.4" width="4.2" height="12.6" rx="1.6"/>
    <rect x="16.2" y="3.6" width="4.2" height="17.4" rx="1.6"/>
  </svg>
);
const GearIcon = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09a1.65 1.65 0 0 0-1-1.51 1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09a1.65 1.65 0 0 0 1.51-1 1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"/></svg>
);
// Dock order = frequency of action (design v4, locked 22.07.2026):
// Today (act) → Train (the plan) → Coach (decide) → Stats (reflect).
// "goal" stays a valid screen (reached from the Today goal cell and
// Settings) but is deliberately NOT in the dock — set once, rarely touched.
const COACH_TEASER_KEY = "reset_coach_teaser_seen_v1";
const TAB_ICONS = { today: TabIconHouse, train: TabIconCalendar, coach: TabIconChat, stats: TabIconChart, goal: TabIconDiamond };
const TAB_LABELS = { today: "Today", train: "Train", coach: "Coach", stats: "Stats", goal: "Goal" };

/* =========================
   CountUpNumber — first-view-only count-up for a single hero metric.

   PRESENTATION ONLY, by construction:
     - It takes the already-computed value as a prop and never reports back.
     - It holds NO React state. The animation writes textContent through a
       ref, so nothing re-renders and no render-path value is ever the
       animated one — the true value is restored the moment it settles.
     - The real value is what React renders as the child, so the correct
       number is in the DOM before any animation starts. If the effect never
       runs (no JS, hydration bail-out) the truth is already on screen.
     - Runs once per viewer, gated by a localStorage flag in try/catch:
       private windows, blocked storage and cleared site data all degrade to
       "just show the number", never to a crash.
     - Skipped entirely under prefers-reduced-motion.
     - aria-label carries the final value, so assistive tech is told the
       answer rather than a stream of intermediate numbers.
========================= */
function CountUpNumber({ value, storageKey, durationMs = 900 }) {
  const ref = useRef(null);
  const finished = useRef(false);
  const target = Number(value);

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el || !Number.isFinite(target)) return;

    const settle = () => { el.textContent = String(target); };

    // Already run once in this mount (or the score changed afterwards):
    // never re-animate, just show the truth.
    if (finished.current) { settle(); return; }

    let seen = false;
    try { seen = window.localStorage.getItem(storageKey) === "1"; } catch (_) { seen = true; }

    let reduce = false;
    try {
      reduce = !!(window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches);
    } catch (_) { reduce = false; }

    if (seen || reduce || target <= 0) { finished.current = true; settle(); return; }

    const t0 = performance.now();
    let raf = 0;
    const easeOut = (t) => 1 - Math.pow(1 - t, 3);
    const tick = (now) => {
      const t = Math.min(1, (now - t0) / durationMs);
      el.textContent = String(Math.round(easeOut(t) * target));
      if (t < 1) { raf = requestAnimationFrame(tick); return; }
      finished.current = true;
      settle();
      /* The flag is written on COMPLETION, not on start. Two reasons:
         StrictMode double-invokes effects in development, so a flag written
         up front makes the second (real) invocation skip and the animation
         never runs; and a viewer who navigates away mid-count has not really
         seen it, so they get it next time. */
      try { window.localStorage.setItem(storageKey, "1"); } catch (_) {}
    };
    el.textContent = "0";
    raf = requestAnimationFrame(tick);
    /* Cleanup cancels the frame AND restores the true value, so the DOM is
       never left showing a half-counted number. It must NOT set
       finished.current or write the flag: StrictMode's development
       mount-unmount-mount would then mark the animation done before it ever
       ran, and (worse) an interrupted run would strand the display at 0
       while the real value sat in aria-label. */
    return () => { if (raf) cancelAnimationFrame(raf); el.textContent = String(target); };
  }, [target, storageKey, durationMs]);

  return <span ref={ref} aria-label={String(target)}>{target}</span>;
}

/* =========================
   Today — the rows
   -------------------------
   Three grey text rows under a living score ring read as filler. Same
   panel and hairlines, but each row now carries its own instrument and
   its state in colour: this week as a ring of sessions, the last run as a
   type-coloured tile with a pace delta and a zone, the goal as a
   likelihood arc with days to race; the plan track is the panel's last
   row. It arrives once per page load, never on a tab change.
========================= */
function CountUpValue({ value, decimals = 0, durationMs = 700, animate = true }) {
  /* Same contract as CountUpNumber (ref-written textContent, aria-label is
     the final value, cleanup restores the truth, reduced motion settles at
     once) but no storage key: the caller's `animate` flag is the gate. */
  const ref = useRef(null);
  const finished = useRef(false);
  const target = Number(value);
  const fmt = (n) => (decimals ? n.toFixed(decimals) : String(Math.round(n)));
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el || !Number.isFinite(target)) return undefined;
    const settle = () => { el.textContent = fmt(target); };
    if (finished.current || !animate) { settle(); return undefined; }   // !animate must NOT set finished
    let reduce = false;
    try { reduce = !!(window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches); } catch (_) {}
    if (reduce) { finished.current = true; settle(); return undefined; }
    if (target <= 0) { settle(); return undefined; }   // the real number may still be on its way
    const t0 = performance.now();
    let raf = 0;
    const easeOut = (t) => 1 - Math.pow(1 - t, 3);
    const tick = (now) => {
      const t = Math.min(1, (now - t0) / durationMs);
      el.textContent = fmt(easeOut(t) * target);
      if (t < 1) { raf = requestAnimationFrame(tick); return; }
      finished.current = true;
      settle();
    };
    el.textContent = fmt(0);
    raf = requestAnimationFrame(tick);
    return () => { if (raf) cancelAnimationFrame(raf); el.textContent = fmt(target); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [target, decimals, durationMs, animate]);
  return <span ref={ref} aria-label={fmt(target)}>{fmt(target)}</span>;
}

const tdKey = (fn) => (e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); fn(); } };
let todayRowsPlayed = false;   // once per page load, never per tab change

function TodayRows({ week, run, goal, track, onWeek, onRun, onGoal }) {
  const panelRef = useRef(null);
  const [fresh, setFresh] = useState(false);
  const [pending, setPending] = useState(() => !todayRowsPlayed);
  useEffect(() => {
    if (todayRowsPlayed || !pending) return undefined;
    const el = panelRef.current;
    let done = false;
    let io = null;
    let timer = 0;
    const flip = () => {
      if (done) return;
      done = true;
      todayRowsPlayed = true;
      setPending(false);
      setFresh(true);
      if (io) io.disconnect();
      if (timer) clearTimeout(timer);
    };
    if (!el || typeof IntersectionObserver === "undefined") { flip(); return undefined; }
    // No fallback timer: a timer spent the arrival while the panel was
    // still below the fold, and the athlete scrolled down to a static panel.
    io = new IntersectionObserver((es) => { if (es.some((x) => x.isIntersecting)) flip(); }, { threshold: 0, rootMargin: "0px 0px -64px 0px" });
    io.observe(el);
    return () => { if (io) io.disconnect(); if (timer) clearTimeout(timer); };
  }, [pending]);

  const segN = week.segs.length || 1;
  return (
    <div ref={panelRef} className={`tdPanel${fresh ? " is-fresh" : pending ? " is-pending" : ""}`} data-today-rows>
      {/* 1 · This week */}
      <div className={`tdRow tdRow--week tdRow--${week.tone}`} role="button" tabIndex={0} style={{ "--i": 0 }} aria-label={week.aria} onClick={onWeek} onKeyDown={tdKey(onWeek)}>
        <div className="tdRow__viz">
          <svg className={`tdRing${week.segs.length >= 5 ? " tdRing--dense" : ""}`} viewBox="0 0 36 36" aria-hidden="true" style={{ "--n": segN }}>
            {week.segs.length === 0 ? <circle className="tdRing__track" cx="18" cy="18" r="15.5" pathLength="100" /> : null}
            {week.segs.map((st, k) => <circle key={k} className={`tdRing__seg is-${st}`} cx="18" cy="18" r="15.5" pathLength="100" style={{ "--k": k }} />)}
          </svg>
        </div>
        <div className="tdRow__main">
          <div className="tdRow__label">This week</div>
          <div className="tdRow__sub">{week.sub}</div>
        </div>
        <div className="tdRow__side">
          <div className="tdRow__value">
            {week.hasPlan
              ? <><span className="tdRow__num"><CountUpValue value={week.actualKm} decimals={1} animate={fresh} /></span><span className="tdRow__unit">/ {week.plannedKm} km</span></>
              : <span className="tdRow__num tdRow__num--empty">—</span>}
          </div>
          <div className="tdRow__chips">{week.chip ? <span className={`tdChip tdChip--${week.chip.tone}`}>{week.chip.text}</span> : null}</div>
        </div>
        <ChevronRight size={14} className="tdRow__chev" aria-hidden="true" />
      </div>

      {/* 2 · Last run */}
      <div
        className={`tdRow tdRow--run tdRow--${run.tone}${run.empty ? " is-empty" : ""}`}
        role={run.empty ? undefined : "button"}
        tabIndex={run.empty ? undefined : 0}
        style={{ "--i": 1, "--type": run.typeVar }}
        aria-label={run.aria}
        onClick={run.empty ? undefined : onRun}
        onKeyDown={run.empty ? undefined : tdKey(onRun)}
      >
        <div className="tdRow__viz"><span className={`tdType${run.empty ? " tdType--empty" : ""}`} aria-hidden="true">{run.glyph}</span></div>
        <div className="tdRow__main">
          <div className="tdRow__label">Last run</div>
          <div className="tdRow__sub">{run.sub}</div>
        </div>
        <div className="tdRow__side">
          <div className="tdRow__value">
            {run.km != null
              ? <><span className="tdRow__num"><CountUpValue value={run.km} decimals={1} animate={fresh} /></span><span className="tdRow__unit">km</span></>
              : <span className="tdRow__num tdRow__num--empty">—</span>}
          </div>
          <div className="tdRow__chips">
            {run.chip ? <span className={`tdChip tdChip--${run.chip.tone}`}>{run.chip.text}</span> : null}
            {run.zone ? <span className="tdChip tdChip--zone" style={{ "--zone": run.zone.color }}>{run.zone.zone}</span> : null}
          </div>
        </div>
        {!run.empty ? <ChevronRight size={14} className="tdRow__chev" aria-hidden="true" /> : null}
      </div>

      {/* 3 · Goal */}
      <div className={`tdRow tdRow--goal tdRow--${goal.tone}`} role="button" tabIndex={0} style={{ "--i": 2 }} aria-label={goal.aria} onClick={onGoal} onKeyDown={tdKey(onGoal)}>
        <div className="tdRow__viz">
          <svg className="tdRing" viewBox="0 0 36 36" aria-hidden="true">
            <circle className="tdRing__track" cx="18" cy="18" r="15.5" pathLength="100" />
            {goal.score != null ? <circle className="tdRing__arc" cx="18" cy="18" r="15.5" pathLength="100" style={{ "--p": goal.score }} /> : null}
          </svg>
        </div>
        <div className="tdRow__main">
          <div className="tdRow__label">Goal</div>
          <div className="tdRow__sub">{goal.sub}</div>
        </div>
        <div className="tdRow__side">
          <div className="tdRow__value">
            {goal.days == null ? <span className="tdRow__num tdRow__num--empty">—</span>
              : goal.days === 0 ? <span className="tdRow__num">Race day</span>
              : <><span className="tdRow__num"><CountUpValue value={goal.days} animate={fresh} /></span><span className="tdRow__unit">{goal.days === 1 ? "day" : "days"}</span></>}
          </div>
          <div className="tdRow__chips">{goal.chip ? <span className={`tdChip tdChip--${goal.chip.tone}`}>{goal.chip.text}</span> : null}</div>
        </div>
        <ChevronRight size={14} className="tdRow__chev" aria-hidden="true" />
      </div>

      {/* 4 · Plan track */}
      {track ? (
        <div className="tdTrack" style={{ "--i": 3, "--pos": track.todayPct, "--phase": track.phaseFg }}>
          <div className="tdTrack__top">
            <span className="tdTrack__week">Week {track.weekIdx}<span className="tdTrack__of"> / {track.totalWeeks}</span></span>
            <span className="tdTrack__phase">{track.phaseLabel}{track.isRecovery ? " · recovery" : ""}</span>
            {track.raceDateStr ? <span className="tdTrack__race">Race <b>{track.raceDateStr}</b></span> : null}
          </div>
          <div className="tdTrack__bar" role="img" aria-label={`Week ${track.weekIdx} of ${track.totalWeeks}, ${track.phaseLabel} phase`}>
            {track.regions.map((r, i) => <span key={i} className="tdTrack__region" style={{ left: `${r.start}%`, width: `${r.width}%`, background: r.fg }} />)}
            <span className="tdTrack__future" aria-hidden="true" />
            <span className="tdTrack__sweep" aria-hidden="true" />
            <span className="tdTrack__marker" aria-hidden="true" />
          </div>
          <div className="tdTrack__caption">
            <span>{track.baseline > 0 ? <><b>{track.baseline}</b> → </> : null}<b>{track.peak}</b> km/wk</span>
            <span>{track.wksLeftInPhase > 0
              ? <><b>{track.wksLeftInPhase}</b> {track.wksLeftInPhase === 1 ? "wk" : "wks"} left in {track.phaseLabel}</>
              : <>Last week of {track.phaseLabel}</>}</span>
          </div>
        </div>
      ) : null}
    </div>
  );
}

/* =========================
   Plan Generator — 3-screen flow
========================= */
function PlanGenerator({ onClose, onComplete, goalSaved, goalTimeStr, userMaxHr, userAge, actualWeeklyMap, conn, serverPlan, availableDays, setAvailableDays, runsPerWeek, setRunsPerWeek, userBaseline, generateRemote, intermediateGoals = [] }) {
  const [screen, setScreen] = useState(1);
  const [error, setError] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [previewPlan, setPreviewPlan] = useState(null);
  // v8: a BLOCKED generator result (readiness gate or pace-guard). When set,
  // the wizard shows the gate screen with the three honest options instead
  // of the plan preview. See /docs/training_methodology.md §"Readiness gate".
  const [gate, setGate] = useState(null);

  /* ---- Screen 1 state ---- */
  const distOpts = [
    { value: "5K", km: 5, sub: "speed goal" },
    { value: "10K", km: 10, sub: "speed goal" },
    { value: "Half Marathon", km: 21.0975, sub: "21.1 km" },
    { value: "Marathon", km: 42.195, sub: "42.2 km" },
    { value: "50K", km: 50, sub: "~5–9 hrs" },
    { value: "100K", km: 100, sub: "~9–16 hrs" },
    { value: "100M", km: 161, sub: "~16–30 hrs" },
    { value: "Custom", km: 0, sub: "enter km" },
  ];
  // Terrain options. Road is first + the default — most plans start here and
  // it's the cleanest baseline for pace estimation (terrain multiplier = 1.0).
  // Trail/Mixed/Mountain add a pace multiplier to the weekly plannedHours.
  const terrainOpts = [
    { value: "road", label: "Road ultra", sub: "flat, paved" },
    { value: "trail", label: "Trail", sub: "technical, elevation" },
    { value: "mixed", label: "Mixed", sub: "trail + road" },
    { value: "mountain", label: "Mountain", sub: "high vertical" },
  ];
  const defaultDist = distOpts.some((d) => d.value === goalSaved?.type) ? goalSaved.type : goalSaved?.type ? "Custom" : "100K";
  const [dist, setDist] = useState(defaultDist);
  const [customKm, setCustomKm] = useState(goalSaved?.type === "Custom" ? "80" : "");
  // Default terrain is "road" — user can override via the terrain selector.
  // If an existing plan is loaded, its terrain takes precedence via the
  // Screen-1 useEffect below.
  const [terrain, setTerrain] = useState(goalSaved?.terrain || serverPlan?.terrain || "road");
  const [raceDate, setRaceDate] = useState(goalSaved?.date || "");
  const [targetFinish, setTargetFinish] = useState(goalTimeStr || "12:00:00");
  // Elevation gain in meters. 0 / empty = flat/unknown. Drives the hiking-gate
  // per `/docs/training_methodology.md` §Pillar 4: hikes only generated when
  // elevationGain ≥ 1500m AND the week is in 'specific' phase.
  const [elevationGain, setElevationGain] = useState(goalSaved?.elevationGain || "");

  const medianWeeklyKm = useMemo(() => {
    if (!actualWeeklyMap || actualWeeklyMap.size === 0) return 30;
    return Math.round(estimateWeeklyKmNow(actualWeeklyMap));
  }, [actualWeeklyMap]);

  const distKm = dist === "Custom" ? Number(customKm) || 0 : distOpts.find(d => d.value === dist)?.km || 100;
  const weeksToRace = raceDate ? Math.max(1, Math.ceil((new Date(raceDate) - new Date()) / (7 * 864e5))) : 0;

  /* ---- Screen 2 state ----
     Defaults follow the methodology doc:
       - B2B on by distance (§Pillar 2): ≥50K defaults to true.
       - Hikes default OFF (§Anti-pattern #4); user can enable but the server
         will still strip the toggle when the elevation gate isn't met.
       - Vertical defaults off; only meaningful on trail/mixed/mountain. */
  const [b2b, setB2b] = useState(distKm >= 50);
  const [hikes, setHikes] = useState(false);
  const [vertical, setVertical] = useState(false);
  const [editMaxHr, setEditMaxHr] = useState(userMaxHr || "");
  const [editAge, setEditAge] = useState(userAge || "");

  // Re-derive the B2B default if the distance is changed after mount.
  useEffect(() => { setB2b(distKm >= 50); }, [distKm]);

  // Hiking eligibility (mirrors the server gate): elevationGain ≥ 1500m OR
  // terrain = mountain. If not eligible, the toggle is disabled + forced off.
  const elevNum = Number(elevationGain) || 0;
  const hikingEligible = elevNum >= 1500 || terrain === "mountain";
  useEffect(() => {
    if (!hikingEligible && hikes) setHikes(false);
  }, [hikingEligible, hikes]);

  /* ---- Generate plan ----
     Runs CLIENT-SIDE via src/lib/planGenerator.js. No server round trip —
     fixes the server-restart-coupling bug (Node caches require()d modules
     so edits to the server's generator don't take effect until restart).
     The plan is persisted to localStorage when the user confirms (see the
     parent's onComplete handler).

     v8: the generator may REFUSE — readiness gate or pace-guard — returning
     a { blocked:true, … } result. `opts` lets the gate screen re-run with a
     fix applied: a later raceDate, a shorter distance, finish-only mode, or
     an explicit acknowledgeReadiness override. */
  const handleGenerate = async (opts = {}) => {
    setGenerating(true);
    setError(null);
    try {
      const effDistKm = opts.distKm != null ? opts.distKm : distKm;
      const effRaceDate = opts.raceDate || raceDate;
      const params = {
        raceDistance: effDistKm,
        raceDate: effRaceDate,
        targetFinish,
        terrain,
        elevationGain: Number(elevationGain) || 0,
        sessionsPerWeek: runsPerWeek,
        availableDays: availableDays.map(dow => DAY_NAMES_LONG[dow]),
        includeBackToBack: b2b,
        includeHikes: hikes,
        includeVertical: vertical,
        currentWeeklyKm: medianWeeklyKm,
        userBaseline,
        goalMode: opts.goalMode || "standard",
        acknowledgeReadiness: !!opts.acknowledgeReadiness,
      };
      // Server-side macro plan (goals → one plan). Falls back to the bundled
      // generator only when no remote generator is wired (tests).
      const remote = generateRemote ? await generateRemote(params) : null;
      if (generateRemote && !remote) throw new Error("Plan generation failed — check your connection and try again.");
      const result = generateRemote ? (remote.blocked ? remote : remote.plan) : clientGeneratePlan(params);
      if (result && result.blocked) {
        // Refused — surface the gate (3 options) instead of a preview.
        setGate(result);
        setScreen("gate");
      } else {
        setPreviewPlan(result);
        setGate(null);
        setScreen(3);
      }
    } catch (e) {
      setError(e.message || String(e));
    } finally {
      setGenerating(false);
    }
  };

  /* ---- Gate-screen actions ---- */
  // (a) Move the race later — adopt the suggested timeline and regenerate.
  const gateMoveDate = (isoDate) => { setRaceDate(isoDate); handleGenerate({ raceDate: isoDate }); };
  // (b) Drop to a shorter distance on the same date.
  const gateDropDistance = (label, km) => {
    if (label === "50K" || label === "100K" || label === "100M") setDist(label);
    else { setDist("Custom"); setCustomKm(String(Math.round(km))); }
    handleGenerate({ distKm: km });
  };
  // (c) Generate the honest finish-only plan.
  const gateFinishOnly = () => handleGenerate({ goalMode: "finish-only" });
  // Conscious override — the runner has trained at this volume before.
  const gateAcknowledge = () => handleGenerate({ acknowledgeReadiness: true });

  /* ---- Confirm plan ---- */
  const handleConfirm = () => {
    onComplete(previewPlan);
  };

  /* ---- Phase colours — four phases only (§Pillar 3).
     "peak" is a legacy alias kept for defensive rendering of stale plans;
     recovery is not a phase. */
  const phaseColor = (p) => {
    const lc = (p || "").toLowerCase();
    if (lc === "base") return "var(--accent-base)";
    if (lc === "build") return "var(--caution)";
    if (lc === "specific" || lc === "peak") return "var(--critical)";
    if (lc === "taper") return "var(--content-tertiary)";
    return "var(--content-tertiary)";
  };

  const sessionTypeColor2 = (t) => {
    const lc = (t || "").toLowerCase();
    if (lc.includes("easy") || lc.includes("recovery")) return "var(--accent-base)";
    if (lc.includes("tempo") || lc.includes("vertical")) return "var(--caution)";
    if (lc.includes("long") || lc.includes("b2b")) return "var(--info-base)";
    if (lc.includes("time-on-feet") || lc.includes("time on feet")) return "var(--viz-long)";
    if (lc.includes("hike")) return "var(--accent-base)";
    return "var(--content-tertiary)";
  };

  /* ---- Option grid item ---- */
  const OptItem = ({ selected, label, sub, onClick }) => (
    <button
      type="button"
      className="pgOptItem"
      style={{ borderColor: selected ? "var(--accent-base)" : "var(--line-default)", background: selected ? "var(--surface-raised)" : "transparent" }}
      onClick={onClick}
    >
      <div style={{ fontSize: "var(--text-base-size)", fontWeight: 700, color: selected ? "var(--accent-base)" : "var(--content-primary)" }}>{label}</div>
      {sub && <div style={{ fontSize: "var(--text-xs-size)", color: "var(--content-tertiary)", marginTop: 2 }}>{sub}</div>}
    </button>
  );

  /* ---- Gate-screen option card ---- */
  const GateOption = ({ tag, title, sub, onClick }) => (
    <div className="pgSection">
      <button
        type="button"
        onClick={onClick}
        disabled={generating}
        style={{
          width: "100%", textAlign: "left", border: "0.5px solid var(--line-subtle)",
          borderRadius: 12, padding: 14, background: "var(--surface-raised)",
          cursor: generating ? "default" : "pointer", opacity: generating ? 0.5 : 1,
        }}
      >
        <div style={{ fontSize: "var(--text-xs-size)", fontWeight: 800, letterSpacing: "0.08em", color: "var(--accent-base)" }}>{tag}</div>
        <div style={{ fontSize: "var(--text-base-size)", fontWeight: 700, color: "var(--content-primary)", marginTop: 4 }}>{title}</div>
        <div style={{ fontSize: "var(--text-xs-size)", color: "var(--content-tertiary)", marginTop: 4, lineHeight: 1.45 }}>{sub}</div>
      </button>
    </div>
  );
  const fmtPaceSec = (s) => (s > 0 ? `${Math.floor(s / 60)}:${String(Math.round(s % 60)).padStart(2, "0")}` : "—");
  const fmtShortDate = (iso) => {
    try { return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }); }
    catch (_) { return iso; }
  };

  const headerStyle = { display: "flex", alignItems: "center", justifyContent: "space-between", padding: "16px 24px", borderBottom: "0.5px solid var(--line-subtle)" };

  return (
    <div className="pgOverlay">
      {/* ===== SCREEN 1 ===== */}
      {screen === 1 && (
        <div className="pgScreen">
          <div style={headerStyle}>
            <button type="button" className="pgBack" onClick={onClose}>&larr; Back</button>
            <span style={{ fontSize: "var(--text-sm-size)", color: "var(--content-tertiary)" }}>1 of 3</span>
          </div>
          <div className="pgBody">
            <div className="pgHero">
              <div style={{ fontSize: "var(--text-3xl-size)", fontWeight: 800, color: "var(--content-primary)" }}>Your race</div>
              <div style={{ fontSize: "var(--text-sm-size)", color: "var(--content-tertiary)", marginTop: 4 }}>Tell us about your goal race.</div>
            </div>

            <div className="pgSection">
              <div className="v7sectionLabel">DISTANCE</div>
              <div className="pgOptGrid">
                {distOpts.map(o => <OptItem key={o.value} selected={dist === o.value} label={o.value} sub={o.sub} onClick={() => setDist(o.value)} />)}
              </div>
              {dist === "Custom" && (
                <input className="input" type="number" placeholder="Distance in km" value={customKm} onChange={e => setCustomKm(e.target.value)} style={{ marginTop: 10 }} />
              )}
            </div>

            <div className="pgSection">
              <div className="v7sectionLabel">TERRAIN</div>
              <div className="pgOptGrid">
                {terrainOpts.map(o => <OptItem key={o.value} selected={terrain === o.value} label={o.label} sub={o.sub} onClick={() => setTerrain(o.value)} />)}
              </div>
            </div>

            <div className="pgSection">
              <div className="v7sectionLabel">RACE DATE</div>
              <input className="input" type="date" value={raceDate} onChange={e => setRaceDate(e.target.value)} />
            </div>

            <div className="pgSection">
              <div className="v7sectionLabel">TARGET FINISH</div>
              <input className="input" value={targetFinish} onChange={e => setTargetFinish(e.target.value)} placeholder="HH:MM:SS" />
            </div>

            {/* Elevation Gain — optional. Drives hiking eligibility (§Pillar 4).
                Leave blank for flat/road ultras → hiking stays off server-side. */}
            <div className="pgSection">
              <div className="v7sectionLabel">ELEVATION GAIN (optional)</div>
              <input
                className="input"
                type="number"
                min={0}
                step={100}
                placeholder="e.g. 2500 (total meters)"
                value={elevationGain}
                onChange={e => setElevationGain(e.target.value)}
              />
              <div style={{ fontSize: "var(--text-xs-size)", color: "var(--content-tertiary)", marginTop: 6, lineHeight: 1.4 }}>
                {elevNum >= 1500
                  ? "Hiking / power-hiking sessions will be available in the Specific phase."
                  : (terrain === "mountain"
                    ? "Mountain terrain → hiking available in Specific phase regardless of elevation number."
                    : "Leave empty or under 1500 m total for flat/road ultras. Hiking stays off.")}
              </div>
            </div>

            {/* YOUR BASELINE — surfaced BEFORE generation so the runner can
                sanity-check the numbers the plan (and the readiness gate)
                will be built on. See /docs/training_methodology.md
                §"User baseline". */}
            <div className="pgSection">
              <div className="v7sectionLabel">YOUR BASELINE</div>
              {userBaseline && userBaseline.dataConfidence !== "none" ? (
                <div style={{ border: "0.5px solid var(--line-subtle)", borderRadius: 10, padding: 14, background: "var(--surface-raised)" }}>
                  <div style={{ display: "flex", gap: 14 }}>
                    {[
                      { label: "4-WK MEDIAN", value: `${userBaseline.medianWeeklyKm}`, unit: "km/wk" },
                      {
                        label: "LONGEST · 60D",
                        value: userBaseline.longestRunKm > 0 ? `${userBaseline.longestRunKm}` : "—",
                        unit: userBaseline.longestRunKm > 0
                          ? `km · ${Math.round(userBaseline.longestRunDurationSec / 60)} min`
                          : "no long run",
                      },
                      { label: "CONSISTENCY", value: `${userBaseline.weeksActive}/4`, unit: "weeks active" },
                    ].map((s, i) => (
                      <div key={i} style={{ flex: 1 }}>
                        <div style={{ fontSize: "var(--text-xs-size)", fontWeight: 800, letterSpacing: "0.07em", color: "var(--content-tertiary)" }}>{s.label}</div>
                        <div style={{ fontSize: "var(--text-xl-size)", fontWeight: 800, color: "var(--content-primary)", marginTop: 3 }}>{s.value}</div>
                        <div style={{ fontSize: "var(--text-xs-size)", color: "var(--content-tertiary)" }}>{s.unit}</div>
                      </div>
                    ))}
                  </div>
                  <div style={{ fontSize: "var(--text-xs-size)", color: "var(--content-tertiary)", marginTop: 10, lineHeight: 1.4 }}>
                    {userBaseline.observedThresholdPaceSec
                      ? `Training paces anchored to your observed pace (${fmtPaceSec(userBaseline.observedThresholdPaceSec)}/km, from recent Strava runs).`
                      : "No recent short-run data yet — training paces are projected from your goal time until Strava shows more."}
                  </div>
                  {userBaseline.isInconsistent && (
                    <div style={{ marginTop: 10, padding: "8px 10px", borderRadius: 8, background: "rgb(var(--caution-rgb) / 0.10)", border: "0.5px solid rgb(var(--caution-rgb) / 0.35)" }}>
                      <div style={{ fontSize: "var(--text-xs-size)", fontWeight: 700, color: "var(--caution)" }}>Inconsistent recent training</div>
                      <div style={{ fontSize: "var(--text-xs-size)", color: "var(--content-secondary)", marginTop: 2, lineHeight: 1.4 }}>
                        Only {userBaseline.weeksActive} of the last 4 weeks were active — this baseline may understate your true capacity. Adjust the plan if the last 4 weeks aren't representative of your fitness.
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <span style={{ width: 8, height: 8, borderRadius: "50%", background: "var(--content-tertiary)", flexShrink: 0 }} />
                  <span style={{ fontSize: "var(--text-sm-size)", color: "var(--content-tertiary)" }}>
                    {conn?.data
                      ? "No recent runs — sync or import to compute your training baseline."
                      : "Connect Strava or import your runs for an accurate, baseline-driven plan."}
                  </span>
                </div>
              )}
            </div>

            <div className="pgSection">
              <button className="v7exportBtn" disabled={!raceDate} onClick={() => setScreen(2)} style={{ opacity: raceDate ? 1 : 0.4 }}>
                Next — Training preferences
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ===== SCREEN 2 ===== */}
      {screen === 2 && (
        <div className="pgScreen">
          <div style={headerStyle}>
            <button type="button" className="pgBack" onClick={() => setScreen(1)}>&larr; Back</button>
            <span style={{ fontSize: "var(--text-sm-size)", color: "var(--content-tertiary)" }}>2 of 3</span>
          </div>
          <div className="pgBody">
            <div className="pgHero">
              <div style={{ fontSize: "var(--text-3xl-size)", fontWeight: 800, color: "var(--content-primary)" }}>Your training</div>
              <div style={{ fontSize: "var(--text-sm-size)", color: "var(--content-tertiary)", marginTop: 4 }}>How you train shapes your plan.</div>
            </div>

            <div className="pgSection">
              <div className="v7sectionLabel">RUNS PER WEEK</div>
              <div style={{ display: "flex", gap: 8 }}>
                {[3, 4, 5].map(n => (
                  <button key={n} type="button" className="pgOptItem" style={{ flex: 1, borderColor: runsPerWeek === n ? "var(--accent-base)" : "var(--line-default)", background: runsPerWeek === n ? "var(--surface-raised)" : "transparent" }} onClick={() => {
                    setRunsPerWeek(n);
                    // Keep availableDays >= runsPerWeek. Pad with sensible defaults if under.
                    if (availableDays.length < n) setAvailableDays(defaultAvailableDaysForCount(n, availableDays));
                  }}>
                    <div style={{ fontSize: "var(--text-base-size)", fontWeight: 700, color: runsPerWeek === n ? "var(--accent-base)" : "var(--content-primary)" }}>{n}</div>
                    <div style={{ fontSize: "var(--text-xs-size)", color: "var(--content-tertiary)" }}>{n === 3 ? "minimal" : n === 4 ? "standard" : "high"}</div>
                  </button>
                ))}
              </div>
            </div>

            <div className="pgSection">
              <div className="v7sectionLabel">AVAILABLE DAYS</div>
              <DayPills value={availableDays} onChange={setAvailableDays} minDays={runsPerWeek} />
              {availableDays.length < runsPerWeek && (
                <div className="muted deltaNeg" style={{ fontSize: "var(--text-xs-size)", marginTop: 6 }}>
                  Pick at least {runsPerWeek} days ({availableDays.length} currently).
                </div>
              )}
            </div>

            <div className="pgSection">
              <div className="v7sectionLabel">INCLUDE IN PLAN</div>
              <div className="pgToggleRow">
                <span style={{ fontSize: "var(--text-sm-size)", color: "var(--content-primary)" }}>Back-to-back long runs</span>
                <button type="button" className="pgToggle" style={{ background: b2b ? "var(--accent-base)" : "var(--line-default)" }} onClick={() => setB2b(!b2b)}><span className="pgToggleKnob" style={{ transform: b2b ? "translateX(18px)" : "translateX(2px)" }} /></button>
              </div>
              {/* Hiking toggle is gated per §Pillar 4: elevationGain ≥ 1500m
                  OR terrain = mountain. When not eligible, the toggle is
                  visibly disabled and locked off so the user sees WHY. */}
              <div className="pgToggleRow">
                <span style={{ fontSize: "var(--text-sm-size)", color: hikingEligible ? "var(--content-primary)" : "var(--content-tertiary)" }}>
                  Hiking sessions{hikingEligible ? "" : " (needs ≥1500 m elevation)"}
                </span>
                <button
                  type="button"
                  className="pgToggle"
                  style={{
                    background: hikes ? "var(--accent-base)" : "var(--line-default)",
                    opacity: hikingEligible ? 1 : 0.35,
                    cursor: hikingEligible ? "pointer" : "not-allowed",
                  }}
                  disabled={!hikingEligible}
                  onClick={() => { if (hikingEligible) setHikes(!hikes); }}
                >
                  <span className="pgToggleKnob" style={{ transform: hikes && hikingEligible ? "translateX(18px)" : "translateX(2px)" }} />
                </button>
              </div>
              <div className="pgToggleRow">
                <span style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: "var(--text-sm-size)", color: "var(--content-primary)" }}>
                  Recovery weeks every 4th
                  <Lock size={11} color="var(--content-tertiary)" aria-label="Locked on per methodology §Pillar 3" />
                </span>
                <button type="button" className="pgToggle" disabled style={{ background: "var(--accent-base)", opacity: 0.55, cursor: "default" }} aria-disabled="true">
                  <span className="pgToggleKnob" style={{ transform: "translateX(18px)" }} />
                </button>
              </div>
              {(terrain === "trail" || terrain === "mountain") && (
                <div className="pgToggleRow">
                  <span style={{ fontSize: "var(--text-sm-size)", color: "var(--content-primary)" }}>Vertical / elevation focus</span>
                  <button type="button" className="pgToggle" style={{ background: vertical ? "var(--accent-base)" : "var(--line-default)" }} onClick={() => setVertical(!vertical)}><span className="pgToggleKnob" style={{ transform: vertical ? "translateX(18px)" : "translateX(2px)" }} /></button>
                </div>
              )}
            </div>

            <div className="pgSection">
              <div className="v7sectionLabel">HR CALIBRATION</div>
              <div className="pgInputRow">
                <span className="pgInputRow__label">Max HR</span>
                <input
                  className="input pgInputRow__input"
                  type="number"
                  placeholder={conn?.strava ? "Auto from Strava" : "e.g. 190"}
                  value={editMaxHr}
                  onChange={e => setEditMaxHr(e.target.value)}
                />
              </div>
              <div className="pgInputRow">
                <span className="pgInputRow__label">Age</span>
                <input
                  className="input pgInputRow__input"
                  type="number"
                  placeholder="—"
                  value={editAge}
                  onChange={e => setEditAge(e.target.value)}
                />
              </div>
            </div>

            <div className="pgSection">
              <div style={{ fontSize: "var(--text-xs-size)", color: "var(--content-tertiary)", marginBottom: 12, textAlign: "center" }}>
                {weeksToRace} weeks · {runsPerWeek} sessions/week · {terrain} {dist === "Custom" ? `${customKm}km` : dist}
              </div>
              <button className="v7exportBtn" disabled={generating || availableDays.length < runsPerWeek} onClick={handleGenerate} style={{ opacity: (generating || availableDays.length < runsPerWeek) ? 0.5 : 1 }}>
                {generating
                  ? (serverPlan?.weeks?.length ? "Regenerating…" : "Generating…")
                  : (serverPlan?.weeks?.length ? "Regenerate my plan" : "Generate my plan")}
              </button>
              {error && <div style={{ fontSize: "var(--text-xs-size)", color: "var(--critical)", marginTop: 8, textAlign: "center" }}>{error}</div>}
            </div>
          </div>
        </div>
      )}

      {/* ===== SCREEN 3 ===== */}
      {screen === 3 && previewPlan && (
        <div className="pgScreen">
          <div style={headerStyle}>
            <button type="button" className="pgBack" onClick={() => setScreen(2)}>&larr; Back</button>
            <span style={{ fontSize: "var(--text-sm-size)", color: "var(--content-tertiary)" }}>3 of 3</span>
          </div>
          <div className="pgBody">
            <div className="pgHero">
              <div style={{ fontSize: "var(--text-3xl-size)", fontWeight: 800, color: "var(--content-primary)" }}>{previewPlan.weeks?.length || weeksToRace} weeks</div>
              <div style={{ fontSize: "var(--text-sm-size)", color: "var(--content-tertiary)", marginTop: 4 }}>
                {terrain} {dist === "Custom" ? `${customKm}km` : dist} · {runsPerWeek} sessions/week
                {previewPlan.goalMode === "finish-only" ? " · finish-focused" : ""}
                {b2b ? " · B2B" : ""}{hikes ? " · hikes" : ""}{vertical ? " · vert" : ""}
              </div>
            </div>

            {/* Finish-only plans carry an honest expectations note. */}
            {previewPlan.goalMode === "finish-only" && previewPlan.finishNote && (
              <div className="pgSection">
                <div style={{ padding: "12px 14px", borderRadius: 10, background: "rgb(var(--accent-rgb) / 0.10)", border: "0.5px solid rgb(var(--accent-rgb) / 0.35)" }}>
                  <div style={{ fontSize: "var(--text-xs-size)", fontWeight: 800, letterSpacing: "0.07em", color: "var(--accent-base)" }}>FINISH-FOCUSED PLAN</div>
                  <div style={{ fontSize: "var(--text-xs-size)", color: "var(--content-secondary)", marginTop: 5, lineHeight: 1.5 }}>{previewPlan.finishNote}</div>
                </div>
              </div>
            )}

            {/* Phase blocks */}
            {(() => {
              const weeks = previewPlan.weeks || [];
              if (!weeks.length) return <div className="pgSection" style={{ color: "var(--content-tertiary)", fontSize: "var(--text-sm-size)" }}>No weeks in plan.</div>;
              const phases = [];
              let cur = null;
              weeks.forEach((w, i) => {
                const p = (w.phase || "base").toLowerCase();
                if (!cur || cur.phase !== p) {
                  cur = { phase: p, startWk: i + 1, endWk: i + 1, sessionTypes: new Set(), minKm: Infinity, maxKm: 0, minHrs: Infinity, maxHrs: 0 };
                  phases.push(cur);
                } else {
                  cur.endWk = i + 1;
                }
                const wkKm = w.plannedKm != null ? w.plannedKm : (w.sessions || []).reduce((s, sess) => s + (sess.plannedKm ?? sess.distanceKm ?? 0), 0);
                const wkHrs = w.plannedHours != null ? w.plannedHours : (w.sessions || []).reduce((s, sess) => s + (((sess.plannedDuration ?? sess.durationMin) || 0) / 60), 0);
                if (wkKm < cur.minKm) cur.minKm = wkKm;
                if (wkKm > cur.maxKm) cur.maxKm = wkKm;
                if (wkHrs < cur.minHrs) cur.minHrs = wkHrs;
                if (wkHrs > cur.maxHrs) cur.maxHrs = wkHrs;
                (w.sessions || []).forEach(sess => cur.sessionTypes.add(sess.type || sess.title || ""));
              });
              return phases.map((ph, i) => (
                <div className="pgSection" key={i}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
                    <span style={{ fontSize: "var(--text-xs-size)", fontWeight: 700, color: "var(--content-primary)" }}>WEEK {ph.startWk}{ph.endWk !== ph.startWk ? `–${ph.endWk}` : ""}</span>
                    <span style={{ fontSize: "var(--text-xs-size)", fontWeight: 800, letterSpacing: "0.08em", textTransform: "uppercase", color: phaseColor(ph.phase), padding: "3px 10px", borderRadius: 999, border: `1px solid ${phaseColor(ph.phase)}33`, background: `${phaseColor(ph.phase)}15` }}>
                      {ph.phase}
                    </span>
                  </div>
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 8 }}>
                    {Array.from(ph.sessionTypes).map((t, j) => (
                      <span key={j} style={{ fontSize: "var(--text-xs-size)", fontWeight: 700, padding: "3px 8px", borderRadius: 999, color: sessionTypeColor2(t), border: `1px solid ${sessionTypeColor2(t)}44`, background: `${sessionTypeColor2(t)}15` }}>{t}</span>
                    ))}
                  </div>
                  <div style={{ fontSize: "var(--text-xs-size)", color: "var(--content-tertiary)" }}>
                    ~{Math.round(ph.minKm)}–{Math.round(ph.maxKm)} km/week · {fmt1(ph.minHrs)}–{fmt1(ph.maxHrs)} hrs
                  </div>
                </div>
              ));
            })()}

            <div className="pgSection">
              <button className="v7exportBtn" onClick={handleConfirm}>Start this plan</button>
              <div style={{ fontSize: "var(--text-xs-size)", color: "var(--content-tertiary)", marginTop: 8, textAlign: "center" }}>RESET Score and goal likelihood activate immediately</div>
              {error && <div style={{ fontSize: "var(--text-xs-size)", color: "var(--critical)", marginTop: 8, textAlign: "center" }}>{error}</div>}
            </div>
          </div>
        </div>
      )}

      {/* ===== GATE SCREEN — readiness gate / pace-guard =====
          Shown when the v8 generator REFUSES to build the plan. Per
          /docs/training_methodology.md §"Readiness gate" and §"Pace sanity
          guard": RESET never silently emits an injury-bait plan — it shows
          the runner honest alternatives instead. */}
      {screen === "gate" && gate && (
        <div className="pgScreen">
          <div style={headerStyle}>
            <button type="button" className="pgBack" onClick={() => { setGate(null); setScreen(2); }}>&larr; Back</button>
            <span style={{ fontSize: "var(--text-sm-size)", color: "var(--content-tertiary)" }}>Readiness check</span>
          </div>
          <div className="pgBody">
            {gate.reason === "pace-guard" ? (
              <>
                <div className="pgHero">
                  <div style={{ fontSize: "var(--text-3xl-size)", fontWeight: 800, color: "var(--content-primary)" }}>That goal time doesn't add up</div>
                  <div style={{ fontSize: "var(--text-sm-size)", color: "var(--content-tertiary)", marginTop: 8, lineHeight: 1.55 }}>
                    The finish time you entered implies a training pace about{" "}
                    <b style={{ color: "var(--caution)" }}>
                      {Math.max(1, Math.round((1 - (gate.paceGuard?.ratio || 1)) * 100))}% faster
                    </b>{" "}
                    than your recent runs
                    {gate.paceGuard?.observedMedianRunPaceSec
                      ? ` (${fmtPaceSec(gate.paceGuard.observedMedianRunPaceSec)}/km median)`
                      : ""}.
                    Prescribing paces that far ahead of your current fitness is exactly how
                    runners get hurt — so RESET won't do it.
                  </div>
                </div>
                <div className="pgSection">
                  <button className="v7exportBtn" onClick={() => { setGate(null); setScreen(1); }}>
                    Adjust my goal time
                  </button>
                  <div style={{ fontSize: "var(--text-xs-size)", color: "var(--content-tertiary)", marginTop: 8, textAlign: "center" }}>
                    Or sync more Strava history so RESET can read your real threshold pace.
                  </div>
                </div>
              </>
            ) : (
              <>
                <div className="pgHero">
                  <div style={{ fontSize: "var(--text-3xl-size)", fontWeight: 800, color: "var(--content-primary)" }}>This ramp isn't safe yet</div>
                  <div style={{ fontSize: "var(--text-sm-size)", color: "var(--content-tertiary)", marginTop: 8, lineHeight: 1.55 }}>
                    Your 4-week median is{" "}
                    <b style={{ color: "var(--content-primary)" }}>{gate.readiness?.baselineWeeklyKm} km/wk</b>.
                    A {dist === "Custom" ? `${customKm}K` : dist} plan over this window would have to
                    {" "}<b style={{ color: "var(--caution)" }}>
                      start near {Math.round(gate.readiness?.requiredStart || 0)} km/wk
                    </b>{" "}
                    and peak around {Math.round(gate.readiness?.peakKm || 0)} km/wk — a{" "}
                    {gate.readiness?.stretchFactor}× jump. That's an injury risk, not a training
                    plan. Three honest ways forward:
                  </div>
                </div>

                {gate.readiness?.options?.laterDate && (
                  <GateOption
                    tag="MOVE THE RACE"
                    title={`Race on ${fmtShortDate(gate.readiness.options.laterDate.suggestedRaceDate)}`}
                    sub={`${gate.readiness.options.laterDate.suggestedWeeks} weeks out — enough runway to ramp safely to your goal time.`}
                    onClick={() => gateMoveDate(gate.readiness.options.laterDate.suggestedRaceDate)}
                  />
                )}
                {gate.readiness?.options?.shorterDistance && (
                  <GateOption
                    tag="SHORTER DISTANCE"
                    title={`Switch to ${gate.readiness.options.shorterDistance.label}, same date`}
                    sub="A distance your current base can safely support in the time you have."
                    onClick={() => gateDropDistance(
                      gate.readiness.options.shorterDistance.label,
                      gate.readiness.options.shorterDistance.km
                    )}
                  />
                )}
                <GateOption
                  tag="FINISH-FOCUSED PLAN"
                  title="Train to finish, not to race"
                  sub={`A safe plan peaking near ${Math.round(gate.readiness?.options?.finishOnly?.projectedPeakKm || 0)} km/wk. Honest about a slower finish — built around time-on-feet, a walk-run rhythm, and fuelling practice.`}
                  onClick={gateFinishOnly}
                />

                <div className="pgSection">
                  <button
                    type="button"
                    onClick={gateAcknowledge}
                    disabled={generating}
                    style={{
                      width: "100%", background: "transparent", border: "none",
                      color: "var(--content-tertiary)", fontSize: "var(--text-xs-size)", padding: "10px 0",
                      cursor: generating ? "default" : "pointer", textDecoration: "underline",
                    }}
                  >
                    I've trained at this volume before — build the plan anyway
                  </button>
                </div>
              </>
            )}
            {error && <div style={{ fontSize: "var(--text-xs-size)", color: "var(--critical)", marginTop: 8, textAlign: "center" }}>{error}</div>}
          </div>
        </div>
      )}
    </div>
  );
}

/* =========================
   App
========================= */
/** Owner-only: mint beta invite codes from Settings. */
function InviteRow() {
  const [open, setOpen] = useState(false);
  const [invites, setInvites] = useState([]);
  const [busy, setBusy] = useState(false);
  const load = async () => {
    try { const r = await api.get("/api/admin/invites"); setInvites(r.invites || []); } catch (_) {}
  };
  const mint = async () => {
    setBusy(true);
    try { await api.post("/api/admin/invites", { maxUses: 1 }); await load(); } finally { setBusy(false); }
  };
  useEffect(() => { if (open) load(); }, [open]);
  const active = invites.filter((i) => i.uses < i.maxUses && (!i.expiresAt || i.expiresAt > Date.now()));
  return (
    <>
      <button className="settingsRow" onClick={() => setOpen((v) => !v)}>
        <span className="settingsTile settingsTile--sage"><Users size={14} /></span>
        <span className="settingsRow__titleWrap">
          <span className="settingsRow__title">Beta invites</span>
          <span className="settingsRow__sub">Create codes for testers</span>
        </span>
        <span className="settingsRow__chev">{open ? "▾" : "›"}</span>
      </button>
      {open && (
        <div className="settingsRow settingsRow--static" style={{ flexDirection: "column", alignItems: "stretch", gap: 8 }}>
          {active.length === 0 && <div className="muted" style={{ fontSize: "var(--text-xs-size)" }}>No unused codes.</div>}
          {active.map((i) => (
            <div key={i.code} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8 }}>
              <code style={{ fontSize: "var(--text-sm-size)", letterSpacing: "0.04em" }}>{i.code}</code>
              <button type="button" className="btn btnGhost" style={{ padding: "4px 10px", fontSize: "var(--text-xs-size)" }} onClick={() => { try { navigator.clipboard.writeText(i.code); } catch (_) {} }}>Copy</button>
            </div>
          ))}
          <button type="button" className="btn btnOk" style={{ alignSelf: "flex-start" }} onClick={mint} disabled={busy}>{busy ? "…" : "New invite code"}</button>
          <div className="muted" style={{ fontSize: "var(--text-xs-size)" }}>Testers sign in with their e-mail + this code. Single use.</div>
        </div>
      )}
    </>
  );
}

export default function App() {
  // session
  const [session, setSession] = useState(null);
  const [loadingSession, setLoadingSession] = useState(true);

  // Account (auth rewrite): null = anonymous → LoginScreen. Settings, goals,
  // plan and coach thread are all keyed by this user server-side.
  const [authUser, setAuthUser] = useState(null);
  const [authInfo, setAuthInfo] = useState(null);
  const [betaLocked, setBetaLocked] = useState(false);
  // First-run story, read once at mount so it cannot flash in mid-session.
  const [showStory, setShowStory] = useState(() => !hasOnboarded());
  /* Coach notifications (opt-in). Mirrors user.settings.notify; the server
     is the source of truth and this is a cache for the Settings UI. */
  const [scoreDetailsOpen, setScoreDetailsOpen] = useState(false);
  const [notifyPrefs, setNotifyPrefs] = useState({ enabled: false, cadence: "daily", hour: 7 });
  const [pushState, setPushState] = useState({ serverEnabled: false, subscribed: false, support: null, busy: false, msg: "" });


  const [authLoading, setAuthLoading] = useState(true);
  const [authError, setAuthError] = useState(null);
  // Goals (A + intermediates) — server is the source of truth.
  const [goals, setGoals] = useState([]);
  const [rebuilding, setRebuilding] = useState(false);
  // Guards the settings write-through: never push client defaults over the
  // server copy before the server copy has been applied.
  const settingsHydratedRef = useRef(false);
  const settingsSaveTimer = useRef(null);

  // Server-generated plan (new dynamic plan system)
  const [serverPlan, setServerPlan] = useState(null);
  const [serverPlanLoading, setServerPlanLoading] = useState(false);

  // Server-computed metrics (replaces all client-side KPI computation)
  const [serverMetrics, setServerMetrics] = useState(null);

  // modals
  const [openConnect, setOpenConnect] = useState(false);
  const [openMethod, setOpenMethod] = useState(false);
  const [openAdjustGoal, setOpenAdjustGoal] = useState(false);
  const [adjustError, setAdjustError] = useState("");
  const [adjustSaving, setAdjustSaving] = useState(false);
  const openAdjustGoalSheet = () => { setAdjustError(""); setOpenAdjustGoal(true); };
  /* Writes the A goal on the server; a date change rebuilds the plan
     through the readiness gate. The sheet validates live; this is the
     backstop and the only path that persists. */
  const saveAdjustedGoal = async ({ time, date, terrain }) => {
    const v = validateAdjustGoal({ time, date, todayIso: localIsoDate(new Date()), currentDate: goalSaved?.date || "" });
    if (!v.ok) { setAdjustError(v.error); return; }
    const a = goals.find((g) => g.status === "planned" && g.priority === "A");
    const distKm = (a && a.distanceKm) || goalDistanceKm;
    if (!distKm) { setAdjustError("Set up a goal race first."); return; }
    const currentTerrain = (a && a.terrain) || goalSaved?.terrain || null;
    const terrainChanged = !!terrain && terrain !== currentTerrain;
    setAdjustSaving(true);
    setAdjustError("");
    try {
      await syncPrimaryGoal({
        raceDistance: distKm,
        raceDate: v.raceDate,
        targetFinish: v.targetFinish,
        terrain: terrain || currentTerrain,
        elevationGain: (a && a.elevationGain) || goalSaved?.elevationGain || 0,
      });
    } catch (e) {
      setAdjustError(e?.message || "Could not save the goal. Try again.");
      setAdjustSaving(false);
      return;
    }
    setAdjustSaving(false);
    setOpenAdjustGoal(false);
    if ((v.dateChanged || terrainChanged) && serverPlan) await rebuildFromGoals();
  };
  const [openGoal, setOpenGoal] = useState(false);
  const [showPlanGenerator, setShowPlanGenerator] = useState(false);
  // A refused (blocked) generation from outside the wizard — Goals rebuild,
  // day re-layout, readiness banner. Opens the wizard straight on its gate
  // screen, prefilled with the refused goal, so the athlete always gets the
  // server's options (§"Readiness gate") instead of a silent no-op.
  const [wizardGate, setWizardGate] = useState(null); // { gate, prefill }

  // reset knobs (explicit only)
  const [forcedRecoveryWeekStartKey, setForcedRecoveryWeekStartKey] = useState(null); // number (+startOfWeek)
  const [lastResetDismissedAt, setLastResetDismissedAt] = useState(null); // optional future use

  // training schedule — CANONICAL single source of truth
  // (see DAY_NAMES_LONG / PILL_DOW_ORDER comment block for model details)
  const [runsPerWeek, setRunsPerWeek] = useState(3);
  const [availableDays, setAvailableDays] = useState(DEFAULT_AVAILABLE_DAYS);
  // Preferred long-run day (dow 0=Mon..6=Sun) or null = auto (latest day).
  // Persisted with the goal so a regenerate keeps the choice.
  const [longRunDow, setLongRunDow] = useState(() => {
    try { const v = localStorage.getItem("reset_long_run_dow"); return v == null || v === "" ? null : Number(v); } catch (_) { return null; }
  });
  const longRunDayName = longRunDow != null ? DAY_NAMES_LONG[longRunDow] : null;
  const [tempoDow, setTempoDow] = useState(null);
  const tempoDayName = tempoDow != null ? DAY_NAMES_LONG[tempoDow] : null;
  useEffect(() => {
    try {
      if (longRunDow == null) localStorage.removeItem("reset_long_run_dow");
      else localStorage.setItem("reset_long_run_dow", String(longRunDow));
    } catch (_) {}
  }, [longRunDow]);
  const [scheduleChangedAt, setScheduleChangedAt] = useState(null); // +startOfWeek when schedule last changed
  // When true, Strava "Hike" / "Walk" activities count toward weekly volume +
  // adherence. Default off (Pillar 4 anti-pattern #4 — no hikes in flat-road
  // plans by default). Surfaced as a toggle in Goal & Plan Settings.
  const [includeHikesInVolume, setIncludeHikesInVolume] = useState(false);

  // Theme is now single cool-dark (see App.css `:root`). The old 3-way
  // toggle was removed — it was cosmetic after the Update 3 palette
  // rewrite since all three modes resolved to the same CSS variables.

  const isMobile = useIsMobileLayout();

  // status/errors
  const [stravaStatus, setStravaStatus] = useState("");
  const [whoopStatus, setWhoopStatus] = useState("");
  const [stravaError, setStravaError] = useState("");
  const [whoopError, setWhoopError] = useState("");
  const [garminStatus, setGarminStatus] = useState("");
  const [garminError, setGarminError] = useState("");
  // Garmin feature flag + connection state. The flag comes from the server
  // (/api/config, GARMIN_ENABLED) — with the flag off the Garmin card does
  // not render at all.
  const [garminEnabled, setGarminEnabled] = useState(false);
  const [garminConn, setGarminConn] = useState(null);

  // GOAL: draft (editable) + saved (plan-anchored)
  const [goalDraftType, setGoalDraftType] = useState("100K");
  const [goalDraftDate, setGoalDraftDate] = useState(""); // yyyy-mm-dd
  const [goalSaved, setGoalSaved] = useState(null); // { type, date, savedAt, planAnchorDate }

  // Plan knob (does NOT change goal)
  const [goalTimeStr, setGoalTimeStr] = useState(""); // "" = no time goal

  // HR zone calibration
  const [userMaxHr, setUserMaxHr] = useState(""); // manually entered max HR
  const [userAge, setUserAge] = useState("");      // age for 220-age fallback
  const [userPaceStr, setUserPaceStr] = useState(""); // manual reference pace (min/km); overrides Strava-derived threshold
  const [picker, setPicker] = useState(null); // wheel-picker sheet: null | "pace" | "hr" | "age"

  // UI
  const [windowDays, setWindowDays] = useState(14);
  const [pastLimit, setPastLimit] = useState(3);
  const [nextLimit, setNextLimit] = useState(3);
  /* Plan vs Actual shows the last 7 days and lets you pull earlier runs in.
     Not persisted: every visit starts at the last week, which is what the
     list is for. (`windowDays` above is the older setting; it still syncs
     with the server so nothing already stored is lost, but the list no
     longer reads it.) */
  const [pvaDays, setPvaDays] = useState(7);
  /* Train: this week and next are shown; the rest of the plan is folded
     behind one disclosure, and each later week opens on its own. */
  const [trainShowRest, setTrainShowRest] = useState(false);
  const [trainOpenWeeks, setTrainOpenWeeks] = useState(() => new Set());

  // Mobile tab navigation
  const [activeTab, setActiveTab] = useState(() => {
    // Storage can throw (site data blocked, some private modes); the app
    // must still open — on Today.
    let saved = null;
    try { saved = localStorage.getItem(TAB_KEY); } catch (_) {}
    return TABS.includes(saved) ? saved : "today";
  });
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [detailSheet, setDetailSheet] = useState(null); // for HR bottom sheet
  const [trainSessionSheet, setTrainSessionSheet] = useState(null); // { session, originalKey }
  /* Which Stats number is open in the detail sheet:
     'adherence' | 'pace' | 'load' | 'fitness' | 'form' | 'hr' | null */
  const [statSheet, setStatSheet] = useState(null);
  const [sessionEdits, setSessionEdits] = useState(() => {
    try {
      const raw = localStorage.getItem(SESSION_EDITS_KEY);
      return raw ? JSON.parse(raw) : {};
    } catch (_) { return {}; }
  });

  useEffect(() => {
    try { localStorage.setItem(TAB_KEY, activeTab); } catch (_) {}
  }, [activeTab]);

  /* -------------------------
     Settings: server-first, localStorage as cache/legacy import.
     Hydration order: (1) user.settings.ui from /api/me, (2) legacy
     localStorage (pre-auth builds) — pushed to the server once.
  ------------------------- */
  const applyUiSettings = (p) => {
    if (!p) return;
    try {
      if (p.goalDraftType) setGoalDraftType(String(p.goalDraftType));
      if (p.goalDraftDate) setGoalDraftDate(String(p.goalDraftDate));
      if (p.goalSaved) setGoalSaved(p.goalSaved);
      if (p.goalTimeStr) setGoalTimeStr(String(p.goalTimeStr));
      if (p.windowDays != null) setWindowDays(Number(p.windowDays));
      if (p.pastLimit != null) setPastLimit(Number(p.pastLimit));
      if (p.nextLimit != null) setNextLimit(Number(p.nextLimit));
      if (p.forcedRecoveryWeekStartKey != null) setForcedRecoveryWeekStartKey(Number(p.forcedRecoveryWeekStartKey));
      if (p.lastResetDismissedAt != null) setLastResetDismissedAt(Number(p.lastResetDismissedAt));
      if (p.runsPerWeek != null) setRunsPerWeek(clamp(Number(p.runsPerWeek), 3, 5));
      if (p.availableDays && Array.isArray(p.availableDays)) {
        const sorted = sortDow(p.availableDays);
        if (sorted.length >= 3) setAvailableDays(sorted);
      } else if (p.trainingDays) {
        const migrated = availableDaysFromLegacyTrainingDays(p.trainingDays);
        if (migrated && migrated.length >= 3) setAvailableDays(migrated);
      }
      if (p.scheduleChangedAt != null) setScheduleChangedAt(Number(p.scheduleChangedAt));
      if (p.userMaxHr != null) setUserMaxHr(String(p.userMaxHr));
      if (p.userAge != null) setUserAge(String(p.userAge));
      // Only restore a stored reference pace that is inside the valid range.
      if (p.userPaceSec != null && Number(p.userPaceSec) >= REF_PACE_MIN_SEC && Number(p.userPaceSec) <= REF_PACE_MAX_SEC) {
        setUserPaceStr(secToMMSS(Number(p.userPaceSec)));
      }
      if (p.includeHikesInVolume != null) setIncludeHikesInVolume(!!p.includeHikesInVolume);
      if (p.longRunDow !== undefined) setLongRunDow(p.longRunDow == null ? null : Number(p.longRunDow));
      if (p.sessionEdits && typeof p.sessionEdits === "object") setSessionEdits(p.sessionEdits);
    } catch (_) {}
  };

  const readLegacyLocalSettings = () => {
    try {
      const out = {};
      const rawGoal = localStorage.getItem(GOAL_KEY);
      if (rawGoal) {
        const g = JSON.parse(rawGoal);
        if (g?.type) out.goalDraftType = String(g.type);
        if (g?.date) out.goalDraftDate = String(g.date);
      }
      const rawPlan = localStorage.getItem(PLAN_KEY);
      if (rawPlan) Object.assign(out, JSON.parse(rawPlan) || {});
      const rawEdits = localStorage.getItem(SESSION_EDITS_KEY);
      if (rawEdits) out.sessionEdits = JSON.parse(rawEdits) || {};
      const lr = localStorage.getItem("reset_long_run_dow");
      if (lr != null && lr !== "") out.longRunDow = Number(lr);
      return Object.keys(out).length ? out : null;
    } catch (_) { return null; }
  };

  const uiSettingsSnapshot = () => ({
    goalDraftType,
    goalDraftDate,
    goalSaved,
    goalTimeStr,
    windowDays,
    pastLimit,
    nextLimit,
    forcedRecoveryWeekStartKey,
    lastResetDismissedAt,
    runsPerWeek,
    availableDays,
    scheduleChangedAt,
    userMaxHr,
    userAge,
    userPaceSec: refPaceSec(userPaceStr) || null,
    includeHikesInVolume,
    longRunDow,
    sessionEdits,
  });

  // Hydrate once the account is known.
  useEffect(() => {
    if (!authUser) return;
    const server = authUser.settings && authUser.settings.ui;
    if (server && Object.keys(server).length) {
      applyUiSettings(server);
    } else {
      const legacy = readLegacyLocalSettings();
      if (legacy) {
        applyUiSettings(legacy);
        api.patch("/api/me", { settings: { ui: legacy } }).catch(() => {});
      }
    }
    settingsHydratedRef.current = true;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authUser?.id]);

  // Write-through: localStorage immediately (offline cache), server debounced.
  useEffect(() => {
    try {
      localStorage.setItem(GOAL_KEY, JSON.stringify({ type: goalDraftType, date: goalDraftDate }));
      localStorage.setItem(
        PLAN_KEY,
        JSON.stringify({
          goalSaved,
          goalTimeStr,
          windowDays,
          pastLimit,
          nextLimit,
          forcedRecoveryWeekStartKey,
          lastResetDismissedAt,
          runsPerWeek,
          availableDays,
          scheduleChangedAt,
          userMaxHr,
          userAge,
          includeHikesInVolume,
        })
      );
    } catch (_) {}
    if (!authUser || !settingsHydratedRef.current) return;
    if (settingsSaveTimer.current) clearTimeout(settingsSaveTimer.current);
    const snapshot = uiSettingsSnapshot();
    settingsSaveTimer.current = setTimeout(() => {
      api.patch("/api/me", { settings: { ui: snapshot } }).catch((e) => console.warn("settings sync failed:", e?.message || e));
    }, 800);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    goalDraftType,
    goalDraftDate,
    goalSaved,
    goalTimeStr,
    windowDays,
    pastLimit,
    nextLimit,
    forcedRecoveryWeekStartKey,
    lastResetDismissedAt,
    runsPerWeek,
    availableDays,
    scheduleChangedAt,
    userMaxHr,
    userAge,
    userPaceStr,
    includeHikesInVolume,
    longRunDow,
    sessionEdits,
    authUser,
  ]);

  /* -------------------------
     Account + goals
  ------------------------- */
  const refreshMe = async () => {
    setAuthLoading(true);
    try {
      const me = await api.get("/api/me");
      setAuthUser(me.user || null);
      setAuthInfo(me.auth || null);
      setBetaLocked(!!(me.beta && me.beta.locked) && !me.user);
      return me.user || null;
    } catch (_) {
      setAuthUser(null);
      return null;
    } finally {
      setAuthLoading(false);
    }
  };

  const refreshGoals = async () => {
    try {
      const r = await api.get("/api/goals");
      const list = r.goals || [];
      setGoals(list);
      syncGoalSavedFromGoals(list);
      return list;
    } catch (_) {
      return [];
    }
  };

  // The legacy single-goal state (goalSaved / goalTimeStr) is derived from
  // the A goal so every existing surface (charts, likelihood, stats) keeps
  // working unchanged.
  const syncGoalSavedFromGoals = (list) => {
    const planned = (list || []).filter((g) => g.status === "planned" && g.raceDate);
    const a = planned.find((g) => g.priority === "A") || (planned.length ? planned[planned.length - 1] : null);
    if (!a) return;
    setGoalDraftType(goalTypeFromKm(a.distanceKm));
    setGoalDraftDate(a.raceDate);
    // No target on the goal = "just finish" — clear, never keep a stale time.
    setGoalTimeStr(a.targetFinishSec ? secondsToHhmmss(a.targetFinishSec) : "");
    setGoalSaved((prev) => ({
      ...(prev || {}),
      goalId: a.id,
      type: goalTypeFromKm(a.distanceKm),
      distanceKm: Number(a.distanceKm) || null,
      date: a.raceDate,
      terrain: a.terrain,
      elevationGain: a.elevationGain || 0,
      savedAt: prev?.savedAt || Date.now(),
      planAnchorDate: prev?.planAnchorDate ?? +startOfWeek(new Date()),
    }));
  };

  /** Upsert the A goal from wizard/readiness parameters. Returns the goal. */
  const syncPrimaryGoal = async ({ raceDistance, raceDate, targetFinish, terrain, elevationGain }) => {
    const planned = goals.filter((g) => g.status === "planned");
    const a = planned.find((g) => g.priority === "A");
    const body = {
      id: a ? a.id : undefined,
      name: a && Math.abs(a.distanceKm - Number(raceDistance)) < 0.5 ? a.name : undefined,
      distanceKm: Number(raceDistance),
      raceDate,
      // null = "just finish". Never a fabricated 12:00:00 (that fed Riegel
      // and the pace guard as if the athlete had entered it).
      targetFinish: targetFinish || null,
      priority: "A",
      // Keep the goal's surface; with none the server picks by distance.
      terrain: terrain || a?.terrain || undefined,
      elevationGain: Number(elevationGain) || 0,
    };
    const r = await api.post("/api/goals", body);
    setGoals(r.goals || []);
    syncGoalSavedFromGoals(r.goals || []);
    return r.goal;
  };

  const logout = async () => {
    try { await api.post("/api/auth/logout", {}); } catch (_) {}
    try {
      localStorage.removeItem(SERVER_PLAN_KEY);
      localStorage.removeItem(PLAN_KEY);
      localStorage.removeItem(GOAL_KEY);
      localStorage.removeItem(SESSION_EDITS_KEY);
    } catch (_) {}
    window.location.href = "/";
  };

  /* -------------------------
     Fetch session
  ------------------------- */
  const refreshSession = async () => {
    setLoadingSession(true);
    try {
      const s = await fetchJsonSafe(`${API_BASE}/api/session`, { credentials: "include" });
      setSession(s);
    } catch (_) {
      setSession(null);
    } finally {
      setLoadingSession(false);
    }
  };

  /**
   * Garmin feature flag + connection status. Backends without the Garmin
   * integration (legacy Express server) have no /api/config — the catch
   * leaves the flag off and nothing Garmin-related renders.
   */
  const refreshGarmin = async () => {
    try {
      const cfg = await fetchJsonSafe(`${API_BASE}/api/config`, { credentials: "include" });
      const enabled = !!cfg?.garminEnabled;
      setGarminEnabled(enabled);
      if (enabled) {
        const st = await fetchJsonSafe(`${API_BASE}/api/garmin/status`, { credentials: "include" });
        setGarminConn(st);
      } else {
        setGarminConn(null);
      }
    } catch (_) {
      setGarminEnabled(false);
      setGarminConn(null);
    }
  };

  /**
   * Load the active plan — SERVER-FIRST since the auth rewrite. The plan is
   * keyed by the user, so it follows the account across devices and
   * survives cleared storage. localStorage keeps a cache for instant paint
   * and a one-time backfill path for pre-auth builds (plan only in
   * localStorage, nothing on the server yet).
   */
  const refreshServerPlan = async () => {
    let cached = null;
    try {
      const raw = localStorage.getItem(SERVER_PLAN_KEY);
      if (raw) {
        cached = JSON.parse(raw);
        if (cached && cached.weeks) setServerPlan(cached);
      }
    } catch (_) {}
    try {
      const data = await fetchJsonSafe(`${API_BASE}/api/plan/current`, { credentials: "include" });
      if (data?.plan?.weeks) {
        setServerPlan(data.plan);
        try { localStorage.setItem(SERVER_PLAN_KEY, JSON.stringify(data.plan)); } catch (_) {}
        return;
      }
      if (cached && cached.weeks) {
        // Backfill: legacy client-generated plan → server (once).
        try {
          const synced = sessionStorage.getItem(SERVER_PLAN_SYNC_KEY);
          const key = JSON.stringify(cached.generatedAt || cached.raceDate || "");
          if (synced !== key) {
            await fetchJsonSafe(`${API_BASE}/api/plan/save`, {
              method: "POST",
              credentials: "include",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({ plan: cached }),
            });
            try { sessionStorage.setItem(SERVER_PLAN_SYNC_KEY, key); } catch (_) {}
            refreshServerMetrics();
          }
        } catch (e) { console.warn("Plan backfill sync failed:", e?.message || e); }
        return;
      }
      setServerPlan(null);
    } catch (_) {
      if (!cached) setServerPlan(null);
    }
  };

  /** Generator knobs shared by every server-side (re)generation. */
  const generatorKnobs = (overrides = {}) => ({
    sessionsPerWeek: runsPerWeek,
    availableDays: availableDays.map((dow) => DAY_NAMES_LONG[dow]),
    longRunDay: longRunDayName,
    tempoDay: tempoDayName,
    includeBackToBack: serverPlan?.includeBackToBack,
    includeHikes: !!serverPlan?.includeHikes,
    includeVertical: !!serverPlan?.includeVertical,
    includeHikesInVolume,
    currentWeeklyKm: Math.round(estimateWeeklyKmNow(actualWeeklyMap) || 30),
    goalMode: serverPlan?.goalMode || "standard",
    thresholdPaceSecOverride: refPaceSec(userPaceStr) || undefined,
    ...overrides,
  });

  const cachePlan = (plan) => {
    try { localStorage.setItem(SERVER_PLAN_KEY, JSON.stringify(plan)); } catch (_) {}
  };

  /**
   * Generate the plan SERVER-SIDE from the user's goals (macro plan: the A
   * goal drives periodisation, B/C races are embedded). `params` may carry
   * A-goal fields (raceDistance/raceDate/targetFinish/terrain/elevationGain)
   * — they are written to the A goal first — plus generator knobs.
   * `preview: true` returns the plan without persisting it.
   */
  const pendingPrimaryRef = useRef(null);
  const lastAttemptRef = useRef(null);
  const wizardFromRebuildRef = useRef(false);
  const openReadinessGate = (blocked) => {
    const a = goals.find((g) => g.status === "planned" && g.priority === "A");
    const att = lastAttemptRef.current || {};
    const tf = att.targetFinish || (a && a.targetFinishSec ? secondsToHhmmss(a.targetFinishSec) : null);
    const prefill = {
      distanceKm: Number(att.raceDistance || (a && a.distanceKm) || serverPlan?.raceDistance) || null,
      raceDate: att.raceDate || (a && a.raceDate) || serverPlan?.raceDate || "",
      targetFinish: tf || null,
      terrain: att.terrain || (a && a.terrain) || serverPlan?.terrain || null,
      runsPerWeek,
      days: availableDays.slice(),
      longDay: longRunDow,
      tempoDay: tempoDow,
    };
    setWizardGate({ gate: blocked, prefill });
    wizardFromRebuildRef.current = true;
    setOpenGoal(false);
    setShowPlanGenerator(true);
  };
  const generateServerPlan = async (params = {}) => {
    setServerPlanLoading(true);
    try {
      const { raceDistance, raceDate, targetFinish, terrain, elevationGain, preview, userBaseline: _ub, ...knobs } = params;
      const body = Object.assign(generatorKnobs(), knobs, { preview: !!preview });
      lastAttemptRef.current = raceDistance && raceDate ? { raceDistance, raceDate, targetFinish, terrain, elevationGain } : null;
      if (raceDistance && raceDate) {
        const primary = { raceDistance, raceDate, targetFinish, terrain, elevationGain };
        const curA = goals.find((g) => g.status === "planned" && g.priority === "A");
        if (preview) {
          // Preview: the server applies the A goal in memory only; it is
          // written on commit (commitPreviewPlan). A gated attempt therefore
          // never leaves a changed A goal behind.
          pendingPrimaryRef.current = primary;
          body.primaryGoal = {
            distanceKm: Number(raceDistance),
            raceDate,
            targetFinish: targetFinish || undefined,
            terrain: terrain || (curA && curA.terrain) || undefined,
            elevationGain: Number(elevationGain) || 0,
          };
        } else {
          await syncPrimaryGoal(primary);
        }
      }
      const result = await api.post("/api/plan/generate-macro", body);
      if (result && result.blocked) {
        console.warn(`[plan] generation blocked: ${result.reason}`);
        return { blocked: true, reason: result.reason, readiness: result.readiness, paceGuard: result.paceGuard, goalMode: result.goalMode };
      }
      const plan = result.plan;
      if (!preview) {
        cachePlan(plan);
        setServerPlan(plan);
        await refreshServerMetrics();
        refreshGoals();
      }
      return { plan, planId: result.planId || null };
    } catch (err) {
      console.error("Plan generation failed:", err);
      return null;
    } finally {
      setServerPlanLoading(false);
    }
  };

  /** Persist a previewed plan as the active plan. */
  const commitPreviewPlan = async (plan) => {
    // Write the A goal the preview was built for (deferred from preview so a
    // refused attempt never mutated it), and point the plan at its id.
    const pending = pendingPrimaryRef.current;
    pendingPrimaryRef.current = null;
    if (pending) {
      try {
        const g = await syncPrimaryGoal(pending);
        if (g && g.id != null) {
          if (plan.primaryGoal) plan.primaryGoal.id = g.id;
          if (plan.macro) plan.macro.primaryGoalId = g.id;
        }
      } catch (e) {
        console.warn("Goal save failed:", e?.message || e);
      }
    }
    // What the server stored is the plan: it keeps the past weeks and puts
    // the accepted coach changes back into the rebuilt ones. Showing the
    // local preview instead made an accepted change vanish until reload.
    let stored = plan;
    try {
      const r = await fetchJsonSafe(`${API_BASE}/api/plan/save`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ plan }),
      });
      if (r && r.plan && Array.isArray(r.plan.weeks)) stored = r.plan;
    } catch (e) {
      console.warn("Plan save failed:", e?.message || e);
    }
    cachePlan(stored);
    setServerPlan(stored);
    await refreshServerMetrics();
    refreshGoals();
  };

  /**
   * GoalWizard submit: create milestone (B) goals from the guided flow, then
   * generate the plan via the SAME server generator + readiness gate (preview).
   * Returns { plan } | { blocked, ... } | null (null = ambition with no date yet).
   */
  const wizardSubmit = async (plIn, opts = {}) => {
    // Gate actions retry with an adjusted goal (one tap, server suggestion).
    const pl = applyGateOverrides(plIn, opts);
    const dows = (pl.days || []).slice().sort((a, b) => a - b);
    setRunsPerWeek(clamp(Number(pl.runsPerWeek) || 3, 3, 5));
    if (dows.length) setAvailableDays(sortDow(dows));
    if (pl.longDay != null) setLongRunDow(pl.longDay);
    setTempoDow(pl.tempoDay != null ? pl.tempoDay : null);
    const anyDate = (!pl.someday && !!pl.raceDate) || (pl.milestones || []).some((m) => m.date);
    // The surface the athlete chose. Milestones default the way the server
    // does: road up to the marathon, trail beyond. The A goal is never
    // guessed from its distance (a road 50K used to become a trail plan).
    const terr = (km) => (km > 42.3 ? "trail" : "road");
    const aTerrain = pl.terrain || terr(pl.distanceKm);
    if (!opts.skipGoals) {
      // Retries (gate → back → build again) must not duplicate milestones.
      let existing = [];
      try { existing = (await api.get("/api/goals")).goals || []; } catch (_) {}
      for (const m of pl.milestones || []) {
        if (isDuplicateMilestone(existing, m)) continue;
        try {
          await api.post("/api/goals", { distanceKm: m.distanceKm, raceDate: m.date || undefined, targetFinish: m.time || undefined, priority: "B", terrain: terr(m.distanceKm) });
        } catch (_) {}
      }
      if (pl.someday || !pl.raceDate) {
        try {
          await api.post("/api/goals", { distanceKm: Number(pl.distanceKm), raceDate: undefined, targetFinish: pl.mode === "time" ? pl.time : undefined, priority: "A", terrain: aTerrain });
        } catch (_) {}
        refreshGoals();
        if (!anyDate) return null;
      }
    }
    const res = await generateServerPlan({
      raceDistance: Number(pl.distanceKm),
      raceDate: pl.someday ? undefined : pl.raceDate,
      targetFinish: pl.mode === "time" ? pl.time : undefined,
      terrain: aTerrain,
      elevationGain: 0,
      sessionsPerWeek: clamp(Number(pl.runsPerWeek) || 3, 3, 5),
      availableDays: dows.map((d) => DAY_NAMES_LONG[d]),
      longRunDay: pl.longDay != null ? DAY_NAMES_LONG[pl.longDay] : (dows.length ? DAY_NAMES_LONG[dows[dows.length - 1]] : undefined),
      tempoDay: pl.tempoDay != null ? DAY_NAMES_LONG[pl.tempoDay] : undefined,
      goalMode: opts.finishOnly ? "finish-only" : "standard",
      acknowledgeReadiness: !!opts.acknowledgeReadiness || !!opts.finishOnly,
      preview: true,
    });
    return res;
  };

  /** Rebuild from the Goals panel — uses the stored knobs, no wizard. */
  const rebuildFromGoals = async () => {
    setRebuilding(true);
    try {
      // Goals changed → the readiness gate runs again. No silent override.
      const r = await generateServerPlan({});
      if (r && r.blocked) openReadinessGate(r);
    } finally {
      setRebuilding(false);
    }
  };

  /**
   * Stale-plan detection (memoised). If the active plan was generated by an
   * outdated server process or with a different day set, this returns a reason
   * code. Used by the prominent banner on the Train tab and the inline prompt
   * in Goal & Plan Settings. null = plan is fresh.
   */
  const planStaleReason = useMemo(
    () => diagnosePlanStale(serverPlan, availableDays),
    [serverPlan, availableDays]
  );

  // NO automatic regeneration on a planFormatVersion bump (turned off
  // 21.09.2026). A regeneration builds a plan that starts in the current week,
  // and doing it silently on load removed every stored plan's past weeks from
  // view — the chart lost plan-vs-actual, adherence dropped to 0%, logged runs
  // showed as extras. A stale plan now only shows the stale-plan banner;
  // regenerating is the athlete's explicit choice (and keeps past weeks, see
  // server/services/planHistory.js).

  /**
   * Regenerate the active plan using the current `availableDays` selection.
   * Pulls all other plan params from the active serverPlan so the race, terrain,
   * toggles, etc. stay the same — only the day layout changes. Called when the
   * user changes available days in Goal & Plan Settings and clicks "Regenerate".
   */
  const regeneratePlanWithCurrentDays = async () => {
    if (!serverPlan) return null;
    // A re-layout keeps an override the athlete already made EXPLICITLY on
    // this plan (acknowledgedStretch). Anything else is gated as usual — a
    // blocked result opens the gate instead of being dropped.
    const r = await generateServerPlan({ acknowledgeReadiness: regenerateAcknowledge(serverPlan) });
    if (r && r.blocked) openReadinessGate(r);
    return r;
  };

  // Fetch all metrics from server (replaces client-side KPI computation).
  //
  // The KPI strip at the top of the page (Adherence / Comfortable pace /
  // Weekly load) must show ONE consistent value, independent of the
  // 7d/14d/28d toggle below in the Plan vs Actual section. To guarantee
  // that, we always pull metrics over a fixed 28-day window. The toggle
  // is a pure client-side filter on the run list — it never refetches
  // server metrics and never changes the headline adherence number.
  const ADHERENCE_WINDOW_DAYS = 28;
  const refreshServerMetrics = async () => {
    try {
      const data = await fetchJsonSafe(`${API_BASE}/api/metrics?windowDays=${ADHERENCE_WINDOW_DAYS}`, { credentials: "include" });
      setServerMetrics(data?.metrics || null);
    } catch (_) {
      setServerMetrics(null);
    }
  };

  useEffect(() => {
    const u0 = new URL(window.location.href);
    const loginParam = u0.searchParams.get("login");
    if (loginParam === "error") setAuthError(u0.searchParams.get("reason") || "error");
    if (loginParam) {
      u0.searchParams.delete("login");
      u0.searchParams.delete("reason");
      window.history.replaceState({}, "", u0.pathname + (u0.search || "") + u0.hash);
    }
    (async () => {
      const user = await refreshMe();
      if (!user) { setLoadingSession(false); return; }
      // Notifications: reflect what the server has and what this device is
      // actually subscribed to — they can disagree (new device, revoked
      // permission, cleared site data).
      (async () => {
        const n = user.settings?.notify || {};
        setNotifyPrefs({
          enabled: n.enabled === true,
          cadence: ["daily", "weekly", "sessions"].includes(n.cadence) ? n.cadence : "daily",
          hour: Number.isInteger(n.hour) ? n.hour : 7,
        });
        const support = pushSupport();
        const cfg = await fetchPushConfig();
        const sub = support.ok ? await currentSubscription() : null;
        setPushState((p) => ({ ...p, serverEnabled: !!cfg.enabled, subscribed: !!sub, support }));
      })();
      refreshSession();
      refreshGoals();
      refreshServerPlan();
      refreshServerMetrics();
      refreshGarmin();
    })();

    const u = new URL(window.location.href);
    const connected = u.searchParams.get("connected");
    const se = u.searchParams.get("strava_error");
    const we = u.searchParams.get("whoop_error");
    const ge = u.searchParams.get("garmin_error");

    if (connected === "strava") setStravaStatus("Connected ✔");
    if (connected === "whoop") setWhoopStatus("Connected ✔");
    if (connected === "garmin") setGarminStatus("Connected");
    if (se) setStravaError(humanizeStravaError(se));
    if (we) setWhoopError(String(we));
    if (ge) setGarminError(String(ge));

    if (connected || se || we || ge) {
      u.searchParams.delete("connected");
      u.searchParams.delete("strava_error");
      u.searchParams.delete("whoop_error");
      u.searchParams.delete("garmin_error");
      window.history.replaceState({}, "", u.toString());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const conn = useMemo(
    () => ({
      strava: !!session?.connected?.strava,
      whoop: !!session?.connected?.whoop,
      imported: !!session?.connected?.imported,
      // Any activity source at all — Strava, Apple Health, Health Connect or
      // uploaded files. Gates onboarding; Strava-specific UI keeps conn.strava.
      data: !!session?.connected?.strava || !!session?.connected?.imported,
    }),
    [session]
  );

  const runs = useMemo(() => {
    // session.activities = server-merged list across every source (present
    // once anything was imported); otherwise the Strava store as before.
    const list = session?.activities || session?.strava?.runs || [];
    return (list || []).filter((r) => {
      const t = r?.type || "Run";
      if (RUN_TYPES.has(t)) return true;
      // Opt-in hike/walk recognition (default off per §Anti-pattern #4).
      if (includeHikesInVolume && HIKE_TYPES.has(t)) return true;
      return false;
    });
  }, [session, includeHikesInVolume]);

  // HR zone calibration: effective max HR via fallback chain
  const stravaMaxHr = useMemo(() => {
    const vals = (runs || []).filter(r => r.max_hr > 0).map(r => r.max_hr);
    return vals.length > 0 ? Math.max(...vals) : null;
  }, [runs]);

  /**
   * Observed threshold pace — fastest average pace over recent 3–15 km runs
   * in the last 60 days. Feeds derivePlannedDurationMin() for 'tempo'
   * sessions. Much more accurate than extrapolating from goal time: reflects
   * the runner's actual current fitness, and doesn't get thrown off by
   * conservative ultra goals.
   *
   * Filter rationale:
   *   - 3 km minimum: excludes warmups and sub-5K all-out efforts that are
   *     faster than true threshold.
   *   - 15 km maximum: excludes long runs where pace is below threshold.
   *   - 60-day window: recent enough to reflect current fitness, long
   *     enough to catch occasional tempo sessions.
   */
  const observedThresholdPaceSec = useMemo(() => {
    const manualPace = refPaceSec(userPaceStr);
    if (manualPace && manualPace > 0) return manualPace;
    if (!Array.isArray(runs) || !runs.length) return null;
    const SIXTY_DAYS_MS = 60 * 24 * 3600 * 1000;
    const now = Date.now();
    let fastest = null;
    for (const r of runs) {
      if (!r.distance_m || !r.moving_time_s) continue;
      const km = r.distance_m / 1000;
      if (km < 3 || km > 15) continue;
      const t = new Date(r.start_date).getTime();
      if (isNaN(t) || now - t > SIXTY_DAYS_MS) continue;
      const paceSec = r.moving_time_s / km;
      if (paceSec > 0 && (fastest === null || paceSec < fastest)) {
        fastest = paceSec;
      }
    }
    return fastest;
  }, [runs, userPaceStr]);

  /**
   * userBaseline — the structured training-reality snapshot the v8 plan
   * generator consumes (4-week median volume, consistency, 60-day longest
   * run, observed threshold + median run pace). Computed from the SAME
   * Strava run list via the canonical module, so the UI's baseline card and
   * the generator agree exactly. See /docs/training_methodology.md
   * §"User baseline". `includeHikesInVolume` mirrors the volume toggle.
   */
  const userBaseline = useMemo(
    () => computeUserBaseline(runs || [], { includeHikes: includeHikesInVolume }),
    [runs, includeHikesInVolume]
  );

  const effectiveMaxHr = useMemo(() => {
    const manual = Number(userMaxHr);
    if (manual > 0) return manual;
    if (stravaMaxHr) return stravaMaxHr;
    const age = Number(userAge);
    if (age > 0) return 220 - age;
    return null;
  }, [userMaxHr, stravaMaxHr, userAge]);

  /* -------------------------
     Fitness & Form (CTL / ATL / TSB)
     ----------------------------------------------------------------------
     Industry-standard endurance metrics from Coggan's Performance Manager
     Chart. Complementary to the single-week KPIs above (Adherence /
     PaceFit / WK Load): those answer "what about THIS week?", these
     answer "what's my current chronic state?".

       CTL (Chronic Training Load)   exp-weighted 42-day TSS  = "fitness"
       ATL (Acute Training Load)     exp-weighted 7-day TSS   = "fatigue"
       TSB (Training Stress Balance) CTL - ATL                = "form"

     TSS per run uses the running variant (rTSS), pace-based, on
     GRADE-ADJUSTED pace (§Pillar 6 "Grade-adjusted stress"):
       equivKm = min(km × 1.35, km + elevGain_m × 7.92 / 1000)
       gapPace = moving_time / equivKm         (flat-equivalent pace)
       IF  = thresholdPace / gapPace           (faster pace → higher IF)
       TSS = duration_h × IF² × 100            (1h at threshold = 100 TSS)
     Without the GAP correction, slow uphill pace reads as LOW intensity
     and mountain training is systematically under-scored — the exact
     failure mode for the trail runners this app is built for. Threshold
     pace stays raw: it is derived from the fastest 3–15 km efforts,
     which are flat/tempo by construction.

     Daily update (Coggan EMA):
       CTL_t = CTL_{t-1} + (TSS_t - CTL_{t-1}) × (1 - e^(-1/42))
       ATL_t = ATL_{t-1} + (TSS_t - ATL_{t-1}) × (1 - e^(-1/7))

     Returns null if there's no observed threshold pace yet (the IF
     denominator) or no run data — both are required to make the math
     honest. Once data flows in, the EMA converges over ~2 months.
  ------------------------- */
  const fitnessForm = useMemo(() => {
    if (!observedThresholdPaceSec || !Array.isArray(runs) || !runs.length) return null;

    const tssByDay = new Map();
    let earliestDay = null;
    for (const r of runs) {
      if (!r.distance_m || !r.moving_time_s || !r.start_date) continue;
      const km = r.distance_m / 1000;
      if (km <= 0) continue;
      const t = new Date(r.start_date);
      if (isNaN(+t)) continue;
      const dayTs = +startOfDay(t);
      // Grade-adjusted pace: flat-equivalent distance credits climbing via
      // Scarf's equivalence (1 m gain ≈ 7.92 m flat). Summary data only
      // carries total gain, so this is an approximation; uplift is capped
      // at +35% so GPS-noisy elevation can't inflate stress absurdly.
      const elevM = r.elev_m > 0 ? r.elev_m : 0;
      const equivKm = Math.min(km * 1.35, km + (elevM * 7.92) / 1000);
      const paceSec = r.moving_time_s / equivKm;
      if (paceSec <= 0) continue;
      const IF = observedThresholdPaceSec / paceSec;
      const durH = r.moving_time_s / 3600;
      const tss = durH * IF * IF * 100;
      if (!isFinite(tss) || tss <= 0) continue;
      tssByDay.set(dayTs, (tssByDay.get(dayTs) || 0) + tss);
      if (earliestDay === null || dayTs < earliestDay) earliestDay = dayTs;
    }
    if (earliestDay === null) return null;

    const today = +startOfDay(new Date());
    const dCTL = 1 - Math.exp(-1 / 42);
    const dATL = 1 - Math.exp(-1 / 7);
    const totalDays = Math.round((today - earliestDay) / 86400000) + 1;
    const sevenDaysAgoIdx = totalDays - 7;
    // History window for the corner sparkline. 28 days = 4 weeks, enough
    // to read direction (up / flat / down) without overpowering the cell.
    // New users with < 28 days of data just render fewer points; the path
    // scales to fit the same tiny SVG box.
    // 90 days are kept: the cards show the last 28, the detail sheet can
    // show the full quarter.
    const HISTORY_DAYS = 90;
    const histStartIdx = Math.max(0, totalDays - HISTORY_DAYS);
    const history = [];
    let ctl = 0;
    let atl = 0;
    let ctl7d = 0;
    for (let i = 0; i < totalDays; i++) {
      if (i === sevenDaysAgoIdx) ctl7d = ctl;
      const dayTs = earliestDay + i * 86400000;
      const tss = tssByDay.get(dayTs) || 0;
      ctl = ctl + (tss - ctl) * dCTL;
      atl = atl + (tss - atl) * dATL;
      if (i >= histStartIdx) history.push({ ts: dayTs, ctl, atl, tsb: ctl - atl });
    }
    return {
      ctl: Math.round(ctl),
      atl: Math.round(atl),
      tsb: Math.round(ctl - atl),
      ctlDelta7d: sevenDaysAgoIdx >= 0 ? Math.round((ctl - ctl7d) * 10) / 10 : null,
      daysOfData: totalDays,
      history,
    };
  }, [runs, observedThresholdPaceSec]);

  /* -------------------------
     Connect / Disconnect / Sync
  ------------------------- */
  const connectStrava = async () => {
    setStravaError("");
    setStravaStatus("Opening Strava…");
    try {
      const { url } = await fetchJsonSafe(`${API_BASE}/api/strava/auth-url`, { credentials: "include" });
      window.location.href = url;
    } catch (e) {
      setStravaStatus("");
      setStravaError(humanizeStravaError(e.message) || "Failed to start Strava auth");
    }
  };

  /* ---- Coach notifications ---- */
  const applyNotify = async (patch) => {
    const next = { ...notifyPrefs, ...patch };
    setNotifyPrefs(next);                       // optimistic
    try { await saveNotifySettings(next); }
    catch (_) { setPushState((p) => ({ ...p, msg: "Could not save — try again." })); }
  };

  const toggleNotifications = async (wantOn) => {
    setPushState((p) => ({ ...p, busy: true, msg: "" }));
    try {
      if (wantOn) {
        const r = await enablePush();
        if (!r.ok) {
          const msg =
            r.reason === "denied" ? "Notifications are blocked. Turn them on for this site in your browser settings."
            : r.reason === "server-disabled" ? "Notifications are not switched on for this deployment yet."
            : SUPPORT_COPY[r.reason] || "Could not enable notifications.";
          setPushState((p) => ({ ...p, busy: false, msg }));
          return;
        }
        await applyNotify({ enabled: true });
        setPushState((p) => ({ ...p, busy: false, subscribed: true, msg: "" }));
      } else {
        await disablePush();
        await applyNotify({ enabled: false });
        setPushState((p) => ({ ...p, busy: false, subscribed: false, msg: "" }));
      }
    } catch (_) {
      setPushState((p) => ({ ...p, busy: false, msg: "Could not change notifications." }));
    }
  };

  const testNotification = async () => {
    setPushState((p) => ({ ...p, busy: true, msg: "" }));
    const r = await sendTestPush();
    setPushState((p) => ({
      ...p, busy: false,
      msg: r?.ok ? "Sent — it should arrive in a few seconds."
        : r?.error === "no_subscription" ? "This device is not subscribed yet."
        : r?.error === "notifications_disabled" ? "Notifications are not switched on for this deployment."
        : "Could not send a test notification.",
    }));
  };

  const connectWhoop = async () => {
    setWhoopError("");
    setWhoopStatus("Opening WHOOP…");
    try {
      const { url } = await fetchJsonSafe(`${API_BASE}/api/whoop/auth-url`, { credentials: "include" });
      window.location.href = url;
    } catch (e) {
      setWhoopStatus("");
      setWhoopError(e.message || "Failed to start WHOOP auth");
    }
  };

  const connectGarmin = () => {
    setGarminError("");
    setGarminStatus("Opening Garmin…");
    // /auth/garmin runs the OAuth 2.0 + PKCE flow server-side and redirects
    // back with ?connected=garmin.
    window.location.href = `${API_BASE}/auth/garmin`;
  };

  const disconnect = async (service) => {
    try {
      await fetchJsonSafe(`${API_BASE}/api/disconnect`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ service }),
      });
      await refreshSession();
      if (service === "garmin" || service === "all") {
        setGarminStatus("");
        await refreshGarmin();
      }
    } catch (_) {}
  };

  const syncStrava = async () => {
    setStravaError("");
    setStravaStatus("Syncing…");
    try {
      await fetchJsonSafe(`${API_BASE}/api/strava/sync`, { method: "POST", credentials: "include" });
      setStravaStatus("Synced ✔");
      await refreshSession();
      await refreshServerMetrics();
    } catch (e) {
      setStravaStatus("");
      setStravaError(e.message || "Sync failed");
    }
  };

  /* -------------------------
     Apple Health / Health Connect (native shell only) + file uploads
  ------------------------- */
  const [importState, setImportState] = useState({ busy: false, msg: "", error: "" });
  const importFileRef = useRef(null);
  const hasHealth = healthAvailable();
  const healthName = healthLabel();

  const runHealthSync = async (opts) => {
    setImportState({ busy: true, msg: `Reading ${healthName}…`, error: "" });
    try {
      const r = await syncHealth(opts);
      const n = (r.added || 0) + (r.updated || 0);
      setImportState({ busy: false, msg: n ? `${n} workout${n === 1 ? "" : "s"} from ${healthName}` : `No new workouts in ${healthName}`, error: "" });
      await refreshSession();
      await refreshServerMetrics();
    } catch (e) {
      setImportState({ busy: false, msg: "", error: e.message || "Sync failed" });
    }
  };

  const pickActivityFiles = () => {
    if (importFileRef.current) importFileRef.current.click();
  };

  const onActivityFiles = async (e) => {
    const files = Array.from((e.target && e.target.files) || []);
    if (e.target) e.target.value = "";
    if (!files.length) return;
    let ok = 0;
    const errors = [];
    for (let i = 0; i < files.length; i++) {
      setImportState({ busy: true, msg: `Importing ${i + 1} of ${files.length}…`, error: "" });
      try {
        const r = await uploadActivityFile(files[i]);
        ok += (r.activities || []).length;
      } catch (err) {
        errors.push(`${files[i].name}: ${err.message}`);
      }
    }
    setImportState({
      busy: false,
      msg: ok ? `${ok} activit${ok === 1 ? "y" : "ies"} imported` : "",
      error: errors.join(" · "),
    });
    if (ok) {
      await refreshSession();
      await refreshServerMetrics();
    }
  };

  // In the app shell, pull new workouts whenever the app opens with a
  // signed-in user who has already granted access once.
  const autoHealthSynced = useRef(false);
  useEffect(() => {
    if (!hasHealth || autoHealthSynced.current || !session?.user) return;
    const src = healthSource();
    if (!session?.imported?.[src]?.connected) return;
    autoHealthSynced.current = true;
    runHealthSync();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasHealth, session?.user?.id]);

  /* -------------------------
     Goal auto-recovery from serverPlan
     If localStorage was wiped (incognito, manual clear, browser reset) but a
     plan still lives on the server, synthesise `goalSaved` from the plan's
     raceDistance / raceDate / terrain so the UI keeps rendering without the
     user having to re-enter their goal. Runs once whenever serverPlan arrives
     and goalSaved is empty.
  ------------------------- */
  useEffect(() => {
    // Recover if there's no goal OR if planAnchorDate is non-numeric (was
    // accidentally stored as a string by an earlier buggy recovery — that
    // shape makes `new Date(Number(planAnchorDate))` evaluate to an invalid
    // Date and blanks the chart downstream).
    const anchorBroken = goalSaved?.date && !isFinite(Number(goalSaved?.planAnchorDate));
    if (goalSaved?.date && !anchorBroken) return;
    if (!serverPlan?.raceDistance || !serverPlan?.raceDate) return;

    const typeFromKm = goalTypeFromKm(serverPlan.raceDistance);

    // planAnchorDate must be a numeric timestamp — downstream code does
    // `new Date(Number(goalSaved.planAnchorDate))`. Passing a string like
    // "2026-04-13" makes Number() return NaN, which blanks the whole chart.
    const anchorStr = serverPlan.weeks?.[0]?.sessions?.[0]?.date || null;
    const anchorTs  = anchorStr ? +new Date(anchorStr) : null;
    if (!anchorTs || !isFinite(anchorTs)) return;

    const recovered = {
      type: typeFromKm,
      distanceKm: Number(serverPlan.raceDistance) || null,
      date: serverPlan.raceDate,
      terrain: serverPlan.terrain || "road",
      savedAt: Date.now(),
      planAnchorDate: anchorTs,
    };

    setGoalSaved(recovered);
    if (serverPlan.targetFinish && !goalTimeStr) {
      setGoalTimeStr(String(serverPlan.targetFinish));
    }

    // Persist so the next page-load doesn't need to recover again.
    try {
      const raw = localStorage.getItem(PLAN_KEY);
      const base = raw ? JSON.parse(raw) : {};
      localStorage.setItem(PLAN_KEY, JSON.stringify({
        ...base,
        goalSaved: recovered,
        goalTimeStr: serverPlan.targetFinish || base?.goalTimeStr || null,
      }));
    } catch (_) {}
  }, [serverPlan, goalSaved, goalTimeStr]);

  /* -------------------------
     Goal (stable) + plan inputs
  ------------------------- */
  const hasSavedGoal = !!goalSaved?.date;
  const effectiveGoalType = goalSaved?.type || goalDraftType;
  const effectiveGoalDate = goalSaved?.date || goalDraftDate; // eslint-disable-line no-unused-vars

  // Stored km wins — the label ("50M", "Custom") can't carry the distance.
  const goalDistanceKm = useMemo(() => resolveGoalDistanceKm(goalSaved, effectiveGoalType), [goalSaved, effectiveGoalType]);

  const goalDateObj = useMemo(() => {
    if (!hasSavedGoal) return null;
    const d = new Date(goalSaved.date);
    if (!isFinite(+d)) return null;
    return startOfDay(d);
  }, [hasSavedGoal, goalSaved]);

  const goalTimeSec = useMemo(() => timeStrToSeconds(goalTimeStr), [goalTimeStr]);

  const goalPaceSec = useMemo(() => {
    if (!goalTimeSec || !goalDistanceKm) return null;
    return goalTimeSec / goalDistanceKm;
  }, [goalTimeSec, goalDistanceKm]);

  const planStartDate = useMemo(() => {
    if (!hasSavedGoal) return startOfWeek(new Date());
    return new Date(Number(goalSaved.planAnchorDate));
  }, [hasSavedGoal, goalSaved]);

  /* -------------------------
     Actual aggregates (from Strava)
  ------------------------- */
  const actualWeekly = useMemo(() => buildWeeklyActualKm(runs), [runs]);
  const actualWeeklyMap = useMemo(() => {
    const m = new Map();
    for (const p of actualWeekly || []) m.set(+startOfWeek(p.x), Number(p.y || 0));
    return m;
  }, [actualWeekly]);

  const actualWeeklySecsMap = useMemo(() => buildWeeklyActualSeconds(runs), [runs]);

  const actualComfort = useMemo(() => buildWeeklyComfortPace(runs), [runs]);

  const weeklyKmNowNum = useMemo(() => estimateWeeklyKmNow(actualWeeklyMap), [actualWeeklyMap]);
  const comfortablePaceSec = useMemo(() => pickCurrentComfortPace(actualComfort), [actualComfort]);

  // Unusually slow "runs" from the last 4 weeks the athlete has not yet
  // confirmed or reclassified. Newest first.
  const suspectRuns = useMemo(() => {
    const overrides = session?.activityOverrides || {};
    const list = session?.activities || session?.strava?.runs || [];
    const cutoff = Date.now() - 28 * DAY_MS;
    return list
      .filter((a) => a && a.id != null && !overrides[String(a.id)])
      .filter((a) => +new Date(a.start_date) >= cutoff)
      .filter((a) => isSuspectRun(a, comfortablePaceSec))
      .sort((a, b) => +new Date(b.start_date) - +new Date(a.start_date));
  }, [session, comfortablePaceSec]);
  const suspectIds = useMemo(() => new Set(suspectRuns.map((a) => String(a.id))), [suspectRuns]);
  const [classifyBusy, setClassifyBusy] = useState(false);
  const classifyActivity = async (id, as) => {
    setClassifyBusy(true);
    try {
      await api.post("/api/activities/classify", { id, as });
      await refreshSession();
      await refreshServerMetrics();
    } catch (_) {
      /* the card stays; the athlete can try again */
    } finally {
      setClassifyBusy(false);
    }
  };

  /* -------------------------
     Plan computations (ONLY from saved goal)
  ------------------------- */
  const tuneUpRaces = useMemo(() => {
    if (!goalDateObj) return [];
    return generateTuneUpRaces({
      startDate: planStartDate,
      goalDate: goalDateObj,
      goalDistanceKm,
      comfortablePaceSecPerKm: comfortablePaceSec,
      goalPaceSecPerKm: goalPaceSec,
    });
  }, [planStartDate, goalDateObj, goalDistanceKm, comfortablePaceSec, goalPaceSec]);

  // --- SERVER PLAN: replaces legacy generateFixedPlanUntilGoal ---
  //
  // Belt-and-suspenders: filter out any session whose ORIGINAL server-assigned
  // date lands on a day-of-week NOT in the current `availableDays`. This keeps
  // the "Next Session" card honest even if the active plan was generated with
  // older day selections — the "Regenerate plan" prompt in Goal & Plan Settings
  // is how the user realigns the server-side plan.
  //
  // User-edited dates (edit.dateISO) are respected regardless of availableDays
  // — if the user explicitly moves a session onto a Monday, that's a conscious
  // choice, not the generator violating the schedule.
  const plannedSessionsFull = useMemo(() => {
    if (!serverPlan?.weeks) return [];
    const allowedDows = new Set(availableDays);
    return serverPlan.weeks.flatMap(w => (w.sessions || [])
      .filter(s => {
        const od = parseLocalDate(s.date);
        return allowedDows.has(od.getDay());
      })
      .map(s => {
        const originalDate = parseLocalDate(s.date);
        const editKey = sessionEditKey(originalDate, s.type);
        const edit = sessionEdits[editKey] || null;

        const km = (edit && edit.distanceKm != null) ? edit.distanceKm : (s.plannedKm || 0);
        const date = edit && edit.dateISO ? parseLocalDate(edit.dateISO) : originalDate;
        // Use derivePlannedDurationMin as the ONLY source of truth. For
        // tempo, it prefers observedThresholdPaceSec (the user's fastest
        // recent 3–15 km pace) over Riegel projection. DO NOT re-read
        // s.plannedDuration — a stored plan may hold a stale duration
        // from a deprecated multiplier.
        const durMin = derivePlannedDurationMin(s, serverPlan, observedThresholdPaceSec);
        const typeLabel = formatSessionTypeLabel(s.type || 'easy');
        // Per-session planned pace (sec/km) — recovered from duration/km.
        // The duration already includes the session-type and terrain multipliers
        // (either baked by planGenerator v3 or reconstructed by
        // derivePlannedDurationMin), so division yields the intended pace with
        // no double-application of the multipliers.
        const paceSecPerKm = (km > 0 && durMin > 0) ? (durMin * 60) / km : null;
        const paceStr = paceSecPerKm ? secPerKmToPace(paceSecPerKm) : null;
        return {
          kind: "planned",
          date,
          originalDate,
          editKey,
          notes: edit?.notes || "",
          edited: !!edit,
          title: typeLabel,
          label: typeLabel,
          type: s.type,
          zone: s.hrZone || 'Z2',
          distanceKm: km,
          // Duration-only sessions (hike, time-on-feet) prescribe no km but
          // carry the generator's estimate — the week totals count it, the
          // way the chart and Weekly Load already do.
          estimatedKm: s.estimatedKm != null ? Number(s.estimatedKm) : null,
          paceSecPerKm,
          // `pace` was previously used to display a km or duration summary
          // when actualP was missing — preserving that fallback behaviour, but
          // now that paceSecPerKm is real, consumers that want a pace string
          // should prefer secPerKmToPace(paceSecPerKm). Keeping the legacy
          // fallback keeps existing render paths unchanged.
          pace: paceStr || (durMin ? `${durMin} min` : (km > 0 ? `${fmt1(km)} km` : '—')),
          timeStr: durMin ? `${Math.floor(durMin / 60)}:${pad2(durMin % 60)}:00` : '—',
          badge: `Planned • ${w.phase} • ${s.type}`,
          purpose: s.purpose || '',
          durationMin: durMin,
        };
      }));
  }, [serverPlan, sessionEdits, availableDays, observedThresholdPaceSec]);

  const plannedWeeklyMap = useMemo(() => {
    const m = new Map();
    if (!serverPlan?.weeks) return m;
    for (const w of serverPlan.weeks) {
      if (!w.sessions?.length) continue;
      // Use the first session date to derive week start
      // Append T00:00:00 to force local-time parsing (bare "YYYY-MM-DD" is UTC)
      const d = parseLocalDate(w.sessions[0].date);
      m.set(+startOfWeek(d), Number(w.plannedKm || 0));
    }
    return m;
  }, [serverPlan]);

  /* Weekly phase map — per-week { phase, isRecoveryWeek }, keyed by week-start
     timestamp. Fed to the chart so planned bars are coloured by their
     parent phase (§Pillar 3 — the four phases: base, build, specific, taper)
     and recovery weeks render as visually-distinct variants inside the
     parent colour. Recovery is not a phase.

     Backward-compat for legacy plans (planFormatVersion ≤ 4):
       - phase: "recovery" → treat as isRecoveryWeek=true, map back to parent
         phase using the legacy `underlying` field if present, else infer by
         week position against the current four-phase percentages.
       - phase: "peak"     → renamed to "specific" on 19.04.2026.
     Users with stale plans see the correct chrome until they regenerate. */
  const phaseByWeek = useMemo(() => {
    const m = new Map();
    if (!serverPlan?.weeks) return m;
    const totalWeeks = serverPlan.weeks.length || 1;
    const baseEnd = Math.max(1, Math.floor(totalWeeks * 0.35));
    const buildEnd = Math.max(baseEnd + 1, baseEnd + Math.floor(totalWeeks * 0.30));
    const taperWeeks = (serverPlan.raceDistance || 0) >= 80 ? 3 : 2;

    serverPlan.weeks.forEach((w, idx) => {
      if (!w.sessions?.length) return;
      const d = parseLocalDate(w.sessions[0].date);
      const raw = (w.phase || "base").toLowerCase();
      let phase = raw;
      let isRecoveryWeek = !!w.isRecoveryWeek;

      if (raw === "recovery") {
        isRecoveryWeek = true;
        // Prefer legacy `underlying`; fall back to position-based inference.
        const underlying = (w.underlying || "").toLowerCase();
        if (["base", "build", "specific", "taper"].includes(underlying)) {
          phase = underlying;
        } else if (idx >= totalWeeks - taperWeeks) {
          phase = "taper";
        } else if (idx < baseEnd) {
          phase = "base";
        } else if (idx < buildEnd) {
          phase = "build";
        } else {
          phase = "specific";
        }
      } else if (raw === "peak") {
        phase = "specific";  // 19.04.2026 rename
      }

      m.set(+startOfWeek(d), { phase, isRecoveryWeek });
    });
    return m;
  }, [serverPlan]);

  const plannedComfort = useMemo(() => {
    // Derive a per-week planned pace from the server plan:
    //   plannedHours and plannedKm are set on every plan week, so
    //   pace (sec/km) = (plannedHours * 3600) / plannedKm.
    // This represents the average effort the plan asks for that week, which
    // is the most faithful "planned comfort" signal we can produce without
    // the generator explicitly emitting one.
    if (!serverPlan?.weeks) return [];
    const out = [];
    for (const w of serverPlan.weeks) {
      if (!w.sessions?.length) continue;
      const km = Number(w.plannedKm || 0);
      const hours = Number(w.plannedHours || 0);
      if (km <= 0 || hours <= 0) continue;
      const paceSecPerKm = (hours * 3600) / km;
      if (!isFinite(paceSecPerKm) || paceSecPerKm <= 0) continue;
      const weekStart = startOfWeek(parseLocalDate(w.sessions[0].date));
      out.push({ x: weekStart, y: paceSecPerKm });
    }
    return out.sort((a, b) => +a.x - +b.x);
  }, [serverPlan]);

  /* -------------------------
     Weeks + markers for chart
  ------------------------- */
  const goalWeekStart = useMemo(
    () => (goalDateObj ? startOfWeek(goalDateObj) : startOfWeek(addWeeks(new Date(), 16))),
    [goalDateObj]
  );

  /* The chart must cover every week the PLAN covers. `planStartDate` is the
     stored anchor (goalSaved.planAnchorDate) and the Week-N counter keys off
     it, but a plan carries restored past weeks from earlier plans
     (server/services/planHistory.js), which can start well before the anchor
     — and several paths used to reset the anchor to today. Weeks before the
     axis start are dropped by the chart (minX), which hid the history even
     though it was loaded. Axis start = the earlier of the two. */
  const chartStartDate = useMemo(() => {
    const ms = chartStartMs(+planStartDate, serverPlan?.weeks?.[0]?.sessions?.[0]?.date || null);
    return ms != null ? new Date(ms) : planStartDate;
  }, [serverPlan, planStartDate]);

  const weeks = useMemo(() => buildWeeksRange(chartStartDate, goalWeekStart), [chartStartDate, goalWeekStart]);
  /* A number, stable for the day, so the chart's memoised plot is not
     redrawn by every App render handing it a fresh `new Date()`. */
  const todayKey = new Date().toDateString();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const todayWeekStartMs = useMemo(() => +startOfWeek(new Date()), [todayKey]);

  /* =========================
     Readiness — runs the same methodology math the generator uses and
     returns a verdict on whether the plan's ramp is safely achievable
     from the user's current weekly volume. When stretchFactor > 1.5
     the readiness banner proposes (a) a longer timeline or (b) a safer
     distance, each applicable with one tap.
  ========================= */
  // Same verdict as the server gate: the generator's own assessReadiness()
  // (client mirror) on the measured 4-week median, over the generator's
  // week count. The old client re-implementation (mean of active weeks,
  // 1.65× threshold, no short-distance peak) could disagree with the gate
  // on the same screen (§Pillar 5).
  const readiness = useMemo(() => {
    if (!goalSaved?.date || !goalDistanceKm) return null;
    const weeksAvailable = generatorWeeksAvailable(goalSaved.date);
    if (!weeksAvailable) return null;
    const baselineKnown = !!userBaseline && Number.isFinite(userBaseline.medianWeeklyKm);
    return bannerFromReadiness(assessReadiness({
      baselineWeeklyKm: baselineKnown ? userBaseline.medianWeeklyKm : 0,
      raceDistanceKm: goalDistanceKm,
      weeksAvailable,
      now: Date.now(),
      baselineKnown,
    }));
  }, [goalSaved?.date, goalDistanceKm, userBaseline]);

  /* Dismissed-readiness-banner state (keyed by {raceDate, raceDistanceKm}).
     If the user has manually acknowledged a stretch for THIS goal — e.g.
     because they've previously trained at higher volumes than their recent
     4-week Strava shows — the banner stays hidden until they change the
     goal. Stored in localStorage so it survives reloads. */
  const readinessDismissKey = `${goalSaved?.date || ""}|${goalDistanceKm || ""}`;
  const [readinessDismissed, setReadinessDismissed] = useState(() => {
    try {
      const raw = localStorage.getItem("ultrarunner_readiness_dismissed_v1");
      return raw ? JSON.parse(raw) : "";
    } catch (_) { return ""; }
  });
  const isReadinessDismissed = readinessDismissed === readinessDismissKey && readinessDismissKey !== "|";
  const dismissReadiness = () => {
    setReadinessDismissed(readinessDismissKey);
    try { localStorage.setItem("ultrarunner_readiness_dismissed_v1", JSON.stringify(readinessDismissKey)); } catch (_) {}
  };

  /* Apply a one-tap readiness suggestion:
       { newRaceDate: Date }       — move the race date forward
       { newDistanceKm, newType }  — drop to a shorter distance, same date
     Updates goalSaved (localStorage) AND regenerates the plan so the user
     sees a methodology-compliant, safely-ramped plan immediately. */
  /** The A goal's own target ("H:MM:SS") or null — never a default. */
  const currentGoalTargetStr = () => {
    const a = goals.find((g) => g.status === "planned" && g.priority === "A");
    if (a) return a.targetFinishSec ? secondsToHhmmss(a.targetFinishSec) : null;
    return goalTimeStr || null;
  };
  const applyReadinessFix = async ({ newRaceDate, newDistanceKm, newType }) => {
    const prev = goalSaved || {};
    const fmtISO = (d) => {
      const dd = new Date(d);
      return `${dd.getFullYear()}-${String(dd.getMonth()+1).padStart(2,"0")}-${String(dd.getDate()).padStart(2,"0")}`;
    };
    const newDateStr = newRaceDate ? fmtISO(newRaceDate) : prev.date;
    const updated = {
      ...prev,
      date: newDateStr,
      type: newType || prev.type,
      distanceKm: newDistanceKm || prev.distanceKm || null,
      savedAt: Date.now(),
      // The anchor is kept: moving the race or dropping distance changes what
      // lies AHEAD, it does not restart the block the athlete has trained.
      // Re-anchoring to today used to hide every past week from the chart
      // (the plan keeps them — server/services/planHistory.js).
      planAnchorDate: prev.planAnchorDate ?? +startOfWeek(new Date()),
    };
    setGoalSaved(updated);
    try {
      const raw = localStorage.getItem(PLAN_KEY);
      const base = raw ? JSON.parse(raw) : {};
      localStorage.setItem(PLAN_KEY, JSON.stringify({ ...base, goalSaved: updated }));
    } catch (_) {}

    // Regenerate the plan with the new target.
    const r = await generateServerPlan({
      raceDistance: newDistanceKm || serverPlan?.raceDistance || goalDistanceKm,
      raceDate: newDateStr,
      targetFinish: currentGoalTargetStr(),
      terrain: serverPlan?.terrain || "road",
      sessionsPerWeek: runsPerWeek,
      availableDays: availableDays.map((dow) => DAY_NAMES_LONG[dow]),
      longRunDay: longRunDayName,
    tempoDay: tempoDayName,
      includeBackToBack: !!(serverPlan?.includeBackToBack),
      includeHikes:      !!(serverPlan?.includeHikes),
      includeVertical:   !!(serverPlan?.includeVertical),
      currentWeeklyKm: Math.round(estimateWeeklyKmNow(actualWeeklyMap) || 30),
      userBaseline,
      // The server re-checks the new target; if it still refuses, the gate
      // opens with the server's own options (never a silent override).
    });
    if (r && r.blocked) openReadinessGate(r);
  };

  /* Readiness banner option (c): rebuild the CURRENT race as a finish-only
     plan — a safe forward ramp from the real baseline, no quality work,
     honest about a slower finish. finish-only bypasses the readiness gate
     by design, so this never blocks. */
  const applyFinishOnlyPlan = async () => {
    const r = await generateServerPlan({
      raceDistance: serverPlan?.raceDistance ?? goalDistanceKm,
      raceDate: serverPlan?.raceDate || goalSaved?.date,
      targetFinish: currentGoalTargetStr(),
      terrain: serverPlan?.terrain || "road",
      sessionsPerWeek: runsPerWeek,
      availableDays: availableDays.map((dow) => DAY_NAMES_LONG[dow]),
      longRunDay: longRunDayName,
    tempoDay: tempoDayName,
      includeBackToBack: !!(serverPlan?.includeBackToBack),
      includeHikes:      !!(serverPlan?.includeHikes),
      includeVertical:   !!(serverPlan?.includeVertical),
      currentWeeklyKm: Math.round(estimateWeeklyKmNow(actualWeeklyMap) || 30),
      userBaseline,
      goalMode: "finish-only",
    });
    if (r && r.blocked) openReadinessGate(r);
  };

  const markers = useMemo(() => {
    if (!goalDateObj) return [];
    const out = [];
    out.push({ date: planStartDate, label: "Start", color: "var(--content-tertiary)" }); // --text-muted

    // Tune-up races are intentionally NOT rendered as markers.
    // Per /docs/training_methodology.md §"Session types — definitive taxonomy":
    // the generator does not invent new session types, and tune-up races are
    // not part of the defined taxonomy. Showing them as plan milestones while
    // the generator is blind to them creates real training bugs
    // (Long Run placed 3 days after a HM, Recovery placed before a race,
    // volume distribution broken). Only the plan's Start and Goal anchors
    // are rendered. Re-introduce when the methodology doc adds race-aware
    // taper + recovery logic.

    out.push({
      date: goalDateObj,
      label: "Goal",
      color: raceColor(goalDistanceKm),
      title: "Goal race",
      distanceKm: goalDistanceKm,
    });

    const seen = new Set();
    return out.filter((m) => {
      const k = +startOfWeek(m.date) + "|" + m.label;
      if (seen.has(k)) return false;
      seen.add(k);
      return true;
    });
  }, [planStartDate, tuneUpRaces, goalDateObj, goalDistanceKm]);

  // Plan-vs-Actual matched rows.
  //
  // Source strategy:
  //   1. If the active `serverPlan` is the current format (planFormatVersion
  //      >= 2, i.e. generated client-side by the in-bundle generator), run
  //      the matcher RIGHT HERE in the browser against the live plan + all
  //      Strava activities. This is the truth-telling path — the table
  //      always reflects what the user sees in the Train tab.
  //   2. Otherwise fall back to `serverMetrics.matchedSessions`. That's the
  //      legacy path for plans generated before the client-side refactor;
  //      the stale-plan banner prompts a regenerate in this case.
  //
  // Why this matters: the Node backend caches require()'d modules, so
  // `/api/metrics` computes matchedSessions against whatever plan was in
  // the DB when it last ran its matcher. If the user regenerated locally
  // without restarting the Node process, Train tab shows the new plan but
  // Stats' Plan-vs-Actual showed the OLD one — that's the "hike missed,
  // today missed" bug. Client-side matching eliminates that divergence.
  const serverMatchedSessions = useMemo(() => {
    const planIsCurrent = serverPlan && (serverPlan.planFormatVersion ?? 1) >= PLAN_FORMAT_VERSION;
    if (planIsCurrent) {
      // Flatten all planned sessions from the local plan.
      const plannedSessions = (serverPlan.weeks || []).flatMap((w) => w.sessions || []);
      // All Strava activities (INCLUDING hikes/walks — the matcher needs them
      // to match 'hike' planned sessions, even though they don't count toward
      // weekly volume unless `includeHikesInVolume` is on).
      const allActivities = session?.activities || session?.strava?.runs || [];
      return clientMatchPlan(plannedSessions, allActivities);
    }
    return serverMetrics?.matchedSessions || [];
  }, [serverPlan, session, serverMetrics]);

  /* Per-week runs for the chart week-detail card. */
  const weekRuns = useMemo(() => {
    const m = new Map();
    for (const row of (serverMatchedSessions || [])) {
      if (!row.date) continue;
      const wk = +startOfWeek(parseLocalDate(row.date));
      const a = row.actual, pl = row.planned;
      const name = (a && a.name) || (pl && formatSessionTypeLabel(pl.type)) || (pl && pl.type) || (a && a.type) || "Run";
      // Duration-only sessions (time-on-feet, hike) carry plannedKm: null and
      // an estimatedKm derived from the plan's own threshold pace — the same
      // value the week total and Weekly Load count (planFormatVersion 13).
      // Without the fallback the row read "— km" under a 70 km week total.
      const plannedKm = (pl && pl.plannedKm != null) ? pl.plannedKm
        : (pl && pl.estimatedKm != null) ? pl.estimatedKm : null;
      const km = (a && a.distance_m) ? a.distance_m / 1000 : plannedKm;
      const est = !(a && a.distance_m) && !!(pl && pl.plannedKm == null && pl.estimatedKm != null);
      const arr = m.get(wk) || [];
      arr.push({ date: row.date, name, km, est, type: (pl && pl.type) || (a && a.type) || null, status: (row.status || "").toLowerCase(), done: !!a });
      m.set(wk, arr);
    }
    return m;
  }, [serverMatchedSessions]);

  /* -------------------------
     Plan vs actual (window = last N days)
  ------------------------- */
  // Status mapping: server uses lowercase, UI expects capitalized
  const PVA_STATUS_MAP = { matched: "Matched", partial: "Partial", missed: "Missed", extra: "Extra", upcoming: "Upcoming", today: "Today" };
  /* One enrichment for a matched row, shared by the list and the Last-run
     KPI so the KPI does not depend on how many days the list is showing. */
  const enrichMatchedRow = useCallback((row) => {
        const statusMap = PVA_STATUS_MAP;
        const actual = row.actual;
        const planned = row.planned;
        // Compute pace from actual activity
        const actualP = actual && actual.distance_m > 0 && actual.moving_time_s > 0
          ? actual.moving_time_s / (actual.distance_m / 1000)
          : null;
        // Get planned duration. For tempo, derivePlannedDurationMin prefers
        // observedThresholdPaceSec (fastest recent 3-15 km pace from Strava)
        // so the prescribed Tempo matches actual current fitness, not an
        // extrapolation from a potentially-conservative goal time.
        const plannedDurMin = derivePlannedDurationMin(planned, serverPlan, observedThresholdPaceSec);
        // Planned pace (sec/km) = duration × 60 / km. One source of truth:
        // duration already encodes the session-type & terrain multipliers,
        // so a single division recovers the intended pace.
        const plannedPSec = (planned && planned.plannedKm > 0 && plannedDurMin > 0)
          ? (plannedDurMin * 60) / planned.plannedKm
          : null;

        return {
          ...row,
          status: statusMap[row.status] || row.status,
          actualP,
          plannedP: plannedPSec,
          dateLabel: formatDateShort(parseLocalDate(row.date)),
          // Enrich `planned` with the field names the detail sheet reads.
          // The raw session has `plannedKm` / `plannedDuration`; the detail
          // sheet expects `distanceKm` / `durationMin` / `pace` / `paceSecPerKm`.
          // durationMin uses ONLY plannedDurMin (which returns null for
          // intensity types and for missing-pace-multiplier cases). Do NOT
          // fall back to planned.plannedDuration — that would leak stale
          // values from plans generated with the deprecated intensity
          // multiplier (tempo getting a bogus 21-min estimate, etc).
          planned: planned ? {
            ...planned,
            title: formatSessionTypeLabel(planned.type),
            distanceKm: planned.plannedKm ?? null,
            durationMin: plannedDurMin,
            paceSecPerKm: plannedPSec,
            pace: plannedPSec ? secPerKmToPace(plannedPSec) : null,
          } : null,
          actual: actual ? { ...actual, name: actual.name || actual.type || '' } : null,
        };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [serverPlan, observedThresholdPaceSec]);

  /* The list: every matched session in the last `pvaDays` days, newest
     first. Rows up to and including today only — the weeks ahead are the
     Train tab's job. */
  /* Every run on record is reachable: the matcher emits a row for each
     synced activity, plan or no plan, and "Earlier runs" walks back through
     all of them — pre-plan history included, because it is still your
     running. */
  const allRowsInWindow = useMemo(() => {
    const rows = serverMatchedSessions;
    if (!rows || !rows.length) return [];
    const today = startOfDay(new Date());
    // "Last 7 days" is today and the six before it, not eight calendar days.
    const start = startOfDay(new Date(+today - (pvaDays - 1) * DAY_MS));
    return rows
      .filter((row) => {
        const d = startOfDay(parseLocalDate(row.date));
        return +d >= +start && +d <= +today;
      })
      .map(enrichMatchedRow)
      .sort((a, b) => +parseLocalDate(b.date) - +parseLocalDate(a.date));
  }, [serverMatchedSessions, pvaDays, enrichMatchedRow]);

  const visibleRows = allRowsInWindow || [];

  /* How many past rows the window is NOT showing — the count on the
     "Earlier runs" row, and 0 means the list has reached the oldest run. */
  const pvaPastTotal = useMemo(() => {
    const today = startOfDay(new Date());
    return (serverMatchedSessions || []).filter((r) => r && r.date && +startOfDay(parseLocalDate(r.date)) <= +today).length;
  }, [serverMatchedSessions]);
  const pvaRemaining = Math.max(0, pvaPastTotal - visibleRows.length);
  /* 7 → 14 → 28 → +28 at a time. */
  const loadEarlierRuns = () => setPvaDays((d) => (d < 14 ? 14 : d < 28 ? 28 : d + 28));
  /* Rows past this index were not on screen at the last render — they are
     the ones that just arrived, and they get the entrance. */
  const pvaPrevCountRef = useRef(0);
  useEffect(() => { pvaPrevCountRef.current = visibleRows.length; });

  /* The most recent run that actually happened, independent of the list
     window: a Strava activity with an id, never a planned-only row. */
  const lastRunRow = useMemo(() => {
    const rows = (serverMatchedSessions || []).filter((r) => r && r.actual && r.actual.id != null && r.date);
    if (!rows.length) return null;
    rows.sort((a, b) => +parseLocalDate(b.date) - +parseLocalDate(a.date));
    return enrichMatchedRow(rows[0]);
  }, [serverMatchedSessions, enrichMatchedRow]);

  /* -------------------------
     Next runs
  ------------------------- */
  const futureSessionsAll = useMemo(() => {
    if (!goalDateObj) return [];
    const today = startOfDay(new Date());
    // Build a set of "already completed" planned-session keys (date + type)
    // from the server-matched rows. A planned session is considered
    // completed once a Strava activity has been matched to it (status
    // 'matched' or 'partial' — anything where row.actual is a real Strava
    // activity). Today's session must drop out of "Next Session" the moment
    // it's matched, but stay visible until then.
    const completedKeys = new Set();
    for (const row of (serverMatchedSessions || [])) {
      if (!row || !row.actual || row.actual.id == null || !row.planned) continue;
      const ds = typeof row.date === 'string' ? row.date : dateToLocalISO(row.date);
      completedKeys.add(`${ds}|${row.planned.type || ''}`);
    }
    return (plannedSessionsFull || [])
      // INCLUDE today (>=, not >). Today's planned session is the next
      // session until Strava matches it; the completedKeys filter below
      // drops it once that match exists.
      .filter((s) => +startOfDay(s.date) >= +today)
      .filter((s) => {
        // Use ORIGINAL date for the match key — that's what matchPlan keys
        // off (s.date in the row corresponds to the original planned date).
        const od = s.originalDate || s.date;
        const ds = dateToLocalISO(od);
        return !completedKeys.has(`${ds}|${s.type || ''}`);
      })
      .sort((a, b) => +a.date - +b.date);
  }, [goalDateObj, plannedSessionsFull, serverMatchedSessions]);

  const nextRuns = useMemo(() => {
    const lim = clamp(nextLimit, 1, futureSessionsAll.length || 1e9);
    return futureSessionsAll.slice(0, lim);
  }, [futureSessionsAll, nextLimit]);

  const canLoadMoreNext = useMemo(
    () => (futureSessionsAll?.length || 0) > (nextRuns?.length || 0),
    [futureSessionsAll, nextRuns]
  );

  /* -------------------------
     KPI cards
  ------------------------- */
  const actualWeeklyKeyMap = useMemo(() => {
    const m = new Map();
    for (const p of actualWeekly || []) m.set(+startOfWeek(p.x), Number(p.y || 0));
    return m;
  }, [actualWeekly]);

  const thisWeekKey = +startOfWeek(new Date());
  const lastWeekKey = +startOfWeek(addWeeks(new Date(), -1));

  const activeWeekKey = useMemo(() => {
    if (actualWeeklyKeyMap.has(thisWeekKey) || plannedWeeklyMap.has(thisWeekKey)) return thisWeekKey;
    return lastWeekKey;
  }, [actualWeeklyKeyMap, plannedWeeklyMap, thisWeekKey, lastWeekKey]);

  const currentWeeklyKm = actualWeeklyKeyMap.get(activeWeekKey) ?? 0;
  const currentWeeklySecs = actualWeeklySecsMap.get(activeWeekKey) ?? 0;
  const plannedThisWeekKm = plannedWeeklyMap.get(activeWeekKey) ?? null;
  const deltaWeeklyVsPlan = plannedThisWeekKm == null ? null : currentWeeklyKm - plannedThisWeekKm;

  /* -----------------------------------------------------------------
   * Plan-to-date — sum of this week's planned km counted as "due so
   * far". Used by the Today card and by the WK Load KPI so the
   * "vs plan" delta answers "am I on track right now?" rather than
   * the full-week question (which is pessimistic mid-week before the
   * user has run).
   *
   * Rules:
   *   - Days STRICTLY BEFORE today: always included (those sessions
   *     are due / past-due).
   *   - TODAY: only included once a Strava run for today exists.
   *     This is the fix for a bug where the WK Load KPI read
   *     "On track" when the user did 4.6 km of a planned 15 km
   *     tempo earlier in the day — the planned 15 km was excluded
   *     because the day hadn't ended, so the delta computed against
   *     0 km of plan and looked like a surplus.
   *   - Days AFTER today: always excluded.
   *
   * The "is there a run today" check uses the raw runs list rather
   * than `serverMatchedSessions` because matching is best-effort and
   * a run that fails to match (wrong type, off-day) should still
   * count toward "the user has finished today's running, count the
   * planned km as due now".
   * ----------------------------------------------------------------- */
  // ONE Weekly Load definition shared with the server (lib/weeklyLoad.js):
  // plan-to-date denominator (§Pillar 5 rule 5) and the > 120 % "over" band
  // (§Adaptation Rules R5). Session edits and the day filter are applied to
  // plannedSessionsFull before it gets here.
  const weeklyLoad = useMemo(() => weeklyLoadToDate({
    plannedSessions: (plannedSessionsFull || []).map((s) => ({
      date: localIsoDate(s.date instanceof Date ? s.date : parseLocalDate(s.date)),
      plannedKm: s.distanceKm,
    })),
    activities: runs || [],
    now: new Date(),
    includeHikes: includeHikesInVolume,
  }), [plannedSessionsFull, runs, includeHikesInVolume]);
  const plannedThisWeekToDateKm = weeklyLoad.hasPlan ? weeklyLoad.plannedToDateKm : null;
  const deltaWeeklyVsPlanToDate = plannedThisWeekToDateKm == null
    ? null
    : Math.round((currentWeeklyKm - plannedThisWeekToDateKm) * 10) / 10;

  const currentComfortPace = useMemo(() => {
    const pts = (actualComfort || []).slice().sort((a, b) => +a.x - +b.x);
    return pts.length ? pts[pts.length - 1].y : null;
  }, [actualComfort]);

  const plannedComfortThisWeek = useMemo(() => {
    const p = (plannedComfort || []).find((x) => +startOfWeek(x.x) === activeWeekKey);
    return p ? p.y : null;
  }, [plannedComfort, activeWeekKey]);

  /* --------------------------------------------------------------
   * Comfort-pace trend delta (THIS week vs 4-week rolling baseline).
   *
   * Why this exists: the old delta compared `currentComfortPace`
   * (28-day rolling median of actual easy runs, in sec/km) against
   * `plannedComfortThisWeek` (the plan's *average* weekly pace — weekly
   * km ÷ planned hours, where planned hours include the trail-terrain
   * multiplier). These two things aren't commensurable: the comfort
   * pace is a fast easy-run median (~5:30/km), the planned pace is a
   * slow race-effort projection (~9:30/km on trail). The resulting
   * delta was routinely −3:00/km or more — meaningless.
   *
   * The new delta answers "am I getting faster?" by comparing THIS
   * week's pace point to the median of the PREVIOUS 4 weeks (exclusive
   * of this week). Negative = faster than your recent average. Null
   * if either side has too little data (<2 points in baseline, or no
   * runs this week).
   * -------------------------------------------------------------- */
  const comfortPaceThisWeek = useMemo(() => {
    const thisWk = +startOfWeek(new Date());
    const p = (actualComfort || []).find((pt) => +startOfWeek(pt.x) === thisWk);
    return p ? p.y : null;
  }, [actualComfort]);

  const comfortPaceBaseline = useMemo(() => {
    const FOUR_WEEKS_MS = 28 * 24 * 60 * 60 * 1000;
    const thisWk = +startOfWeek(new Date());
    const paces = (actualComfort || [])
      .filter((p) => {
        const wk = +startOfWeek(p.x);
        return wk < thisWk && wk >= thisWk - FOUR_WEEKS_MS;
      })
      .map((p) => p.y)
      .filter((v) => v != null && isFinite(v));
    if (paces.length < 2) return null; // not enough data for a stable baseline
    return median(paces);
  }, [actualComfort]);

  const comfortPaceDelta = useMemo(() => {
    if (comfortPaceThisWeek == null || comfortPaceBaseline == null) return null;
    return comfortPaceThisWeek - comfortPaceBaseline;
  }, [comfortPaceThisWeek, comfortPaceBaseline]);

  // --- SERVER-BACKED METRICS (replaces all client-side KPI computation) ---
  const adherence = useMemo(() => {
    if (serverMetrics?.adherence) {
      const s = serverMetrics.adherence;
      // The server's calcPlanAdherence returns `completedCredit` — a
      // fractional sum across recent sessions (e.g. one partial run
      // counts as 0.31, not 0). The earlier client read `s.completed`
      // which doesn't exist on the server response, so `matched` was
      // always 0 — visible in the trajectory subtitle as
      // "only 0 of 1 sessions completed" while the score showed 31%.
      const credit = Number(s.completedCredit ?? s.completed ?? 0);
      return {
        score: (s.score || 0) / 100, // normalize to 0–1
        label: s.score != null ? `${s.score}%` : 'n/a',
        details: {
          matched: Math.round(credit),
          credit,
          total: s.total || 0,
        },
      };
    }
    // Fallback: safe defaults while server metrics load
    return { score: 0, label: 'n/a', details: { matched: 0, credit: 0, total: 0 } };
  }, [serverMetrics]);

  const likelihood = useMemo(() => {
    if (serverMetrics?.goalLikelihood) {
      const gl = serverMetrics.goalLikelihood;
      return {
        score: Math.round(gl.score || 0),
        label: gl.score >= 80 ? "Very likely" : gl.score >= 65 ? "Likely" : gl.score >= 50 ? "Uncertain" : gl.score >= 35 ? "Unlikely" : "Very unlikely",
        pill: (gl.score || 0) >= 65 ? { ok: true, warn: false } : (gl.score || 0) >= 50 ? { ok: false, warn: true } : { ok: false, warn: false },
        action: gl.action || '',
      };
    }
    // Fallback: safe defaults while server metrics load
    return { score: 0, label: "—", pill: { ok: false, warn: false }, action: '' };
  }, [serverMetrics]);

  // RESET Score — separate from Goal Likelihood
  const resetScore = useMemo(() => {
    const base = serverMetrics?.resetScore;
    const hasExecution = (serverMatchedSessions || []).some((r) => r.planned && (r.status === "matched" || r.status === "Matched"));
    // Real, data-driven score wins only once there's actual plan execution to
    // judge. Before any completed session the score would be dominated by
    // empty adherence/volume and read as a scary low number, so we fall through
    // to a neutral readiness-based starting score (~50) instead.
    if (base && base.score > 0 && hasExecution) return base;
    // A freshly generated plan has no completed sessions yet, so the server
    // score legitimately computes to 0 — but a bare "0" reads as broken. Floor
    // it to a readiness-based STARTING score derived from the plan's own
    // readiness verdict + baseline consistency, so a new plan always opens
    // with a meaningful number that the real score takes over once runs land.
    if (hasSavedGoal && serverPlan) {
      const ready = !!serverPlan.readiness?.ready;
      const consistency = Number(serverPlan.userBaseline?.consistencyScore) || 0; // 0..1
      const s = Math.max(38, Math.min(62, Math.round((ready ? 52 : 47) + consistency * 8)));
      return { score: s, label: s >= 65 ? 'On track' : s >= 40 ? 'Building' : 'Needs attention', provisional: true };
    }
    // No plan at all: there is genuinely nothing to score. The old fallback
    // rendered the likelihood proxy, which without a plan is always 0 — so a
    // brand-new account opened on "0 / Needs attention. Consider adjustments."
    // and read as a broken app. Render an explicit blank instead.
    return { score: null, label: 'No plan yet', pending: true };
  }, [serverMetrics, likelihood, hasSavedGoal, serverPlan, serverMatchedSessions]);

  // NOTE: server metrics are intentionally NOT refetched when windowDays
  // changes. Adherence is computed once over a fixed 28-day window so the
  // headline number stays stable as the user toggles 7d/14d/28d on the
  // Plan vs Actual list below. See refreshServerMetrics().

  /* -------------------------
     Weekly adherence history (for controlled reset trigger)
  ------------------------- */
  const weeklyAdherenceHistory = useMemo(() => {
    // Derive from server matched sessions
    const rows = serverMatchedSessions;
    if (!rows || !rows.length) return [];
    const today = startOfDay(new Date());

    // Group by week
    const weekMap = new Map();
    for (const row of rows) {
      if (!row.date) continue;
      const d = parseLocalDate(row.date);
      if (+startOfDay(d) > +today) continue; // skip future
      const wk = +startOfWeek(d);
      if (!weekMap.has(wk)) weekMap.set(wk, { total: 0, matched: 0 });
      const entry = weekMap.get(wk);
      if (row.planned) { // only count sessions that have a planned component
        entry.total++;
        if (row.status === 'matched' || row.status === 'Matched') entry.matched++;
      }
    }

    const out = [];
    for (const [wk, { total, matched }] of [...weekMap.entries()].sort((a, b) => a[0] - b[0])) {
      if (total === 0) continue;
      const score = matched / total;
      out.push({ weekStart: new Date(wk), score, label: `${Math.round(score * 100)}%` });
    }

    return out.slice(-10);
  }, [serverMatchedSessions]);

  const recommendations = useMemo(() => {
    if (!hasSavedGoal) return [];
    const recs = [];

    const adherenceLow = adherenceBelowThresholdForWeeks(weeklyAdherenceHistory, 0.6, 3);
    if (adherenceLow) {
      const actions = [
        { id: "rebuild", label: "Rebuild plan from today", icon: RotateCcw, primary: true },
        { id: "recovery", label: "Insert recovery week", icon: HeartPulse, primary: false },
        { id: "adjust_goal", label: "Adjust goal time", icon: CalendarDays, primary: false },
      ];
      if (runsPerWeek > 3) {
        actions.push({ id: "fewer_days", label: `Drop to ${runsPerWeek - 1} runs/week`, icon: Minus, primary: false });
      }
      recs.push({
        type: "adherence_low",
        reason: "Adherence < 60% for 3 weeks",
        description: "RESET never changes your plan silently. Conditions changed — choose how you want to adapt.",
        actions,
      });
    }

    const volDef = computeVolumeDeficit(actualWeeklyMap, plannedWeeklyMap);
    if (!adherenceLow && volDef.deficit) {
      const actions = [];
      if (runsPerWeek < 5) {
        actions.push({ id: "more_days", label: `Add run (${runsPerWeek} → ${runsPerWeek + 1}/week)`, icon: Plus, primary: true });
      }
      actions.push({ id: "rebuild", label: "Rebuild plan (lower targets)", icon: RotateCcw, primary: false });
      actions.push({ id: "adjust_goal", label: "Adjust goal time", icon: CalendarDays, primary: false });
      recs.push({
        type: "volume_deficit",
        reason: `Volume at ${round0(volDef.avgRatio * 100)}% of plan (last 4 weeks)`,
        description: "Your volume is below target. Consider adding training or adjusting expectations.",
        actions,
      });
    }

    const volAhead = computeVolumeAhead(actualWeeklyMap, plannedWeeklyMap);
    if (!adherenceLow && !volDef.deficit && volAhead.ahead && runsPerWeek < 5) {
      recs.push({
        type: "ahead_of_plan",
        reason: `Volume at ${round0(volAhead.avgRatio * 100)}% of plan (last 4 weeks)`,
        description: "You're consistently exceeding your plan. Consider increasing training frequency.",
        actions: [
          { id: "more_days", label: `Add run (${runsPerWeek} → ${runsPerWeek + 1}/week)`, icon: Plus, primary: true },
        ],
      });
    }

    return recs;
  }, [hasSavedGoal, weeklyAdherenceHistory, actualWeeklyMap, plannedWeeklyMap, runsPerWeek]);

  const handleRecommendationAction = (actionId) => {
    switch (actionId) {
      case "rebuild": rebuildPlanFromToday(); break;
      case "recovery": insertRecoveryWeek(); break;
      case "adjust_goal": openAdjustGoalSheet(); break;
      case "more_days": {
        const next = clamp(runsPerWeek + 1, 3, 5);
        setRunsPerWeek(next);
        setAvailableDays(defaultAvailableDaysForCount(next, availableDays));
        break;
      }
      case "fewer_days": {
        const next = clamp(runsPerWeek - 1, 3, 5);
        setRunsPerWeek(next);
        // When shrinking, just take the first N — preserves the earliest days
        // in the week. (User can re-pick from Goal & Plan Settings pills.)
        setAvailableDays(sortDow(availableDays).slice(0, next));
        break;
      }
      default: break;
    }
  };

  /* -------------------------
     Controlled RESET actions (explicit only)
  ------------------------- */
  const rebuildPlanFromToday = () => {
    if (!goalSaved) return;
    const anchor = startOfWeek(new Date());
    setGoalSaved((g) => ({
      ...g,
      planAnchorDate: +anchor,
      rebuiltAt: Date.now(),
    }));
    setForcedRecoveryWeekStartKey(null);
    setPastLimit(3);
    setNextLimit(3);
  };

  const insertRecoveryWeek = () => {
    // next week Monday
    const nextMon = startOfWeek(addWeeks(new Date(), 1));
    setForcedRecoveryWeekStartKey(+nextMon);
  };

  const longRunReference = useMemo(
    () => pickLongRunReference(runs),
    [runs]
  );

  const suggestedGoalTimeStr = useMemo(() => {
    if (!goalDistanceKm) return goalTimeStr;
    const cp = currentComfortPace || comfortablePaceSec || 6 * 60;
    const sec = estimateGoalTime(cp, goalDistanceKm, longRunReference);
    if (!sec || !isFinite(sec) || sec <= 0) return goalTimeStr;
    return secondsToHhmmss(sec);
  }, [goalDistanceKm, currentComfortPace, comfortablePaceSec, goalTimeStr, longRunReference]);

  /* -------------------------
     [Removed] Projected finish range — was a Riegel-based projection
     from current comfort pace. Replaced by planTrajectory below, which
     derives the projected finish from plan execution (per-session pace
     ratio vs plan) instead of raw fitness. The plan was generated for
     the goal, so hitting plan = hitting goal; drift from plan paces is
     the only legitimate reason for the projection to read off. See
     spaces/.../memory/feedback_plan_centric_model.md for the full
     rationale.
     suggestedGoalTimeStr above is kept — it's only used in the
     goal-edit modal as a one-time "what does fitness suggest as a
     starting target?" hint, which is appropriate at goal-set time.
  ------------------------- */

  /* -------------------------
     Pace Fit
     ----------------------------------------------------------------------
     Average pace deviation vs plan, weighted by session importance, over
     a recent window. This is the in-flight "are you running the plan's
     paces?" signal — replaces the old Comfortable Pace KPI which was a
     28-day easy-run median and could read green even on a day the user
     just blew a tempo.

     Weights (per the "Tempo + Long heaviest" choice in the design quiz):
       tempo / threshold       2.0   — race-pace work matters most
       long  / b2b / time-on-feet  2.0   — endurance pace matters most
       vertical / hills        1.5   — quality work
       easy  / moderate        0.5   — support sessions, low signal
       recovery                0.3   — pace-irrelevant, near-zero weight

     Output:
       ratio          weighted Σ(actual_pace / planned_pace)
       deltaSecPerKm  pretty signed delta in seconds-per-km vs plan
       sessions       how many sessions contributed
       status         'onplan' (≤ 2%), 'slightly' (2–5%), 'behind' (> 5%)
  ------------------------- */
  const PACE_FIT_WINDOW_DAYS = 14;
  const paceFit = useMemo(() => {
    if (!serverMatchedSessions?.length) return null;
    const cutoffMs = Date.now() - PACE_FIT_WINDOW_DAYS * DAY_MS;
    const cutoff = startOfDay(new Date(cutoffMs));
    const today = startOfDay(new Date());

    const weightFor = (type) => {
      const t = String(type || '').toLowerCase();
      if (t.includes('tempo') || t.includes('threshold')) return 2.0;
      if (t.includes('long') || t.includes('b2b') || t.includes('time-on-feet') || t.includes('time on feet')) return 2.0;
      if (t.includes('vertical') || t.includes('hill')) return 1.5;
      if (t.includes('recovery')) return 0.3;
      if (t.includes('easy') || t.includes('moderate')) return 0.5;
      return 1.0;
    };

    let totalWeight = 0;
    let weightedRatioSum = 0;
    let weightedPlannedPSum = 0; // for converting ratio back to a sec/km delta
    let sessions = 0;

    for (const row of serverMatchedSessions) {
      if (!row.date) continue;
      const d = startOfDay(parseLocalDate(row.date));
      if (+d < +cutoff || +d > +today) continue;

      const status = String(row.status || '').toLowerCase();
      if (status !== 'matched' && status !== 'partial') continue;

      const planned = row.planned;
      const actual = row.actual;
      if (!planned || !actual) continue;
      // Unconfirmed walking-pace "run": not evidence about running fitness
      // until the athlete says it was a run.
      if (suspectIds.has(String(actual.id))) continue;

      const actualP = (actual.distance_m > 0 && actual.moving_time_s > 0)
        ? actual.moving_time_s / (actual.distance_m / 1000)
        : null;
      if (!actualP) continue;

      const plannedDurMin = derivePlannedDurationMin(planned, serverPlan, observedThresholdPaceSec);
      const plannedP = (planned.plannedKm > 0 && plannedDurMin > 0)
        ? (plannedDurMin * 60) / planned.plannedKm
        : null;
      if (!plannedP) continue;

      // Easy/recovery/moderate: running FASTER than the prescribed cap is not
      // a fitness deficit and must not deflate Pace Fit (the "too fast" nudge
      // lives on the run card). Floor the ratio at on-pace so one fast easy run
      // can't read as a huge negative or fake a faster-than-goal projection.
      // Quality work (tempo/long/intervals) keeps the true ratio both ways.
      const _pt = String(planned.type || '').toLowerCase();
      const _easyType = _pt.includes('easy') || _pt.includes('recovery') || _pt.includes('moderate');
      let ratio = actualP / plannedP;
      if (_easyType && ratio < 1) ratio = 1;
      const w = weightFor(planned.type);

      weightedRatioSum  += ratio * w;
      weightedPlannedPSum += plannedP * w;
      totalWeight += w;
      sessions++;
    }

    if (totalWeight === 0 || sessions === 0) return null;

    const ratio = weightedRatioSum / totalWeight;
    const avgPlannedP = weightedPlannedPSum / totalWeight;
    const deltaSecPerKm = Math.round((ratio - 1) * avgPlannedP);

    let status;
    if (ratio <= 1.02)      status = 'onplan';
    else if (ratio <= 1.05) status = 'slightly';
    else                    status = 'behind';

    return { ratio, deltaSecPerKm, sessions, status, windowDays: PACE_FIT_WINDOW_DAYS };
  }, [serverMatchedSessions, serverPlan, observedThresholdPaceSec, suspectIds]);

  /* -------------------------
     Metric detail sheets — the development behind each Stats number.
     Every number on Stats opens one: the value, how it moved over the
     period, and the rows it was computed from.
  ------------------------- */
  const openStat = (kind) => (e) => {
    // The (i) inside the cell is its own tap, and so is the sheet it opens
    // (portaled, but React bubbles it here all the same).
    if (e && e.target && e.target.closest && e.target.closest("button, .infoModal, .infoModalBackdrop")) return;
    setStatSheet(kind);
  };
  const statKey = (kind) => (e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setStatSheet(kind); } };
  const statSheetData = useMemo(() => {
    if (!statSheet) return null;
    const today = startOfDay(new Date());
    const thisWk = +startOfWeek(today);
    const weekLabelOf = (ts) => { const d = new Date(ts); return `${d.getDate()} ${monthShort(d)}`; };
    const weeksBack = (n) => { const out = []; for (let i = n - 1; i >= 0; i--) out.push(+startOfWeek(addWeeks(new Date(thisWk), -i))); return out; };
    if (statSheet === "adherence") {
      const wks = weeksBack(8);
      const byWk = new Map(wks.map((w) => [w, { credit: 0, n: 0, matched: 0 }]));
      for (const row of serverMatchedSessions || []) {
        if (!row || !row.date || !row.planned) continue;
        const d = startOfDay(parseLocalDate(row.date));
        if (+d > +today) continue;
        const b = byWk.get(+startOfWeek(d));
        if (!b) continue;
        const st = String(row.status || "").toLowerCase();
        if (st === "today" || st === "upcoming") continue;
        b.n += 1;
        if (st === "matched") { b.credit += 1; b.matched += 1; }
        else if (st === "partial") { b.credit += typeof row.completionRatio === "number" ? clamp(row.completionRatio, 0, 1) : 0.5; b.matched += 1; }
      }
      const pct = (b) => (b.n ? Math.round((b.credit / b.n) * 100) : null);
      const points = wks.map((w) => ({ t: w, v: pct(byWk.get(w)) }));
      const rows = wks.slice().reverse().map((w) => { const b = byWk.get(w); const p = pct(b); return { label: `Week of ${weekLabelOf(w)}`, sub: b.n ? `${b.matched} of ${b.n} sessions` : "No sessions due", value: p != null ? `${p}%` : "—", tone: p == null ? null : p >= 85 ? "ok" : p >= 60 ? "warn" : "bad" }; });
      return { title: "Adherence", color: "var(--accent)", value: adherence.label, verdict: `last ${ADHERENCE_WINDOW_DAYS} days`, period: "Week by week · last 8 weeks", chart: { type: "bars", points, kind: "pct", yFormat: (v) => `${Math.round(v)}%` }, rows, note: "The share of planned sessions completed within tolerance, week by week. A partial run counts in proportion." };
    }
    if (statSheet === "pace") {
      const cutoff = +startOfDay(new Date(Date.now() - 56 * DAY_MS));
      const pts = [];
      for (const row of serverMatchedSessions || []) {
        if (!row || !row.date || !row.planned || !row.actual) continue;
        const d = startOfDay(parseLocalDate(row.date));
        if (+d < cutoff || +d > +today) continue;
        const st = String(row.status || "").toLowerCase();
        if (st !== "matched" && st !== "partial") continue;
        const a = row.actual;
        const actualP = a.distance_m > 0 && a.moving_time_s > 0 ? a.moving_time_s / (a.distance_m / 1000) : null;
        if (!actualP) continue;
        const durMin = derivePlannedDurationMin(row.planned, serverPlan, observedThresholdPaceSec);
        const plannedP = row.planned.plannedKm > 0 && durMin > 0 ? (durMin * 60) / row.planned.plannedKm : null;
        if (!plannedP) continue;
        const pt = String(row.planned.type || "").toLowerCase();
        const easy = pt.includes("easy") || pt.includes("recovery") || pt.includes("moderate");
        let delta = Math.round(actualP - plannedP);
        if (easy && delta < 0) delta = 0;   // a quick easy day is on pace, as the headline treats it
        pts.push({ t: +d, v: delta, type: formatSessionTypeLabel(row.planned.type) || row.planned.type });
      }
      pts.sort((a, b) => a.t - b.t);
      const fmtDelta = (v) => (Math.abs(v) < 5 ? "On pace" : `${v > 0 ? "+" : "−"}${secToMMSS(Math.abs(v))} /km`);
      const rows = pts.slice().reverse().slice(0, 10).map((p) => ({ label: p.type, sub: formatDateShort(new Date(p.t)), value: fmtDelta(p.v), tone: Math.abs(p.v) < 5 || p.v < 0 ? "ok" : p.v > 20 ? "bad" : "warn" }));
      const headline = paceFit ? fmtDelta(paceFit.deltaSecPerKm).replace(" /km", "") : "—";
      return { title: "Pace fit", color: "var(--accent)", value: headline, unit: paceFit && Math.abs(paceFit.deltaSecPerKm) >= 5 ? "/km" : "", verdict: paceFit ? `${paceFit.sessions} session${paceFit.sessions === 1 ? "" : "s"} · last ${paceFit.windowDays} d` : "", period: "Per session · last 8 weeks", chart: { type: "signed", points: pts, zeroLine: true, yFormat: (v) => `${v > 0 ? "+" : v < 0 ? "−" : ""}${Math.abs(Math.round(v))}s` }, rows, note: "Each bar is one completed session against its prescribed pace — above the line slower than plan, below it faster. The headline weights tempo and long runs most." };
    }
    if (statSheet === "load") {
      const wks = weeksBack(8);
      const points = wks.map((w) => ({ t: w, v: actualWeeklyMap.get(w) ?? 0, v2: plannedWeeklyMap.get(w) ?? null }));
      const rows = wks.slice().reverse().map((w) => { const a = actualWeeklyMap.get(w) ?? 0; const p = plannedWeeklyMap.get(w); return { label: `Week of ${weekLabelOf(w)}`, sub: p != null ? `of ${fmt1(p)} km planned` : "No plan", value: `${fmt1(a)} km`, tone: p == null ? null : a >= p * 0.95 ? "ok" : a >= p * 0.8 ? "warn" : "bad" }; });
      return { title: "Weekly load", color: "var(--accent)", value: fmt1(weeklyLoad.actualKm), unit: weeklyLoad.hasPlan ? `/ ${fmt1(weeklyLoad.plannedWeekKm)} km` : "km", verdict: "this week", period: "Run against planned · last 8 weeks", chart: { type: "bars", points, kind: "km", yFormat: (v) => String(Math.round(v)) }, legend: [{ label: "Run" }, { label: "Planned", hollow: true }], rows, note: "This week counts what has been run so far against the full week's plan." };
    }
    if ((statSheet === "fitness" || statSheet === "form") && fitnessForm) {
      const hist = fitnessForm.history || [];
      const isFit = statSheet === "fitness";
      const points = hist.map((p) => ({ t: p.ts, v: isFit ? p.ctl : p.tsb }));
      const sample = hist.filter((_, i) => i === hist.length - 1 || (hist.length - 1 - i) % 7 === 0).slice(-8).reverse();
      const rows = sample.map((p) => ({ label: formatDateShort(new Date(p.ts)), sub: isFit ? `fatigue ${Math.round(p.atl)}` : "", value: isFit ? String(Math.round(p.ctl)) : `${p.tsb >= 0 ? "+" : ""}${Math.round(p.tsb)}` }));
      return { title: isFit ? "Fitness" : "Form", color: isFit ? "var(--accent)" : "var(--content-secondary)", value: isFit ? String(fitnessForm.ctl) : `${fitnessForm.tsb >= 0 ? "+" : ""}${fitnessForm.tsb}`, verdict: isFit ? (fitnessForm.ctlDelta7d != null ? `${fitnessForm.ctlDelta7d >= 0 ? "+" : ""}${fitnessForm.ctlDelta7d} in 7 days` : "") : "fitness minus fatigue", period: `Daily · last ${hist.length} days`, chart: { type: "line", points, zeroLine: !isFit, yFormat: (v) => `${!isFit && v > 0 ? "+" : ""}${Math.round(v)}` }, rows, note: isFit ? "Chronic training load: a 42-day weighted average of session stress. It builds slowly and decays slowly." : "Form is fitness minus fatigue. Above zero you are fresh; between −10 and +5 is balanced; below −25 is overreaching." };
    }
    if (statSheet === "hr" && serverMetrics && serverMetrics.heartRate && serverMetrics.heartRate.available) {
      const hr = serverMetrics.heartRate;
      const series = (hr.series || []).map((p) => ({ t: +parseLocalDate(p.date), v: p.beatsPerKm, n: p.n }));
      const rows = series.slice().reverse().slice(0, 8).map((p) => ({ label: `Week of ${weekLabelOf(p.t)}`, sub: `${p.n} easy run${p.n === 1 ? "" : "s"}`, value: `${p.v} beats/km` }));
      const eff = hr.efficiency;
      const trendWord = eff && eff.trend === "improving" ? "improving" : eff && eff.trend === "slowing" ? "slipping" : eff && eff.trend ? "steady" : "";
      return { title: "Aerobic efficiency", color: "var(--viz-measure)", value: eff ? String(eff.beatsPerKm) : "—", unit: "beats / km", verdict: trendWord, period: `Weekly · last ${series.length} weeks`, chart: { type: "line", points: series, yFormat: (v) => String(Math.round(v)) }, rows, note: "Heartbeats per kilometre on easy running, 28-day windows week by week. Fewer beats for the same pace is the aerobic engine improving." };
    }
    return null;
  }, [statSheet, serverMatchedSessions, serverPlan, observedThresholdPaceSec, adherence, paceFit, weeklyLoad, actualWeeklyMap, plannedWeeklyMap, fitnessForm, serverMetrics, ADHERENCE_WINDOW_DAYS]);

  /* -------------------------
     Plan Trajectory
     ----------------------------------------------------------------------
     The in-flight projected finish, derived from plan execution.
     Composite of TWO inputs:

       1. Pace Fit  — weighted pace ratio over completed quality sessions
                      in the last 14 days. Tells us "when you're running,
                      are you hitting prescribed paces?"
       2. Adherence — % of planned sessions in the last 28 days that
                      were actually completed. Tells us "are you doing
                      the work the plan prescribed?"

     Why both: pace alone is misleading. A user who completes 5 of 16
     sessions but runs them at goal pace would otherwise show "ON PLAN"
     — that's nonsense; the missed long runs and tempos are precisely
     what build the endurance the race demands. Pace measures intensity,
     adherence measures volume. Both are required to project a finish
     time the user can trust.

     Logic:
       - Each input maps to one of three states ('onplan' / 'slightly' /
         'behind') with its own thresholds.
       - Final status = the WORSE of the two. A perfect pace doesn't
         rescue terrible adherence, and great adherence doesn't rescue
         a runner who's missing all paces.
       - `cause` flag tells the UI which factor (pace / adherence /
         both) is dragging the status — so the hero card can render a
         specific message rather than a generic "behind plan".

     Adherence thresholds (calibrated against §Pillar 1 — most users
     hit 85–95% on a working plan; below 65% the projection is unsafe):
       onplan   ≥ 0.85  (≥85% of sessions completed)
       slightly ≥ 0.65  (65–84%)
       behind   < 0.65

     `projectedSec` still uses the pace-only formula (goalTimeSec ×
     paceFit.ratio) — that's the most calibrated number we have for
     what the user's *current pace* projects to. The status caveats
     it; users with low adherence see "BEHIND" with a "X of Y missed"
     subtitle, which is the honest framing.
  ------------------------- */
  const planTrajectory = useMemo(() => {
    if (!hasSavedGoal || !goalTimeSec || !paceFit) return null;

    const adhScore = Number(adherence?.score ?? 0);
    const adhTotal = Number(adherence?.details?.total ?? 0);

    // Adherence penalty on the projected finish time. Without this, a
    // user could see paceFit.ratio < 1.00 (because they ran their few
    // completed sessions faster than threshold) and get a projected
    // finish FASTER than their goal — even at 31% adherence. That's
    // the bug Rafa hit on 2026-04-28: RESET Score 38 + projected
    // 11:58 vs goal 12:00.
    //
    // Model: penalty scales linearly from 1.0 (full adherence) to
    // 1.10 (zero adherence). 10% is the cap because beyond that the
    // projection is meaningless anyway — the BEHIND label + "X of Y
    // missed" subtitle carries the rest of the story.
    //
    // Gate: apply once there's at least ONE planned session in the
    // 28-day window. The previous threshold of 5 was too aggressive —
    // when the user just regenerated their plan (or just started),
    // the metricsEngine only counts sessions with status !== 'upcoming'
    // (i.e. past-due sessions in the window), so total can legitimately
    // be 1–4 even on an active plan. Skipping the penalty in that range
    // produced the "Trending 11:58" bug at 31% adherence on 2026-04-28
    // where total was 1. Threshold of 1 is the lowest meaningful gate;
    // total=0 means no real data and is correctly excluded.
    const ADH_PENALTY_MAX = 0.10;
    const adhPenaltyActive = adhTotal >= 1;
    const adhPenalty = adhPenaltyActive
      ? 1 + Math.max(0, Math.min(1, 1 - adhScore)) * ADH_PENALTY_MAX
      : 1;

    // Compute the raw projection then apply a defensive coherence clamp:
    // when adherence is below 0.50, the displayed projection MUST NOT be
    // faster than the goal time, no matter what other logic produced.
    // This is belt-and-braces — the multiplicative penalty above already
    // produces projected ≥ goal at low adherence with normal paceRatio,
    // but a future caller that constructs projectedSec from raw paceFit
    // would skip the penalty and re-introduce the "RESET 38 + 11:58 vs
    // goal 12:00" contradiction. The clamp is a hard guarantee.
    const rawProjectedSec = goalTimeSec * paceFit.ratio * adhPenalty;
    const projectedSec =
      adhPenaltyActive && adhScore < 0.50 && rawProjectedSec < goalTimeSec
        ? goalTimeSec
        : rawProjectedSec;
    const deltaSec = projectedSec - goalTimeSec;

    const adhStatus =
      adhScore >= 0.85 ? 'onplan' :
      adhScore >= 0.65 ? 'slightly' :
                         'behind';
    const paceStatus = paceFit.status;
    const order = { onplan: 0, slightly: 1, behind: 2 };
    const finalStatus =
      order[paceStatus] >= order[adhStatus] ? paceStatus : adhStatus;
    const cause =
      finalStatus === 'onplan'
        ? null
        : paceStatus !== 'onplan' && adhStatus !== 'onplan'
          ? 'both'
          : paceStatus !== 'onplan'
            ? 'pace'
            : 'adherence';

    const label =
      finalStatus === 'onplan'   ? 'ON PLAN' :
      finalStatus === 'slightly' ? 'SLIGHTLY BEHIND' :
                                   'BEHIND';
    return {
      projectedSec,
      deltaSec,
      ratio: paceFit.ratio,
      adhPenalty,
      status: finalStatus,
      label,
      sessions: paceFit.sessions,
      windowDays: paceFit.windowDays,
      paceStatus,
      adhStatus,
      cause,
      adherenceScore: adhScore,
      sessionsCompleted: adherence?.details?.matched || 0,
      sessionsPlanned: adherence?.details?.total || 0,
    };
  }, [hasSavedGoal, goalTimeSec, paceFit, adherence]);

  /* -------------------------
     Coach — Adaptation Engine v1 (read-only surface).
     Deterministic proposal from current KPI state; rules + guardrails in
     /docs/training_methodology.md §"Adaptation Rules". The engine runs
     client-side via the lib mirror (same pattern as matchPlan). v1 shows
     the proposal on Today; one-tap apply lands with the apply-path.
  ------------------------- */
  // ONE source: the server's /api/adaptation/evaluate (the same call the
  // Coach tab makes). The Today teaser used to run the client mirror on
  // client-computed KPIs — different pace-drift maths, a hard-coded
  // hard-coded three-run count that bypassed the ≥ 3-easy-runs gate (G6), no
  // cooldown — so Today and Coach could disagree. The server evaluation is
  // idempotent (an open proposal for the same rule is reused).
  const [coachProposal, setCoachProposal] = useState(null);
  useEffect(() => {
    if (!hasSavedGoal || !serverPlan?.weeks?.length) { setCoachProposal(null); return undefined; }
    let alive = true;
    api.post("/api/adaptation/evaluate", {})
      .then((r) => { if (alive) setCoachProposal((r && r.proposal) || null); })
      .catch(() => { if (alive) setCoachProposal(null); });
    return () => { alive = false; };
  }, [hasSavedGoal, serverPlan, serverMetrics]);

  /* The Today teaser and the nav dot announce a proposal. They are a
     NOTIFICATION, so they have to behave like one: they appear when there is
     something to review and they go once it has been read. Until now they
     were a pure function of the engine's live verdict — which keeps firing
     for as long as the underlying condition holds — so "Coach · 1 proposal"
     sat on the main screen permanently, including after it had been opened,
     read, and even declined.
     Read is per proposal, keyed by rule and plan week: a NEW proposal, or
     the same rule next week, announces itself again. Local, in try/catch —
     a browser with storage blocked just keeps showing the teaser, which is
     the right way round for a notification. */
  const coachTeaserKey = coachProposal && (coachProposal.action === "reduce_volume" || coachProposal.action === "cap_volume")
    ? `${(coachProposal.reason && coachProposal.reason.rule) || "x"}|${serverMetrics?.plan?.currentWeek ?? "?"}`
    : null;
  const [coachTeaserSeen, setCoachTeaserSeen] = useState(() => {
    try { return localStorage.getItem(COACH_TEASER_KEY) || null; } catch (_) { return null; }
  });
  const coachNeedsReview = !!coachTeaserKey && coachTeaserKey !== coachTeaserSeen;
  const markCoachRead = () => {
    if (!coachTeaserKey) return;
    try { localStorage.setItem(COACH_TEASER_KEY, coachTeaserKey); } catch (_) {}
    setCoachTeaserSeen(coachTeaserKey);
  };

  // Format seconds → "H:MM" for the projected-finish range labels.
  const fmtFinishHM = (sec) => {
    if (!sec || !isFinite(sec)) return '—';
    const h = Math.floor(sec / 3600);
    const m = Math.round((sec % 3600) / 60);
    return `${h}:${String(m).padStart(2, '0')}`;
  };
  // Format a signed delta as "+0:08" / "−0:12" / "+12m" — h:mm if ≥ 1h, else minutes.
  const fmtDeltaHM = (sec) => {
    if (sec == null || !isFinite(sec)) return '';
    const a = Math.abs(Math.round(sec));
    const sign = sec < 0 ? '−' : '+';
    if (a < 60) return `${sign}${a}s`;
    if (a < 3600) return `${sign}${Math.round(a / 60)}m`;
    const h = Math.floor(a / 3600);
    const m = Math.round((a % 3600) / 60);
    return `${sign}${h}:${String(m).padStart(2, '0')}`;
  };

  /* =========================
     Render
  ========================= */
  /* ---- renderable page content (reused inside phone frame or bare) ---- */
  const pageContent = (
    <div className="page">
      {/* ===== HEADER (mobile-first column, shown at all widths) ===== */}
      <header className="mobileHeader">
        <img src="/logo.svg" height="32" alt="RESET Ultra" className="mobileHeaderLogo" />
        <button className="mobileHeaderGear" onClick={() => setSettingsOpen(true)} aria-label="Settings">
          <GearIcon />
        </button>
      </header>

      {/* ===== ONBOARDING GATE — covers the app until Strava is connected.
           Strava OAuth is the only login (locked decision). Instead of an
           empty dashboard, a new user sees exactly three steps and one
           button. Renders above content + tab bar, below modals. ===== */}
      {!loadingSession && !conn?.data && (
        <div className="onboardGate">
          <div className="onboardGate__inner">
            <img src="/logo.svg" height="40" alt="RESET Ultra" className="onboardGate__logo" />
            <h1 className="onboardGate__title">Training plans for ultra distances</h1>
            <p className="onboardGate__sub">Built from your own running history. Honest about what your body can absorb.</p>
            <ol className="onboardSteps">
              <li className="onboardStep onboardStep--active">
                <span className="onboardStep__num">1</span>
                <span className="onboardStep__body"><strong>Bring your runs</strong><em>Your history becomes your baseline — no forms, no guesses.</em></span>
              </li>
              <li className="onboardStep">
                <span className="onboardStep__num">2</span>
                <span className="onboardStep__body"><strong>Set your race</strong><em>Distance, date, terrain. The generator does the rest.</em></span>
              </li>
              <li className="onboardStep">
                <span className="onboardStep__num">3</span>
                <span className="onboardStep__body"><strong>Run the plan</strong><em>Every metric tells you what to do next.</em></span>
              </li>
            </ol>
            {hasHealth ? (
              <button className="onboardCta onboardCta--health" onClick={() => runHealthSync({ full: true })} disabled={importState.busy}>
                <Activity size={16} /> Import from {healthName}
              </button>
            ) : null}
            <button className={hasHealth ? "onboardCta onboardCta--next" : "onboardCta"} onClick={connectStrava}>
              <LinkIcon size={16} /> Connect with Strava
            </button>
            <button type="button" className="onboardLink" onClick={pickActivityFiles} disabled={importState.busy}>
              Upload .fit or .gpx files instead
            </button>
            {stravaStatus ? <div className="onboardGate__note">{stravaStatus}</div> : null}
            {stravaError ? <div className="onboardGate__note deltaNeg" style={{ color: "var(--critical)" }}>{stravaError}</div> : null}
            {importState.msg ? <div className="onboardGate__note" role="status">{importState.msg}</div> : null}
            {importState.error ? <div className="onboardGate__note deltaNeg" style={{ color: "var(--critical)" }}>{importState.error}</div> : null}
            <div className="onboardGate__note">{hasHealth ? "Garmin, Coros, Suunto and Apple Watch runs reach RESET through " + healthName + "." : "Export a run from Garmin Connect, Coros or Suunto as .fit — or connect Strava."}</div>
          </div>
        </div>
      )}

      {/* Shared picker for .fit / .gpx uploads (onboarding + Settings). */}
      <input
        ref={importFileRef}
        type="file"
        accept=".fit,.gpx,application/gpx+xml,application/octet-stream"
        multiple
        hidden
        onChange={onActivityFiles}
      />

      {/* ===== MAIN CONTENT (mobile-first column, shown at all widths) ===== */}
      <div className="mobileContent">
        {/* TODAY TAB */}
        {activeTab === "today" && (() => {
          /* Today = action surface. Design system: Hero + Strip.
               - RESET SCORE     : overall hero, stays prominent.
               - HERO CARD       : next session, themed by session type
                                   (easy=green, tempo=amber, long=purple,
                                   hills=red, rest=muted). Whole card
                                   tappable → detail sheet.
               - STRIP (3 cells) : This week / Last run / Goal. Each cell
                                   tinted by its own status (ontrack=green,
                                   warning=amber, off=red, neutral=grey). */

          const now = new Date();
          const mon = startOfWeek(now);
          const sun = new Date(+mon + 7 * 864e5);
          const todayStart = +startOfDay(now);

          /* THIS WEEK as segments: every planned session of the week, in
             date order, each done / missed / still to do. Matched rows are
             keyed exactly as futureSessionsAll keys them. The old "N left"
             counted from midday, so a session due today read as gone. */
          /* The matcher's own rows: one per planned session, the same set the
             score and the Stats list judge. (plannedSessionsFull is filtered
             by the day picker, which hid a missed Saturday here.) */
          const weekRows = (serverMatchedSessions || [])
            .filter((row) => row && row.planned && row.date && String(row.planned.type || "").toLowerCase() !== "race")
            .map((row) => ({ row, d: startOfDay(parseLocalDate(row.date)) }))
            .filter((x) => +x.d >= +mon && +x.d < +sun)
            .sort((a, b) => +a.d - +b.d);
          const weekSegs = weekRows.length
            ? weekRows.map(({ row, d }) => {
                if (row.actual && row.actual.id != null) return "done";
                const st = String(row.status || "").toLowerCase();
                if (st === "missed") return "missed";
                return +d < todayStart ? "missed" : "todo";
              })
            : (plannedSessionsFull || [])
                .filter((s) => +s.date >= +mon && +s.date < +sun && s.kind !== "race" && String(s.type || "").toLowerCase() !== "race")
                .sort((a, b) => +a.date - +b.date)
                .map((s) => (+startOfDay(s.date) < todayStart ? "missed" : "todo"));
          const weekDone = weekSegs.filter((x) => x === "done").length;
          const weekMissed = weekSegs.filter((x) => x === "missed").length;
          const weekLeft = weekSegs.filter((x) => x === "todo").length;
          /* Sessions due today and not yet run. The load verdict judges
             kilometres due up to yesterday, so on a day with a run it said
             "Nothing due yet". */
          const weekDueToday = weekRows.length
            ? weekRows.filter(({ d }, i) => +d === todayStart && weekSegs[i] === "todo").length
            : (plannedSessionsFull || []).filter((s) => +startOfDay(s.date) === todayStart && s.kind !== "race" && String(s.type || "").toLowerCase() !== "race").length;
          const weekV0 = weeklyLoadVerdict(weeklyLoad);
          const weekV = weeklyLoad.hasPlan && weeklyLoad.status === "not-due" && weekDueToday > 0
            ? { tone: weekV0.tone, label: `${weekDueToday} due today` }
            : weekV0;
          const weekChip = !weeklyLoad.hasPlan ? null
            : weekLeft > 0 ? { text: `${weekLeft} left`, tone: "neutral" }
            : weekMissed > 0 ? { text: `${weekMissed} missed`, tone: "critical" }
            : weekSegs.length > 0 ? { text: "All done", tone: "positive" } : null;
          const week = {
            tone: weeklyLoad.hasPlan ? weekV.tone : "neutral",
            sub: weeklyLoad.hasPlan ? weekV.label : "No plan yet",
            hasPlan: !!weeklyLoad.hasPlan,
            actualKm: weeklyLoad.actualKm || 0,
            plannedKm: Math.round(weeklyLoad.plannedWeekKm || 0),
            segs: weekSegs,
            chip: weekChip,
            aria: weeklyLoad.hasPlan
              ? `This week: ${fmt1(weeklyLoad.actualKm || 0)} of ${Math.round(weeklyLoad.plannedWeekKm || 0)} kilometres, ${weekDone} of ${weekSegs.length} sessions done, ${weekV.label}. Opens Train.`
              : "This week: no plan yet. Opens Train.",
          };

          // LAST RUN — most recent COMPLETED Strava activity, whatever the
          // Stats list is showing. Must have a real `actual` with an id; a
          // planned-only row never appears here.
          const lr = lastRunRow;
          const lrDp = lr && lr.plannedP != null && lr.actualP != null ? lr.actualP - lr.plannedP : null;
          const lrBadge = lr ? statusBadge(lr.status, lrDp) : null;
          const lrTitle = lr?.planned?.title || lr?.actual?.name || lr?.dateLabel || "";
          const lrKm = lr?.actualKm != null ? lr.actualKm : lr?.plannedKm;
          const tdType = (typeStr) => {
            const s = String(typeStr || "").toLowerCase();
            const glyph = s.includes("tempo") || s.includes("threshold") || s.includes("interval") ? "T"
              : s.includes("long") || s.includes("b2b") || s.includes("back-to-back") || s.includes("time-on-feet") || s.includes("time on feet") ? "L"
              : s.includes("vertical") || s.includes("hill") ? "V"
              : s.includes("hike") ? "H"
              : s.includes("recovery") || s.includes("shakeout") || s.includes("walk") ? "R"
              : s.includes("easy") ? "E" : "·";
            return { glyph, v: sessionTypeColor(typeStr) };
          };
          const lrType = tdType(lr?.planned?.title || lr?.planned?.type || lr?.actual?.name);
          const lrTone = !lrBadge ? "neutral" : lrBadge.tone === "ok" ? "ontrack" : lrBadge.tone === "warn" ? "warning" : lrBadge.tone === "danger" ? "off" : "neutral";
          const lrStatus = String(lr?.status || "").toLowerCase();
          const lrChipTone = !lrBadge ? "neutral" : lrBadge.tone === "ok" ? "positive" : lrBadge.tone === "warn" ? "caution" : lrBadge.tone === "danger" ? "critical" : "neutral";
          const lrChip = !lr ? null
            : lrStatus === "partial" ? { text: "Partial", tone: "caution" }
            : lrStatus === "extra" ? { text: "Extra", tone: "neutral" }
            : lrDp != null ? (Math.abs(lrDp) < 5 ? { text: "On pace", tone: "positive" } : { text: `${lrDp < 0 ? "−" : "+"}${Math.round(Math.abs(lrDp))} s/km`, tone: lrChipTone })
            : lr.actualP ? { text: secPerKmToPace(lr.actualP), tone: "neutral" } : null;
          const lrZone = lr ? hrZoneLabel(lr.actual?.avg_hr, effectiveMaxHr) : null;
          const lrZoneColor = lrZone ? (lrZone.zone === "Z1" ? "var(--info-base)" : lrZone.zone === "Z2" ? "var(--accent-base)" : lrZone.zone === "Z5" ? "var(--critical)" : "var(--caution)") : null;
          const run = {
            empty: !lr,
            tone: lrTone,
            glyph: lr ? lrType.glyph : "—",
            typeVar: lrType.v,
            km: lr ? lrKm : null,
            sub: lr ? `${lrTitle} · ${lr.dateLabel || formatDateShort(lr.date)}` : "No runs synced yet",
            chip: lrChip,
            zone: lrZone ? { zone: lrZone.zone, color: lrZoneColor } : null,
            aria: lr ? `Last run: ${lrTitle}, ${fmt1(lrKm || 0)} kilometres on ${lr.dateLabel || ""}${lrChip ? `, ${lrChip.text}` : ""}. Opens details.` : "Last run: no runs synced yet.",
          };

          // GOAL — the likelihood arc only once there is execution to judge;
          // a brand-new plan is never called At risk on day one.
          const goalScore = likelihood?.score ?? 0;
          const hasExecution = (serverMatchedSessions || []).some((r) => r && r.planned && String(r.status || "").toLowerCase() === "matched");
          const goalHasScore = !!goalSaved?.date && !!serverMetrics?.goalLikelihood && hasExecution;
          const daysToRace = goalDateObj ? Math.max(0, Math.ceil((+startOfDay(goalDateObj) - todayStart) / 864e5)) : null;
          const goalDateStr = goalDateObj ? formatDateShort(goalDateObj) : "—";
          /* With a target time the row speaks the trajectory's language, so
             it never says "On track" above a hero that says "+1:31 vs goal". */
          const trajStatus = planTrajectory && goalTimeStr ? planTrajectory.status : null;
          const goalChip = !goalSaved?.date ? { text: "Set goal", tone: "accent" }
            : trajStatus ? (trajStatus === "onplan" ? { text: "On plan", tone: "positive" } : { text: `${fmtDeltaHM(planTrajectory.deltaSec)} vs goal`, tone: trajStatus === "slightly" ? "caution" : "critical" })
            : !goalHasScore ? null
            : goalScore >= 65 ? { text: "Likely", tone: "positive" }
            : goalScore >= 40 ? { text: "Uncertain", tone: "caution" }
            : { text: "At risk", tone: "critical" };
          const goal = {
            tone: !goalSaved?.date ? "neutral"
              : trajStatus ? (trajStatus === "onplan" ? "ontrack" : trajStatus === "slightly" ? "warning" : "off")
              : !goalHasScore ? "neutral" : goalScore >= 65 ? "ontrack" : goalScore >= 40 ? "warning" : "off",
            sub: goalSaved?.date ? `${goalSaved.type || "Goal"} · ${goalDateStr}` : "No goal set",
            days: goalSaved?.date ? daysToRace : null,
            score: goalHasScore ? Math.round(goalScore) : null,
            chip: goalChip,
            aria: goalSaved?.date
              ? `Goal: ${goalSaved.type || ""} on ${goalDateStr}, ${daysToRace} days to go${goalChip ? `, ${goalChip.text}` : ""}. Adjust goal.`
              : "Goal: no goal set. Set a goal.",
          };

          // PLAN TRACK — the phases of the plan as one bar, the current week
          // on it. Phase colours are the pill palette Train uses.
          const planWeeks = serverPlan?.weeks || [];
          let track = null;
          if (hasSavedGoal && planWeeks.length) {
            const totalWeeks = planWeeks.length;
            const baseline = Math.round(estimateWeeklyKmNow(actualWeeklyMap) || 0);
            const peak = Math.round(Math.max(0, ...planWeeks.map((w) => Number(w.plannedKm) || 0)));
            // Today's week by DATE inside the plan — the anchor-based count
            // disagreed with the hero and Train whenever the plan carried
            // weeks from before the anchor.
            const thisWkMs = +startOfWeek(now);
            const byDate = planWeeks.findIndex((w) => w && w.sessions && w.sessions.length && +startOfWeek(parseLocalDate(w.sessions[0].date)) === thisWkMs);
            const weekIdx = byDate >= 0 ? byDate + 1 : Math.max(1, Math.min(totalWeeks, Math.floor((+startOfDay(new Date()) - +planStartDate) / (7 * 864e5)) + 1));
            const regions = [];
            let curr = null, count = 0, startIdx = 0;
            for (let i = 0; i < planWeeks.length; i++) {
              const w = planWeeks[i];
              if (!w?.sessions?.length) continue;
              const wkStart = +startOfWeek(parseLocalDate(w.sessions[0].date));
              const info = phaseByWeek?.get(wkStart) || { phase: w.phase || "base" };
              if (curr !== info.phase) {
                if (curr !== null) regions.push({ phase: curr, start: (startIdx / totalWeeks) * 100, width: (count / totalWeeks) * 100 });
                curr = info.phase; startIdx = i; count = 0;
              }
              count += 1;
            }
            if (curr !== null) regions.push({ phase: curr, start: (startIdx / totalWeeks) * 100, width: (count / totalWeeks) * 100 });
            const currentRegion = regions.find((r) => weekIdx > Math.round((r.start / 100) * totalWeeks) && weekIdx <= Math.round(((r.start + r.width) / 100) * totalWeeks)) || regions[0];
            const liveInfo = phaseByWeek && phaseByWeek.get(thisWkMs);
            const currentPhase = (liveInfo && liveInfo.phase) || (currentRegion && currentRegion.phase) || "base";
            const phaseFg = (p) => (PHASE_PILL_COLOURS[p] || PHASE_PILL_COLOURS.base).fg;
            const phaseLabel = currentPhase.charAt(0).toUpperCase() + currentPhase.slice(1);
            track = {
              weekIdx,
              totalWeeks,
              phaseLabel,
              phaseFg: phaseFg(currentPhase),
              isRecovery: !!(phaseByWeek && phaseByWeek.get(+startOfWeek(now)) && phaseByWeek.get(+startOfWeek(now)).isRecoveryWeek),
              todayPct: ((weekIdx - 0.5) / totalWeeks) * 100,
              raceDateStr: goalDateObj ? formatDateShort(goalDateObj) : "",
              regions: regions.map((r) => ({ start: r.start, width: r.width, fg: phaseFg(r.phase) })),
              baseline,
              peak,
              wksLeftInPhase: currentRegion ? Math.max(0, Math.round(((currentRegion.start + currentRegion.width) / 100) * totalWeeks) - weekIdx) : 0,
            };
          }

          // Hero theme from next session type. Map to a CSS modifier class,
          // a single-letter background glyph, a header label, and a short
          // "purpose" line. Falls back to rest-day theme if no next session.
          const next = nextRuns?.[0];
          const typeKey = String(next?.type || "").toLowerCase();
          const heroTheme =
            typeKey.includes("tempo")      ? { cls: "tempo", glyph: "T", title: "TEMPO",    purpose: "Lactate threshold. Comfortably hard, Z3-Z4 HR." }
          : typeKey.includes("long") || typeKey.includes("b2b") || typeKey.includes("time-on-feet") || typeKey.includes("time on feet")
                                          ? { cls: "long",  glyph: "L", title: "LONG",     purpose: "Endurance. Time on your feet, Z2 HR, no pace pressure." }
          : typeKey.includes("vertical") || typeKey.includes("hill")
                                          ? { cls: "hills", glyph: "V", title: "VERTICAL", purpose: "Hill power. Hike the steep parts. Accumulate vert." }
          : typeKey.includes("hike")     ? { cls: "long",  glyph: "H", title: "HIKE",     purpose: "Active recovery. Trail hike, easy pace." }
          : typeKey.includes("recovery") ? { cls: "easy",  glyph: "R", title: "RECOVERY", purpose: "Recovery-easy. Genuinely easy. Protect adaptation." }
          : typeKey.includes("easy")     ? { cls: "easy",  glyph: "E", title: "EASY",     purpose: "Aerobic base. Conversational pace, Z2 HR throughout." }
          :                                { cls: "rest",  glyph: "·", title: "REST DAY", purpose: "Rest day. Recovery is part of training." };

          const nextZoneClass = next?.zone === "Z1" || next?.zone === "Z2" || next?.zone === "Z1–Z2" ? "green"
                              : next?.zone === "Z5" ? "red" : "amber";
          const nextPace = next?.paceSecPerKm ? secPerKmToPace(next.paceSecPerKm).replace("/km","") : null;
          const nextDur = next?.durationMin ? fmtDuration(next.durationMin, "min") : null;
          const nextIsToday = !!next?.date && formatRelativeDay(next.date) === "Today";

          return (
            <div className="tabPage">
              {/* RESET score hero — phase pill sits directly under the
                  insight line so the phase context (Base/Build/Specific/
                  Taper) reads in the right place: it qualifies "what kind
                  of week is this?", which is closer to the score's
                  storyline than to the raw kpi line below. */}
              <div className="resetScoreCard">
                <div className="resetScoreLabel">RESET SCORE <InfoTooltip text="Your RESET Score (0–100) combines plan adherence (25%), volume consistency (20%), pace trend (20%), goal-pace fit (25%), and elevation compliance (10% trail). Above 65 = on track, 40–65 = building, below 40 = needs attention. The Projected finish line below answers a separate question: where current fitness lands you on race day." isMobile={isMobile} /></div>
                <div className="resetScoreValue" style={{
                  color: resetScore.pending ? "var(--text-muted)" : resetScore.score >= 65 ? "var(--accent)" : resetScore.score >= 40 ? "var(--warning)" : "var(--danger)",
                  /* Presentational only: exposes the already-computed score
                     to CSS so the theme can draw the progress ring from it
                     (see .resetScoreValue in src/styles/theme.css). Nothing
                     reads it back; the value itself is untouched. */
                  "--score": resetScore.pending ? 0 : Number(resetScore.score) || 0,
                }}>
                  {resetScore.pending
                    ? "—"
                    : <CountUpNumber value={resetScore.score} storageKey="reset_score_countup_seen_v1" />}
                </div>
                <div className="resetScoreInsight">
                  {resetScore.pending
                    ? "Generate your plan to activate the score."
                    : resetScore.provisional
                    ? "Starting score from your baseline — it sharpens with every run you log."
                    : resetScore.score >= 65 ? "On track. Trust the plan." : resetScore.score >= 40 ? "Building. Stay consistent." : "Needs attention. Consider adjustments."}
                </div>

                {/* Plan trajectory line — derived from how you're hitting
                    the plan's prescribed paces, not from raw fitness Riegel.
                    "On plan" means the recent quality sessions are landing
                    at planned pace; the projection is your goal time exactly.
                    "Slightly / Behind" means paces have drifted, and the
                    projection drifts proportionally. */}
                {planTrajectory && goalTimeStr && (
                  <div className="resetScoreFinish">
                    <span className="resetScoreFinish__label">Plan trajectory</span>
                    <span className="resetScoreFinish__value">
                      {planTrajectory.status === 'onplan'
                        ? <>Trending {goalTimeStr}</>
                        : <>Trending {fmtFinishHM(planTrajectory.projectedSec)}</>}
                    </span>
                    <span
                      className={`resetScoreFinish__pill resetScoreFinish__pill--${planTrajectory.status === 'onplan' ? 'inrange' : planTrajectory.status === 'slightly' ? 'behind' : 'behind'}`}
                    >
                      {planTrajectory.status === 'onplan'
                        ? 'on plan'
                        : `${fmtDeltaHM(planTrajectory.deltaSec)} vs goal`}
                    </span>
                  </div>
                )}
                {(() => {
                  const wkInfo = hasSavedGoal ? phaseByWeek?.get(+startOfWeek(new Date())) : null;
                  const phaseKey = wkInfo?.phase || null;
                  if (!phaseKey) return null;
                  const phaseLabel = phaseKey.charAt(0).toUpperCase() + phaseKey.slice(1);
                  const palette =
                    phaseKey === "base"     ? { fg: "var(--accent-on-tint)", bg: "rgb(var(--accent-rgb) / 0.16)",  br: "rgb(var(--accent-rgb) / 0.38)" } :
                    phaseKey === "build"    ? { fg: "var(--caution)", bg: "rgb(var(--caution-rgb) / 0.16)",  br: "rgb(var(--caution-rgb) / 0.38)" } :
                    phaseKey === "specific" ? { fg: "var(--critical)", bg: "rgb(var(--critical-rgb) / 0.16)",  br: "rgb(var(--critical-rgb) / 0.38)" } :
                    phaseKey === "taper"    ? { fg: "var(--content-secondary)", bg: "rgb(var(--neutral-rgb) / 0.22)",  br: "rgb(var(--neutral-rgb) / 0.45)" } :
                                              { fg: "var(--text-muted)", bg: "transparent", br: "var(--border)" };
                  return (
                    <div style={{ marginTop: 10, display: "flex", justifyContent: "center" }}>
                      <span style={{
                        fontSize: "var(--text-xs-size)",
                        fontWeight: 700,
                        letterSpacing: "0.10em",
                        textTransform: "uppercase",
                        padding: "4px 10px",
                        borderRadius: 999,
                        color: palette.fg,
                        background: palette.bg,
                        border: `1px solid ${palette.br}`,
                      }}>
                        {phaseLabel} phase{wkInfo?.isRecoveryWeek ? " · recovery" : ""}
                      </span>
                    </div>
                  );
                })()}
                {/* Contributing factors — the five components the score is
                    already built from (metricsEngine calcResetScore returns
                    them as `breakdown`). They were computed on every request
                    and shown nowhere, so the score was a number with no
                    account of itself. Pattern: a labelled row per factor with
                    a thin proportional bar, weakest first so the thing to act
                    on is at the top. */}
                {!resetScore.pending && resetScore.breakdown && (
                  <div className="factorList">
                    {/* Collapsed by default. Oura keeps the factor breakdown
                        one level in — the headline screen stays a single
                        answer, and the account of it is there when asked for. */}
                    <button
                      type="button"
                      className={`factorList__toggle factorList__toggle--quiet${scoreDetailsOpen ? " is-open" : ""}`}
                      aria-expanded={scoreDetailsOpen}
                      onClick={() => setScoreDetailsOpen((v) => !v)}
                    >
                      <span>{scoreDetailsOpen ? "Hide score details" : "Score details"}</span>
                      <ChevronDown size={16} aria-hidden="true" />
                    </button>
                    {scoreDetailsOpen && (
                    <div className="factorList__body">
                    <div className="factorList__label">What the score is made of</div>
                    {/* Each row says what it measures. "Elevation 0" with no
                        account of itself reads as a bug — it is only ever
                        present on a trail goal, and only once the week has
                        actually prescribed vert. */}
                    {Object.entries({
                      adherence: ["Adherence", "sessions completed, last 28 days"],
                      volume: ["Volume", "kilometres run against the plan"],
                      pace: ["Pace", "prescribed paces you are hitting"],
                      likelihood: ["Goal fit", "projected finish against your target"],
                      elevation: ["Elevation", "vert climbed against the plan — trail goals only"],
                    })
                      .map(([key, [label, note]]) => [label, resetScore.breakdown[key], note])
                      .filter(([, v]) => typeof v === "number" && isFinite(v))
                      .sort((a, b) => a[1] - b[1])
                      .map(([label, v, note]) => (
                        <div className="factorRow" key={label}>
                          <span className="factorRow__label">
                            {label}
                            <em className="factorRow__note">{note}</em>
                          </span>
                          <span className="factorRow__track">
                            <span
                              className={`factorRow__fill${v >= 65 ? " is-good" : v >= 40 ? " is-mid" : " is-low"}`}
                              style={{ width: `${Math.max(2, Math.min(100, v))}%` }}
                            />
                          </span>
                          <span className="factorRow__value">{Math.round(v)}</span>
                        </div>
                      ))}
                    </div>
                    )}
                  </div>
                )}
              </div>

              {/* Onboarding step 2 — connected, but no goal saved yet.
                  One clear next action instead of empty widgets. */}
              {conn?.data && !hasSavedGoal && (
                <button className="goalNudge" onClick={() => setShowPlanGenerator(true)}>
                  <span className="goalNudge__step">STEP 2 OF 3</span>
                  <span className="goalNudge__title">Set your race goal</span>
                  <span className="goalNudge__sub">Distance, date, terrain — your plan generates from your running history. Under a minute.</span>
                  <span className="goalNudge__cta">Set goal ›</span>
                </button>
              )}

              {/* Onboarding step 3 — goal saved but no plan generated yet.
                  saveGoal() opens the generator directly; this is the recovery
                  path for anyone who backs out of the wizard, so they are never
                  stranded on an empty Train tab with a score of 0. */}
              {conn?.data && hasSavedGoal && !serverPlan?.weeks?.length && (
                <button className="goalNudge" onClick={() => setShowPlanGenerator(true)}>
                  <span className="goalNudge__step">STEP 3 OF 3</span>
                  <span className="goalNudge__title">Generate your plan</span>
                  <span className="goalNudge__sub">Your goal is saved. Build the weekly sessions from your running history.</span>
                  <span className="goalNudge__cta">Generate plan ›</span>
                </button>
              )}

              {/* Today's session comes first: it is the one thing on this
                  screen to act on, and it used to sit a full scroll down,
                  under a "This week" row that said nothing was due. */}
              {next ? (
                <div
                  className={`heroCard heroCard--${heroTheme.cls}`}
                  role="button"
                  tabIndex={0}
                  onClick={() => setTrainSessionSheet(next)}
                  onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setTrainSessionSheet(next); } }}
                >
                  <div className="heroCard__glyph" aria-hidden="true">{heroTheme.glyph}</div>
                  <div className="heroCard__label">
                    <span className="heroCard__theme">▶ {heroTheme.title} · {nextIsToday ? "TODAY" : "NEXT SESSION"}</span>
                    {next.zone && <span className={`pvaZonePill pvaZonePill--${nextZoneClass}`}>{next.zone}</span>}
                  </div>
                  <div className="heroCard__name">
                    {next.title || next.label || next.type}
                    {next.distanceKm > 0 && <> · {fmt1(next.distanceKm)} km</>}
                  </div>
                  <div className="heroCard__meta">
                    {nextPace && <><strong>{nextPace}/km</strong>{(nextDur || next.date) && " · "}</>}
                    {nextDur && <><strong>{nextDur}</strong>{next.date && " · "}</>}
                    {next.date && (() => {
                      // Combined "relative · absolute" date for the next-session
                      // card: "Today · Wed 28 Apr", "Tomorrow · Thu 29 Apr",
                      // "In 3 days · Sat 1 May". Past 7 days the relative
                      // phrasing is dropped — at that distance the weekday
                      // and date alone read more clearly than "In 47 days".
                      const rel = formatRelativeDay(next.date);
                      const abs = next.dayLabel || formatDateShort(next.date);
                      return rel
                        ? <><strong>{rel}</strong>{" · "}{abs}</>
                        : abs;
                    })()}
                  </div>
                  <div className="heroCard__bottom">
                    <div className="heroCard__purpose"><strong>{heroTheme.title.charAt(0) + heroTheme.title.slice(1).toLowerCase()}.</strong> {heroTheme.purpose.split('. ').slice(1).join('. ')}</div>
                    <span className="heroCard__tap">Tap for details ›</span>
                  </div>
                </div>
              ) : (
                <div className="heroCard heroCard--rest">
                  <div className="heroCard__glyph" aria-hidden="true">·</div>
                  <div className="heroCard__label">
                    <span className="heroCard__theme">◎ REST · NO NEXT SESSION</span>
                  </div>
                  <div className="heroCard__name">Rest day</div>
                  <div className="heroCard__meta">No session scheduled. Recovery is part of training.</div>
                </div>
              )}

              <TodayRows
                week={week}
                run={run}
                goal={goal}
                track={track}
                onWeek={() => setActiveTab("train")}
                onRun={() => lr && setDetailSheet(lr)}
                onGoal={() => { if (!goalSaved?.date) { setShowPlanGenerator(true); return; } openAdjustGoalSheet(); }}
              />

              {/* "Was this a run?" — one unusually slow run at a time. */}
              {suspectRuns.length > 0 && (() => {
                const a = suspectRuns[0];
                const d = new Date(a.start_date);
                const km = a.distance_m / 1000;
                const pace = Math.round(a.moving_time_s / km);
                return (
                  <div className="suspectCard" role="group" aria-label="Was this a run?">
                    <div className="suspectCard__t1">Was this a run?</div>
                    <div className="suspectCard__t2">
                      {d.toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short" })} · {km.toFixed(2)} km · {Math.floor(pace / 60)}:{String(pace % 60).padStart(2, "0")}/km.
                      {" "}Much slower than you run, so it is left out of your finish forecast until you confirm.
                    </div>
                    <div className="suspectCard__actions">
                      <button type="button" className="btn btnGhost" disabled={classifyBusy} onClick={() => classifyActivity(String(a.id), "run")}>It was a run</button>
                      <button type="button" className="btn btnOk" disabled={classifyBusy} onClick={() => classifyActivity(String(a.id), "walk")}>It was a walk</button>
                    </div>
                  </div>
                );
              })()}

              {/* Coach teaser — one line, always tappable, opens the Coach
                  tab. Amber styling only when a proposal is pending;
                  otherwise a calm neutral line. Decisions before actions:
                  this sits above the next-session hero on purpose. */}
              {hasSavedGoal && (
                <button
                  type="button"
                  className={`coachLine${coachNeedsReview ? " coachLine--pending" : ""}`}
                  onClick={() => { markCoachRead(); setActiveTab("coach"); }}
                >
                  <span className="coachLine__av"><TabIconChat /></span>
                  <span className="coachLine__meta">
                    <span className="coachLine__t1">
                      {coachNeedsReview ? "Coach · 1 proposal" : "Coach"}
                    </span>
                    <span className="coachLine__t2">
                      {coachNeedsReview
                        ? `${coachProposal.headline} — tap to review.`
                        : "Ask anything about your plan."}
                    </span>
                  </span>
                  <span className="coachLine__chev">›</span>
                </button>
              )}

              {/* Rest of this week — compact list of remaining planned
                  sessions AFTER the "Next" hero session. Answers the "what
                  else is on my plate this week" question without forcing a
                  tab change. Rows are tappable → same routing as Train
                  (past → Stats detail, upcoming → editor).
                  Filter is forward-looking only — past sessions from earlier
                  this week have already happened and showed up as Matched/
                  Missed/Partial in Stats; surfacing them here as if still
                  upcoming was confusing. */}
              {(() => {
                const todayTs = +startOfDay(new Date());
                const sundayTs = +startOfWeek(new Date()) + 7 * 864e5;
                const nextTs = next ? +startOfDay(next.date) : null;
                // Lookup of already-completed planned sessions (matched OR
                // partial) so we hide a session that's scheduled for today
                // but already done this morning. Without this, a 13km Easy
                // run scheduled today and already logged at 6 a.m. would
                // still appear under "Rest of this week" as if upcoming.
                const matchedKey = (() => {
                  const m = new Map();
                  for (const r of (serverMatchedSessions || [])) {
                    if (!r?.planned) continue;
                    const d = r.planned.date;
                    const dateISO = typeof d === "string" ? d : `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}-${String(d.getDate()).padStart(2,"0")}`;
                    if (r.status === 'matched' || r.status === 'partial') {
                      m.set(`${dateISO}|${r.planned.type}`, true);
                    }
                  }
                  return m;
                })();
                const sessionDoneKey = (s) => {
                  const d = s.originalDate || s.date;
                  const dateISO = typeof d === "string" ? d : `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}-${String(d.getDate()).padStart(2,"0")}`;
                  return `${dateISO}|${s.type}`;
                };
                const remaining = (plannedSessionsFull || [])
                  .filter(s => {
                    const ts = +startOfDay(s.date);
                    if (ts < todayTs || ts >= sundayTs) return false;
                    if (ts === nextTs) return false;
                    if (matchedKey.has(sessionDoneKey(s))) return false;
                    return true;
                  })
                  .sort((a, b) => +a.date - +b.date);
                if (remaining.length === 0) return null;
                return (
                  <div className="todayWeekAhead">
                    <div className="todayWeekAhead__label">REST OF THIS WEEK</div>
                    <div className="todayWeekAhead__list">
                      {remaining.map((s, i) => {
                        const col = sessionTypeColor(s.title || s.type);
                        const title = s.title || s.label || s.type;
                        const zoneStr = s.zone || "";
                        const zoneClass = zoneStr === "Z1" || zoneStr === "Z2" || zoneStr === "Z1–Z2" ? "green" : zoneStr === "Z5" ? "red" : "amber";
                        return (
                          <div
                            key={i}
                            className="todayWeekAhead__row"
                            role="button"
                            tabIndex={0}
                            onClick={() => setTrainSessionSheet(s)}
                            onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setTrainSessionSheet(s); } }}
                          >
                            <span className="todayWeekAhead__accent" style={{ background: col }} />
                            <span className="todayWeekAhead__body">
                              <span className="todayWeekAhead__title">{title}</span>
                              <span className="todayWeekAhead__meta">
                                {s.distanceKm > 0 ? `${fmt1(s.distanceKm)} km · ` : ""}
                                {formatDateShort(s.date)}
                              </span>
                            </span>
                            {zoneStr && <span className={`pvaZonePill pvaZonePill--${zoneClass}`}>{zoneStr}</span>}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })()}
            </div>
          );
        })()}

        {/* COACH TAB — the always-available coach surface (design v4).
            v1 is deterministic: the engine's proposal as an actionable card
            plus canned, data-backed answers behind suggestion chips. The
            free-text input is present but honest about what it can't do yet
            — the LLM backend lands next; no fake AI answers. */}
        {activeTab === "coach" && (
          <CoachChat
            hasPlan={!!serverPlan?.weeks?.length}
            goalLabel={goalSaved?.type || "race"}
            /* What the coach is reading. Without this, a week with nothing
               wrong rendered as "All signals healthy — the plan stands as
               generated": engine-speak, no numbers, and no reason to believe
               it. These are the three the adaptation rules actually watch. */
            weekContext={(() => {
              const wl = weeklyLoad;
              const verdict = weeklyLoadVerdict(wl);
              const pfSec = paceFit && paceFit.deltaSecPerKm != null ? Math.round(paceFit.deltaSecPerKm) : null;
              const signals = [];
              if (adherence && adherence.details && adherence.details.total > 0) {
                const pct = Math.round((adherence.score || 0) * 100);
                signals.push({
                  key: "adherence",
                  label: "Adherence",
                  value: `${pct}%`,
                  note: `${adherence.details.matched} of ${adherence.details.total} sessions · 28 days`,
                  tone: pct >= 85 ? "ok" : pct >= 65 ? "warn" : "off",
                });
              }
              if (wl && wl.hasPlan) {
                signals.push({
                  key: "load",
                  label: "This week",
                  value: `${fmt1(wl.actualKm)} / ${fmt1(wl.plannedWeekKm)} km`,
                  note: verdict.label,
                  tone: verdict.tone === "ontrack" ? "ok" : verdict.tone === "warning" ? "warn" : verdict.tone === "off" ? "off" : "neutral",
                });
              }
              if (pfSec != null) {
                signals.push({
                  key: "pace",
                  label: "Pace fit",
                  value: `${pfSec > 0 ? "+" : ""}${pfSec}s/km`,
                  note: Math.abs(pfSec) < 5 ? "on plan pace" : pfSec > 0 ? "slower than plan" : "faster than plan",
                  tone: Math.abs(pfSec) < 15 ? "ok" : Math.abs(pfSec) < 35 ? "warn" : "off",
                });
              }
              return {
                weekNumber: serverMetrics?.plan?.currentWeek ?? null,
                totalWeeks: serverMetrics?.plan?.totalWeeks ?? null,
                phase: serverMetrics?.plan?.currentPhase ?? null,
                signals,
              };
            })()}
            refreshKey={`${authUser?.id || ""}:${serverPlan?.generatedAt || ""}:${serverPlan?.adjustedAt || ""}`}
            onPlanChanged={(plan, preview) => {
              /* An accepted change is the latest word on the sessions it
                 touched. A distance set by hand on one of them earlier
                 (the Train sheet's stepper, kept on this device) would
                 otherwise keep overriding the plan, and the accepted
                 change would never show. */
              const dates = new Set();
              for (const s of (preview && preview.sessions) || []) { if (s.date) dates.add(s.date); if (s.toDate) dates.add(s.toDate); }
              if (dates.size) {
                setSessionEdits((prev) => {
                  const next = {};
                  let dropped = false;
                  for (const k of Object.keys(prev || {})) {
                    if (dates.has(String(k).split("|")[0])) { dropped = true; continue; }
                    next[k] = prev[k];
                  }
                  if (!dropped) return prev;
                  try { localStorage.setItem(SESSION_EDITS_KEY, JSON.stringify(next)); } catch (_) {}
                  return next;
                });
              }
              cachePlan(plan);
              setServerPlan(plan);
              refreshServerMetrics();
            }}
          />
        )}

        {/* STATS TAB */}
        {activeTab === "stats" && (() => {
          // Plan Trajectory hero — derived from plan execution (paceFit ratio
          // applied to goal time), NOT from raw fitness Riegel. The plan
          // was generated for the goal, so hitting plan targets = hitting
          // the goal by construction. Drift from plan paces is the only
          // legitimate reason for the trajectory to read off.
          //
          // Theme zones map directly from paceFit thresholds:
          //   green  — onplan        (weighted ratio ≤ 1.02)
          //   amber  — slightly      (1.02 – 1.05)
          //   red    — behind        (> 1.05)
          //   neutral — no data yet  (no completed quality sessions in window)
          const traj = planTrajectory;
          const trajTheme = !goalSaved?.date || !traj ? null
            : traj.status === 'onplan'
              ? { cls: "likelihood-ontrack",   label: "ON PLAN",         zone: "green" }
            : traj.status === 'slightly'
              ? { cls: "likelihood-uncertain", label: "SLIGHTLY BEHIND", zone: "amber" }
              : { cls: "likelihood-atrisk",    label: "BEHIND",          zone: "red"   };
          const goalDateShort = goalDateObj ? formatDateShort(goalDateObj) : "—";
          const goalWeeksLeft = goalDateObj ? Math.max(0, Math.ceil((+goalDateObj - Date.now()) / (7 * 864e5))) : null;
          return (
          <div className="tabPage">
            {/* Readiness banner — runs the methodology math against the
                user's recent weekly volume. If the starting ramp is
                >1.5× the user's 4-week average, surfaces the gap as an
                advisory (not a hard stop). User can keep the plan if they
                know they have historical fitness above the recent average;
                otherwise, one-tap fixes to move the race or drop distance. */}
            {readiness && !readiness.ready && !isReadinessDismissed && serverPlan?.goalMode !== "finish-only" && serverPlan?.acknowledgedStretch !== true && (
              <div className="readinessBanner">
                <div className="readinessBanner__head">
                  <span className="readinessBanner__tag">READINESS CHECK</span>
                  {readiness.gapFactor != null && <span className="readinessBanner__factor">{readiness.gapFactor.toFixed(1)}×&nbsp;gap</span>}
                </div>
                <div className="readinessBanner__title">Big ramp-up ahead</div>
                <div className="readinessBanner__copy">
                  Your 4-week median is <b>{readiness.baselineWeeklyKm}&nbsp;km/wk</b>.
                  A {effectiveGoalType} plan over this window starts at
                  <b> {Math.round(readiness.requiredStart)}&nbsp;km/wk</b> and peaks near
                  <b> {Math.round(readiness.peakKm)}&nbsp;km/wk</b>.
                  {" "}That's a big jump — pick the path that fits:
                </div>
                <div className="readinessBanner__actions">
                  {readiness.suggestedRaceDate ? (
                    <button
                      type="button"
                      className="readinessBanner__btn"
                      onClick={() => applyReadinessFix({ newRaceDate: parseLocalDate(readiness.suggestedRaceDate) })}
                    >
                      Move race to {formatDateShort(parseLocalDate(readiness.suggestedRaceDate))} <span style={{ opacity: 0.6 }}>({readiness.suggestedWeeks}&nbsp;wks)</span>
                    </button>
                  ) : null}
                  {readiness.saferDistance ? (
                    <button
                      type="button"
                      className="readinessBanner__btn"
                      onClick={() => applyReadinessFix({ newDistanceKm: readiness.saferDistanceKm, newType: readiness.saferDistance })}
                    >
                      Drop to {readiness.saferDistance} on same date
                    </button>
                  ) : null}
                  {readiness.finishOnly ? (
                    <button
                      type="button"
                      className="readinessBanner__btn"
                      onClick={applyFinishOnlyPlan}
                    >
                      Build a finish-focused plan
                    </button>
                  ) : null}
                  <button
                    type="button"
                    className="readinessBanner__btn readinessBanner__btn--primary"
                    onClick={dismissReadiness}
                  >
                    Keep this plan — I've trained at this volume before
                  </button>
                </div>
              </div>
            )}

            {/* Plan Trajectory Hero — replaces the old Riegel-based
                Projected Finish hero. The headline answers "if you keep
                executing at this rate, where do you finish?" — and the
                rate is your weighted pace ratio over the last two weeks
                of completed quality sessions. ON PLAN means trending to
                exactly your goal; SLIGHTLY BEHIND / BEHIND means recent
                paces are dragging the projection. */}
            {trajTheme && traj && (
              <div
                className={`heroCard heroCard--${trajTheme.cls}`}
                role="button"
                tabIndex={0}
                onClick={() => { openAdjustGoalSheet(); }}
                onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); openAdjustGoalSheet(); } }}
              >
                <div className="heroCard__glyph" aria-hidden="true">◆</div>
                <div className="heroCard__label">
                  <span className="heroCard__theme">PLAN TRAJECTORY</span>
                  <span className={`pvaZonePill pvaZonePill--${trajTheme.zone}`}>{trajTheme.label}</span>
                </div>
                <div className="heroCard__name">
                  {traj.status === 'onplan'
                    ? <>Trending {goalTimeStr || fmtFinishHM(traj.projectedSec)}</>
                    : <>Trending {fmtFinishHM(traj.projectedSec)}</>}
                </div>
                <div className="heroCard__meta">
                  {goalTimeStr ? <><strong>Goal {goalTimeStr}</strong>{traj.deltaSec !== 0 ? <> · {fmtDeltaHM(traj.deltaSec)} vs goal</> : null} · </> : null}
                  {goalSaved?.type || "—"} on {goalDateShort}
                </div>
                <div className="heroCard__bottom">
                  <div className="heroCard__purpose">
                    {goalWeeksLeft != null ? <>{goalWeeksLeft} {goalWeeksLeft === 1 ? "week" : "weeks"} to race · </> : null}
                    {/* Message switches on `cause` so the user sees the
                        SPECIFIC reason for the status — not a generic
                        "behind plan". An honest projection requires
                        honest framing. */}
                    {traj.status === 'onplan' ? (
                      "hitting plan paces and showing up — trust the plan."
                    ) : traj.cause === 'pace' ? (
                      traj.status === 'slightly'
                        ? "recent paces drifting. Hit the next quality sessions at plan pace."
                        : "recent paces well behind plan. Focus on tempo + long pace fit."
                    ) : traj.cause === 'adherence' ? (
                      /* Same misleading zero as the ADHERENCE cell: with no
                         sessions in the window there is nothing to be 0% of.
                         adherence.details.total is already computed; this only
                         changes what is printed. */
                      Number(adherence?.details?.total ?? 0) > 0 ? (
                        <>
                          only <b>{Math.round((traj.adherenceScore || 0) * 100)}%</b> of planned work done in 28 days. The missed work is what builds the endurance.
                        </>
                      ) : (
                        <>no sessions logged in the last 28 days yet, so there is no attendance to read.</>
                      )
                    ) : traj.cause === 'both' ? (
                      Number(adherence?.details?.total ?? 0) > 0 ? (
                        <>
                          behind on pace, and only <b>{Math.round((traj.adherenceScore || 0) * 100)}%</b> of the work done. Paces first, then attendance.
                        </>
                      ) : (
                        <>behind on pace, and no sessions logged yet in the last 28 days.</>
                      )
                    ) : (
                      "behind plan."
                    )}
                  </div>
                  <span className="heroCard__tap">Tap to adjust ›</span>
                </div>
              </div>
            )}

            {/* KPI strip — 3 cells (Adherence / Comfortable pace / Weekly load).
                Goal Likelihood is now the Stats hero above, so it's omitted
                here to avoid duplication. Each cell is tinted by its own
                status so the row scans at a glance.
                STRIP SITS ABOVE THE CHART — matches Train and Goal page
                structure (Hero → Strip → content list). */}
            {(() => {
              // Adherence — green ≥ 70%, amber 50–70%, red < 50%.
              const adhScore = Number(adherence?.score ?? 0);
              /* "0%" is a VALUE, and a damning one. With no sessions in the
                 window yet there is nothing to be 0% of — the honest state is
                 "no data", which is what PACE FIT already showed with an
                 em dash. Two empty conventions in one strip; this is the
                 display-side fix. adherence.details.total is already computed,
                 nothing new is derived here. */
              const adhHasData = Number(adherence?.details?.total ?? 0) > 0;
              const adhCls = !adhHasData      ? "statCell--neutral"
                           : adhScore >= 0.70 ? "statCell--ontrack"
                           : adhScore >= 0.50 ? "statCell--warning"
                           :                    "statCell--off";

              // Pace Fit — weighted pace ratio over recent quality sessions vs
              // the plan's prescribed paces. Replaces the old Comfortable Pace
              // KPI which was a 28-day median of easy-pace runs and could read
              // green even on a day the user just blew a tempo. Pace Fit
              // surfaces session-level pace drift directly (and immediately —
              // a single off-pace tempo moves the number).
              const pfCls = !paceFit ? "statCell--neutral"
                          : paceFit.status === 'onplan'   ? "statCell--ontrack"
                          : paceFit.status === 'slightly' ? "statCell--warning"
                          :                                 "statCell--off";
              const pfVerdict = !paceFit
                ? "—"
                : `${paceFit.sessions} session${paceFit.sessions === 1 ? '' : 's'} · last ${paceFit.windowDays} d`;

              // Weekly load — same rule as Today/hero. On-track if actual ≥ plan-to-date.
              // Off-track verdict now includes the delta-to-date so the
              // user sees HOW FAR behind they are. The big number (4.6/78.5)
              // shows full-week context, but "Off track" alone read as
              // "obvious, the week isn't over yet" — the negative delta
              // makes it clear the badge compares against plan-to-date.
              const loadV = weeklyLoadVerdict(weeklyLoad);
              const loadCls = `statCell--${loadV.tone}`;
              const loadVerdict = loadV.label;

              // Muted unit suffix so the big number stays dominant.
              const unit = (u) => <span style={{ fontSize: "var(--text-xs-size)", fontWeight: 500, color: "var(--text-muted)", marginLeft: 3 }}>{u}</span>;

              // One panel, three aligned rows — not three boxes of which the
              // third spans the full width. A 2+1 grid reads as a mistake
              // however deliberate it is, and five separate shadowed cards
              // down one screen is a lot of furniture for three numbers.
              return (
                <div className="statStrip statStrip--rows" style={{ marginTop: 4 }}>
                  <div className={`statCell statCell--tap ${adhCls}`} role="button" tabIndex={0} onClick={openStat("adherence")} onKeyDown={statKey("adherence")}>
                    <div className="statCell__label">ADHERENCE <InfoTooltip text={TOOLTIP_ADHERENCE} isMobile={isMobile} /><ChevronRight size={14} className="statCell__chev" aria-hidden="true" /></div>
                    <div className="statCell__value">{adhHasData ? adherence.label : "—"}</div>
                    <div className="statCell__verdict"><span className="statCell__dot" />{adhHasData ? tableWindowLabel(ADHERENCE_WINDOW_DAYS) : "No sessions yet"}</div>
                  </div>
                  <div className={`statCell statCell--tap ${pfCls}`} role="button" tabIndex={0} onClick={openStat("pace")} onKeyDown={statKey("pace")}>
                    <div className="statCell__label">PACE FIT <InfoTooltip text={"Average pace deviation vs your plan's prescribed paces, weighted toward race-pace work (tempo and long carry the most weight, easy and recovery the least). Computed over the last 14 days of completed sessions. \"On pace\" = within 5 s/km of plan; positive = slower than plan; negative = faster. Easy and recovery runs that are faster than planned count as on pace — running an easy day quick is not a fitness gap. Green ≤ 2% off plan, amber 2–5%, red beyond 5%."} isMobile={isMobile} /><ChevronRight size={14} className="statCell__chev" aria-hidden="true" /></div>
                    <div className="statCell__value">
                      {paceFit
                        ? (Math.abs(paceFit.deltaSecPerKm) < 5 ? 'On pace' : <>{paceFit.deltaSecPerKm > 0 ? '+' : '−'}{secToMMSS(Math.abs(paceFit.deltaSecPerKm))}{unit('/km')}</>)
                        : "—"}
                    </div>
                    <div className="statCell__verdict"><span className="statCell__dot" />{pfVerdict}</div>
                  </div>
                  <div className={`statCell statCell--tap ${loadCls}`} role="button" tabIndex={0} onClick={openStat("load")} onKeyDown={statKey("load")}>
                    <div className="statCell__label">WK LOAD <InfoTooltip text={TOOLTIP_WEEKLY_VOLUME} isMobile={isMobile} /><ChevronRight size={14} className="statCell__chev" aria-hidden="true" /></div>
                    <div className="statCell__value">
                      {weeklyLoad.hasPlan ? <>{fmt1(weeklyLoad.actualKm)}<span style={{ color: "var(--text-muted)" }}>/{fmt1(weeklyLoad.plannedWeekKm)}</span>{unit("km")}</> : <>{fmt1(weeklyLoad.actualKm)}{unit("km")}</>}
                    </div>
                    <div className="statCell__verdict"><span className="statCell__dot" />{loadVerdict}</div>
                  </div>
                </div>
              );
            })()}

            {/* Fitness & Form strip — CTL / TSB from Coggan's Performance
                Manager Chart. Complementary to the single-week KPIs above:
                those answer "this week", these answer "chronic state".
                Hidden until both inputs (observed threshold pace + at
                least one run) are available. */}
            {fitnessForm && (() => {
              const ctl = fitnessForm.ctl;
              const tsb = fitnessForm.tsb;
              const trend = fitnessForm.ctlDelta7d;

              // Fitness label + colour. CTL ranges (rough industry consensus):
              //   < 25  detrained / very low
              //   25–45 building
              //   45–65 strong base
              //   65+   advanced ultra
              // Colour status comes from 7-day trend AND absolute level —
              // building (rising trend) is green even at a modest absolute,
              // declining is red regardless of absolute.
              let fitnessVerdict, fitnessCls;
              if (trend == null) {
                fitnessVerdict = "Building";
                fitnessCls = "statCell--neutral";
              } else if (trend >= 1.5) {
                fitnessVerdict = "Building +" + trend.toFixed(1);
                fitnessCls = "statCell--ontrack";
              } else if (trend >= -0.5) {
                fitnessVerdict = "Maintaining";
                fitnessCls = ctl >= 25 ? "statCell--ontrack" : "statCell--warning";
              } else {
                fitnessVerdict = "Detraining " + trend.toFixed(1);
                fitnessCls = "statCell--off";
              }

              // Form (TSB) bands per Coggan:
              //   > +25  Peaked / risk of detraining if held too long
              //   +5..+25 Fresh — race window
              //   -10..+5 Balanced
              //   -25..-10 Fatigued — productive heavy block
              //   < -25  Overreaching — back off before injury
              let formVerdict, formCls;
              if (tsb > 25)        { formVerdict = "Peaked";       formCls = "statCell--warning"; }
              else if (tsb >= 5)   { formVerdict = "Fresh";        formCls = "statCell--ontrack"; }
              else if (tsb >= -10) { formVerdict = "Balanced";     formCls = "statCell--neutral"; }
              else if (tsb >= -25) { formVerdict = "Fatigued";     formCls = "statCell--warning"; }
              else                 { formVerdict = "Overreaching"; formCls = "statCell--off";     }

              const FITNESS_TT = "Chronic Training Load (CTL) — exponentially weighted 42-day average of training stress (TSS) per session. Higher = more aerobic fitness baked in. Builds slowly, decays slowly. Rough ranges: < 25 detrained, 25–45 building, 45–65 strong base, 65+ advanced ultra.";
              const FORM_TT = "Training Stress Balance (TSB) = CTL − ATL. Negative = carrying training fatigue (heavy block); positive = fresh (taper / recovery). Race-ready window is roughly +5 to +25. Below −25 indicates overreaching — back off before injury.";

              // Compact corner sparkline geometry. The SVG sits in the
              // top-right corner of each cell at ~70 × 26 device px (sized
              // by CSS); preserveAspectRatio="none" stretches the path to
              // fill that box. Both cards use the same tiny shape — just a
              // line + endpoint dot, no area fill, no zone bands, no axis.
              // Goal is a glanceable up/down indicator, not a chart.
              /* Full-width trend chart, not a 70x26 corner glyph. CTL/ATL/TSB
                 are ratio quantities whose meaning is position relative to a
                 reference, so a line with no baseline and no range was
                 decoration. Now: a readable box, a labelled y-range, a
                 reference line and an endpoint marker. */
              const W = 300;
              const H = 56;
              const hist = (fitnessForm.history || []).slice(-28);
              const N = hist.length;

              // Auto-scale CTL line to the visible 28 days. Padding keeps
              // the line off the top/bottom edges of the tiny SVG so it
              // doesn't flatten against the bounds.
              const ctlVals = N ? hist.map((p) => p.ctl) : [0];
              const ctlMin = Math.min(...ctlVals);
              const ctlMax = Math.max(...ctlVals);
              const ctlSpan = Math.max(1, ctlMax - ctlMin);
              const ctlPad = ctlSpan * 0.2;
              const fyMin = Math.max(0, ctlMin - ctlPad);
              const fyMax = ctlMax + ctlPad;
              const fyRange = Math.max(0.5, fyMax - fyMin);
              const fitColor = fitnessCls === "statCell--off" ? "var(--danger)"
                             : fitnessCls === "statCell--warning" ? "var(--warning)"
                             : "var(--accent)";

              // Auto-scale TSB line the same way. Form's zone bands belong
              // on a dedicated chart, not this 26px-tall corner glyph —
              // here we just want "trending up" vs "trending down".
              const tsbVals = N ? hist.map((p) => p.tsb) : [0];
              const tsbMin = Math.min(...tsbVals);
              const tsbMax = Math.max(...tsbVals);
              const tsbSpan = Math.max(1, tsbMax - tsbMin);
              const tsbPad = tsbSpan * 0.2;
              const tyMin = tsbMin - tsbPad;
              const tyMax = tsbMax + tsbPad;
              const tyRange = Math.max(0.5, tyMax - tyMin);
              const formColor = formCls === "statCell--off" ? "var(--danger)"
                              : formCls === "statCell--warning" ? "var(--warning)"
                              : formCls === "statCell--ontrack" ? "var(--accent)"
                              : "var(--text-secondary)";

              const INSET = 4;   // keep the stroke and the end dot off the edges
              const plotW = W - INSET * 2;
              const plotHh = H - INSET * 2;
              const sparkX = (i) => N <= 1 ? W / 2 : INSET + (i / (N - 1)) * plotW;
              const fitY = (v) => INSET + (1 - (v - fyMin) / fyRange) * plotHh;
              const formY = (v) => INSET + (1 - (v - tyMin) / tyRange) * plotHh;
              /* TSB = 0 is the real threshold: above it you are fresh, below it
                 you are carrying fatigue. It is the single most important mark
                 on the Form chart and it was not drawn at all. */
              const zeroInRange = tyMin <= 0 && tyMax >= 0;
              const tsbZeroY = formY(0);
              /* Fitness has no meaningful zero (CTL is always positive), so the
                 reference is where the window started — that is what makes
                 "building" vs "decaying" legible. */
              const ctlStartY = N ? fitY(ctlVals[0]) : H / 2;

              let ctlLinePath = "";
              let tsbLinePath = "";
              for (let i = 0; i < N; i++) {
                ctlLinePath += `${i === 0 ? "M" : "L"}${sparkX(i).toFixed(1)},${fitY(ctlVals[i]).toFixed(1)} `;
                tsbLinePath += `${i === 0 ? "M" : "L"}${sparkX(i).toFixed(1)},${formY(tsbVals[i]).toFixed(1)} `;
              }
              const ctlEndY = N ? fitY(ctlVals[N - 1]) : H / 2;
              const tsbEndY = N ? formY(tsbVals[N - 1]) : H / 2;
              const areaOf = (linePath) =>
                N > 1 ? `${linePath}L${sparkX(N - 1).toFixed(1)},${H - INSET} L${sparkX(0).toFixed(1)},${H - INSET} Z` : "";
              const ctlAreaPath = areaOf(ctlLinePath);
              const tsbAreaPath = areaOf(tsbLinePath);
              const fmtAx = (v) => (Math.abs(v) >= 10 ? Math.round(v) : Math.round(v * 10) / 10);

              /* The sparklines carry no numbers any more: a "28d" and two
                 axis values pinned to the card's edges read as clutter, and
                 the period belongs in a heading. The numbers are one tap
                 away in the detail sheet. */
              return (
                <>
                <div className="statPeriod">
                  <span className="v7sectionLabel" style={{ marginBottom: 0 }}>FITNESS &amp; FORM</span>
                  <span className="pvaWindowLabel">Last {N} days</span>
                </div>
                <div className="statStrip" style={{ gridTemplateColumns: "repeat(2, 1fr)", marginTop: 4 }}>
                  <div className={`statCell statCell--trend statCell--tap ${fitnessCls}`} role="button" tabIndex={0} onClick={openStat("fitness")} onKeyDown={statKey("fitness")}>
                    <div className="statCell__label">FITNESS <InfoTooltip text={FITNESS_TT} isMobile={isMobile} /><ChevronRight size={14} className="statCell__chev" aria-hidden="true" /></div>
                    {N > 1 && (
                      <div className="trendChart">
                        <svg className="trendChart__svg" viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" role="img"
                             aria-label={`Fitness over the last ${N} days: ${fmtAx(ctlVals[0])} to ${ctl}`}>
                          <line x1="0" y1={ctlStartY} x2={W} y2={ctlStartY}
                                stroke="var(--viz-baseline)" strokeWidth="1" strokeDasharray="3 3"
                                vectorEffect="non-scaling-stroke" />
                          <path d={ctlAreaPath} fill={fitColor} opacity="0.12" />
                          <path d={ctlLinePath} fill="none" stroke={fitColor} strokeWidth="1.5" strokeLinejoin="round" strokeLinecap="round" vectorEffect="non-scaling-stroke" />
                          <circle cx={sparkX(N - 1)} cy={ctlEndY} r="3.5" fill="var(--viz-halo)" />
                          <circle cx={sparkX(N - 1)} cy={ctlEndY} r="2.2" fill={fitColor} />
                        </svg>
                      </div>
                    )}
                    <div className="statCell__value">{ctl}</div>
                    <div className="statCell__verdict"><span className="statCell__dot" />{fitnessVerdict}</div>
                  </div>
                  <div className={`statCell statCell--trend statCell--tap ${formCls}`} role="button" tabIndex={0} onClick={openStat("form")} onKeyDown={statKey("form")}>
                    <div className="statCell__label">FORM <InfoTooltip text={FORM_TT} isMobile={isMobile} /><ChevronRight size={14} className="statCell__chev" aria-hidden="true" /></div>
                    {N > 1 && (
                      <div className="trendChart">
                        <svg className="trendChart__svg" viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" role="img"
                             aria-label={`Form over the last ${N} days: ${fmtAx(tsbVals[0])} to ${tsb}, zero is the fresh/fatigued line`}>
                          {zeroInRange && (
                            <line x1="0" y1={tsbZeroY} x2={W} y2={tsbZeroY}
                                  stroke="var(--viz-baseline)" strokeWidth="1"
                                  vectorEffect="non-scaling-stroke" />
                          )}
                          <path d={tsbAreaPath} fill={formColor} opacity="0.12" />
                          <path d={tsbLinePath} fill="none" stroke={formColor} strokeWidth="1.5" strokeLinejoin="round" strokeLinecap="round" vectorEffect="non-scaling-stroke" />
                          <circle cx={sparkX(N - 1)} cy={tsbEndY} r="3.5" fill="var(--viz-halo)" />
                          <circle cx={sparkX(N - 1)} cy={tsbEndY} r="2.2" fill={formColor} />
                        </svg>
                      </div>
                    )}
                    <div className="statCell__value">{tsb >= 0 ? `+${tsb}` : `${tsb}`}</div>
                    <div className="statCell__verdict"><span className="statCell__dot" />{formVerdict}</div>
                  </div>
                </div>
                </>
              );
            })()}

            {/* Heart rate — see server/services/metricsEngine.js calcHeartRate.
                Strava gives an average and a max per activity, never a stream,
                so this reports the two things an average CAN answer: the zone
                each session's effort sat in (the training mix) and beats per
                kilometre on easy running (aerobic efficiency). Both are
                labelled as what they are; neither claims time-in-zone. */}
            {(() => {
              const hr = serverMetrics?.heartRate;
              if (!hr) return null;
              const eff = hr.efficiency;
              const trendCls = eff ? `is-${eff.trend}` : "";
              return (
                <div className="v7section">
                  <div className="v7sectionLabel">HEART RATE</div>
                  <div
                    className={"hrCard" + (hr.available ? " hrCard--tap" : "")}
                    role={hr.available ? "button" : undefined}
                    tabIndex={hr.available ? 0 : undefined}
                    onClick={hr.available ? openStat("hr") : undefined}
                    onKeyDown={hr.available ? statKey("hr") : undefined}
                  >
                    {!hr.available ? (
                      <div className="hrCard__empty">
                        {hr.reason === "no-max-hr"
                          ? "No maximum heart rate recorded yet. Log a hard session with a monitor and the zones appear here."
                          : "No heart-rate data in the last 28 days."}
                      </div>
                    ) : (
                      <>
                        <div className="hrCard__head">
                          <div>
                            <div className="hrCard__eyebrow">Aerobic efficiency</div>
                            <div className="hrCard__readout">
                              <span className="hrCard__num">{eff ? eff.beatsPerKm : "—"}</span>
                              <span className="hrCard__unit">beats / km</span>
                              {eff && eff.deltaPct != null ? (
                                <span className={`hrCard__delta ${trendCls}`}>
                                  {eff.deltaPct > 0 ? "+" : "−"}{Math.abs(eff.deltaPct)}%
                                </span>
                              ) : null}
                            </div>
                            <div className="hrCard__sub">
                              {eff && eff.deltaPct != null
                                ? (eff.trend === "improving"
                                    ? "Same easy running, fewer beats — aerobic fitness is rising."
                                    : eff.trend === "declining"
                                      ? "Easy running is costing more beats than last month."
                                      : "Holding steady against last month.")
                                : eff
                                  ? `From ${eff.sampleN} easy ${eff.sampleN === 1 ? "run" : "runs"} — not enough yet to call a direction.`
                                  : "No easy runs with heart rate in this window."}
                            </div>
                          </div>
                        </div>

                        {/* The block, not just the window. Each point is the
                            same measure as the number above it — median
                            beats/km of easy running over a trailing 28 days —
                            so the line and the headline cannot disagree. Down
                            is the good direction, which the axis labels say
                            out loud because no chart convention carries it. */}
                        {hr.series && hr.series.length >= 3 && (() => {
                          const pts = hr.series;
                          const vals = pts.map((p) => p.beatsPerKm);
                          const lo = Math.min(...vals);
                          const hi = Math.max(...vals);
                          const span = Math.max(1, hi - lo);
                          const W = 100, H = 34;
                          const x = (i) => (i / Math.max(1, pts.length - 1)) * W;
                          const y = (v) => H - ((v - lo) / span) * H;
                          const line = pts.map((p, i) => `${x(i).toFixed(2)},${y(p.beatsPerKm).toFixed(2)}`).join(" ");
                          const improving = hr.blockTrend && hr.blockTrend.trend === "improving";
                          const col = improving ? "var(--accent)"
                            : hr.blockTrend && hr.blockTrend.trend === "declining" ? "var(--caution)"
                            : "var(--content-secondary)";
                          return (
                            <div className="hrTrend">
                              <div className="hrTrend__head">
                                <span className="hrCard__eyebrow">Last {pts.length} weeks</span>
                                {hr.blockTrend ? (
                                  <span className={`hrCard__delta is-${hr.blockTrend.trend}`}>
                                    {hr.blockTrend.deltaPct > 0 ? "+" : "−"}{Math.abs(hr.blockTrend.deltaPct)}%
                                  </span>
                                ) : null}
                              </div>
                              <div className="hrTrend__plot">
                                <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" role="img"
                                     aria-label={`Aerobic efficiency over ${pts.length} weeks, ${vals[0]} to ${vals[vals.length - 1]} beats per kilometre. Lower is better.`}>
                                  <polyline
                                    points={`0,${H} ${line} ${W},${H}`}
                                    fill={col}
                                    opacity="0.12"
                                    stroke="none"
                                  />
                                  <polyline
                                    points={line}
                                    fill="none"
                                    stroke={col}
                                    strokeWidth="1.5"
                                    strokeLinejoin="round"
                                    strokeLinecap="round"
                                    vectorEffect="non-scaling-stroke"
                                  />
                                  <circle cx={W} cy={y(vals[vals.length - 1])} r="2.6" fill="var(--viz-halo)" />
                                  <circle cx={W} cy={y(vals[vals.length - 1])} r="1.7" fill={col} />
                                </svg>
                                <span className="hrTrend__ax hrTrend__ax--hi">{hi}</span>
                                <span className="hrTrend__ax hrTrend__ax--lo">{lo}</span>
                              </div>
                              <div className="hrTrend__foot">
                                Lower is better — fewer beats to cover a kilometre.
                              </div>
                            </div>
                          );
                        })()}

                        <div className="hrZones">
                          <div className="hrZones__bar" role="img"
                               aria-label={hr.zones.map((z) => `${z.zone} ${z.pct}%`).join(", ")}>
                            {hr.zones.map((z) => (
                              <span
                                key={z.zone}
                                className={`hrZones__seg hrZones__seg--${z.zone.toLowerCase()}`}
                                style={{ width: `${z.pct}%` }}
                              />
                            ))}
                          </div>
                          <div className="hrZones__keys">
                            {hr.zones.map((z) => (
                              <span key={z.zone} className="hrZones__key">
                                <i className={`hrZones__dot hrZones__seg--${z.zone.toLowerCase()}`} />
                                {z.zone}
                                <b>{Math.round(z.pct)}%</b>
                              </span>
                            ))}
                          </div>
                          <div className="hrCard__foot">
                            Session averages over {hr.windowDays} days, weighted by duration — not time in zone.
                            {" "}HR max {hr.maxHr} ({hr.maxHrSource}). {hr.sessionsWithHr} of {hr.sessionsTotal} runs carry heart rate.
                          </div>
                        </div>
                      </>
                    )}
                  </div>
                </div>
              );
            })()}

            {/* Chart */}
            {hasSavedGoal && (
              <div className="v7section">
                <CombinedProgressChart
                  weeks={weeks}
                  actualWeeklyMap={actualWeeklyMap}
                  plannedWeeklyMap={plannedWeeklyMap}
                  phaseByWeek={phaseByWeek}
                  planStartWeek={chartStartDate}
                  goalWeek={goalWeekStart}
                  todayWeekStartMs={todayWeekStartMs}
                  planStartMs={hasSavedGoal && planStartDate ? +startOfWeek(planStartDate) : null}
                  plannedThisWeekToDateKm={plannedThisWeekToDateKm}
                  weekRuns={weekRuns}
                  markers={markers}
                  onMarkerClick={(m) => {
                    if (m.label === "Goal") {
                      openAdjustGoalSheet();
                    }
                  }}
                />
                {/* Key. Phase used to need four swatches and a strip down
                    here; it is named on the rail inside the chart now, so
                    all that is left is what the two series are. */}
                <div className="chartLegend">
                  <div className="chartLegend__group">
                    <span className="legendKey legendKey--area" aria-hidden="true" />
                    <span className="legendKey__text">Planned</span>
                    <span className="legendKey legendKey--bar" aria-hidden="true" />
                    <span className="legendKey__text">Actual</span>
                    <span className="legendKey legendKey--dot" aria-hidden="true" />
                    <span className="legendKey__text">Recovery week</span>
                  </div>
                </div>
              </div>
            )}

            {/* Plan vs actual */}
            {hasSavedGoal && (
              <div className="v7section pvaSection">
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 10 }}>
                  <div className="v7sectionLabel" style={{ marginBottom: 0 }}>PLAN VS ACTUAL</div>
                  <span className="pvaWindowLabel">{pvaRemaining > 0 || pvaPastTotal === 0 ? `Last ${pvaDays} days` : "All runs"}</span>
                </div>
                {visibleRows?.map((row, i) => {
                  // List-row layout — actuals primary. Verdict is carried by
                  // the status badge ("Matched · On pace / Too fast / Too slow",
                  // "Partial · 88%", "Off pace · +18s/km", "Missed", etc).
                  // Numeric deltas are intentionally NOT rendered in the row —
                  // they live in the session detail sheet for audit.
                  const dp = row.plannedP != null && row.actualP != null ? row.actualP - row.plannedP : null;
                  // Derive effective status. The server still labels this
                  // row as 'matched' when distance was hit, but if pace is
                  // > 5% slower than planned we re-badge as 'off-pace' so
                  // the table makes pace misses visible at a glance. Pace
                  // ratio = actual/planned; > 1.05 = > 5% slower than plan.
                  const rawLc = String(row.status || '').toLowerCase();
                  const isOffPace = (
                    rawLc === 'matched' &&
                    row.plannedP != null && row.actualP != null &&
                    row.plannedP > 0 &&
                    (row.actualP / row.plannedP) > 1.05
                  );
                  const statusLc = isOffPace ? 'off-pace' : rawLc;
                  const badge = statusBadge(statusLc, dp);

                  // Pro-rata completion ratio (0..1). For matched/partial/missed
                  // rows the server populates this; older callers fall back
                  // gracefully via the legacy fields.
                  const ratio = (() => {
                    if (typeof row.completionRatio === 'number') return Math.max(0, Math.min(1, row.completionRatio));
                    if (statusLc === 'matched') return 1;
                    if (statusLc === 'missed')  return 0;
                    if (row.plannedKm > 0 && row.actualKm != null) return Math.max(0, Math.min(1, row.actualKm / row.plannedKm));
                    return null;
                  })();
                  const ratioPct = ratio != null ? Math.round(ratio * 100) : null;

                  const zl = row.actual ? hrZoneLabel(row.actual.avg_hr, effectiveMaxHr) : null;
                  const zonePillColor = zl ? (zl.zone === "Z1" ? "var(--info-base)" : zl.zone === "Z2" ? "var(--accent-base)" : zl.zone === "Z5" ? "var(--critical)" : "var(--caution)") : null;
                  const sessionTitle = row.planned?.title || row.actual?.name || "";
                  // Spine + bar + badge color is COMPLETION-RATIO-driven for
                  // past/present rows so a 90% session and a 30% session don't
                  // both show the same red. Buckets:
                  //   ≥ 95%   green   (essentially complete)
                  //   80–95%  yellow-green (close)
                  //   60–80%  amber   (partial but real effort)
                  //   30–60%  orange  (well short)
                  //   < 30%   red     (effectively missed)
                  // Upcoming/today/extra rows keep the older muted styling.
                  const ratioColor = (() => {
                    if (ratio == null) return 'var(--text-muted)';
                    if (ratio >= 0.95) return 'var(--accent)';     // green
                    if (ratio >= 0.80) return 'var(--accent-base)';           // yellow-green
                    if (ratio >= 0.60) return 'var(--warning)';    // amber
                    if (ratio >= 0.30) return 'var(--critical)';           // orange
                    return 'var(--danger)';                         // red
                  })();
                  const spineColor =
                    statusLc === 'off-pace' ? 'var(--warning)'  // amber — distance hit, pace miss
                  : statusLc === 'matched' || statusLc === 'partial' || statusLc === 'missed' ? ratioColor
                  : statusLc === 'today'    ? 'var(--warning)'
                  : 'var(--text-muted)'; // extra / upcoming / unknown

                  // Primary km/pace to render — prefer actuals; fall back to
                  // planned for rows without actual yet (missed / upcoming).
                  const primaryKm = row.actualKm != null ? row.actualKm : row.plannedKm;
                  const primaryP = row.actualP != null ? row.actualP : row.plannedP;
                  const isPlannedOnly = row.actualKm == null && row.plannedKm != null;
                  return (
                  <div
                    key={`${row.date}|${row.planned?.type || ""}|${row.actual?.id ?? i}`}
                    className={"v7sessionRow" + (i >= pvaPrevCountRef.current ? " is-new" : "")}
                    style={{ "--i": Math.max(0, i - pvaPrevCountRef.current) }}
                    data-date={row.date}
                    onClick={() => setDetailSheet(row)}
                  >
                    <div className="v7trainRowAccent" style={{ background: spineColor }} />
                    <div style={{ flex: 1, minWidth: 0 }}>
                    <div className="v7sessionRowLine1">
                      <div>
                        <span style={{ fontSize: "var(--text-base-size)", fontWeight: 600, color: "var(--content-primary)" }}>{sessionTitle || formatDateShort(row.date)}</span>
                        <span style={{ fontSize: "var(--text-xs-size)", color: "var(--content-tertiary)", marginLeft: 8 }}>{row.dateLabel || formatDateShort(row.date)}</span>
                      </div>
                      <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                        {zl && <span className="pvaZonePill" style={{ color: zonePillColor, background: zonePillColor ? `${zonePillColor}22` : undefined }}>{zl.zone}</span>}
                        <span
                          className={`pvaStatusBadge pvaStatusBadge--${statusLc}`}
                          style={statusLc === 'partial' || (statusLc === 'matched' && ratioColor !== 'var(--accent)') || statusLc === 'missed'
                            ? { color: ratioColor, background: 'transparent', borderColor: ratioColor }
                            : undefined}
                        >
                          {badge.label}
                          {badge.subLabel ? <span style={{ opacity: 0.75, fontWeight: 500 }}> · {badge.subLabel}</span> : null}
                          {statusLc === 'partial' && ratioPct != null
                            ? <span style={{ opacity: 0.75, fontWeight: 600 }}> · {ratioPct}%</span>
                            : null}
                        </span>
                      </div>
                    </div>
                    <div className="v7sessionRowLine2" style={{ display: "flex", alignItems: "center", gap: 6, minWidth: 0, flexWrap: "wrap" }}>
                      {primaryKm != null && (
                        <span>
                          {isPlannedOnly && <span className="muted" style={{ marginRight: 4 }}>plan</span>}
                          <strong>{fmt1(primaryKm)}</strong>
                          <span className="muted"> km</span>
                        </span>
                      )}
                      {primaryKm != null && primaryP ? <span style={{ color: "var(--content-tertiary)" }}>·</span> : null}
                      {primaryP && (
                        <span>
                          <strong>{secPerKmToPace(primaryP).replace("/km","")}</strong>
                          <span className="muted"> /km</span>
                        </span>
                      )}
                    </div>
                    {row.plannedKm > 0 && (
                      <div className="v7sessionBar">
                        {row.actualKm != null && (
                          <div
                            className="v7sessionBarFill"
                            style={{
                              width: `${Math.min(100, (row.actualKm / row.plannedKm) * 100)}%`,
                              background: ratioColor,
                            }}
                          />
                        )}
                      </div>
                    )}
                    </div>
                  </div>
                  );
                })}
                {pvaPastTotal === 0 ? (
                  <div className="pvaEmpty">Your first run will appear here.</div>
                ) : (
                  <>
                    {visibleRows.length === 0 ? (
                      <div className="pvaEmpty">No runs in the last {pvaDays} days.</div>
                    ) : null}
                    {pvaRemaining > 0 ? (
                      <ListMore label="Earlier runs" count={pvaRemaining} onClick={loadEarlierRuns} />
                    ) : (
                      <div className="pvaEnd">That is every run on record.</div>
                    )}
                  </>
                )}
              </div>
            )}
          </div>
          );
        })()}

        {/* TRAIN TAB */}
        {activeTab === "train" && (
          <div className="tabPage">
            {(() => {
              const mStr = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
              /* Calendar arithmetic, not milliseconds: across the DST change a
                 week is 7 days and one hour long, and "+7 × 864e5" put the
                 Sunday long run of 1 Nov in no section at all. */
              const thisMonday = startOfWeek(new Date());
              const addDays = (d, k) => { const x = new Date(d); x.setDate(x.getDate() + k); return startOfDay(x); };
              const thisSunday = addDays(thisMonday, 6);
              const nextMonday = startOfWeek(addDays(thisMonday, 7));
              // "19–25 Oct", or "26 Oct – 1 Nov" when the week straddles a month
              // (it used to print "26–1 Nov").
              const fmtRange = (a, b) => a.getMonth() === b.getMonth()
                ? `${a.getDate()}–${b.getDate()} ${mStr[b.getMonth()]}`
                : `${a.getDate()} ${mStr[a.getMonth()]} – ${b.getDate()} ${mStr[b.getMonth()]}`;
              // Plan list is training sessions only — tune-up races are not
              // part of the methodology taxonomy (see /docs/training_methodology.md)
              // so they're intentionally excluded. Goal race is the plan's anchor
              // and is represented separately (the plan ends at it by design).
              // The goal race is pinned at the bottom as its own row; the
              // generator's race-day session would otherwise be counted again
              // inside the race week's summary and total.
              const all = (plannedSessionsFull || [])
                .filter((s) => s.kind !== "race" && String(s.type || "").toLowerCase() !== "race")
                .sort((a, b) => +a.date - +b.date);

              // Session-level match lookup — keyed by the original planned
              // date ISO + session type (the same key the matcher uses).
              // Races are skipped (they never match against Strava runs).
              const origISO = (d) => {
                if (!d) return null;
                if (typeof d === "string") return d;
                return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}-${String(d.getDate()).padStart(2,"0")}`;
              };
              const matchByKey = new Map();
              for (const r of serverMatchedSessions || []) {
                if (!r?.planned) continue;
                matchByKey.set(`${r.planned.date}|${r.planned.type}`, r);
              }
              const statusFor = (s) => {
                if (s.kind === "race") return null;
                const key = `${origISO(s.originalDate || s.date)}|${s.type}`;
                return matchByKey.get(key)?.status || null;
              };
              // Bucket by the week a session's date falls in — equality on
              // the Monday, never a millisecond range.
              const weekOf = (s) => +startOfWeek(s.date);
              const thisWeekSessions = all.filter((s) => weekOf(s) === +thisMonday);
              const phaseOf = (s) => { if (s.kind === "race") return "Race"; const parts = (s.badge || "").split("•").map(p => p.trim()); const phase = parts.length >= 2 ? parts[1] : ""; return phase ? phase.charAt(0).toUpperCase() + phase.slice(1) : ""; };

              // Which phase a week's sessions belong to — read from
              // phaseByWeek (the chart's map) so Train + Chart stay in lockstep.
              const phaseMetaForSessions = (sessions) => {
                if (!sessions || !sessions.length) return null;
                const s = sessions.find(x => x.kind !== "race") || sessions[0];
                const wkStart = +startOfWeek(new Date(s.date));
                const info = phaseByWeek?.get(wkStart);
                if (info) return info;
                const p = (phaseOf(s) || "").toLowerCase();
                if (!p || p === "race") return null;
                return { phase: p, isRecoveryWeek: false };
              };
              // Monday-aligned and rounded: a ceil over raw milliseconds skipped
              // a number at the DST change (the week after 25 Oct is 7 days
              // and one hour long, which ceil counted as two).
              const planWeekNum = (d) => { if (!planStartDate) return null; return Math.max(1, Math.round((+startOfWeek(d) - +startOfWeek(planStartDate)) / (7 * 864e5)) + 1); };
              const thisWeekPhase = thisWeekSessions.length > 0 ? phaseOf(thisWeekSessions[0]) : "";
              const thisWeekNum = planWeekNum(thisMonday);
              const weeksToRace = goalDateObj ? Math.max(0, Math.ceil((+goalDateObj - Date.now()) / (7 * 864e5))) : null;
              const wk1Mon = nextMonday;
              const wk1Sun = addDays(wk1Mon, 6);
              const wk2Mon = startOfWeek(addDays(wk1Mon, 7));
              const next1Sessions = all.filter((s) => weekOf(s) === +wk1Mon);

              // Row click routing:
              //   Races                                  → TrainSessionSheet (no actuals to analyse)
              //   Past sessions (matched/partial/missed) → Stats detailSheet (same rich analysis
              //                                            the user sees from the Stats tab)
              //   Upcoming / today / no-match            → TrainSessionSheet (so user can still edit
              //                                            km / date / notes for a session that
              //                                            hasn't happened yet)
              // This makes "click a session" consistent across Train and Stats for completed runs.
              const rowKey = (s) => `${origISO(s.originalDate || s.date)}|${s.type}`;
              const openDetailFor = (s) => () => {
                if (s.kind === "race") { setTrainSessionSheet(s); return; }
                const matchRow = matchByKey.get(rowKey(s));
                const isPast = matchRow && (matchRow.status === "matched" || matchRow.status === "partial" || matchRow.status === "missed");
                if (isPast) setDetailSheet(matchRow);
                else setTrainSessionSheet(s);
              };
              const todayStart = +startOfDay(new Date());
              const trainRow = (s, likely) => (
                <TrainRow
                  key={rowKey(s)}
                  s={s}
                  status={statusFor(s)}
                  effectiveMaxHr={effectiveMaxHr}
                  likely={!!likely}
                  isToday={+startOfDay(s.date) === todayStart}
                  onOpen={openDetailFor(s)}
                />
              );

              // Empty-state: no sessions anywhere in the plan (almost always
              // means there's no active server plan). Show one prominent CTA
              // instead of six "No sessions planned." lines.
              if (all.length === 0) {
                return (
                  <div className="v7section" style={{ padding: "40px 22px", textAlign: "center" }}>
                    <div style={{ fontSize: "var(--text-4xl-size)", fontWeight: 800, color: "var(--content-primary)", letterSpacing: "-1px", lineHeight: 1 }}>No plan yet</div>
                    <div style={{ fontSize: "var(--text-sm-size)", color: "var(--content-tertiary)", marginTop: 10, marginBottom: 20, maxWidth: 320, marginLeft: "auto", marginRight: "auto" }}>
                      Generate a training plan to see your weekly sessions, phases, and tap through to session details.
                    </div>
                    <button
                      className="v7exportBtn"
                      onClick={() => setShowPlanGenerator(true)}
                      style={{ display: "inline-flex", alignItems: "center", gap: 8 }}
                    >
                      <Plus size={16} /> Generate plan
                    </button>
                  </div>
                );
              }

              // Train hero + strip — same design system as Today/Stats/Goal.
              // Hero uses next-session-type theme. Strip shows this week / next
              // week / race in, each with phase as verdict.
              const trainNext = nextRuns?.[0] || thisWeekSessions[0] || null;
              const trainTypeKey = String(trainNext?.type || "").toLowerCase();
              const trainHeroTheme =
                trainTypeKey.includes("tempo")      ? { cls: "tempo", glyph: "T", title: "TEMPO",    purpose: "Lactate threshold. Z3-Z4 HR." }
              : trainTypeKey.includes("long") || trainTypeKey.includes("b2b") || trainTypeKey.includes("time-on-feet") || trainTypeKey.includes("time on feet")
                                                   ? { cls: "long",  glyph: "L", title: "LONG",     purpose: "Endurance. Z2 HR throughout." }
              : trainTypeKey.includes("vertical") || trainTypeKey.includes("hill")
                                                   ? { cls: "hills", glyph: "V", title: "VERTICAL", purpose: "Hill power. Accumulate vert." }
              : trainTypeKey.includes("hike")      ? { cls: "long",  glyph: "H", title: "HIKE",     purpose: "Active recovery on trail." }
              : trainTypeKey.includes("recovery")  ? { cls: "easy",  glyph: "R", title: "RECOVERY", purpose: "Genuinely easy. Protect adaptation." }
              : trainTypeKey.includes("easy")      ? { cls: "easy",  glyph: "E", title: "EASY",     purpose: "Aerobic base. Z2 HR throughout." }
              :                                      { cls: "rest",  glyph: "·", title: "REST",     purpose: "No session scheduled." };
              const trainNextZoneClass = trainNext?.zone === "Z1" || trainNext?.zone === "Z2" || trainNext?.zone === "Z1–Z2" ? "green"
                                       : trainNext?.zone === "Z5" ? "red" : "amber";
              const trainNextPace = trainNext?.paceSecPerKm ? secPerKmToPace(trainNext.paceSecPerKm).replace("/km","") : null;
              const trainNextDur = trainNext?.durationMin ? fmtDuration(trainNext.durationMin, "min") : null;

              return (
                <>
                  {/* Hero — next session (session-type themed) */}
                  {trainNext ? (
                    <div
                      className={`heroCard heroCard--${trainHeroTheme.cls}`}
                      role="button"
                      tabIndex={0}
                      onClick={() => setTrainSessionSheet(trainNext)}
                      onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setTrainSessionSheet(trainNext); } }}
                    >
                      <div className="heroCard__glyph" aria-hidden="true">{trainHeroTheme.glyph}</div>
                      <div className="heroCard__label">
                        <span className="heroCard__theme">▶ {trainHeroTheme.title} · NEXT SESSION</span>
                        {trainNext.zone && <span className={`pvaZonePill pvaZonePill--${trainNextZoneClass}`}>{trainNext.zone}</span>}
                      </div>
                      <div className="heroCard__name">
                        {trainNext.title || trainNext.label || trainNext.type}
                        {trainNext.distanceKm > 0 && <> · {fmt1(trainNext.distanceKm)} km</>}
                      </div>
                      <div className="heroCard__meta">
                        {trainNextPace && <><strong>{trainNextPace}/km</strong> · </>}
                        {trainNextDur && <><strong>{trainNextDur}</strong> · </>}
                        {trainNext.date && (() => {
                          // Same combined "relative · absolute" treatment as
                          // the Today next-session card — keeps the two
                          // surfaces consistent.
                          const rel = formatRelativeDay(trainNext.date);
                          const abs = trainNext.dayLabel || formatDateShort(trainNext.date);
                          return rel ? <><strong>{rel}</strong>{" · "}{abs}</> : abs;
                        })()}
                      </div>
                      <div className="heroCard__bottom">
                        <div className="heroCard__purpose"><strong>{trainHeroTheme.title.charAt(0) + trainHeroTheme.title.slice(1).toLowerCase()}.</strong> {trainHeroTheme.purpose}</div>
                        <span className="heroCard__tap">Tap to edit ›</span>
                      </div>
                    </div>
                  ) : (
                    <div className="heroCard heroCard--rest">
                      <div className="heroCard__glyph" aria-hidden="true">·</div>
                      <div className="heroCard__label"><span className="heroCard__theme">◎ NO NEXT SESSION</span></div>
                      <div className="heroCard__name">Rest day</div>
                      <div className="heroCard__meta">Nothing scheduled.</div>
                    </div>
                  )}

                  {/* Strip — this week / next week / race in */}
                  {(() => {
                    // Progress stats for this-week cell: count training sessions
                    // (skip races, they don't match against Strava).
                    const trainables = thisWeekSessions.filter(s => s.kind !== "race");
                    const doneCount = trainables.filter(s => {
                      const st = statusFor(s);
                      return st === "matched" || st === "partial";
                    }).length;
                    const totalCount = trainables.length;
                    const allDone = totalCount > 0 && doneCount === totalCount;
                    const cellCls = totalCount === 0
                      ? "statCell--neutral"
                      : allDone
                        ? "statCell--ontrack"
                        : (doneCount > 0 ? "statCell--warning" : "statCell--neutral");
                    /* One quiet line, not a three-row panel: with the hero
                       above it, a panel pushed the first session — the thing
                       this tab is for — under the tab bar at 375 px. */
                    return (
                      <div className={`trainLine trainLine--${cellCls.replace("statCell--", "")}`} aria-label="This week at a glance">
                        <span className="trainLine__dot" aria-hidden="true" />
                        <span>{totalCount > 0 ? (allDone ? "All done" : `${doneCount}/${totalCount} done`) : `${thisWeekSessions.length} ${thisWeekSessions.length === 1 ? "session" : "sessions"}`}</span>
                        {thisWeekNum != null ? <span className="trainLine__sep" /> : null}
                        {thisWeekNum != null ? <span>{`Week ${thisWeekNum}${thisWeekPhase ? ` · ${thisWeekPhase}` : ""}`}</span> : null}
                        {weeksToRace != null ? <span className="trainLine__sep" /> : null}
                        {weeksToRace != null ? <span>{`Race in ${weeksToRace} wk`}</span> : null}
                      </div>
                    );
                  })()}

                  {/* Stale-plan banner — shown above all sessions when the plan
                      was generated by an out-of-date server process or with
                      different day settings. See diagnosePlanStale() for
                      reason codes. */}
                  {planStaleReason && (
                    <div className="v7section" style={{ padding: 14, background: "rgb(var(--critical-rgb) / 0.08)", borderLeft: "3px solid var(--critical)", marginBottom: 8 }}>
                      <div style={{ fontSize: "var(--text-sm-size)", fontWeight: 700, color: "var(--content-primary)" }}>
                        {planStaleReason === 'old-generator'
                          ? 'Plan was generated by an older server version'
                          : planStaleReason === 'corrupt-dates'
                          ? 'Plan dates look wrong (sessions on days you did not select)'
                          : 'You changed available days since the plan was generated'}
                      </div>
                      <div style={{ fontSize: "var(--text-xs-size)", color: "var(--content-secondary)", marginTop: 4, lineHeight: 1.4 }}>
                        {planStaleReason === 'corrupt-dates'
                          ? 'This usually means the Node server needs to be restarted so it picks up the latest generator code. After restart, regenerate the plan.'
                          : planStaleReason === 'old-generator' && planHasAppliedChanges(serverPlan)
                          ? 'Regenerating rebuilds the weeks ahead from your goals with the current generator and replaces the coach adjustments you accepted for them. Past weeks stay as they are.'
                          : 'Regenerate the plan to align it with your current settings.'}
                      </div>
                      <div style={{ marginTop: 10, display: "flex", gap: 8, flexWrap: "wrap" }}>
                        <button
                          className="btn btnOk"
                          style={{ padding: "6px 12px", fontSize: "var(--text-xs-size)" }}
                          onClick={() => regeneratePlanWithCurrentDays()}
                          disabled={serverPlanLoading}
                        >
                          {serverPlanLoading ? "Regenerating…" : "Regenerate plan"}
                        </button>
                        <button
                          className="btn btnGhost"
                          style={{ padding: "6px 12px", fontSize: "var(--text-xs-size)" }}
                          onClick={() => setShowPlanGenerator(true)}
                        >
                          Open Plan Generator
                        </button>
                      </div>
                    </div>
                  )}

                  {/* This week — what you will actually run. */}
                  <div className="v7section trainThisWeek">
                    <div className="v7sectionLabel v7sectionLabel--withPill">
                      <span>THIS WEEK · {fmtRange(thisMonday, thisSunday)}</span>
                      <PhasePill meta={phaseMetaForSessions(thisWeekSessions)} />
                    </div>
                    {thisWeekSessions.length > 0
                      ? thisWeekSessions.map((s) => trainRow(s, false))
                      : <div className="trainEmpty">No sessions planned this week.</div>}
                  </div>

                  {/* Next week — concrete, same as this week. The owner's
                      rule: the week ahead is always firm; only what comes
                      after it is tentative. */}
                  <div className="v7section trainNextWeek">
                    <div className="v7sectionLabel v7sectionLabel--withPill">
                      <span>NEXT WEEK · {fmtRange(wk1Mon, wk1Sun)}</span>
                      <PhasePill meta={phaseMetaForSessions(next1Sessions)} />
                    </div>
                    {next1Sessions.length > 0
                      ? next1Sessions.map((s) => trainRow(s, false))
                      : <div className="trainEmpty">No sessions planned.</div>}
                  </div>

                  {/* The rest of the plan — folded, and tentative: the coach
                      may still move it, so its rows are drawn provisional.
                      One row says how much is behind it; open it and each
                      later week is a line you can open on its own. Nobody
                      needs 120 provisional rows between them and the race. */}
                  {(() => {
                    const remaining = all.filter((s) => weekOf(s) >= +wk2Mon);
                    if (!remaining.length) return null;
                    const weekMap = new Map();
                    for (const s of remaining) {
                      const wk = +startOfWeek(new Date(s.date));
                      if (!weekMap.has(wk)) weekMap.set(wk, []);
                      weekMap.get(wk).push(s);
                    }
                    const weekKeys = Array.from(weekMap.keys()).sort((a, b) => a - b);
                    const kmOf = (list) => list.reduce((a, s) => a + (Number(s.distanceKm) || Number(s.estimatedKm) || 0), 0);
                    const laterKm = kmOf(remaining);
                    // A fixed locale: toLocaleString() on a German phone printed
                    // "1.032 km" beside "35.3 km", and 1.032 reads as one km.
                    const kmText = (v) => Math.round(v).toLocaleString("en-GB");
                    const weeksText = `${weekKeys.length} ${weekKeys.length === 1 ? "week" : "weeks"}`;
                    const toggleWeek = (wk) => setTrainOpenWeeks((prev) => {
                      const next = new Set(prev);
                      if (next.has(wk)) next.delete(wk); else next.add(wk);
                      return next;
                    });
                    return (
                      <div className="v7section trainRest">
                        <ListMore
                          className="trainMore"
                          open={trainShowRest}
                          label={trainShowRest ? "Show less" : <>Rest of the plan <span className="likelyPill">Tentative</span></>}
                          count={trainShowRest ? null : `${weeksText} · ${kmText(laterKm)} km`}
                          onClick={() => setTrainShowRest((v) => !v)}
                        />
                        {trainShowRest ? (
                          <div className="v7sectionCaption trainRest__caption">
                            <span className="likelyPill">Tentative</span> The coach may still move these as the weeks before them go.
                          </div>
                        ) : null}
                        {trainShowRest ? weekKeys.map((wk, i) => {
                          const sessions = weekMap.get(wk);
                          const mon = new Date(wk);
                          const sun = new Date(+mon + 6 * 864e5);
                          const weekNum = planWeekNum(mon);
                          const open = trainOpenWeeks.has(wk);
                          return (
                            <div className={"trainWeek" + (open ? " is-open" : "")} key={wk} style={{ "--i": i }}>
                              <button type="button" className="trainWeek__head" aria-expanded={open} onClick={() => toggleWeek(wk)}>
                                <span className="trainWeek__body">
                                  <span className="trainWeek__title">{weekNum ? `Week ${weekNum}` : "Week"} · {fmtRange(mon, sun)}</span>
                                  <span className="trainWeek__meta">
                                    <PhasePill meta={phaseMetaForSessions(sessions)} />
                                    <span>{sessions.length} {sessions.length === 1 ? "session" : "sessions"} · {Math.round(kmOf(sessions))} km</span>
                                  </span>
                                </span>
                                <ChevronDown size={16} aria-hidden="true" />
                              </button>
                              {open ? <div className="trainWeek__rows">{sessions.map((s) => trainRow(s, true))}</div> : null}
                            </div>
                          );
                        }) : null}
                      </div>
                    );
                  })()}

                  {/* Race — pinned at the very bottom so the plan literally
                      ends at the goal race. plannedSessionsFull never includes
                      the race itself (it's the plan's anchor, not a training
                      session), so we synthesise a minimal race row here from
                      goalSaved + goalDistanceKm and pass it through TrainRow
                      which already knows how to render kind:"race". */}
                  {hasSavedGoal && goalDateObj && (() => {
                    const distKm = Number(goalDistanceKm) || 0;
                    if (!distKm) return null;
                    // Parse target time hh:mm:ss to seconds; fall back to a
                    // sensible default if missing/malformed so we still show
                    // the row instead of suppressing it on bad input.
                    const targetSec = (() => {
                      const t = String(goalTimeStr || "").trim();
                      const m = /^(\d+):(\d{1,2}):(\d{1,2})$/.exec(t);
                      if (!m) return null;
                      return Number(m[1]) * 3600 + Number(m[2]) * 60 + Number(m[3]);
                    })();
                    const paceSec = targetSec && distKm > 0 ? Math.round(targetSec / distKm) : null;
                    const raceLbl = distKm >= 80 ? `${Math.round(distKm)}K`
                                  : distKm >= 40 ? `${Math.round(distKm)}K`
                                  : distKm >= 20 ? "Marathon"
                                  : distKm >= 18 ? "Half Marathon"
                                  :                `${Math.round(distKm)}K`;
                    const raceRow = {
                      kind: "race",
                      date: goalDateObj,
                      title: `${raceLbl} (Goal)`,
                      type: "race",
                      zone: "Race",
                      distanceKm: distKm,
                      pace: paceSec ? secPerKmToPace(paceSec) : null,
                      paceSecPerKm: paceSec,
                      durationMin: targetSec ? Math.round(targetSec / 60) : null,
                      raceColor: "var(--viz-long)",
                      badge: `Race • ${raceLbl}`,
                    };
                    const wksToRace = Math.max(0, Math.ceil((+goalDateObj - Date.now()) / (7 * 864e5)));
                    return (
                      <div className="v7section">
                        <div className="v7sectionLabel v7sectionLabel--withPill">
                          <span>RACE · {formatDateShort(goalDateObj)}</span>
                          <span className="pvaZonePill" style={{ color: "var(--viz-long)", background: "rgb(var(--iris-rgb) / 0.18)", border: "1px solid rgb(var(--iris-rgb) / 0.38)" }}>
                            {wksToRace === 0 ? "This week" : `${wksToRace} ${wksToRace === 1 ? "wk" : "wks"}`}
                          </span>
                        </div>
                        <TrainRow s={raceRow} status={null} effectiveMaxHr={effectiveMaxHr} onOpen={() => setTrainSessionSheet(raceRow)} />
                      </div>
                    );
                  })()}

                  {/* Export */}
                  {plannedSessionsFull.length > 0 && (
                    <div className="v7section">
                      <button className="v7exportBtn" onClick={() => { const ics = generateICS(plannedSessionsFull); downloadICS("ultrarunner-training-plan.ics", ics); }}>
                        <Download size={16} /> Export to calendar (.ics)
                      </button>
                    </div>
                  )}
                </>
              );
            })()}
          </div>
        )}

        {/* GOAL TAB */}
        {activeTab === "goal" && (
          <div className="tabPage">
            {hasSavedGoal ? (() => {
              // Weeks left + elevation pulled from goalSaved for the hero meta line
              const goalElev = Number(goalSaved?.elevationGain || 0);
              const daysToRace = goalDateObj ? Math.max(0, Math.ceil((+goalDateObj - Date.now()) / 864e5)) : null;
              const weeksLeft = goalDateObj ? Math.max(0, Math.ceil((+goalDateObj - Date.now()) / (7 * 864e5))) : null;
              const taperWeeks = goalDistanceKm >= 80 ? 3 : 2;
              const taperInWeeks = weeksLeft != null ? Math.max(0, weeksLeft - taperWeeks) : null;

              // Longest long-run in last 60 days (methodology: ≥ 18km anchor)
              const longestLR = (() => {
                if (!Array.isArray(runs)) return null;
                const cutoff = Date.now() - 60 * 864e5;
                let max = 0;
                for (const r of runs) {
                  if (!(r.distance_m > 0)) continue;
                  const t = new Date(r.start_date || r.date).getTime();
                  if (isNaN(t) || t < cutoff) continue;
                  const km = r.distance_m / 1000;
                  if (km > max) max = km;
                }
                return max > 0 ? max : null;
              })();
              const lrOk = longestLR != null && longestLR >= 18;
              const lrCellCls = longestLR == null ? "statCell--neutral" : lrOk ? "statCell--ontrack" : "statCell--warning";
              const lrVerdict = longestLR == null ? "—" : lrOk ? "✓ ≥18km" : "Target 18km";

              // Fitness trend — use comfortPaceDelta as proxy (negative = improving)
              const fitCls = !isFinite(Number(comfortPaceDelta)) ? "statCell--neutral"
                           : Number(comfortPaceDelta) <= -3 ? "statCell--ontrack"
                           : Number(comfortPaceDelta) <= 3  ? "statCell--neutral"
                           :                                   "statCell--warning";
              const fitValue = !isFinite(Number(comfortPaceDelta)) ? "—"
                             : Number(comfortPaceDelta) <= -3 ? "Improving"
                             : Number(comfortPaceDelta) <= 3  ? "Stable"
                             :                                   "Drifting";

              // Weeks-left verdict tint
              const weeksCellCls = weeksLeft == null ? "statCell--neutral"
                                 : taperInWeeks != null && taperInWeeks <= 2 ? "statCell--warning"
                                 :                                             "statCell--neutral";

              // Phase boundaries (Base 35% / Build 30% / Specific 20% / Taper 15%)
              const planStartMs = planStartDate ? +planStartDate : null;
              const goalMs = goalDateObj ? +goalDateObj : null;
              const totalDays = (planStartMs && goalMs) ? Math.max(1, Math.ceil((goalMs - planStartMs) / 864e5)) : null;
              const nowMs = Date.now();

              const phaseAt = (pct) => planStartMs != null && totalDays != null ? planStartMs + pct * totalDays * 864e5 : null;
              const baseEnd = phaseAt(0.35);
              const buildEnd = phaseAt(0.65);
              const specificEnd = phaseAt(0.85);

              const phaseStatus = (startMs, endMs) => {
                if (startMs == null || endMs == null) return 'future';
                if (endMs < nowMs) return 'done';
                if (startMs <= nowMs && nowMs <= endMs) return 'current';
                return 'future';
              };
              const baseState = phaseStatus(planStartMs, baseEnd);
              const buildState = phaseStatus(baseEnd, buildEnd);
              const specificState = phaseStatus(buildEnd, specificEnd);
              const taperState = phaseStatus(specificEnd, goalMs);

              // Phase-identity colours match the Stats screen phase legend
              // (Pillar 3 in the methodology doc). Done / current / upcoming
              // is conveyed via opacity + a Pill state badge — the phase's
              // own colour is its identity, not its progress.
              const PHASE_COLOR = {
                base:     'rgb(var(--accent-rgb) / 0.55)',     // sage green
                build:    'rgb(var(--caution-rgb) / 0.55)',     // amber
                specific: 'rgb(var(--critical-rgb) / 0.55)',     // coral
                taper:    'rgb(var(--neutral-rgb) / 0.6)',      // neutral grey
              };
              const PHASE_COLOR_DIM = {
                base:     'rgb(var(--accent-rgb) / 0.22)',
                build:    'rgb(var(--caution-rgb) / 0.22)',
                specific: 'rgb(var(--critical-rgb) / 0.22)',
                taper:    'rgb(var(--neutral-rgb) / 0.28)',
              };
              const phaseSpine = (phaseKey, state) => state === 'future' ? PHASE_COLOR_DIM[phaseKey] : PHASE_COLOR[phaseKey];
              // Pill badges so state reads consistently with the rest of the
              // app (Strava "Active" pill above uses the same component). ok
              // = sage filled, warn = amber filled, default = muted outline.
              const phaseStatePill = (state) =>
                state === 'done' ? <Pill ok>Done</Pill>
                : state === 'current' ? <Pill warn>Current</Pill>
                : <Pill>Upcoming</Pill>;
              const phaseRowOpacity = (state) => state === 'future' ? 0.62 : 1;

              return (
              <>
                {/* Hero: race — always purple, unambiguous */}
                <div
                  className="heroCard heroCard--long goalHero"
                  role="button"
                  tabIndex={0}
                  onClick={() => openAdjustGoalSheet()}
                  onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); openAdjustGoalSheet(); } }}
                >
                  <div className="heroCard__glyph" aria-hidden="true">◆</div>
                  <div className="heroCard__label">
                    <span className="heroCard__theme">◆ RACE · {goalSaved?.date ? formatDateShort(new Date(goalSaved.date)) : "—"}</span>
                    <span className="pvaZonePill" style={{ color: "var(--viz-long)", background: "rgb(var(--iris-rgb) / 0.18)" }}>{goalDistanceKm ? `${Math.round(goalDistanceKm)}K` : goalSaved?.type || "—"}</span>
                  </div>
                  <div className="heroCard__name">{goalDistanceKm ? `${Math.round(goalDistanceKm)}K` : goalSaved?.type || "—"}{(goalSaved?.terrain && goalSaved.terrain !== "road") ? ` ${goalSaved.terrain.charAt(0).toUpperCase() + goalSaved.terrain.slice(1)}` : goalSaved?.terrain === "road" ? " Road" : ""}</div>
                  <div className="heroCard__meta">
                    {goalTimeStr && <><strong>Target {goalTimeStr}</strong> · </>}
                    {goalElev > 0 && <>{goalElev} m elevation · </>}
                    {daysToRace != null && <>{daysToRace} days to go</>}
                  </div>
                  <div className="heroCard__bottom">
                    <div className="heroCard__purpose">
                      {planTrajectory ? (
                        <>
                          <strong className="purple">{planTrajectory.label}</strong>
                          {" · "}trending {fmtFinishHM(planTrajectory.projectedSec)}
                          {planTrajectory.deltaSec !== 0 ? <> · {fmtDeltaHM(planTrajectory.deltaSec)} vs goal</> : null}
                        </>
                      ) : suggestedGoalTimeStr ? (
                        <>est. finish {suggestedGoalTimeStr}</>
                      ) : null}
                    </div>
                    <span className="heroCard__tap">Tap to adjust ›</span>
                  </div>
                </div>

                {/* Adaptive action under the hero: act on the trajectory.
                    Behind → adjust the plan. Strong pace while on plan → a calm
                    acknowledgement only. Running faster than planned never adds
                    volume (§Adaptation Rules: volume increases are never
                    automatic — the "add a run" offer was dropped 20.09.2026). */}
                {planTrajectory && (() => {
                  const st = planTrajectory.status;
                  if (st === 'behind' || st === 'slightly') {
                    return (
                      <button type="button" className="btn btnOk goalHeroAction" onClick={() => setShowPlanGenerator(true)}>
                        Adjust plan
                      </button>
                    );
                  }
                  if (st === 'onplan' && paceFit && paceFit.status === 'onplan') {
                    return <div className="goalHeroNote">Ahead on pace — hold steady, keep the long runs.</div>;
                  }
                  return null;
                })()}

                {/* Strip — 3 goal-critical metrics */}
                <div className="statStrip statStrip--rows">
                  <div className={`statCell ${weeksCellCls}`}>
                    <div className="statCell__label">Weeks left</div>
                    <div className="statCell__value">{weeksLeft != null ? weeksLeft : "—"}</div>
                    <div className="statCell__verdict"><span className="statCell__dot" />{taperInWeeks != null ? `Taper in ${taperInWeeks}` : "—"}</div>
                  </div>
                  <div className={`statCell ${lrCellCls}`}>
                    <div className="statCell__label">Longest LR</div>
                    <div className="statCell__value">{longestLR != null ? `${fmt1(longestLR)} km` : "—"}</div>
                    <div className="statCell__verdict"><span className="statCell__dot" />{lrVerdict}</div>
                  </div>
                  <div className={`statCell ${fitCls}`}>
                    <div className="statCell__label">Fitness</div>
                    <div className="statCell__value">{fitValue}</div>
                    <div className="statCell__verdict"><span className="statCell__dot" />{isFinite(Number(comfortPaceDelta)) ? formatPaceDelta(comfortPaceDelta) : "—"}</div>
                  </div>
                </div>

                {/* Key milestones — phase-identity coloured spines, state
                    conveyed via Pill + row opacity. Phase colours match the
                    Stats screen legend. */}
                <div className="v7section">
                  <div className="v7sectionLabel">KEY MILESTONES</div>
                  <div className="v7sessionRow" style={{ cursor: "default", opacity: phaseRowOpacity(baseState) }}>
                    <div className="v7trainRowAccent" style={{ background: phaseSpine('base', baseState) }} />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div className="v7sessionRowLine1">
                        <div><span style={{ fontSize: "var(--text-base-size)", fontWeight: 600, color: "var(--content-primary)" }}>Base phase</span></div>
                        <div>{phaseStatePill(baseState)}</div>
                      </div>
                      <div className="v7sessionRowLine2"><span className="muted">aerobic foundation · {baseEnd ? formatDateShort(new Date(baseEnd)) : "—"}</span></div>
                    </div>
                  </div>
                  <div className="v7sessionRow" style={{ cursor: "default", opacity: phaseRowOpacity(buildState) }}>
                    <div className="v7trainRowAccent" style={{ background: phaseSpine('build', buildState) }} />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div className="v7sessionRowLine1">
                        <div><span style={{ fontSize: "var(--text-base-size)", fontWeight: 600, color: "var(--content-primary)" }}>Build phase</span></div>
                        <div>{phaseStatePill(buildState)}</div>
                      </div>
                      <div className="v7sessionRowLine2"><span className="muted">threshold + peak volume</span></div>
                    </div>
                  </div>
                  <div className="v7sessionRow" style={{ cursor: "default", opacity: phaseRowOpacity(specificState) }}>
                    <div className="v7trainRowAccent" style={{ background: phaseSpine('specific', specificState) }} />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div className="v7sessionRowLine1">
                        <div><span style={{ fontSize: "var(--text-base-size)", fontWeight: 600, color: "var(--content-primary)" }}>Specific phase</span></div>
                        <div>{phaseStatePill(specificState)}</div>
                      </div>
                      <div className="v7sessionRowLine2"><span className="muted">race simulation + B2Bs</span></div>
                    </div>
                  </div>
                  <div className="v7sessionRow" style={{ cursor: "default", opacity: phaseRowOpacity(taperState) }}>
                    <div className="v7trainRowAccent" style={{ background: phaseSpine('taper', taperState) }} />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div className="v7sessionRowLine1">
                        <div><span style={{ fontSize: "var(--text-base-size)", fontWeight: 600, color: "var(--content-primary)" }}>Taper phase</span></div>
                        <div>{phaseStatePill(taperState)}</div>
                      </div>
                      <div className="v7sessionRowLine2"><span className="muted">{taperWeeks} weeks · 70% → 50% → 30%</span></div>
                    </div>
                  </div>
                </div>

                {/* Inline action links — regenerate plan + edit settings.
                    The full goal/connections UI lives in the Settings sheet
                    (gear icon top-right); this row is the inline shortcut so
                    the user doesn't have to hunt for it. */}
                <div className="goalActionRow">
                  <button className="goalLinkBtn" onClick={() => setShowPlanGenerator(true)}>
                    <RotateCcw size={13} style={{ marginRight: 4, verticalAlign: "-2px" }} /> Regenerate plan
                  </button>
                  <button className="goalLinkBtn" onClick={() => setSettingsOpen(true)}>
                    <Sliders size={13} style={{ marginRight: 4, verticalAlign: "-2px" }} /> Edit goal &amp; connections
                  </button>
                </div>
              </>
              );
            })() : (
              <div className="v7section" style={{ textAlign: "center", padding: "40px 24px" }}>
                <div style={{ fontSize: "var(--text-lg-size)", fontWeight: 800, color: "var(--text-primary)", marginBottom: 8 }}>No goal set</div>
                <div style={{ fontSize: "var(--text-sm-size)", color: "var(--text-muted)", marginBottom: 16 }}>Set a race goal to generate a training plan.</div>
                <button className="v7exportBtn" onClick={() => setShowPlanGenerator(true)}><Plus size={16} /> Generate plan</button>
              </div>
            )}
          </div>
        )}
      </div>

      {/* ===== BOTTOM TAB BAR (shown at all widths, fixed inside the centred column) ===== */}
      {/* Oura-pattern nav: a floating pill of destinations plus a DETACHED
          circular action to its right. Coach moves out of the pill and
          becomes that action — it is the "ask something" verb among four
          nouns, which is exactly the split Oura makes. No new behaviour:
          the button sets the same activeTab the pill tab used to. */}
      <div className="navDock">
        <nav className="mobileTabBar" role="tablist">
          {TABS.filter((t) => t !== "coach").map(tab => {
            const Icon = TAB_ICONS[tab];
            return (
              <button
                key={tab}
                role="tab"
                aria-selected={activeTab === tab}
                className={`mobileTab${activeTab === tab ? " active" : ""}`}
                /* Press feedback runs on pointer events, not :active. iOS
                   Safari fires :active on a <button> only intermittently, and
                   -webkit-tap-highlight-color is transparent here, so a tap
                   used to produce NOTHING until the screen swapped. The class
                   is toggled on the node directly rather than through state:
                   this button lives inside the app's root component, and a
                   re-render of the whole tree on finger-down is not a price
                   worth paying for a visual effect. */
                onPointerDown={(e) => e.currentTarget.classList.add("is-pressing")}
                onPointerUp={(e) => e.currentTarget.classList.remove("is-pressing")}
                onPointerCancel={(e) => e.currentTarget.classList.remove("is-pressing")}
                onPointerLeave={(e) => e.currentTarget.classList.remove("is-pressing")}
                onClick={() => {
                  if (typeof navigator !== "undefined" && navigator.vibrate) navigator.vibrate(8);
                  setActiveTab(tab);
                }}
              >
                <Icon active={activeTab === tab} />
                <span>{TAB_LABELS[tab]}</span>
              </button>
            );
          })}
        </nav>
        <button
          type="button"
          className={`navAction${activeTab === "coach" ? " active" : ""}`}
          aria-label={TAB_LABELS.coach}
          aria-pressed={activeTab === "coach"}
          onPointerDown={(e) => e.currentTarget.classList.add("is-pressing")}
          onPointerUp={(e) => e.currentTarget.classList.remove("is-pressing")}
          onPointerCancel={(e) => e.currentTarget.classList.remove("is-pressing")}
          onPointerLeave={(e) => e.currentTarget.classList.remove("is-pressing")}
          onClick={() => {
            if (typeof navigator !== "undefined" && navigator.vibrate) navigator.vibrate(8);
            markCoachRead();
            setActiveTab("coach");
          }}
        >
          {(() => { const Icon = TAB_ICONS.coach; return <Icon />; })()}
          {coachNeedsReview && activeTab !== "coach" && <span className="tabDot" aria-label="Open proposal" />}
        </button>
      </div>

      {/* ===== SETTINGS SLIDE-UP SHEET (v2 — profile header, grouped
           sections, brand tiles, native-feel switches) ===== */}
      {settingsOpen && (
        <div className="bottomSheetOverlay" onClick={() => setSettingsOpen(false)}>
          <div className="bottomSheetPanel settingsPanel" onClick={e => e.stopPropagation()}>
            <div className="bottomSheetDragHandle" />
            <div className="bottomSheetHeader">
              <div style={{ fontWeight: 800, fontSize: "var(--text-base-size)" }}>Settings</div>
              <button type="button" className="bottomSheetClose" onClick={() => setSettingsOpen(false)} aria-label="Close">✕</button>
            </div>

            <div className="settingsList">
              {/* Profile / session card */}
              <div className="settingsProfile">
                <div className="settingsProfile__avatar"><Activity size={20} /></div>
                <div className="settingsProfile__meta">
                  <div className="settingsProfile__name">{authUser?.name || authUser?.email || "RESET · Ultra"}</div>
                  <div className="settingsProfile__sub">
                    {authUser?.name ? `${authUser.email} · ` : ""}{conn?.strava ? <><span className="settingsDot" /> Strava connected</> : conn?.imported ? <><span className="settingsDot" /> Runs imported</> : "No activity source yet"}
                  </div>
                </div>
                {conn?.strava ? (
                  <button type="button" className="settingsSyncBtn" onClick={syncStrava}>
                    <Cloud size={13} /> Sync
                  </button>
                ) : null}
              </div>

              <div className="settingsGroupLabel">Training</div>
              <div className="settingsGroup">
                <button className="settingsRow" onClick={() => { setSettingsOpen(false); setOpenGoal(true); }}>
                  <span className="settingsTile settingsTile--sage"><Flag size={14} /></span>
                  <span className="settingsRow__titleWrap">
                    <span className="settingsRow__title">Goal &amp; plan</span>
                    <span className="settingsRow__sub">Race, dates, days, heart-rate zones</span>
                  </span>
                  <span className="settingsRow__value">{goalSaved?.type || "Not set"}{goalSaved?.date ? ` · ${formatDateShort(new Date(goalSaved.date))}` : ""}</span>
                  <span className="settingsRow__chev">›</span>
                </button>
                <label className="settingsRow settingsRow--static">
                  <span className="settingsTile settingsTile--amber"><TrendingUp size={14} /></span>
                  <span className="settingsRow__titleWrap">
                    <span className="settingsRow__title">Count hikes &amp; walks</span>
                    <span className="settingsRow__sub">Adds Strava hikes to weekly volume</span>
                  </span>
                  <span className={`settingsSwitch${includeHikesInVolume ? " on" : ""}`}>
                    <input
                      type="checkbox"
                      checked={includeHikesInVolume}
                      onChange={(e) => setIncludeHikesInVolume(e.target.checked)}
                    />
                    <span className="settingsSwitch__knob" />
                  </span>
                </label>
              </div>

              {/* Open to all users. The remaining controls are the Worker's
                  NOTIFICATIONS_ENABLED kill switch and each user's own
                  opt-in, which defaults to off. */}
              <div className="settingsGroupLabel">Coach notifications</div>
              <div className="settingsGroup">
                <label className="settingsRow settingsRow--static">
                  <span className="settingsTile"><Bell size={14} /></span>
                  <span className="settingsRow__titleWrap">
                    <span className="settingsRow__title">Coach notifications</span>
                    <span className="settingsRow__sub">
                      {pushState.support && !pushState.support.ok
                        ? SUPPORT_COPY[pushState.support.reason]
                        : !pushState.serverEnabled
                        ? "Not switched on for this deployment yet"
                        : "A short training note, on a schedule you choose"}
                    </span>
                  </span>
                  <span className={`settingsSwitch${notifyPrefs.enabled && pushState.subscribed ? " on" : ""}`}>
                    <input
                      type="checkbox"
                      checked={notifyPrefs.enabled && pushState.subscribed}
                      disabled={pushState.busy || !pushState.serverEnabled || !(pushState.support?.ok)}
                      onChange={(e) => toggleNotifications(e.target.checked)}
                    />
                    <span className="settingsSwitch__knob" />
                  </span>
                </label>

                {notifyPrefs.enabled && pushState.subscribed && (
                  <>
                    <div className="settingsRow settingsRow--static notifyRow">
                      <span className="settingsRow__titleWrap">
                        <span className="settingsRow__title">How often</span>
                        <span className="settingsRow__sub">When the coach gets in touch</span>
                      </span>
                      <span className="notifySeg" role="group" aria-label="Notification frequency">
                        {[["daily", "Daily"], ["weekly", "Weekly"], ["sessions", "Session days"]].map(([v, label]) => (
                          <button
                            key={v}
                            type="button"
                            className={`notifySeg__btn${notifyPrefs.cadence === v ? " on" : ""}`}
                            aria-pressed={notifyPrefs.cadence === v}
                            onClick={() => applyNotify({ cadence: v })}
                          >{label}</button>
                        ))}
                      </span>
                    </div>

                    <div className="settingsRow settingsRow--static notifyRow">
                      <span className="settingsRow__titleWrap">
                        <span className="settingsRow__title">Time of day</span>
                        <span className="settingsRow__sub">Your local time</span>
                      </span>
                      <select
                        className="notifyHour"
                        value={notifyPrefs.hour}
                        aria-label="Notification hour"
                        onChange={(e) => applyNotify({ hour: Number(e.target.value) })}
                      >
                        {Array.from({ length: 24 }, (_, h) => (
                          <option key={h} value={h}>{String(h).padStart(2, "0")}:00</option>
                        ))}
                      </select>
                    </div>

                    <button type="button" className="settingsRow" onClick={testNotification} disabled={pushState.busy}>
                      <span className="settingsRow__title">Send a test notification</span>
                      <span className="settingsRow__chev">›</span>
                    </button>
                  </>
                )}

                {pushState.msg && (
                  <div className="notifyMsg" role="status" aria-live="polite">{pushState.msg}</div>
                )}
              </div>

              <div className="settingsGroupLabel">Connections</div>
              <div className="settingsGroup">
                <button className="settingsRow" onClick={() => { setSettingsOpen(false); setOpenConnect(true); }}>
                  <span className="settingsTile settingsTile--strava"><LinkIcon size={14} /></span>
                  <span className="settingsRow__title">Strava</span>
                  <span className="settingsRow__value">{conn?.strava ? <><span className="settingsDot" /> Connected</> : "Not connected"}</span>
                  <span className="settingsRow__chev">›</span>
                </button>
                {hasHealth ? (
                  <button className="settingsRow" onClick={() => runHealthSync()} disabled={importState.busy}>
                    <span className="settingsTile settingsTile--sage"><Activity size={14} /></span>
                    <span className="settingsRow__titleWrap">
                      <span className="settingsRow__title">{healthName}</span>
                      <span className="settingsRow__sub">
                        {session?.imported?.[healthSource()]?.count
                          ? `${session.imported[healthSource()].count} workouts · tap to sync`
                          : "Garmin, Coros, Suunto, Apple Watch"}
                      </span>
                    </span>
                    <span className="settingsRow__value">{session?.imported?.[healthSource()]?.connected ? <><span className="settingsDot" /> Connected</> : "Connect"}</span>
                    <span className="settingsRow__chev">›</span>
                  </button>
                ) : null}
                <button className="settingsRow" onClick={pickActivityFiles} disabled={importState.busy}>
                  <span className="settingsTile settingsTile--grey"><Cloud size={14} /></span>
                  <span className="settingsRow__titleWrap">
                    <span className="settingsRow__title">Upload activity files</span>
                    <span className="settingsRow__sub">
                      {session?.imported?.fit?.count ? `${session.imported.fit.count} imported · .fit or .gpx` : ".fit or .gpx from your watch app"}
                    </span>
                  </span>
                  <span className="settingsRow__chev">›</span>
                </button>
                {(importState.msg || importState.error) && (
                  <div className={importState.error ? "notifyMsg deltaNeg" : "notifyMsg"} role="status" aria-live="polite">
                    {importState.error || importState.msg}
                  </div>
                )}
                {garminEnabled ? (
                  <button className="settingsRow" onClick={() => { setSettingsOpen(false); setOpenConnect(true); }}>
                    <span className="settingsTile settingsTile--garmin"><Activity size={14} /></span>
                    <span className="settingsRow__title">Garmin</span>
                    <span className="settingsRow__value">{garminConn?.connected ? <><span className="settingsDot" /> Connected</> : "Not connected"}</span>
                    <span className="settingsRow__chev">›</span>
                  </button>
                ) : (
                  <div className="settingsRow settingsRow--static">
                    <span className="settingsTile settingsTile--garmin"><Activity size={14} /></span>
                    <span className="settingsRow__title">Garmin</span>
                    <span className="settingsBadge">Coming soon</span>
                  </div>
                )}
                {/* WHOOP entry point hidden for now (30.09.2026). Only the UI
                    is gone — connectWhoop(), /api/whoop/*, the session field
                    and the disconnect path are all untouched, so restoring
                    this is putting the row back. */}
              </div>

              <div className="settingsGroupLabel">About</div>
              <div className="settingsGroup">
                <button className="settingsRow" onClick={() => { setSettingsOpen(false); setOpenMethod(true); }}>
                  <span className="settingsTile settingsTile--grey"><Info size={14} /></span>
                  <span className="settingsRow__titleWrap">
                    <span className="settingsRow__title">How RESET works</span>
                    <span className="settingsRow__sub">The method, your plan, every number</span>
                  </span>
                  <span className="settingsRow__chev">›</span>
                </button>
                <div className="settingsRow settingsRow--static">
                  <span className="settingsTile settingsTile--grey"><Sliders size={14} /></span>
                  <span className="settingsRow__title">Version</span>
                  <span className="settingsRow__value">Beta · Jul 2026</span>
                </div>
              </div>

              <div className="settingsGroupLabel">Account</div>
              <div className="settingsGroup">
                {authUser?.role === "owner" && (
                  <InviteRow />
                )}
                {conn?.strava ? (
                  <button className="settingsRow settingsRow--danger" onClick={() => disconnect("strava")}>
                    <span className="settingsTile settingsTile--danger"><Lock size={14} /></span>
                    <span className="settingsRow__titleWrap">
                      <span className="settingsRow__title">Disconnect Strava</span>
                      <span className="settingsRow__sub">Removes access — reconnect anytime</span>
                    </span>
                  </button>
                ) : null}
                <button className="settingsRow" onClick={logout}>
                  <span className="settingsTile settingsTile--danger"><Lock size={14} /></span>
                  <span className="settingsRow__titleWrap">
                    <span className="settingsRow__title">Sign out</span>
                    <span className="settingsRow__sub">{authUser?.email}</span>
                  </span>
                </button>
              </div>

              <div className="settingsFooter">RESET · Ultra</div>
            </div>
          </div>
        </div>
      )}

      {/* ===== TRAIN SESSION DETAIL / EDIT SHEET ===== */}
      {statSheet && statSheetData && (
        <StatDetailSheet sheet={statSheetData} onClose={() => setStatSheet(null)} />
      )}
      {trainSessionSheet && (
        <TrainSessionSheet
          session={trainSessionSheet}
          onClose={() => setTrainSessionSheet(null)}
          onSave={(editKey, payload) => {
            setSessionEdits(prev => {
              const next = { ...prev, [editKey]: payload };
              try { localStorage.setItem(SESSION_EDITS_KEY, JSON.stringify(next)); } catch (_) {}
              return next;
            });
          }}
        />
      )}

      {/* ===== SESSION DETAIL FULL PAGE ===== */}
      {detailSheet ? <RunDetailSheet row={detailSheet} effectiveMaxHr={effectiveMaxHr} onClose={() => setDetailSheet(null)} /> : null}

      {/* ===== EXISTING MODALS (shared by mobile + desktop) ===== */}
{/* GOAL & PLAN MODAL */}
<Modal
  open={openGoal}
  title="Goal & plan"
  icon={Flag}
  onClose={() => setOpenGoal(false)}
>
  {/* The races and the training week, as rows. Only reachable once Strava
      is connected; syncing lives in Settings. One concern per section. */}
        <div className="goalSheet gpSheet">
          <section className="gpSection">
            <div className="gpSection__label">Races</div>
            <GoalsPanel
              goals={goals}
              onOpenWizard={() => { setOpenGoal(false); setShowPlanGenerator(true); }}
              onGoalsChange={(list) => { setGoals(list); syncGoalSavedFromGoals(list); }}
              onRebuild={rebuildFromGoals}
              rebuilding={rebuilding || serverPlanLoading}
              planWarnings={serverPlan?.macro?.warnings || []}
              planGoalIds={serverPlan?.macro ? [serverPlan.macro.primaryGoalId, ...(serverPlan.macro.embedded || []).map((e) => e.goalId)] : []}
              planGoals={serverPlan?.goals || []}
            />
          </section>

          {/* Training week — the days are the single source of truth. The
              generator decides which session lands on which day (long on
              the latest day, back-to-back consecutive, tempo with a buffer);
              here the athlete only says WHICH days exist. */}
          <section className="gpSection">
            <div className="gpSection__label">Training week</div>
            <div className="gpRow">
              <span className="gpRow__label">Runs per week</span>
              <div className="geSeg geSeg--4 gpSeg" role="radiogroup" aria-label="Runs per week">
                {[3, 4, 5, 6].map((n) => (
                  <button
                    type="button"
                    key={n}
                    role="radio"
                    aria-checked={runsPerWeek === n}
                    className={"geSeg__opt" + (runsPerWeek === n ? " is-on" : "")}
                    onClick={() => {
                      setRunsPerWeek(n);
                      // Keep availableDays >= runsPerWeek; pad if needed, keep existing selections.
                      if (availableDays.length < n) {
                        setAvailableDays(defaultAvailableDaysForCount(n, availableDays));
                      }
                    }}
                  >
                    {n}
                  </button>
                ))}
              </div>
            </div>
            <div className="gpRow gpRow--days">
              <span className="gpRow__label">
                Days
                <em className="gpRow__sub">Long-press a day for the long run{longRunDow != null ? ` · ${DAY_NAMES_LONG[longRunDow]}` : ""}</em>
              </span>
              <DayPills
                value={availableDays}
                onChange={setAvailableDays}
                minDays={runsPerWeek}
                longRunDow={longRunDow}
                onLongRunChange={setLongRunDow}
              />
            </div>
            {availableDays.length < runsPerWeek ? (
              <div className="agError" role="alert">Pick at least {runsPerWeek} days ({availableDays.length} selected).</div>
            ) : null}
            {/* If the active plan was generated with different days, offer regen. */}
            {serverPlan && !sameDayNameSet(serverPlan.availableDays, availableDays) && availableDays.length >= runsPerWeek ? (
              <div className="gpNotice">
                <span>The plan was built for {(serverPlan.availableDays || []).map((d) => String(d).slice(0, 3)).join(", ")}.</span>
                <button type="button" className="gpFix" onClick={() => regeneratePlanWithCurrentDays()} disabled={serverPlanLoading}>
                  {serverPlanLoading ? "Regenerating…" : "Regenerate"}
                </button>
              </div>
            ) : null}
            <div className="gpHint">Tempo keeps two days from the long run, back-to-back stays consecutive. Easy runs fill the rest.</div>
          </section>

          {/* Pace and heart rate — manual overrides for the plan's paces
              (Pace fit) and the HR zones. Each row opens its wheel. */}
          <section className="gpSection">
            <div className="gpSection__label">Pace and heart rate</div>
            <div className="gpFields">
              <button type="button" className={"gpField" + (userPaceStr ? "" : " is-empty")} onClick={() => setPicker("pace")}>
                <span className="gpField__text">
                  <span className="gpField__label">Threshold pace</span>
                  <span className="gpField__sub">{userPaceStr ? "Manual. Every training pace derives from it." : "From your Strava runs. Set it to override."}</span>
                </span>
                <span className="gpField__value">{userPaceStr ? `${userPaceStr}/km` : "Strava"}<ChevronRight size={16} className="gpField__chev" aria-hidden="true" /></span>
              </button>
              {userPaceStr ? (
                <button type="button" className="agLink gpFieldLink" onClick={() => setUserPaceStr("")}>Use my Strava pace instead</button>
              ) : null}
              <button type="button" className={"gpField" + (Number(userMaxHr) > 0 ? "" : " is-empty")} onClick={() => setPicker("hr")}>
                <span className="gpField__text">
                  <span className="gpField__label">Max heart rate</span>
                  <span className="gpField__sub">
                    {effectiveMaxHr
                      ? (Number(userMaxHr) > 0 ? "Manual. Zones are built on it." : stravaMaxHr ? `From Strava, ${effectiveMaxHr} bpm. Set it to override.` : `From your age, ${effectiveMaxHr} bpm. Set it to override.`)
                      : "Not set. Set it, or your age below."}
                  </span>
                </span>
                <span className="gpField__value">{Number(userMaxHr) > 0 ? `${userMaxHr} bpm` : effectiveMaxHr ? `${effectiveMaxHr} bpm` : "Set"}<ChevronRight size={16} className="gpField__chev" aria-hidden="true" /></span>
              </button>
              <button type="button" className={"gpField" + (Number(userAge) > 0 ? "" : " is-empty")} onClick={() => setPicker("age")}>
                <span className="gpField__text">
                  <span className="gpField__label">Age</span>
                  <span className="gpField__sub">The fallback for max heart rate: 220 minus age.</span>
                </span>
                <span className="gpField__value">{Number(userAge) > 0 ? `${userAge} yr` : "Set"}<ChevronRight size={16} className="gpField__chev" aria-hidden="true" /></span>
              </button>
            </div>
          </section>

          <WheelSheet
            key={picker || "none"}
            open={picker != null}
            title={picker === "pace" ? "Threshold pace" : picker === "hr" ? "Max heart rate" : "Age"}
            unit={picker === "pace" ? (<><span>min</span><span className="sep" /><span>sec</span></>) : picker === "hr" ? "bpm" : "yrs"}
            cols={picker === "hr"
              ? [{ max: 210, min: 120, initial: Number(userMaxHr) || 185 }]
              : picker === "age"
              ? [{ max: 99, initial: Number(userAge) || 35 }]
              : (() => { const sc = refPaceSec(userPaceStr) || 320; return [{ min: 2, max: 9, initial: Math.floor(sc / 60) }, { max: 59, initial: sc % 60, sepChar: ":" }]; })()}
            onCancel={() => setPicker(null)}
            onSet={(vals) => {
              if (picker === "pace") {
                // Clamp to the valid range (the wheel allows e.g. 9:59).
                const sec = Math.min(REF_PACE_MAX_SEC, Math.max(REF_PACE_MIN_SEC, vals[0] * 60 + vals[1]));
                setUserPaceStr(Math.floor(sec / 60) + ":" + wpad(sec % 60));
              }
              else if (picker === "hr") setUserMaxHr(String(vals[0]));
              else if (picker === "age") setUserAge(String(vals[0]));
              setPicker(null);
            }}
          />

          {/* Hikes/walks inclusion lives in Settings → Training — it is an
              account preference, not part of goal setting. */}
          <div className="gpActions">
            {!hasSavedGoal ? (
              <div className="gpHint">Add your goal race above to build a plan.</div>
            ) : !serverPlan?.weeks?.length ? (
              <button type="button" className="sessionSheetBtn sessionSheetBtn--primary gpWizardBtn" onClick={() => { setOpenGoal(false); setShowPlanGenerator(true); }}>
                Build plan
              </button>
            ) : (
              <button type="button" className="sessionSheetBtn sessionSheetBtn--secondary gpWizardBtn" onClick={() => { setOpenGoal(false); setShowPlanGenerator(true); }}>
                Open the plan wizard
              </button>
            )}
          </div>

          {forcedRecoveryWeekStartKey != null ? (
            <div className="gpHint">Recovery week inserted starting {formatDateLong(new Date(Number(forcedRecoveryWeekStartKey)))} (explicit).</div>
          ) : null}
        </div>
</Modal>

        {/* CONNECT MODAL */}
        <Modal open={openConnect} title="Connect services" icon={LinkIcon} onClose={() => setOpenConnect(false)}>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px,1fr))", gap: 12 }}>
            <div className="card" style={{ padding: 14 }}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10 }}>
                <div style={{ fontWeight: 950 }}>Strava</div>
                <Pill ok={conn.strava} warn={!conn.strava}>
                  {conn.strava ? "✅ Connected" : "➕ Not connected"}
                </Pill>
              </div>
              <div className="muted" style={{ marginTop: 8, fontSize: "var(--text-xs-size)", lineHeight: 1.35 }}>
                Used for runs, weekly volume, and comfortable pace.
              </div>

              {stravaStatus ? <div className="muted" style={{ marginTop: 10, fontSize: "var(--text-xs-size)" }}>{stravaStatus}</div> : null}
              {stravaError ? <div className="muted deltaNeg" style={{ marginTop: 8, fontSize: "var(--text-xs-size)" }}>{stravaError}</div> : null}

              <div style={{ marginTop: 12, display: "flex", gap: 8, flexWrap: "wrap" }}>
                {!conn.strava ? (
                  <button className="btn btnOk" onClick={connectStrava}>
                    <LinkIcon size={16} /> Connect Strava
                  </button>
                ) : (
                  <>
                    <button className="btn btnOk" onClick={syncStrava}>
                      <Cloud size={16} /> Sync
                    </button>
                    <button className="btn btnGhost" onClick={() => disconnect("strava")}>
                      Disconnect
                    </button>
                  </>
                )}
              </div>
            </div>

            {/* WHOOP connect card hidden for now — see the note above. */}

            {/* Garmin — rendered only when the server-side feature flag
                (GARMIN_ENABLED) is on. See refreshGarmin(). */}
            {garminEnabled ? (
              <div className="card" style={{ padding: 14 }}>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10 }}>
                  <div style={{ fontWeight: 950 }}>Garmin</div>
                  <Pill ok={!!garminConn?.connected} warn={!garminConn?.connected}>
                    {garminConn?.connected ? "Connected" : "Not connected"}
                  </Pill>
                </div>
                <div className="muted" style={{ marginTop: 8, fontSize: "var(--text-xs-size)", lineHeight: 1.35 }}>
                  Connect Garmin to sync activities directly from your watch.
                </div>

                {garminConn?.connected ? (
                  <div className="muted" style={{ marginTop: 10, fontSize: "var(--text-xs-size)" }}>
                    Account {String(garminConn.userId || "—").slice(0, 8)} ·{" "}
                    {garminConn.lastSyncAt
                      ? `last sync ${new Date(garminConn.lastSyncAt).toLocaleString()}`
                      : "no activities received yet"}
                  </div>
                ) : null}
                {garminStatus ? <div className="muted" style={{ marginTop: 10, fontSize: "var(--text-xs-size)" }}>{garminStatus}</div> : null}
                {garminError ? <div className="muted deltaNeg" style={{ marginTop: 8, fontSize: "var(--text-xs-size)" }}>{garminError}</div> : null}

                <div style={{ marginTop: 12, display: "flex", gap: 8, flexWrap: "wrap" }}>
                  {!garminConn?.connected ? (
                    <button className="btn btnOk" onClick={connectGarmin}>
                      <LinkIcon size={16} /> Connect Garmin
                    </button>
                  ) : (
                    <button className="btn btnGhost" onClick={() => disconnect("garmin")}>
                      Disconnect
                    </button>
                  )}
                </div>
              </div>
            ) : null}
          </div>

          <div className="muted" style={{ marginTop: 12, fontSize: "var(--text-xs-size)" }}>
            Session:{" "}
            {loadingSession ? (
              "Loading…"
            ) : session ? (
              <span className="deltaPos">OK</span>
            ) : (
              <span className="deltaNeg">Not logged in / no session</span>
            )}
          </div>
        </Modal>

        {/* METHOD MODAL */}
        {openMethod ? (
          <MethodSheet
            onClose={() => setOpenMethod(false)}
            now={(() => {
              // Where the athlete is in their own plan: the week by date,
              // the phase lengths as the plan actually has them.
              const weeks = (serverPlan?.weeks || []).filter((w) => w?.sessions?.length);
              if (!weeks.length) return null;
              const thisWk = +startOfWeek(new Date());
              const idx = weeks.findIndex((w) => +startOfWeek(parseLocalDate(w.sessions[0].date)) === thisWk);
              if (idx < 0) return null;
              const shares = { base: 0, build: 0, specific: 0, taper: 0 };
              for (const w of weeks) { if (shares[w.phase] != null) shares[w.phase] += 1; }
              const ph = weeks[idx].phase || "";
              return { weekIdx: idx + 1, totalWeeks: weeks.length, phaseLabel: ph ? ph.charAt(0).toUpperCase() + ph.slice(1) : "", shares };
            })()}
          />
        ) : null}

        {/* ADJUST GOAL MODAL */}
        {openAdjustGoal ? (() => {
          const aGoal = goals.find((g) => g.status === "planned" && g.priority === "A") || null;
          return (
            <AdjustGoalSheet
              goal={{ name: aGoal?.name, distanceKm: goalDistanceKm, typeLabel: goalSaved?.type, terrain: aGoal?.terrain || goalSaved?.terrain, date: goalSaved?.date || "", timeStr: goalTimeStr }}
              suggestedTimeStr={suggestedGoalTimeStr}
              planTrajectory={planTrajectory}
              fmtFinish={fmtFinishHM}
              fmtDelta={fmtDeltaHM}
              saving={adjustSaving}
              error={adjustError}
              onSave={saveAdjustedGoal}
              onClose={() => setOpenAdjustGoal(false)}
            />
          );
        })() : null}

      {/* ===== PLAN GENERATOR FULL-PAGE FLOW ===== */}
      {showPlanGenerator && (
        <GoalWizard
          initialGate={wizardGate ? wizardGate.gate : null}
          prefill={wizardGate ? wizardGate.prefill : null}
          onClose={() => {
            // A refused rebuild from Goal & plan opened the wizard on the
            // gate; leaving it goes back there with the current plan intact.
            const fromRebuild = wizardFromRebuildRef.current;
            wizardFromRebuildRef.current = false;
            setShowPlanGenerator(false);
            setWizardGate(null);
            if (fromRebuild) setOpenGoal(true);
          }}
          onSubmit={wizardSubmit}
          onComplete={(plan) => {
            wizardFromRebuildRef.current = false;
            commitPreviewPlan(plan);
            setShowPlanGenerator(false);
            setWizardGate(null);
            if (plan?.raceDate && plan?.raceDistance) {
              const distLabel = goalTypeFromKm(plan.raceDistance);
              setGoalSaved((prev) => ({
                ...(prev || {}),
                type: distLabel,
                distanceKm: Number(plan.raceDistance) || null,
                date: plan.raceDate,
                elevationGain: Number(plan.elevationGain) || 0,
                savedAt: Date.now(),
                planAnchorDate: prev?.planAnchorDate ?? +startOfWeek(new Date()),
              }));
            }
            setTimeout(() => refreshServerMetrics(), 200);
          }}
        />
      )}
    </div>
  );

  if (authLoading) {
    return (
      <div className="loginScreen">
        <div className="loginScreen__inner"><img src="/logo.svg" height="40" alt="RESET Ultra" className="loginScreen__logo" /></div>
      </div>
    );
  }
  if (!authUser) {
    /* The story runs BEFORE the sign-in screen: it is the reason to sign up,
       so putting it behind the sign-up would be the wrong way round. Skipping
       it, or finishing it, writes the flag and lands on the same screen. */
    if (showStory) return <Onboarding onDone={() => setShowStory(false)} />;
    return <LoginScreen auth={authInfo} initialError={authError} betaLocked={betaLocked} onBetaUnlocked={() => refreshMe()} />;
  }
  return pageContent;
}
